/**
 * Internationalization System
 * Supported: English (en), Deutsch (de), Čeština (cs), Slovenčina (sk)
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type Language = 'en' | 'de' | 'cs' | 'sk';

export const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
];

/* ═══════════════════════════════════════════════════════════════
   TRANSLATION KEYS — English (Default)
   ═══════════════════════════════════════════════════════════════ */

const en: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.back': 'Go back',
  'nav.library': 'Library',
  'nav.favorites': 'Favorites',
  'nav.categories': 'Categories',
  'nav.workspaces': 'Workspaces',
  'nav.collections': 'Collections',
  'nav.notifications': 'Notifications',
  'nav.settings': 'Settings',
  'nav.history': 'History',
  'nav.trash': 'Trash',
  'nav.users': 'Users',
  'nav.usersRoles': 'Users & Roles',
  'nav.teams': 'Teams',
  'nav.feedback': 'Feedback',
  'nav.subscription': 'Subscription',
  'nav.pricing': 'Pricing',
  'nav.logout': 'Logout',
  'nav.auditTrail': 'Audit Trail',
  'nav.accounting': 'Accounting',
  'nav.smartHub': 'Smart Hub',
  'nav.aiAssistant': 'AI Assistant',
  'nav.duplicateFinder': 'Duplicate Finder',
  'nav.hiddenFolders': 'Hidden Folders',
  'nav.importExport': 'Import / Export',
  'nav.sectionMenu': 'Menu',
  'nav.sectionOther': 'Other',
  'nav.sectionBilling': 'Billing',
  'nav.searchPlaceholder': 'Search sections…',
  'nav.searchNoResults': 'No sections found',
  'action.save': 'Save',
  'action.saveChanges': 'Save Changes',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.edit': 'Edit',
  'action.add': 'Add',
  'action.create': 'Create',
  'action.search': 'Search',
  'action.filter': 'Filter',
  'action.filters': 'Filters',
  'action.export': 'Export',
  'action.import': 'Import',
  'action.refresh': 'Refresh',
  'action.close': 'Close',
  'action.confirm': 'Confirm',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.loading': 'Loading...',
  'action.saving': 'Saving...',
  'action.selectAll': 'Select All',
  'action.deselectAll': 'Deselect All',
  'action.enableAll': 'Enable All',
  'action.disableAll': 'Disable All',
  'action.view': 'View',
  'action.move': 'Move',
  'action.restore': 'Restore',
  'action.remove': 'Remove',
  'action.clear': 'Clear',
  'action.clearAll': 'Clear All',
  'action.done': 'Done',
  'action.apply': 'Apply',
  'action.reset': 'Reset',
  'action.resetFilters': 'Reset Filters',
  'action.upgrade': 'Upgrade',
  'action.download': 'Download',
  'action.upload': 'Upload',
  'action.submit': 'Submit',
  'action.continue': 'Continue',
  'action.getStarted': 'Get Started',
  'action.learnMore': 'Learn More',
  'action.viewAll': 'View All',
  'action.showMore': 'Show More',
  'action.showLess': 'Show Less',
  'action.hideStats': 'Hide Stats',
  'action.showStats': 'Show Stats',
  'action.reply': 'Reply',
  'action.post': 'Post',
  'action.posting': 'Posting...',

  // Intervals
  'interval.day': 'Day',
  'interval.week': 'Week',
  'interval.month': 'Month',
  'interval.quarter': 'Quarter',
  'interval.year': 'Year',

  // Sort Options
  'sort.newestFirst': 'Newest First',
  'sort.oldestFirst': 'Oldest First',
  'sort.nameAZ': 'Name (A-Z)',
  'sort.nameZA': 'Name (Z-A)',
  'sort.highestValue': 'Highest Value',
  'sort.lowestValue': 'Lowest Value',
  'sort.recentlyUpdated': 'Recently Updated',
  'sort.mostItems': 'Most Items',

  // Item Fields
  'item.name': 'Name',
  'item.namePlaceholder': 'Enter item name',
  'item.description': 'Description',
  'item.descriptionPlaceholder': 'Enter description',
  'item.category': 'Category',
  'item.selectCategory': 'Select category',
  'item.condition': 'Condition',
  'item.location': 'Location',
  'item.locationPlaceholder': 'Where is this item stored?',
  'item.purchasePrice': 'Purchase Price',
  'item.purchaseDate': 'Purchase Date',
  'item.estimatedValue': 'Estimated Value',
  'item.aiValue': 'AI Value',
  'item.tags': 'Tags',
  'item.tagsPlaceholder': 'Add a tag and press Enter',
  'item.images': 'Images',
  'item.chooseImages': 'Choose Images (up to 50)',
  'item.addMore': 'Add More',
  'item.createdAt': 'Created',
  'item.updatedAt': 'Updated',

  // Item Modal
  'itemModal.editItem': 'Edit Item',
  'itemModal.addNewItem': 'Add New Item',
  'itemModal.updateItem': 'Update Item',
  'itemModal.createItem': 'Create Item',
  'itemModal.saving': 'Saving...',
  'itemModal.aiAnalyzing': 'AI analyzing...',
  'itemModal.aiSuggestions': 'AI Suggestions',
  'itemModal.purchasePlace': 'Purchase Place',
  'itemModal.purchasePlacePlaceholder': 'Store, website, or seller',
  'itemModal.currency': 'Currency',
  'itemModal.purchaseDateTime': 'Purchase Date & Time',
  'itemModal.selectDate': 'Select date',
  'itemModal.timeOptional': 'Time (optional)',
  'itemModal.noTime': 'No time',
  'itemModal.timezone': 'Timezone',
  'itemModal.autoDetected': 'Auto-detected',
  'itemModal.highMatch': 'High match',
  'itemModal.mediumMatch': 'Medium match',
  'itemModal.lowMatch': 'Low match',
  'itemModal.add': 'Add',

  // Conditions
  'condition.new': 'New',
  'condition.likeNew': 'Like New',
  'condition.excellent': 'Excellent',
  'condition.good': 'Good',
  'condition.fair': 'Fair',
  'condition.poor': 'Poor',
  'condition.all': 'All Conditions',

  // Advanced Filters
  'filter.advancedFilters': 'Advanced Filters',
  'filter.condition': 'Condition',
  'filter.conditionAll': 'All',
  'filter.valueRange': 'Value Range',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.purchaseDate': 'Purchase Date',
  'filter.from': 'From',
  'filter.to': 'To',
  'filter.aiValueTrend': 'AI Value Trend',
  'filter.trendAll': 'All',
  'filter.appreciating': 'Appreciating',
  'filter.stable': 'Stable',
  'filter.depreciating': 'Depreciating',
  'filter.locations': 'Locations',
  'filter.tags': 'Tags',
  'filter.additionalOptions': 'Additional Options',
  'filter.hasAIValue': 'Has AI Value Prediction',
  'filter.favoritesOnly': 'Favorites Only',
  'filter.sortBy': 'Sort By',
  'filter.newestFirst': 'Newest First',
  'filter.oldestFirst': 'Oldest First',
  'filter.nameAZ': 'Name (A-Z)',
  'filter.nameZA': 'Name (Z-A)',
  'filter.highestValue': 'Highest Value',
  'filter.lowestValue': 'Lowest Value',
  'filter.bestCondition': 'Best Condition',
  'filter.worstCondition': 'Worst Condition',
  'filter.resetAll': 'Reset All',
  'filter.applyFilters': 'Apply Filters',

  // Collections — delete confirm
  'collections.deleteConfirmDesc': 'Items in this collection will not be deleted.',

  // Collections — library search
  'collections.addFromLibrary': 'Add from Library',
  'collections.searchLibraryPlaceholder': 'Search your items...',
  'collections.noLibraryResults': 'No items found',
  'collections.orAddManually': 'or add manually',
  'collections.searchTab': 'Search',
  'collections.browseAllTab': 'Browse All',
  'collections.filterItems': 'Filter items...',
  'collections.allCategories': 'All',
  'collections.allConditions': 'All conditions',
  'collections.searchHint': 'Type at least 2 characters to search',
  'collections.itemsTab': 'Items',
  'collections.addItemsTab': 'Add Items',
  'collections.searchItems': 'Search items...',

  // Categories
  'categories.title': 'Categories',
  'categories.addCategory': 'Add Category',
  'categories.editCategory': 'Edit Category',
  'categories.deleteCategory': 'Delete Category',
  'categories.noCategories': 'No categories yet',
  'categories.noCategoriesFound': 'No categories found',
  'categories.totalItems': 'Total Items',
  'categories.totalValue': 'Total Value',
  'categories.avgValue': 'Avg. Value',
  'categories.sortBy': 'Sort by:',
  'categories.name': 'Name',
  'categories.items': 'Items',
  'categories.color': 'Color',
  'categories.icon': 'Icon',
  'categories.subtitle': 'Organize your items into collections',
  'categories.showStats': 'Show Stats',
  'categories.hideStats': 'Hide Stats',
  'categories.newCategory': 'New Category',
  'categories.categories': 'Categories',
  'categories.avgValuePerItem': 'Avg Value/Item',
  'categories.mostValuable': 'Most Valuable',
  'categories.conditionDistribution': 'Condition Distribution',
  'categories.searchPlaceholder': 'Search categories...',
  'categories.itemCount': 'Item Count',
  'categories.dateCreated': 'Date Created',
  'categories.customizeDefault': 'Customize Default Category',
  'categories.createNew': 'Create New Category',
  'categories.infoBanner': 'You can customize default categories to fit your needs. Changes will be saved.',
  'categories.categoryName': 'Category Name *',
  'categories.enterName': 'Enter category name',
  'categories.description': 'Description',
  'categories.describeCategory': 'Describe this category',
  'categories.searchIcons': 'Search icons...',
  'categories.updateCategory': 'Update Category',
  'categories.createCategory': 'Create Category',
  'categories.cancel': 'Cancel',
  'categories.default': 'Default',
  'categories.noDescription': 'No description',
  'categories.avg': 'Avg:',
  'categories.tryDifferent': 'Try a different search term',
  'categories.createToOrganize': 'Create categories to organize your items',
  'categories.recentItems': 'Recent items',
  'categories.viewAllInLibrary': 'View All in Library',
  'categories.loadingItems': 'Loading items...',
  'categories.noItemsInCategory': 'No items in this category.',
  'categories.cannotDeleteWithItems': 'Cannot delete category with {count} item(s). Please move or delete the items first.',
  'categories.confirmDelete': 'Are you sure you want to delete this category?',
  'categories.confirmDeleteDefault': 'This is a default category. Are you sure you want to delete it? You can always recreate it later.',
  'categories.duplicateTitle': 'Duplicate category',
  'categories.loading': 'Loading categories...',

  // Item Detail
  'itemDetail.photos': 'photos',
  'itemDetail.noImage': 'No image',
  'itemDetail.removeFromFavorites': 'Remove from favorites',
  'itemDetail.addToFavorites': 'Add to favorites',
  'itemDetail.added': 'Added',
  'itemDetail.uncategorized': 'Uncategorized',
  'itemDetail.info': 'Info',
  'itemDetail.files': 'Files',
  'itemDetail.comments': 'Comments',
  'itemDetail.log': 'Log',
  'itemDetail.description': 'Description',
  'itemDetail.noDescription': 'No description provided.',
  'itemDetail.location': 'Location',
  'itemDetail.notSet': 'Not set',
  'itemDetail.purchasePlace': 'Purchase Place',
  'itemDetail.purchased': 'Purchased',
  'itemDetail.purchasePrice': 'Purchase Price',
  'itemDetail.condition': 'Condition',
  'itemDetail.tags': 'Tags',
  'itemDetail.aiPrediction': 'AI Value Prediction',
  'itemDetail.highConfidence': 'High confidence',
  'itemDetail.mediumConfidence': 'Medium confidence',
  'itemDetail.lowConfidence': 'Low confidence',
  'itemDetail.basisPurchase': 'Based on purchase price & condition',
  'itemDetail.basisCategory': 'Based on category average',
  'itemDetail.basisAI': 'AI estimate',
  'itemDetail.previousPrediction': 'Previous prediction',
  'itemDetail.recalculate': 'Recalculate',
  'itemDetail.analyzing': 'Analyzing item data...',
  'itemDetail.tryAgain': 'Try again',
  'itemDetail.aiEstimateDesc': 'Get an AI-powered value estimate based on item data, category, and condition.',
  'itemDetail.predictValue': 'Predict Value',
  'itemDetail.itemInfo': 'Item Info',
  'itemDetail.created': 'Created',
  'itemDetail.lastUpdated': 'Last Updated',
  'itemDetail.itemId': 'Item ID',
  'itemDetail.uploadFile': 'Upload File',
  'itemDetail.uploading': 'Uploading...',
  'itemDetail.deselectAll': 'Deselect All',
  'itemDetail.selectAll': 'Select All',
  'itemDetail.downloadSelected': 'Download {count} Selected',
  'itemDetail.noFiles': 'No files attached yet',
  'itemDetail.addComment': 'Add a comment...',
  'itemDetail.posting': 'Posting...',
  'itemDetail.post': 'Post',
  'itemDetail.loadingComments': 'Loading comments...',
  'itemDetail.loadingFiles': 'Loading files...',
  'itemDetail.loadingHistory': 'Loading history...',
  'itemDetail.edited': '(edited)',
  'itemDetail.editHistory': 'Edit History',
  'itemDetail.save': 'Save',
  'itemDetail.cancel': 'Cancel',
  'itemDetail.noComments': 'No comments yet. Be the first to comment!',
  'itemDetail.noHistory': 'No history available for this item yet.',
  'itemDetail.changesTracked': 'Changes will be tracked here automatically.',
  'itemDetail.itemCreated': 'Item Created',
  'itemDetail.updatedField': 'Updated {field}',
  'itemDetail.itemDeleted': 'Item Deleted',
  'itemDetail.locked': 'Locked',
  'itemDetail.changePin': 'Change Pin',
  'itemDetail.removeLock': 'Remove Lock',
  'itemDetail.lockItem': 'Lock Item',
  'itemDetail.edit': 'Edit',
  'itemDetail.delete': 'Delete',
  'itemDetail.conditionNew': 'New',
  'itemDetail.conditionLikeNew': 'Like New',
  'itemDetail.conditionExcellent': 'Excellent',
  'itemDetail.conditionGood': 'Good',
  'itemDetail.conditionFair': 'Fair',
  'itemDetail.conditionPoor': 'Poor',
  'itemDetail.conditionNA': 'N/A',
  'itemDetail.viewFullSize': 'View full size',

  // Lock Modal
  'lockModal.lockItem': 'Lock Item',
  'lockModal.unlockItem': 'Unlock Item',
  'lockModal.verifyPassword': 'Verify Password',
  'lockModal.changeLockPassword': 'Change Lock Password',
  'lockModal.lockDesc': 'Set a password to protect this item. It will be hidden and require the password to view.',
  'lockModal.unlockDesc': 'Enter the password to permanently unlock this item.',
  'lockModal.verifyDesc': 'Enter the password to view this locked item.',
  'lockModal.changeDesc': 'Change the lock password for this item.',
  'lockModal.removeLock': 'Remove Lock',
  'lockModal.removeLockDesc': 'This will remove the lock from this item without requiring a password.',
  'lockModal.currentPassword': 'Current Password',
  'lockModal.password': 'Password',
  'lockModal.newPassword': 'New Password',
  'lockModal.confirmPassword': 'Confirm Password',
  'lockModal.confirmNewPassword': 'Confirm New Password',
  'lockModal.choosePlaceholder': 'Choose a password (min 4 chars)',
  'lockModal.enterPlaceholder': 'Enter password',
  'lockModal.newPlaceholder': 'New password (min 4 chars)',
  'lockModal.reenterPlaceholder': 'Re-enter password',
  'lockModal.processing': 'Processing...',
  'lockModal.verify': 'Verify',
  'lockModal.changePassword': 'Change Password',
  'lockModal.minCharsError': 'Password must be at least 4 characters',
  'lockModal.mismatchError': 'Passwords do not match',
  'lockModal.newMinCharsError': 'New password must be at least 4 characters',
  'lockModal.newMismatchError': 'New passwords do not match',
  'lockModal.operationFailed': 'Operation failed. Please try again.',

  // Favorites
  'favorites.title': 'Favorites',
  'favorites.noFavorites': 'No favorites yet',
  'favorites.noFavoritesDesc': 'Click the heart icon on items to add them to your favorites list',
  'favorites.browseLibrary': 'Browse Library',
  'favorites.shown': 'shown',
  'favorites.averageValue': 'Average Value',
  'favorites.minPrice': 'Min Price',
  'favorites.maxPrice': 'Max Price',
  'favorites.location': 'Location',
  'favorites.allLocations': 'All Locations',
  'favorites.tag': 'Tag',
  'favorites.allTags': 'All Tags',
  'favorites.loading': 'Loading favorites...',
  'favorites.searchPlaceholder': 'Search favorites...',
  'favorites.filters': 'Filters',
  'favorites.toggleView': 'Toggle view',
  'favorites.export': 'Export',
  'favorites.exportCSV': 'Export CSV',
  'favorites.items': 'Items',
  'favorites.totalValue': 'Total Value',
  'favorites.average': 'Average',
  'favorites.category': 'Category',
  'favorites.condition': 'Condition',
  'favorites.allCategories': 'All Categories',
  'favorites.allConditions': 'All Conditions',
  'favorites.reset': 'Reset',
  'favorites.sortMostRecent': 'Most Recent',
  'favorites.sortPrice': 'Price',
  'favorites.sortName': 'Name',
  'favorites.sortCondition': 'Condition',
  'favorites.sortLocation': 'Location',
  'favorites.all': 'All',
  'favorites.remove': 'Remove',
  'favorites.removeFromFavorites': 'Remove from favorites',
  'favorites.removeConfirm': 'Remove {count} items from favorites?',
  'favorites.noMatch': 'No items match filters',
  'favorites.noMatchDesc': 'Try adjusting your filters or search query',
  'favorites.addFromCollection': 'Mark items as favorites from your collection to see them here',

  // Default Category Names
  'categoryName.Electronics': 'Electronics',
  'categoryName.Furniture': 'Furniture',
  'categoryName.Books': 'Books',
  'categoryName.Clothing': 'Clothing',
  'categoryName.Jewelry': 'Jewelry',
  'categoryName.Sports': 'Sports',
  'categoryName.Art & Collectibles': 'Art & Collectibles',
  'categoryName.Tools': 'Tools',
  'categoryDesc.Electronics & gadgets': 'Electronics & gadgets',
  'categoryDesc.Furniture & decor': 'Furniture & decor',
  'categoryDesc.Books & media': 'Books & media',
  'categoryDesc.Clothing & accessories': 'Clothing & accessories',
  'categoryDesc.Jewelry & watches': 'Jewelry & watches',
  'categoryDesc.Sports & outdoors': 'Sports & outdoors',
  'categoryDesc.Art & collectibles': 'Art & collectibles',
  'categoryDesc.Tools & equipment': 'Tools & equipment',

  // Unit of Measure
  'unit.title': 'Unit of Measure',
  'unit.type': 'Unit Type',
  'unit.subtype': 'Unit',
  'unit.quantity': 'Quantity',
  'unit.selectType': 'Select type...',
  'unit.selectUnit': 'Select unit...',
  'unit.enterQuantity': 'Enter quantity...',
  'unit.customUnit': 'Custom unit name',
  'unit.type.weight': 'Weight',
  'unit.type.length': 'Length',
  'unit.type.volume': 'Volume',
  'unit.type.area': 'Area',
  'unit.type.quantity': 'Quantity',
  'unit.type.digital': 'Digital Storage',
  'unit.type.time': 'Time',
  'unit.type.energy': 'Energy',
  'unit.type.temperature': 'Temperature',
  'unit.type.speed': 'Speed',
  'unit.type.custom': 'Custom',
  'unit.sub.mg': 'Milligrams (mg)',
  'unit.sub.g': 'Grams (g)',
  'unit.sub.kg': 'Kilograms (kg)',
  'unit.sub.t': 'Tonnes (t)',
  'unit.sub.oz': 'Ounces (oz)',
  'unit.sub.lb': 'Pounds (lb)',
  'unit.sub.mm': 'Millimeters (mm)',
  'unit.sub.cm': 'Centimeters (cm)',
  'unit.sub.m': 'Meters (m)',
  'unit.sub.km': 'Kilometers (km)',
  'unit.sub.in': 'Inches (in)',
  'unit.sub.ft': 'Feet (ft)',
  'unit.sub.yd': 'Yards (yd)',
  'unit.sub.mi': 'Miles (mi)',
  'unit.sub.ml': 'Milliliters (ml)',
  'unit.sub.cl': 'Centiliters (cl)',
  'unit.sub.dl': 'Deciliters (dl)',
  'unit.sub.l': 'Liters (L)',
  'unit.sub.hl': 'Hectoliters (hL)',
  'unit.sub.floz': 'Fluid ounces (fl oz)',
  'unit.sub.cup': 'Cups',
  'unit.sub.pt': 'Pints (pt)',
  'unit.sub.qt': 'Quarts (qt)',
  'unit.sub.gal': 'Gallons (gal)',
  'unit.sub.sqcm': 'Square cm (cm²)',
  'unit.sub.sqm': 'Square meters (m²)',
  'unit.sub.ha': 'Hectares (ha)',
  'unit.sub.sqkm': 'Square km (km²)',
  'unit.sub.sqft': 'Square feet (ft²)',
  'unit.sub.sqyd': 'Square yards (yd²)',
  'unit.sub.ac': 'Acres (ac)',
  'unit.sub.sqmi': 'Square miles (mi²)',
  'unit.sub.pcs': 'Pieces (pcs)',
  'unit.sub.pairs': 'Pairs',
  'unit.sub.sets': 'Sets',
  'unit.sub.packs': 'Packs',
  'unit.sub.boxes': 'Boxes',
  'unit.sub.rolls': 'Rolls',
  'unit.sub.sheets': 'Sheets',
  'unit.sub.bottles': 'Bottles',
  'unit.sub.cans': 'Cans',
  'unit.sub.bags': 'Bags',
  'unit.sub.dozens': 'Dozens (dz)',
  'unit.sub.b': 'Bytes (B)',
  'unit.sub.kb': 'Kilobytes (KB)',
  'unit.sub.mb': 'Megabytes (MB)',
  'unit.sub.gb': 'Gigabytes (GB)',
  'unit.sub.tb': 'Terabytes (TB)',
  'unit.sub.sec': 'Seconds (s)',
  'unit.sub.min': 'Minutes (min)',
  'unit.sub.hr': 'Hours (h)',
  'unit.sub.day': 'Days (d)',
  'unit.sub.wk': 'Weeks (wk)',
  'unit.sub.mo': 'Months (mo)',
  'unit.sub.yr': 'Years (yr)',
  'unit.sub.wh': 'Watt-hours (Wh)',
  'unit.sub.kwh': 'Kilowatt-hours (kWh)',
  'unit.sub.cal': 'Calories (cal)',
  'unit.sub.kcal': 'Kilocalories (kcal)',
  'unit.sub.j': 'Joules (J)',
  'unit.sub.kj': 'Kilojoules (kJ)',
  'unit.sub.c': 'Celsius (°C)',
  'unit.sub.f': 'Fahrenheit (°F)',
  'unit.sub.k': 'Kelvin (K)',
  'unit.sub.kmh': 'km/h',
  'unit.sub.mph': 'mph',
  'unit.sub.ms': 'm/s',
  'unit.sub.kn': 'Knots (kn)',

  // Trash
  'trash.title': 'Trash',
  'trash.noItems': 'Trash is empty',
  'trash.noItemsDesc': 'Deleted items will appear here',
  'trash.emptyTrash': 'Empty Trash',
  'trash.restoreAll': 'Restore All',
  'trash.deleted': 'Deleted',
  'trash.expiresIn': 'Expires in',
  'trash.days': 'days',
  'trash.permanentDelete': 'Are you sure? This action cannot be undone.',
  'trash.loading': 'Loading trash...',
  'trash.items': '{count} items',
  'trash.warning': 'Items in trash will be permanently deleted after 30 days.',
  'trash.emptyTitle': 'Trash is Empty',
  'trash.emptyDesc': 'Deleted items will appear here. Items are permanently removed after 30 days.',
  'trash.confirmDelete': 'Are you sure you want to permanently delete this item? This cannot be undone.',
  'trash.confirmEmpty': 'Are you sure you want to permanently delete ALL items in trash? This cannot be undone.',
  'trash.unknown': 'Unknown',
  'trash.daysAgo': '{count} days ago',
  'trash.hoursAgo': '{count} hours ago',
  'trash.justNow': 'Just now',
  'trash.restore': 'Restore',
  'trash.delete': 'Delete',

  // Library
  'library.title': 'Library',
  'library.noItemsDesc': 'Start by adding your first item to the library',
  'library.searchPlaceholder': 'Search items...',
  'library.addItem': 'Add Item',
  'library.allCategories': 'All Categories',
  'library.newestFirst': 'Newest First',
  'library.oldestFirst': 'Oldest First',
  'library.nameAZ': 'Name (A-Z)',
  'library.nameZA': 'Name (Z-A)',
  'library.highestValue': 'Highest Value',
  'library.lowestValue': 'Lowest Value',
  'library.advancedFilters': 'Advanced Filters',
  'library.gridView': 'Grid view',
  'library.listView': 'List view',
  'library.edit': 'Edit',
  'library.move': 'Move',
  'library.delete': 'Delete',
  'library.value': 'Value',
  'library.original': 'Original',
  'library.aiValue': 'AI Value',
  'library.noItems': 'No items found',
  'library.noItemsSearch': 'Try adjusting your search or filters',
  'library.noItemsStart': 'Start by adding your first item',
  'library.addFirstItem': '+ Add First Item',
  'library.moveItem': 'Move Item',
  'library.moving': 'Moving:',
  'library.currentCategory': 'Current category:',
  'library.uncategorized': 'Uncategorized',
  'library.selectCategory': 'Select new category:',
  'library.loading': 'Loading library...',
  'library.confirmDelete': 'Are you sure you want to delete this item?',
  'library.moveFailed': 'Failed to move item',
  'library.lockedItem': 'This item is locked',

  // Dashboard
  'dashboard.analytics': 'Analytics',
  'dashboard.itemsTracked': 'items tracked',
  'dashboard.categoriesLabel': 'categories',
  'dashboard.customize': 'Customize',
  'dashboard.widgets': 'Widgets',
  'dashboard.save': 'Save',
  'dashboard.loading': 'Loading analytics…',
  'dashboard.addWidget': 'Add Widget',
  'dashboard.totalItems': 'Total Items',
  'dashboard.portfolioValue': 'Portfolio Value',
  'dashboard.favorites': 'Favorites',
  'dashboard.avgItemValue': 'Avg. Item Value',
  'dashboard.newThisPeriod': 'new this period',
  'dashboard.vsLastPeriod': 'vs last period',
  'dashboard.marked': 'marked',
  'dashboard.perItem': 'per item',
  'dashboard.day': 'Day',
  'dashboard.week': 'Week',
  'dashboard.month': 'Month',
  'dashboard.quarter': 'Quarter',
  'dashboard.year': 'Year',
  'dashboard.valueTrend': 'Value Trend',
  'dashboard.itemsAdded': 'Items Added',
  'dashboard.conditionBreakdown': 'Condition Breakdown',
  'dashboard.categoryDistribution': 'Category Distribution',
  'dashboard.priceDistribution': 'Price Range Distribution',
  'dashboard.mostValuable': 'Most Valuable Items',
  'dashboard.byLocation': 'Items by Location',
  'dashboard.cumulativeValue': 'Cumulative Value',
  'dashboard.favoritesOverTime': 'Favorites Over Time',
  'dashboard.topTags': 'Top Tags',
  'dashboard.purchaseSources': 'Purchase Sources',
  'dashboard.avgByCategory': 'Avg Value by Category',
  'dashboard.valueTrendDesc': 'Total value added per period',
  'dashboard.itemsAddedDesc': 'Number of new items per period',
  'dashboard.conditionDesc': 'Distribution by item condition',
  'dashboard.categoryDesc': 'Items grouped by category',
  'dashboard.priceDistDesc': 'How item values are distributed across price ranges',
  'dashboard.mostValuableDesc': 'Your highest-value items',
  'dashboard.byLocationDesc': 'Items grouped by storage location',
  'dashboard.cumulativeDesc': 'Running total of portfolio value over time',
  'dashboard.favTrendDesc': 'Favorite items added per period',
  'dashboard.topTagsDesc': 'Most frequently used tags',
  'dashboard.purchaseDesc': 'Where items were purchased',
  'dashboard.avgCatDesc': 'Average item value per category',
  'dashboard.chooseChartType': 'Choose Chart Type',
  'dashboard.configureData': 'Configure Data',
  'dashboard.previewCreate': 'Preview & Create',
  'dashboard.stepType': '1. Type',
  'dashboard.stepData': '2. Data',
  'dashboard.stepPreview': '3. Preview',
  'dashboard.barChart': 'Bar Chart',
  'dashboard.pieChart': 'Pie Chart',
  'dashboard.lineChart': 'Line Chart',
  'dashboard.metricCard': 'Metric Card',
  'dashboard.barChartDesc': 'Compare values across categories',
  'dashboard.pieChartDesc': 'Show proportions of a whole',
  'dashboard.lineChartDesc': 'Track trends over time',
  'dashboard.metricCardDesc': 'Display a single key metric',
  'dashboard.chartTitle': 'Chart Title *',
  'dashboard.description': 'Description',
  'dashboard.value': 'Value',
  'dashboard.dataPoints': 'Data Points',
  'dashboard.addDataPoint': 'Add Data Point',
  'dashboard.back': 'Back',
  'dashboard.nextConfigure': 'Next: Configure Data',
  'dashboard.nextPreview': 'Next: Preview',
  'dashboard.createChart': 'Create Chart',
  'dashboard.dashboardWidgets': 'Dashboard Widgets',
  'dashboard.widgetHint': 'Toggle visibility · drag to reorder',

  // Subscription extras
  'subscription.noWorkspaceError': 'No workspace selected. Please select a workspace first.',
  'subscription.checkoutError': 'Failed to create checkout session. Please try again.',
  'subscription.redirecting': 'Redirecting to Stripe...',

  // Notification Settings
  'notifSettings.loading': 'Loading notification settings...',
  'notifSettings.title': 'Notification Settings',
  'notifSettings.subtitle': 'Control when and how you receive notifications',
  'notifSettings.disableAll': 'Disable All',
  'notifSettings.enableAll': 'Enable All',
  'notifSettings.saving': 'Saving...',
  'notifSettings.saveChanges': 'Save Changes',
  'notifSettings.saved': 'Notification settings saved!',
  'notifSettings.saveFailed': 'Failed to save settings',
  'notifSettings.typesTitle': 'Notification Types',
  'notifSettings.typesDesc': 'Choose which notifications you want to receive.',
  'notifSettings.emailToggleTitle': 'Send email for this notification',
  'notifSettings.itemUpdates': 'Item Updates',
  'notifSettings.itemUpdatesDesc': 'Get notified when items you\'re watching are updated',
  'notifSettings.comments': 'Comments',
  'notifSettings.commentsDesc': 'Get notified when someone comments on your items',
  'notifSettings.mentions': 'Mentions',
  'notifSettings.mentionsDesc': 'Get notified when someone mentions you in a comment',
  'notifSettings.duplicateDetection': 'Duplicate Detection',
  'notifSettings.duplicateDetectionDesc': 'Get notified when AI detects potential duplicate items',
  'notifSettings.valuePredictions': 'Value Predictions',
  'notifSettings.valuePredictionsDesc': 'Get notified when AI predicts value changes for your items',
  'notifSettings.securityAlerts': 'Security Alerts',
  'notifSettings.securityAlertsDesc': 'Important security notifications (login attempts, password changes)',
  'notifSettings.emailPrefs': 'Email Preferences',
  'notifSettings.emailPrefsDesc': 'Manage email notifications',
  'notifSettings.weeklyDigest': 'Weekly Digest',
  'notifSettings.weeklyDigestDesc': 'Receive a weekly summary of activity in your workspaces',
  'notifSettings.marketingEmails': 'Marketing Emails',
  'notifSettings.marketingEmailsDesc': 'Product updates, tips, and promotional content',
  'notifSettings.channels': 'Delivery Channels',
  'notifSettings.channelsDesc': 'Choose how you want to receive notifications',
  'notifSettings.email': 'Email',
  'notifSettings.emailDesc': 'Receive notifications via email',
  'notifSettings.push': 'Push Notifications',
  'notifSettings.pushDesc': 'Receive push notifications on your devices',
  'notifSettings.inApp': 'In-App Notifications',
  'notifSettings.inAppDesc': 'See notifications in the app',
  'notifSettings.quietHours': 'Quiet Hours',
  'notifSettings.quietHoursDesc': 'Pause notifications during specific hours',
  'notifSettings.enableQuietHours': 'Enable Quiet Hours',
  'notifSettings.quietHoursSilenced': 'Notifications will be silenced during the specified time',
  'notifSettings.startTime': 'Start Time',
  'notifSettings.endTime': 'End Time',
  'notifSettings.timeTo': 'to',
  'notifSettings.unsavedChanges': 'You have unsaved changes',

  // Time
  'time.today': 'Today',
  'time.yesterday': 'Yesterday',
  'time.daysAgo': 'days ago',
  'time.justNow': 'just now',
  'time.minutesAgo': 'minutes ago',
  'time.hoursAgo': 'hours ago',

  // Settings
  'settings.title': 'Settings',
  'settings.subtitle': 'Manage your account preferences and security',
  'settings.general': 'General',
  'settings.appearance': 'Appearance',
  'settings.security': 'Security',
  'settings.accessibility': 'Accessibility',
  'settings.privacy': 'Privacy',
  'settings.theme': 'Theme',
  'settings.themeDesc': 'Switch between light and dark mode',
  'settings.language': 'Language',
  'settings.languageDesc': 'Choose your preferred language',
  'settings.darkMode': 'Dark Mode',
  'settings.lightMode': 'Light Mode',
  'settings.darkTheme': 'Dark',
  'settings.lightTheme': 'Light',
  'settings.profile': 'Profile Information',
  'settings.profileDesc': 'Update your personal information',
  'settings.saved': 'Settings saved!',
  'settings.saveFailed': 'Failed to save settings',

  // Accounting & Assets
  'accounting.title': 'Asset Accounting',
  'accounting.subtitle': 'Manage fixed assets, depreciation, and allocations',
  'accounting.assetRegister': 'Asset Register',
  'accounting.allItems': 'All Items',
  'accounting.transactions': 'Transactions',
  'accounting.createAsset': 'Create Asset',
  'accounting.assetDetails': 'Asset Details',
  'accounting.trackedAssets': 'Tracked Assets',
  'accounting.totalBookValue': 'Total Book Value',
  'accounting.accumulatedDepreciation': 'Accumulated Depreciation',
  'accounting.acquisitionCost': 'Acquisition Cost',
  'accounting.assetNumber': 'Asset Number',
  'accounting.assetClass': 'Asset Class',
  'accounting.subClass': 'Sub-Class',
  'accounting.depreciationMethod': 'Depreciation Method',
  'accounting.usefulLifeMonths': 'Useful Life (Months)',
  'accounting.salvageValue': 'Salvage Value',
  'accounting.acquisitionDate': 'Acquisition Date',
  'accounting.bookValue': 'Book Value',
  'accounting.fairMarketValue': 'Fair Market Value',
  'accounting.status': 'Status',
  'accounting.active': 'Active',
  'accounting.disposed': 'Disposed',
  'accounting.writtenOff': 'Written Off',
  'accounting.heldForSale': 'Held for Sale',
  'accounting.impaired': 'Impaired',
  'accounting.transferred': 'Transferred',
  'accounting.straightLine': 'Straight Line',
  'accounting.decliningBalance': 'Declining Balance',
  'accounting.doubleDeclining': 'Double Declining',
  'accounting.sumOfYears': 'Sum of Years Digits',
  'accounting.unitsOfProduction': 'Units of Production',
  'accounting.noDepreciation': 'No Depreciation',
  'accounting.classBuildings': 'Buildings',
  'accounting.classMachinery': 'Machinery',
  'accounting.classEquipment': 'Equipment',
  'accounting.classVehicles': 'Vehicles',
  'accounting.classFurniture': 'Furniture',
  'accounting.classComputers': 'Computers',
  'accounting.classSoftware': 'Software',
  'accounting.classCollectibles': 'Collectibles',
  'accounting.classArt': 'Art',
  'accounting.classJewelry': 'Jewelry',
  'accounting.classRealEstate': 'Real Estate',
  'accounting.classInvestments': 'Investments',
  'accounting.classOther': 'Other',
  'accounting.monthlyDepreciation': 'Monthly Depreciation',
  'accounting.yearlyDepreciation': 'Yearly Depreciation',
  'accounting.remainingLife': 'Remaining Life',
  'accounting.percentDepreciated': 'Percent Depreciated',
  'accounting.selectItem': 'Select Item',
  'accounting.selectMethod': 'Select Depreciation Method',
  'accounting.selectClass': 'Select Asset Class',
  'accounting.selectStatus': 'Select Status',
  'accounting.recordImpairment': 'Record Impairment',
  'accounting.recordRevaluation': 'Record Revaluation',
  'accounting.disposeAsset': 'Dispose Asset',
  'accounting.impairmentAmount': 'Impairment Amount',
  'accounting.revaluedAmount': 'Revalued Amount',
  'accounting.disposalDate': 'Disposal Date',
  'accounting.disposalProceeds': 'Disposal Proceeds',
  'accounting.notes': 'Notes',
  'accounting.depreciationSchedule': 'Depreciation Schedule',
  'accounting.transactionHistory': 'Transaction History',
  'accounting.noAssets': 'No assets found',
  'accounting.noItems': 'No items found',
  'accounting.noTransactions': 'No transactions found',
  'accounting.searchAssets': 'Search assets...',
  'accounting.searchItems': 'Search items...',
  'accounting.filterByStatus': 'Filter by Status',
  'accounting.filterByMethod': 'Filter by Method',
  'accounting.filterByClass': 'Filter by Class',
  'accounting.filterByCategory': 'Filter by Category',
  'accounting.showTracked': 'Tracked Only',
  'accounting.showUntracked': 'Untracked Only',
  'accounting.sortBy': 'Sort by',
  'accounting.sortByName': 'Name',
  'accounting.sortByCost': 'Cost',
  'accounting.sortByBook': 'Book Value',
  'accounting.sortByDepreciation': 'Depreciation',
  'accounting.sortByDate': 'Date',
  'accounting.page': 'Page',
  'accounting.of': 'of',
  'accounting.showing': 'Showing',
  'accounting.assets': 'assets',
  'accounting.items': 'items',
  'accounting.months': 'months',
  'accounting.years': 'years',
  'accounting.assetCreated': 'Asset created successfully',
  'accounting.assetUpdated': 'Asset updated successfully',
  'accounting.assetDisposed': 'Asset disposed successfully',
  'accounting.impairmentRecorded': 'Impairment recorded successfully',
  'accounting.revaluationRecorded': 'Revaluation recorded successfully',
  'accounting.costAllocation': 'Cost Allocation',
  'accounting.allocations': 'Allocations',
  'accounting.department': 'Department',
  'accounting.project': 'Project',
  'accounting.costCenter': 'Cost Center',
  'accounting.allocatedTo': 'Allocated To',
  'accounting.allocationPercent': 'Allocation %',
  'accounting.addAllocation': 'Add Allocation',
  'accounting.removeAllocation': 'Remove Allocation',
  'accounting.totalAllocation': 'Total Allocation',
  'accounting.mustBe100': 'Total allocation must equal 100%',
  'accounting.selectDepartment': 'Select Department',
  'accounting.selectProject': 'Select Project',
  'accounting.selectCostCenter': 'Select Cost Center',
  'accounting.noDepartments': 'No departments found',
  'accounting.noProjects': 'No projects found',
  'accounting.noCostCenters': 'No cost centers found',
  'accounting.createDepartment': 'Create Department',
  'accounting.createProject': 'Create Project',
  'accounting.createCostCenter': 'Create Cost Center',
  'accounting.departmentName': 'Department Name',
  'accounting.projectName': 'Project Name',
  'accounting.costCenterName': 'Cost Center Name',
  'accounting.departmentCode': 'Department Code',
  'accounting.projectCode': 'Project Code',
  'accounting.costCenterCode': 'Cost Center Code',
  'accounting.manager': 'Manager',
  'accounting.budget': 'Budget',
  'accounting.startDate': 'Start Date',
  'accounting.endDate': 'End Date',
  'accounting.allocationReport': 'Allocation Report',
  'accounting.byDepartment': 'By Department',
  'accounting.byProject': 'By Project',
  'accounting.byCostCenter': 'By Cost Center',
  'accounting.exportReport': 'Export Report',
  'accounting.depreciationReport': 'Depreciation Report',
  'accounting.assetValuation': 'Asset Valuation',
  'accounting.gainLoss': 'Gain/Loss',

  // Accounting KPIs
  'accounting.kpi.trackedAssets': 'Tracked Assets',
  'accounting.kpi.itemsWithAccounting': 'Items with accounting',
  'accounting.kpi.totalBookValue': 'Total Book Value',
  'accounting.kpi.currentNetValue': 'Current net value',
  'accounting.kpi.accDepreciation': 'Acc. Depreciation',
  'accounting.kpi.ofCost': 'of cost',
  'accounting.kpi.acquisitionCost': 'Acquisition Cost',
  'accounting.kpi.originalInvestment': 'Original investment',

  // Accounting Tabs
  'accounting.tabs.register': 'Asset Register',
  'accounting.tabs.items': 'All Items',
  'accounting.tabs.transactions': 'Transactions',
  'accounting.allStatus': 'All Status',
  'accounting.allMethods': 'All Methods',
  'accounting.allClasses': 'All Classes',
  'accounting.allCategories': 'All Categories',
  'accounting.allConditions': 'All Conditions',
  'accounting.allItemsFilter': 'All Items',
  'accounting.trackedOnly': 'Tracked Only',
  'accounting.untrackedOnly': 'Untracked Only',
  'accounting.allTypes': 'All Types',
  'accounting.itemCol': 'Item',
  'accounting.acquisitionCol': 'Acquisition',
  'accounting.bookValueCol': 'Book Value',
  'accounting.depreciationCol': 'Depreciation',
  'accounting.methodCol': 'Method',
  'accounting.statusCol': 'Status',
  'accounting.actionsCol': 'Actions',
  'accounting.categoryCol': 'Category',
  'accounting.purchasePriceCol': 'Purchase Price',
  'accounting.dateCol': 'Date',
  'accounting.trackingCol': 'Tracking',
  'accounting.actionCol': 'Action',
  'accounting.txDateCol': 'Date',
  'accounting.txAssetCol': 'Asset',
  'accounting.txTypeCol': 'Type',
  'accounting.txDescCol': 'Description',
  'accounting.txAmountCol': 'Amount',
  'accounting.bookValueAfterCol': 'Book Value After',
  'accounting.noTrackedYet': 'No tracked assets yet',
  'accounting.noAssetsMatch': 'No assets match your filters',
  'accounting.startTracking': 'Start by adding items to your asset register.',
  'accounting.adjustFilters': 'Try adjusting your search or filters.',
  'accounting.browseItems': 'Browse Items',
  'accounting.clearFilters': 'Clear Filters',
  'accounting.noItemsInLib': 'No items found',
  'accounting.noItemsMatch': 'No items match your filters',
  'accounting.addItemsFirst': 'Add items to your library first.',
  'accounting.noTxYet': 'No transactions yet',
  'accounting.noTxMatch': 'No transactions match your filters',
  'accounting.startDepreciation': 'Start tracking assets and processing depreciation.',
  'accounting.adjustTxFilter': 'Try adjusting your search or filter.',
  'accounting.trackedAssetCount': 'Tracked Assets ({count})',
  'accounting.filteredAssets': '{filtered} of {total} assets',
  'accounting.yourItemsCount': 'Your Items ({count})',
  'accounting.filteredItems': '{filtered} of {total} items',
  'accounting.txCount': 'Transactions ({count})',
  'accounting.filteredTx': '{filtered} of {total} transactions',
  'accounting.unnamed': 'Unnamed',
  'accounting.unclassified': 'Unclassified',
  'accounting.unknown': 'Unknown',
  'accounting.valueInfo': 'Value Information',
  'accounting.depInfo': 'Depreciation',
  'accounting.accDep': 'Acc. Depreciation',
  'accounting.monthly': 'Monthly',
  'accounting.remainingMonths': 'Remaining',
  'accounting.depPercent': 'Depreciated',
  'accounting.recentTx': 'Recent Transactions',
  'accounting.depreciate': 'Depreciate',
  'accounting.impairmentAction': 'Impairment',
  'accounting.revalueAction': 'Revalue',
  'accounting.disposeAction': 'Dispose',
  'accounting.trackAsset': 'Track Asset',
  'accounting.tipAutoFill': 'Tip: Use the "All Items" tab for easier tracking.',
  'accounting.itemIdLabel': 'Item ID *',
  'accounting.pasteItemId': 'Paste item ID',
  'accounting.selectClassOption': 'Select class…',
  'accounting.impairmentDesc': 'Impairment reduces book value when recoverable amount falls below carrying.',
  'accounting.impairmentAmountLabel': 'Impairment Amount *',
  'accounting.reasonLabel': 'Reason',
  'accounting.reasonPlaceholder': 'Reason for impairment',
  'accounting.effectiveDateLabel': 'Effective Date',
  'accounting.revalDesc': 'Adjusts asset value based on professional appraisal or market assessment.',
  'accounting.newAppraisedValue': 'New Appraised Value *',
  'accounting.appraiserName': 'Appraiser Name',
  'accounting.valuationMethodLabel': 'Valuation Method',
  'accounting.valuationMethodPlaceholder': 'e.g. Market Comparison',
  'accounting.notesLabel': 'Notes',
  'accounting.optionalNotes': 'Optional notes',
  'accounting.disposeDesc': 'Disposing removes the asset from active tracking.',
  'accounting.disposalAmountLabel': 'Sale / Disposal Amount',
  'accounting.reasonSelect': 'Reason',
  'accounting.sold': 'Sold',
  'accounting.donated': 'Donated',
  'accounting.scrapped': 'Scrapped',
  'accounting.stolenLost': 'Stolen / Lost',
  'accounting.tradedIn': 'Traded In',
  'accounting.otherReason': 'Other',
  'accounting.confirmDisposal': 'Confirm Disposal',
  'accounting.creating': 'Creating…',
  'accounting.create': 'Create Asset',
  'accounting.searchAssetsPlaceholder': 'Search assets by name, number, class…',
  'accounting.searchItemsPlaceholder': 'Search items by name, description, location…',
  'accounting.searchTxPlaceholder': 'Search transactions…',
  'accounting.tracked': 'Tracked',
  'accounting.notTracked': 'Not tracked',
  'accounting.viewDetail': 'View',
  'accounting.trackItem': 'Track',
  'accounting.optional': 'Optional',
  'accounting.expectedValue': 'Expected value at end of useful life',
  'accounting.cancel': 'Cancel',

  // Workspaces
  'workspaces.title': 'Workspaces',
  'workspaces.myWorkspaces': 'My Workspaces',
  'workspaces.create': 'Create Workspace',
  'workspaces.switchTo': 'Switch to',
  'workspaces.settings': 'Workspace Settings',
  'workspaces.members': 'Members',
  'workspaces.personal': 'Personal',
  'workspaces.organization': 'Organization',
  'workspaces.name': 'Workspace Name',
  'workspaces.description': 'Description',
  'workspaces.type': 'Type',
  'workspaces.items': 'items',
  'workspaces.totalItems': 'Total Items',
  'workspaces.active': 'Active',
  'workspaces.viewAll': 'View All Workspaces',
  'workspaces.noWorkspaces': 'No workspaces yet',
  'workspaces.createFirst': 'Create your first workspace',
  'workspaces.searchPlaceholder': 'Search workspaces...',
  'workspaces.sortByRecent': 'Recent',
  'workspaces.sortByName': 'Name',
  'workspaces.sortByItems': 'Items',
  'workspaces.sortByMembers': 'Members',
  'workspaces.hub': 'Workspace Hub',
  'workspaces.manageSubtitle': 'Manage, organize, and collaborate across all your workspaces in one place',
  'workspaces.new': 'New Workspace',
  'workspaces.organizations': 'Organizations',
  'workspaces.loading': 'Loading workspaces...',
  'workspaces.welcome': 'Welcome to your Workspace Hub',
  'workspaces.welcomeSubtitle': 'Create your first workspace to start organizing items, inviting team members, and collaborating.',
  'workspaces.personalDesc': 'Private workspace just for you',
  'workspaces.orgDesc': 'Collaborate with your team',
  'workspaces.enterName': 'Enter workspace name...',
  'workspaces.creating': 'Creating...',
  'workspaces.getStarted': 'Get Started',
  'workspaces.activeWorkspace': 'Active Workspace',
  'workspaces.switchToWorkspace': 'Switch to Workspace',
  'workspaces.openDashboard': 'Open Dashboard',
  'workspaces.createNew': 'Create New Workspace',
  'workspaces.personalOrOrg': 'Personal or Organization',
  'workspaces.noFound': 'No workspaces found',
  'workspaces.clearSearch': 'Clear Search',
  'workspaces.edit': 'Edit Workspace',
  'workspaces.delete': 'Delete Workspace',
  'workspaces.cannotBeUndone': 'This action cannot be undone',
  'workspaces.deletePermanently': 'Delete Permanently',
  'workspaces.cancel': 'Cancel',
  'workspaces.save': 'Save Changes',
  'workspaces.continue': 'Continue',
  'workspaces.back': 'Back',
  'workspaces.typeStep': 'Type',
  'workspaces.detailsStep': 'Details',
  'workspaces.reviewStep': 'Review',
  'workspaces.chooseType': 'Choose the type of workspace you want to create',
  'workspaces.personalWorkspace': 'Personal Workspace',
  'workspaces.configurePersonal': 'Configure your personal workspace',
  'workspaces.configureOrg': 'Configure your organization',
  'workspaces.reviewAndCreate': 'Review and create your workspace',
  'workspaces.whatYouGet': "What you'll get:",
  'workspaces.nameRequired': 'Workspace Name *',
  'workspaces.descLabel': 'Description',
  'workspaces.whatFor': 'What is this workspace for?',
  'workspaces.privateSecure': 'Private & Secure',
  'workspaces.fullItemMgmt': 'Full Item Management',
  'workspaces.personalAnalytics': 'Personal Analytics',
  'workspaces.teamCollaboration': 'Team Collaboration',
  'workspaces.roleBasedAccess': 'Role-Based Access',
  'workspaces.orgDashboard': 'Org Dashboard',
  'workspaces.inviteMembers': 'Invite Members',
  'workspaces.unlimitedItems': 'Unlimited Items',
  'workspaces.private': 'Private',
  'workspaces.itemMgmtCategories': 'Item management with categories',
  'workspaces.aiCategorization': 'AI-powered categorization',
  'workspaces.analyticsDashboard': 'Analytics dashboard',
  'workspaces.teamInvitations': 'Team member invitations',
  'workspaces.rbac': 'Role-based access control',
  'workspaces.auditTrail': 'Organization audit trail',
  'workspaces.untitled': 'Untitled',
  'workspaces.createWorkspace': 'Create Workspace',
  'workspaces.switchThis': 'Switch to this',
  'workspaces.viewOrg': 'View Org',
  'workspaces.convertToOrg': 'Convert to Org',
  'workspaces.star': 'Star',
  'workspaces.unstar': 'Unstar',
  'workspaces.grid': 'Grid',
  'workspaces.list': 'List',

  // Workspace Settings
  'wsSettings.loading': 'Loading workspace...',
  'wsSettings.notFound': 'Workspace not found',
  'wsSettings.backToWorkspaces': 'Back to Workspaces',
  'wsSettings.savedSuccess': 'Settings saved successfully',
  'wsSettings.inviteSent': 'Invitation sent successfully',
  'wsSettings.roleUpdated': 'Role updated successfully',
  'wsSettings.confirmRemoveMember': 'Are you sure you want to remove this member?',
  'wsSettings.memberRemoved': 'Member removed successfully',
  'wsSettings.inviteRevoked': 'Invitation revoked',
  'wsSettings.ownershipTransferred': 'Ownership transferred successfully',
  'wsSettings.typeNameToConfirm': 'Please type the workspace name to confirm',
  'wsSettings.convertedSuccess': 'Workspace converted to organization!',
  'wsSettings.organization': 'Organization',
  'wsSettings.personalWorkspace': 'Personal Workspace',
  'wsSettings.owner': 'Owner',
  'wsSettings.general': 'General',
  'wsSettings.members': 'Members',
  'wsSettings.convertTab': 'Convert',
  'wsSettings.dangerZone': 'Danger Zone',
  'wsSettings.generalSettings': 'General Settings',
  'wsSettings.workspaceName': 'Workspace Name',
  'wsSettings.enterName': 'Enter workspace name',
  'wsSettings.description': 'Description',
  'wsSettings.descriptionPlaceholder': 'Describe this workspace...',
  'wsSettings.workspaceId': 'Workspace ID',
  'wsSettings.created': 'Created',
  'wsSettings.items': 'Items',
  'wsSettings.categories': 'Categories',
  'wsSettings.saving': 'Saving...',
  'wsSettings.saveChanges': 'Save Changes',
  'wsSettings.inviteMember': 'Invite Member',
  'wsSettings.emailPlaceholder': 'Email address',
  'wsSettings.sendInvite': 'Send Invite',
  'wsSettings.pendingInvitations': 'Pending Invitations',
  'wsSettings.revoke': 'Revoke',
  'wsSettings.currentMembers': 'Current Members',
  'wsSettings.loadingMembers': 'Loading members...',
  'wsSettings.remove': 'Remove',
  'wsSettings.convertToOrg': 'Convert to Organization',
  'wsSettings.upgradeTitle': 'Upgrade to Organization',
  'wsSettings.upgradeDesc': 'Unlock collaboration features by converting your personal workspace into an organization.',
  'wsSettings.featInvite': 'Invite Members',
  'wsSettings.featInviteDesc': 'Add team members and collaborate together',
  'wsSettings.featRBAC': 'Role-Based Access',
  'wsSettings.featRBACDesc': 'Assign roles and control permissions',
  'wsSettings.featTeams': 'Teams & Groups',
  'wsSettings.featTeamsDesc': 'Organize members into teams',
  'wsSettings.convertNote': 'This action is irreversible. Your data and settings will be preserved.',
  'wsSettings.convertNow': 'Convert Now',
  'wsSettings.transferOwnership': 'Transfer Ownership',
  'wsSettings.transferDesc': 'Transfer this workspace to another member.',
  'wsSettings.transfer': 'Transfer',
  'wsSettings.deleteWorkspace': 'Delete Workspace',
  'wsSettings.deleteDesc': 'Permanently delete this workspace and all its data.',
  'wsSettings.delete': 'Delete',
  'wsSettings.cannotBeUndone': 'This action cannot be undone!',
  'wsSettings.transferWarn': 'You will lose owner access and become a regular member.',
  'wsSettings.newOwner': 'New Owner',
  'wsSettings.selectMember': 'Select a member',
  'wsSettings.cancel': 'Cancel',
  'wsSettings.transferring': 'Transferring...',
  'wsSettings.deleteWarning': 'This will permanently delete workspace',
  'wsSettings.typeToConfirm': 'Type workspace name to confirm:',
  'wsSettings.deleting': 'Deleting...',
  'wsSettings.deletePermanently': 'Delete Permanently',
  'wsSettings.converting': 'Converting...',

  // Notifications
  'notifications.title': 'Notifications',
  'notifications.markAsRead': 'Mark as Read',
  'notifications.markAllAsRead': 'Mark All as Read',
  'notifications.delete': 'Delete',
  'notifications.deleteAll': 'Delete All',
  'notifications.clearAll': 'Clear All',
  'notifications.unread': 'Unread',
  'notifications.all': 'All',
  'notifications.noNotifications': 'No notifications',
  'notifications.noUnread': 'No unread notifications',
  'notifications.today': 'Today',
  'notifications.yesterday': 'Yesterday',
  'notifications.older': 'Older',
  'notifications.justNow': 'Just now',
  'notifications.minutesAgo': '{count}m ago',
  'notifications.hoursAgo': '{count}h ago',
  'notifications.daysAgo': '{count}d ago',
  'notifications.search': 'Search notifications...',
  'notifications.settings': 'Notification Settings',
  'notifications.types.itemUpdate': 'Item Updates',
  'notifications.types.comment': 'Comments',
  'notifications.types.aiSuggestion': 'AI Suggestions',
  'notifications.types.duplicate': 'Duplicates',
  'notifications.types.valueChanged': 'Value Changes',
  'notifications.types.security': 'Security',
  'notifications.types.storage': 'Storage',
  'notifications.types.mention': 'Mentions',
  'notifications.types.share': 'Shared',
  'notifications.types.system': 'System',
  'notifications.loading': 'Loading notifications...',
  'notifications.noMatching': 'No matching notifications',
  'notifications.tryAdjusting': 'Try adjusting your filters',
  'notifications.deleteConfirm': 'Delete all notifications? This cannot be undone.',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.markUnread': 'Mark as unread',
  'notifications.allCaughtUp': "You're all caught up!",
  'notifications.showAll': 'Show All',
  'notifications.empty': 'No notifications',
  'notifications.viewAll': 'View all notifications',
  'notifications.clickToView': 'Click to view',
  'notifications.moreNotifications': '+{count} more notifications',

  // Two-Factor Authentication
  'twofa.title': 'Two-Factor Authentication',
  'twofa.subtitle': 'Add an extra layer of security to your account',
  'twofa.enabled': '2FA Enabled',
  'twofa.disabled': '2FA Disabled',
  'twofa.enable': 'Enable 2FA',
  'twofa.disable': 'Disable 2FA',
  'twofa.status': 'Status',
  'twofa.setup': 'Setup',
  'twofa.setupInstructions': 'Scan the QR code with your authenticator app',
  'twofa.manualEntry': 'Or enter this code manually',
  'twofa.verificationCode': 'Verification Code',
  'twofa.enterCode': 'Enter 6-digit code',
  'twofa.verify': 'Verify',
  'twofa.backupCodes': 'Backup Codes',
  'twofa.backupCodesInfo': 'Save these codes in a safe place',
  'twofa.backupCodesRemaining': '{count} backup codes remaining',
  'twofa.regenerateBackupCodes': 'Regenerate Backup Codes',
  'twofa.downloadBackupCodes': 'Download Codes',
  'twofa.copyCode': 'Copy Code',
  'twofa.copied': 'Copied!',
  'twofa.setupSuccess': 'Two-factor authentication enabled successfully',
  'twofa.disableSuccess': 'Two-factor authentication disabled',
  'twofa.invalidCode': 'Invalid verification code',
  'twofa.required': '2FA code required',
  'twofa.step1': 'Step 1: Install Authenticator App',
  'twofa.step2': 'Step 2: Scan QR Code',
  'twofa.step3': 'Step 3: Verify',
  'twofa.apps': 'Recommended apps: Google Authenticator, Authy, Microsoft Authenticator',
  'twofa.loading': 'Loading 2FA settings...',
  'twofa.whyEnable': 'Why enable 2FA?',
  'twofa.protectsAccount': 'Protects your account even if password is compromised',
  'twofa.timeBasedCodes': 'Uses time-based codes from authenticator apps',
  'twofa.backupAccess': 'Backup codes for emergency access',
  'twofa.enableTwoFactor': 'Enable Two-Factor Authentication',
  'twofa.accountProtected': 'Your account is protected by 2FA',
  'twofa.scanQrCode': 'Step 1: Scan QR Code',
  'twofa.scanQrDesc': 'Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code',
  'twofa.saveBackupCodes': 'Step 2: Save Backup Codes',
  'twofa.saveBackupDesc': 'Save these codes in a safe place. Each code can only be used once.',
  'twofa.verifyStep': 'Step 3: Verify',
  'twofa.verifyDesc': 'Enter the 6-digit code from your authenticator app',
  'twofa.cantScan': "Can't scan? Enter this code manually:",
  'twofa.verifyAndEnable': 'Verify & Enable',
  'twofa.cancelSetup': 'Cancel Setup',
  'twofa.disableConfirm': 'Disable Two-Factor Authentication?',
  'twofa.lessSecure': 'Your account will be less secure without 2FA.',
  'twofa.enterCodeConfirm': 'Enter a verification code to confirm:',
  'twofa.codeOrBackup': 'Code or backup code',
  'twofa.regenerateTitle': 'Regenerate Backup Codes',
  'twofa.regenerateWarning': 'This will invalidate all existing backup codes and generate new ones.',
  'twofa.generateNew': 'Generate New Codes',
  'twofa.newCodes': 'New Backup Codes',
  'twofa.saveNewCodes': 'Save these codes in a safe place. Your old codes are now invalid.',
  'twofa.download': 'Download',
  'twofa.done': 'Done',
  'twofa.twofaEnabled': '2FA Enabled!',
  'twofa.saveBeforeClose': 'Make sure you\'ve saved your backup codes before closing.',
  'twofa.savedMyCodes': "I've Saved My Codes",

  // Misc
  'misc.noData': 'No data available',
  'misc.error': 'An error occurred',
  'misc.success': 'Success',
  'misc.to': 'to',
  'misc.yes': 'Yes',
  'misc.no': 'No',

  // Audit Trail
  'audit.title': 'Audit Trail',
  'audit.subtitle': 'Track all changes and activities across your account',
  'audit.tableView': 'Table View',
  'audit.timelineView': 'Timeline View',
  'audit.dashboardView': 'Organization Dashboard',
  'audit.filters': 'Filters',
  'audit.exportCsv': 'Export CSV',
  'audit.search': 'Search by user, action, or resource...',
  'audit.totalEvents': 'Total Events',
  'audit.eventsToday': 'Events Today',
  'audit.activeUsers': 'Active Users',
  'audit.topActions': 'Top Actions',
  'audit.actionType': 'Action Type',
  'audit.allActions': 'All Actions',
  'audit.resourceType': 'Resource Type',
  'audit.allResources': 'All Resources',
  'audit.startDate': 'Start Date',
  'audit.endDate': 'End Date',
  'audit.clear': 'Clear',
  'audit.time': 'Time',
  'audit.user': 'User',
  'audit.action': 'Action',
  'audit.resource': 'Resource',
  'audit.details': 'Details',
  'audit.source': 'Source',
  'audit.noLogs': 'No audit logs found',
  'audit.emptyState': 'Activity will appear here as actions are performed',
  'audit.loading': 'Loading...',
  'audit.loadingTimeline': 'Loading timeline...',
  'audit.loadingDashboard': 'Loading organization dashboard...',
  'audit.page': 'Page {current} of {total}',
  'audit.events': 'events',
  'audit.actions': 'actions',
  'audit.system': 'System',
  'audit.unknown': 'Unknown',
  'audit.empty': '(empty)',
  'audit.revert': 'Revert this change',
  'audit.reverting': 'Reverting...',
  'audit.revertToPrevious': 'Revert to Previous Value',
  'audit.logDetails': 'Audit Log Details',
  'audit.eventInfo': 'Event Information',
  'audit.timestamp': 'Timestamp',
  'audit.resourceId': 'Resource ID',
  'audit.resourceName': 'Resource Name',
  'audit.userInfo': 'User Information',
  'audit.userId': 'User ID',
  'audit.role': 'Role',
  'audit.fieldChange': 'Field Change',
  'audit.oldValue': 'Old Value',
  'audit.newValue': 'New Value',
  'audit.techDetails': 'Technical Details',
  'audit.ipAddress': 'IP Address',
  'audit.device': 'Device',
  'audit.userAgent': 'User Agent',
  'audit.metadata': 'Additional Metadata',
  'audit.revertChange': 'Revert Change',
  'audit.dashboardTitle': 'Organization Audit Dashboard',
  'audit.lastDays': 'Last {days} days',
  'audit.today': 'Today',
  'audit.mostActiveUsers': 'Most Active Users',
  'audit.recentSecurity': 'Recent Security Events',
  'audit.actionDistribution': 'Action Distribution',
  'audit.noData': 'Organization dashboard data is not available',
  'audit.minutesAgo': '{count}m ago',
  'audit.hoursAgo': '{count}h ago',
  'audit.daysAgo': '{count}d ago',
  'audit.justNow': 'just now',
  // Audit action groups
  'audit.group.items': 'Items',
  'audit.group.imagesFiles': 'Images & Files',
  'audit.group.comments': 'Comments',
  'audit.group.categories': 'Categories',
  'audit.group.auth': 'Authentication',
  'audit.group.security': 'Security',
  'audit.group.members': 'Members & Invites',
  'audit.group.ai': 'AI & Automation',
  'audit.group.notifications': 'Notifications & System',
  'audit.group.bulk': 'Bulk & Export',
  // Audit action names
  'audit.action.itemCreated': 'Item Created',
  'audit.action.itemUpdated': 'Item Updated',
  'audit.action.itemDeleted': 'Item Deleted',
  'audit.action.itemRestored': 'Item Restored',
  'audit.action.favoriteAdded': 'Favorite Added',
  'audit.action.favoriteRemoved': 'Favorite Removed',
  'audit.action.imageUploaded': 'Image Uploaded',
  'audit.action.imageDeleted': 'Image Deleted',
  'audit.action.fileUploaded': 'File Uploaded',
  'audit.action.fileDeleted': 'File Deleted',
  'audit.action.commentAdded': 'Comment Added',
  'audit.action.commentEdited': 'Comment Edited',
  'audit.action.commentDeleted': 'Comment Deleted',
  'audit.action.categoryCreated': 'Category Created',
  'audit.action.categoryUpdated': 'Category Updated',
  'audit.action.categoryDeleted': 'Category Deleted',
  'audit.action.login': 'Login',
  'audit.action.logout': 'Logout',
  'audit.action.registration': 'Registration',
  'audit.action.loginFailed': 'Login Failed',
  'audit.action.passwordChanged': 'Password Changed',
  'audit.action.passwordReset': 'Password Reset Requested',
  'audit.action.profileUpdated': 'Profile Updated',
  'audit.action.twoFaEnabled': '2FA Enabled',
  'audit.action.twoFaDisabled': '2FA Disabled',
  'audit.action.accountDeleted': 'Account Deleted',
  'audit.action.unauthorizedAccess': 'Unauthorized Access',
  'audit.action.roleChanged': 'Role Changed',
  'audit.action.permissionChanged': 'Permission Changed',
  'audit.action.invitationSent': 'Invitation Sent',
  'audit.action.memberRemoved': 'Member Removed',
  'audit.action.aiCategorization': 'AI Categorization',
  'audit.action.aiDuplicate': 'AI Duplicate Detection',
  'audit.action.aiValue': 'AI Value Prediction',
  'audit.action.aiAccepted': 'AI Suggestion Accepted',
  'audit.action.aiRejected': 'AI Suggestion Rejected',
  'audit.action.notificationSent': 'Notification Sent',
  'audit.action.systemConfig': 'System Config Changed',
  'audit.action.bulkExport': 'Bulk Export',
  'audit.action.bulkImport': 'Bulk Import',
  'audit.action.bulkDelete': 'Bulk Delete',
  // Resource types
  'audit.resource.items': 'Items',
  'audit.resource.categories': 'Categories',
  'audit.resource.users': 'Users',
  'audit.resource.organization': 'Organization',
  'audit.resource.comments': 'Comments',
  'audit.resource.images': 'Images',
  'audit.resource.files': 'Files',
  'audit.resource.notifications': 'Notifications',
  'audit.resource.system': 'System',

  // Auth Pages
  'auth.welcomeBack': 'Welcome Back',
  'auth.signInSubtitle': 'Sign in to continue to your workspace',
  'auth.email': 'Email Address',
  'auth.emailPlaceholder': 'your@email.com',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signIn': 'Sign In',
  'auth.signingIn': 'Signing In...',
  'auth.forgotPassword': 'Forgot password?',
  'auth.noAccount': "Don't have an account?",
  'auth.createOne': 'Create one',
  'auth.orContinueWith': 'or',
  'auth.rememberMe': 'Remember me',
  'auth.secureLogin': 'Secure & encrypted login',
  'auth.benefit1': 'Smart item management',
  'auth.benefit2': 'Real-time collaboration',
  'auth.benefit3': 'Advanced analytics',
  'auth.emailRequired': 'Email and password are required',
  'auth.loginFailed': 'Login failed. Please try again.',

  // Signup
  'auth.createAccount': 'Create Account',
  'auth.signUpSubtitle': 'Start your journey with us today',
  'auth.firstName': 'First Name',
  'auth.firstNamePlaceholder': 'John',
  'auth.lastName': 'Last Name',
  'auth.lastNamePlaceholder': 'Doe',
  'auth.confirmPassword': 'Confirm Password',
  'auth.signUp': 'Create Account',
  'auth.signingUp': 'Creating Account...',
  'auth.hasAccount': 'Already have an account?',
  'auth.signInLink': 'Sign in',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordTooShort': 'Password must be at least 8 characters',
  'auth.fillAllFields': 'Please fill in all required fields',
  'auth.signupFailed': 'Signup failed. Please try again.',
  'auth.emailAlreadyExists': 'An account with this email address already exists. Please sign in or use a different email.',
  'auth.networkError': 'Unable to connect to the server. Please check your internet connection and try again.',
  'auth.signupSuccess': 'Account created! Check your email for verification.',
  'auth.joinCommunity': 'Join our community',
  'auth.benefitSecure': 'Secure & encrypted',
  'auth.benefitVerified': 'Email verified accounts',
  'auth.benefitSmart': 'Smart categorization',
  'auth.agreeTerms': 'By signing up, you agree to our',
  'auth.termsOfService': 'Terms of Service',
  'auth.and': 'and',
  'auth.privacyPolicy': 'Privacy Policy',
  'auth.passwordStrength': 'Password strength',
  'auth.weak': 'Weak',
  'auth.fair': 'Fair',
  'auth.good': 'Good',
  'auth.strong': 'Strong',

  // Forgot Password
  'auth.forgotPasswordTitle': 'Forgot Password?',
  'auth.forgotPasswordSubtitle': "Enter your email and we'll send you a reset link",
  'auth.sendResetLink': 'Send Reset Link',
  'auth.sending': 'Sending...',
  'auth.resetLinkSent': 'Password reset link has been sent to your email',
  'auth.backToLogin': 'Back to Login',
  'auth.resetYourPassword': 'Reset Your Password',
  'auth.resetDescription': "Enter your email address and we'll send you a link to reset your password",
  'auth.emailRequired2': 'Email is required',
  'auth.sendFailed': 'Failed to send reset email',
  'auth.checkInbox': 'Check your inbox',
  'auth.resetEmailSentDesc': "We've sent a password reset link to your email address. Please check your inbox and spam folder.",

  // Reset Password
  'auth.resetPasswordTitle': 'Reset Password',
  'auth.resetPasswordSubtitle': 'Enter your new password',
  'auth.newPassword': 'New Password',
  'auth.confirmNewPassword': 'Confirm New Password',
  'auth.resetPassword': 'Reset Password',
  'auth.resetting': 'Resetting...',
  'auth.resetSuccess': 'Password reset successfully! Redirecting...',
  'auth.resetFailed': 'Password reset failed',
  'auth.invalidResetLink': 'Invalid Reset Link',
  'auth.invalidResetDesc': 'This password reset link is invalid or has expired',
  'auth.requestNewLink': 'Request New Link',
  'auth.noTokenProvided': 'No reset token provided',
  'auth.createNewPassword': 'Create New Password',
  'auth.createNewPasswordDesc': 'Choose a strong password for your account',
  'auth.tryAgain': 'Try Again',

  // Verify Email
  'auth.verifyEmail': 'Verify Your Email',
  'auth.verifyEmailDesc': 'We sent a verification link to your email address',
  'auth.checkYourInbox': 'Check Your Inbox',
  'auth.weSentVerification': 'We sent a verification link to',
  'auth.checkSpam': 'Click the link in the email to verify your account. If you don\'t see it, check your spam folder.',
  'auth.resendVerification': 'Resend Verification Email',
  'auth.resendSuccess': 'Verification email sent again! Check your inbox.',
  'auth.alreadyVerified': 'Already verified? Sign in',
  'auth.verifying': 'Verifying Your Email',
  'auth.verifyingDesc': 'Please wait while we confirm your email address...',
  'auth.emailVerified': 'Email Verified!',
  'auth.verifiedSuccess': 'Email verified successfully! Redirecting to dashboard...',
  'auth.verificationFailed': 'Verification Failed',
  'auth.resendFailed': 'Failed to resend verification email',
  'auth.goToLogin': 'Go to Login',
  'auth.goToDashboard': 'Go to Dashboard',
  'auth.enterEmailResend': 'Enter your email to resend',
  'auth.verificationSent': 'Verification email sent! Check your inbox.',

  // Support
  'auth.needHelp': 'Need help?',
  'auth.contactSupport': 'Contact Support',
  'auth.supportEmail': 'Send us an email',
  'auth.supportSubject': 'ELVOAQ Support Request',
  'auth.supportBody': 'Hello,\n\nI need assistance with my account.\n\nPlease describe your issue:',
  'auth.havingProblems': 'Having problems?',
  'auth.reachOutToUs': 'Reach out to us',
  'auth.contactUsForHelp': 'Contact us for help with your account',

  // Sidebar
  'sidebar.collapse': 'Collapse',
  'sidebar.collapseSidebar': 'Collapse sidebar',
  'sidebar.expandSidebar': 'Expand sidebar',

  // User Dropdown
  'dropdown.settings': 'Settings',
  'dropdown.subscription': 'Subscription',
  'dropdown.logout': 'Logout',
  'dropdown.openProfile': 'Open profile',

  // Settings — Profile
  'settings.profileTitle': 'Profile Information',
  'role.OWNER': 'Owner',
  'role.ADMIN': 'Super Admin',
  'role.MEMBER': 'Member',
  'role.LEAD': 'Lead',
  'role.VIEWER': 'Viewer',
  'settings.firstName': 'First Name',
  'settings.lastName': 'Last Name',
  'settings.firstNamePlaceholder': 'Enter your first name',
  'settings.lastNamePlaceholder': 'Enter your last name',
  'settings.emailAddress': 'Email Address',
  'settings.changeEmail': 'Change',
  'settings.newEmailAddress': 'New Email Address',
  'settings.enterNewEmail': 'Enter new email address',
  'settings.currentPassword': 'Current Password',
  'settings.currentPasswordConfirm': 'Enter your current password to confirm',
  'settings.updatingEmail': 'Updating...',
  'settings.updateEmail': 'Update Email',
  'settings.bio': 'Bio',
  'settings.bioPlaceholder': 'Tell us a bit about yourself...',
  'settings.defaultCurrency': 'Default Currency',
  'settings.currencyHint': 'This currency will be used for displaying converted values throughout the app.',
  'settings.savingChanges': 'Saving...',

  // Settings — Security
  'settings.securityTitle': 'Security Settings',
  'settings.changePassword': 'Change Password',
  'settings.enterCurrentPassword': 'Enter current password',
  'settings.newPassword': 'New Password',
  'settings.enterNewPassword': 'Enter new password',
  'settings.confirmPassword': 'Confirm Password',
  'settings.confirmNewPassword': 'Confirm new password',
  'settings.updatingPassword': 'Updating...',

  // Settings — Notifications
  'settings.notifPreferences': 'Notification Preferences',
  'settings.notifTypes': 'Notification Types',
  'settings.notifItemUpdates': 'Item Updates',
  'settings.notifItemUpdatesDesc': 'Get notified when items are created, updated, or deleted',
  'settings.notifComments': 'Comments',
  'settings.notifCommentsDesc': 'Notifications for new comments on your items',
  'settings.notifMentions': 'Mentions',
  'settings.notifMentionsDesc': 'Get alerted when someone mentions you',
  'settings.notifDuplicates': 'Duplicate Detection',
  'settings.notifDuplicatesDesc': 'AI-powered alerts when potential duplicates are found',
  'settings.notifValuePredictions': 'Value Predictions',
  'settings.notifValuePredictionsDesc': 'Notifications about value changes and predictions',
  'settings.notifSecurityAlerts': 'Security Alerts',
  'settings.notifSecurityAlertsDesc': 'Important security-related notifications',
  'settings.notifEmailPrefs': 'Email Preferences',
  'settings.notifWeeklyDigest': 'Weekly Digest',
  'settings.notifWeeklyDigestDesc': 'Receive a weekly summary of activity',
  'settings.notifMarketing': 'Marketing Emails',
  'settings.notifMarketingDesc': 'Updates about new features and promotions',
  'settings.notifChannels': 'Delivery Channels',
  'settings.notifEmail': 'Email Notifications',
  'settings.notifEmailDesc': 'Receive notifications via email',
  'settings.notifPush': 'Push Notifications',
  'settings.notifPushDesc': 'Browser and mobile push notifications',
  'settings.notifInApp': 'In-App Notifications',
  'settings.notifInAppDesc': 'Show notifications inside the application',
  'settings.notifQuietHours': 'Quiet Hours',
  'settings.notifEnableQuietHours': 'Enable Quiet Hours',
  'settings.notifQuietHoursDesc': 'Pause non-critical notifications during specified hours',
  'settings.notifStartTime': 'Start Time',
  'settings.notifEndTime': 'End Time',
  'settings.notifSaving': 'Saving...',
  'settings.notifSaveBtn': 'Save Notification Settings',
  'settings.loadingNotifications': 'Loading notification settings...',

  // Settings — Accessibility
  'settings.accessibilityTitle': 'Accessibility & Display',
  'settings.accessibilityDesc': 'Customize animations and interactions for your comfort and device.',
  'settings.motionAnimations': 'Motion & Animations',
  'settings.reduceMotion': 'Reduce Motion',
  'settings.reduceMotionDesc': 'Disable transitions, hover animations, and decorative motion effects across the entire app',
  'settings.reduceMotionHint': 'Also respects your operating system\'s "Reduce Motion" preference automatically.',
  'settings.touchMobile': 'Touch & Mobile',
  'settings.touchFriendly': 'Touch-Friendly Mode',
  'settings.touchFriendlyDesc': 'Replace hover-dependent interactions with tap/click alternatives. Increases button and toggle sizes for easier touch targets.',
  'settings.touchFriendlyHint': 'Recommended when using a tablet or touch-screen device.',
  'settings.mobileWarningTitle': 'Mobile Usage Notice',
  'settings.mobileWarningText': 'ELVOAQ Vault is optimized for desktop and tablet screens. On smaller mobile devices, some features may have limited functionality or require scrolling. For the best experience, we recommend using a desktop browser or tablet.',
  'settings.mobileWarningDarkHint': 'Tip: Dark mode reduces eye strain on mobile screens.',
  'settings.mobileWarningLightHint': 'Tip: Light mode may improve readability in bright environments.',

  // Settings — Privacy
  'settings.privacyTitle': 'Privacy & Data',
  'settings.privacyTab': 'Privacy',
  'settings.yourData': 'Your Data',
  'settings.yourDataDesc': 'Your personal information is protected and stored securely. We never share your data with third parties.',
  'settings.dangerZone': '⚠️ Danger Zone',
  'settings.dangerZoneDesc': 'Deleting your account is permanent and cannot be undone. All your items, data, and settings will be permanently deleted.',
  'settings.deleteAccount': 'Delete My Account',
  'settings.deleteConfirmTitle': '⚠️ Are you absolutely sure?',
  'settings.deleteConfirmDesc': 'This action cannot be undone. Please enter your password to confirm account deletion.',
  'settings.enterPassword': 'Enter your password',
  'settings.deleting': 'Deleting...',
  'settings.confirmDeleteBtn': 'Yes, Delete My Account',
  'settings.privacyPolicy': 'Privacy Policy',
  'settings.privacyPolicyDesc': 'Read our comprehensive privacy policy and terms of service.',
  'settings.viewPrivacyPolicy': 'View Privacy Policy',

  // Settings — AI
  'settings.aiTitle': 'AI Settings',
  'settings.aiTab': 'AI Provider',
  'settings.aboutTab': 'About',
  // Tutorial
  'tutorial.tourBadge': 'Platform Tour',
  'tutorial.back': 'Back',
  'tutorial.next': 'Next',
  'tutorial.skip': 'Skip Tour',
  'tutorial.getStarted': 'Get Started',
  'tutorial.welcome.title': 'Welcome to ELVOAQ',
  'tutorial.welcome.desc': 'ELVOAQ is your all-in-one digital inventory platform. Manage, track, and secure all your valuable items in one place. Let\u2019s give you a quick tour!',
  'tutorial.library.title': 'Your Item Library',
  'tutorial.library.desc': 'The Library is the heart of ELVOAQ. Add items with photos, descriptions, prices, and conditions. Powerful search and filters help you find anything instantly.',
  'tutorial.categories.title': 'Categories',
  'tutorial.categories.desc': 'Organize your items into custom categories with colors and icons \u2014 Electronics, Jewelry, Art, Documents, and more. See total value and item count per category at a glance.',
  'tutorial.favorites.title': 'Favorites',
  'tutorial.favorites.desc': 'Star your most important items for quick access. Sort, filter by location or tag, and export your favorites list as a CSV anytime.',
  'tutorial.collections.title': 'Collections',
  'tutorial.collections.desc': 'Group related items into named Collections \u2014 a travel kit, home office setup, or insurance bundle. Add any item from your library with one click.',
  'tutorial.accounting.title': 'Accounting & Assets',
  'tutorial.accounting.desc': 'Track depreciation, manage asset records, and monitor the financial value of your inventory over time with multiple depreciation methods.',
  'tutorial.ai.title': 'AI-Powered Features',
  'tutorial.ai.desc': 'Let AI work for you. Get value predictions, auto-detect duplicate items, chat with the AI assistant, and set smart reminders \u2014 all powered by advanced AI.',
  'tutorial.security.title': 'Security & Privacy',
  'tutorial.security.desc': 'Lock individual items with a password, create hidden private folders only you can see, enable two-factor authentication, and manage workspace access roles.',
  'tutorial.calendar.title': 'Calendar & Reminders',
  'tutorial.calendar.desc': 'Schedule maintenance reminders, warranty expiry alerts, insurance renewal dates, and custom events \u2014 all linked to your inventory items.',
  'tutorial.settings.title': 'Personalize Everything',
  'tutorial.settings.desc': 'Switch between dark and light mode, choose your language, set your default currency, configure notifications, and manage all your preferences from Settings.',
  'tutorial.audit.title': 'Audit Log',
  'tutorial.audit.desc': 'Monitor every action on the platform in real time. See who changed what, when, and from where \u2014 with full details, IP tracking, and the ability to revert changes.',
  'tutorial.clickHint': 'Click here to navigate to this section',
  'tutorial.done.title': 'You\u2019re All Set!',
  'tutorial.done.desc': 'You now know all the essentials of ELVOAQ. Start by adding your first item to the Library. You can replay this tour anytime from Settings \u2192 About.',
  'tutorial.startTour': 'Start Tour',
  'tutorial.exploreOwn': "I'll explore on my own",
  'tutorial.replayTour': 'Replay Tour',
  // About / Version
  'about.version': 'App Version',
  'about.releaseDate': 'Release Date',
  'about.changelog': 'Changelog',
  'about.newFeatures': 'New Features',
  'about.replayTutorial': 'Replay Tutorial',
  'about.replayTutorialDesc': 'Show the interactive platform tour again',
  'about.autoLogout': 'Auto Logout',
  'about.autoLogoutDesc': 'Automatically log out after a period of inactivity',
  'about.timeout15': '15 minutes',
  'about.timeout30': '30 minutes',
  'about.timeout60': '1 hour',
  'about.timeout120': '2 hours',
  'about.timeoutNever': 'Never (not recommended)',
  'about.privacyPolicy': 'Privacy Policy',
  'about.termsOfService': 'Terms of Service',
  'about.viewPrivacy': 'View Privacy Policy',
  'about.viewTerms': 'View Terms of Service',
  'about.legalDocuments': 'Legal Documents',
  // CAPTCHA
  'auth.captchaQuestion': 'Security check: What is {a} + {b}?',
  'auth.captchaPlaceholder': 'Enter the answer',
  'auth.captchaError': 'Incorrect answer. Please try again.',
  'settings.aiDescription': 'ELVOAQ AI is configured and active. You can use all AI-powered features including the assistant, duplicate detection, value prediction, and more.',
  'settings.aiActive': 'ELVOAQ AI Active',
  'settings.aiActiveDesc': 'Powered by our advanced AI. All features are ready to use throughout the platform.',
  'settings.aiFeatures': 'Available AI Features:',
  'settings.aiAssistant': 'AI Assistant',
  'settings.aiAssistantDesc': 'Get help managing your inventory',
  'settings.aiDuplicateDetection': 'Duplicate Detection',
  'settings.aiDuplicateDetectionDesc': 'Automatically find similar items',
  'settings.aiValuePrediction': 'Value Prediction',
  'settings.aiValuePredictionDesc': 'Estimate item values over time',
  'settings.aiSmartCategorization': 'Smart Categorization',
  'settings.aiSmartCategorizationDesc': 'Auto-suggest categories',

  // Feedback Modal
  'feedback.title': 'Send Feedback',
  'feedback.successTitle': 'Feedback Sent!',
  'feedback.successMessage': 'Thank you! Your feedback has been successfully delivered to our team.',
  'feedback.typeLabel': 'Type',
  'feedback.titleLabel': 'Title *',
  'feedback.titlePlaceholder': 'Brief summary of your feedback',
  'feedback.descriptionLabel': 'Description *',
  'feedback.descriptionPlaceholder': 'Please provide detailed information...',
  'feedback.categoryLabel': 'Category *',
  'feedback.categoryPlaceholder': 'Select a category...',
  'feedback.attachmentLabel': 'Attachment (Optional)',
  'feedback.uploadBtn': 'Upload Screenshot or Video',
  'feedback.maxFileSize': 'Max 5MB. Supported: JPG, PNG, GIF, WebP, MP4, WebM',
  'feedback.systemInfo': '📊 System Information (Auto-captured)',
  'feedback.submitBtn': 'Submit Feedback',
  'feedback.submitting': 'Submitting...',
  'feedback.bugReport': 'Bug Report',
  'feedback.bugReportDesc': 'Report a bug or error',
  'feedback.featureRequest': 'Feature Request',
  'feedback.featureRequestDesc': 'Suggest a new feature',
  'feedback.improvement': 'Improvement',
  'feedback.improvementDesc': 'Suggest an improvement',
  'feedback.question': 'Question',
  'feedback.questionDesc': 'Ask a question',
  'feedback.complaint': 'Complaint',
  'feedback.complaintDesc': 'Report an issue or concern',
  'feedback.other': 'Other',
  'feedback.otherDesc': 'General feedback',
  'feedback.fileTooLarge': 'File size must be less than 5MB',
  'feedback.invalidFileType': 'Only images (JPG, PNG, GIF, WebP) and videos (MP4, WebM) are allowed',
  'feedback.titleDescRequired': 'Title and description are required',
  'feedback.categoryRequired': 'Please select a category',
  'feedback.remove': 'Remove',
  'feedback.cancel': 'Cancel',
  'feedback.display': 'Display',
  'feedback.browser': 'Browser',
  'feedback.locationLang': 'Location & Language',
  'feedback.pageContext': 'Page Context',
  'feedback.screenResolution': 'Screen Resolution',
  'feedback.viewportSize': 'Viewport Size',
  'feedback.colorDepth': 'Color Depth',
  'feedback.userAgent': 'User Agent',
  'feedback.device': 'Device',
  'feedback.os': 'OS',
  'feedback.cookiesEnabled': 'Cookies Enabled',
  'feedback.language': 'Language',
  'feedback.timezone': 'Timezone',
  'feedback.currentUrl': 'Current URL',
  'feedback.referrer': 'Referrer',
  'feedback.onlineStatus': 'Online Status',
  'feedback.errorSubmit': 'Failed to submit feedback',

  // Feedback Categories
  'feedback.category.Dashboard': 'Dashboard',
  'feedback.category.Items & Library': 'Items & Library',
  'feedback.category.Categories': 'Categories',
  'feedback.category.Notifications': 'Notifications',
  'feedback.category.Settings': 'Settings',
  'feedback.category.Workspaces & Teams': 'Workspaces & Teams',
  'feedback.category.Authentication': 'Authentication',
  'feedback.category.Performance': 'Performance',
  'feedback.category.User Interface': 'User Interface',
  'feedback.category.Mobile Experience': 'Mobile Experience',
  'feedback.category.Data & Analytics': 'Data & Analytics',
  'feedback.category.Other': 'Other',

  // Subscription / Plans
  'subscription.title': 'Subscription',
  'subscription.overview': 'Overview',
  'subscription.plans': 'Plans',
  'subscription.storage': 'Storage',
  'subscription.billing': 'Billing',
  'subscription.header': 'Subscription & Plans',
  'subscription.headerDesc': 'Manage your plan, storage, and billing in one place',
  'subscription.loading': 'Loading subscription...',
  'subscription.currentPlan': 'Current Plan',
  'subscription.upgrade': 'Upgrade',
  'subscription.usageOverview': 'Usage Overview',
  'subscription.items': 'Items',
  'subscription.categories': 'Categories',
  'subscription.workspaces': 'Workspaces',
  'subscription.aiMessages': 'AI Messages',
  'subscription.imagesPerItem': 'Images / Item',
  'subscription.max': 'max',
  'subscription.yourPlanFeatures': 'Your Plan Features',
  'subscription.unlimited': 'Unlimited',
  'subscription.itemsLabel': 'items',
  'subscription.customCategories': 'custom categories',
  'subscription.storageLabel': 'storage',
  'subscription.imagesPerItemLabel': 'images per item',
  'subscription.workspacesLabel': 'workspace(s)',
  'subscription.aiMessagesMonth': 'AI messages/mo',
  'subscription.aiCategorization': 'AI Categorization',
  'subscription.valuePrediction': 'Value Prediction',
  'subscription.advancedAnalytics': 'Advanced Analytics',
  'subscription.prioritySupport': 'Priority Support',
  'subscription.compareAllPlans': 'Compare All Plans',
  'subscription.storageWarningPrefix': "You're using",
  'subscription.storageWarningSuffix': 'of your storage. Consider upgrading or adding storage.',
  'subscription.manageStorage': 'Manage Storage',
  'subscription.monthly': 'Monthly',
  'subscription.yearly': 'Yearly',
  'subscription.save20': 'Save 20%',
  'subscription.mostPopular': 'Most Popular',
  'subscription.currentPlanLabel': 'Current Plan',
  'subscription.free': 'Free',
  'subscription.custom': 'Custom',
  'subscription.contactSales': 'Contact Sales',
  'subscription.switch': 'Switch',
  'subscription.viewFullPricing': 'View Full Pricing & Comparison',
  'subscription.storageBreakdown': 'Storage Breakdown',
  'subscription.images': 'Images',
  'subscription.documents': 'Documents',
  'subscription.metadata': 'Metadata',
  'subscription.perMonth': '/mo',
  'subscription.perYear': '/yr',
  'subscription.savePerYear': 'Save €{amount}/yr',
  'subscription.used': 'Used',
  'subscription.freeSpace': 'Free',
  'subscription.total': 'Total',
  'subscription.needMoreSpace': 'Need More Space?',
  'subscription.addExtraStorage': 'Add extra storage to your plan',
  'subscription.addStorage': 'Add Storage',
  'subscription.saveTilde20': 'Save ~20%',
  'subscription.save20Approx': 'Save ~20%',
  'subscription.billingSummary': 'Billing Summary',
  'subscription.billingCycle': 'Billing Cycle',
  'subscription.yearlyCost': 'Yearly Cost',
  'subscription.monthlyCost': 'Monthly Cost',
  'subscription.nextBillingDate': 'Next Billing Date',
  'subscription.paymentMethod': 'Payment Method',
  'subscription.naFree': 'N/A — Free plan',
  'subscription.manageKofi': 'Manage on Ko-fi',
  'subscription.manageStripe': 'Manage Subscription',
  'subscription.downloadInvoice': 'Download Invoice',
  'subscription.cancelSubscription': 'Cancel Subscription',
  'subscription.cancelDesc': 'To cancel your subscription, manage it through Stripe. Your access will continue until the end of the current billing period.',
  'subscription.cancelOnKofi': 'Cancel on Ko-fi',
  'subscription.cancelStripe': 'Cancel Subscription',
  'subscription.paymentHistory': 'Payment History',
  'subscription.noPayments': 'No payment history yet',
  'subscription.noPaymentsDesc': 'Your billing history will appear here after your first payment',
  'subscription.needHelpBilling': 'Need Help with Billing?',
  'subscription.contactBilling': 'Contact us at',
  'subscription.billingQuestions': 'for any billing questions.',
  'subscription.securePayments': 'Secure Payments',
  'subscription.securePaymentsDesc': 'All payments are processed securely via Stripe. We never store your payment details.',
  'subscription.upgradeTo': 'Upgrade to',
  'subscription.customPricing': 'Custom Pricing',
  'subscription.whatsIncluded': "What's included:",
  'subscription.save20Yearly': 'Save 20% with yearly billing',
  'subscription.subscribeKofi': 'Subscribe on Ko-fi',
  'subscription.subscribeStripe': 'Subscribe with Stripe',
  'subscription.afterPayment': 'After payment, your plan will be upgraded within 24 hours.',
  'subscription.afterPaymentStripe': 'You will be redirected to Stripe secure checkout. Your plan will be activated automatically.',
  'subscription.secureVia': 'Secure payment via Ko-fi. Cancel anytime.',
  'subscription.secureViaStripe': 'Secure payments processed by Stripe. Cancel anytime.',
  'subscription.contactForAddon': 'Contact us',
  'subscription.contactSalesDesc': "We'll get back to you within 24 hours with a custom offer.",
  'subscription.compareFeatures': 'Compare All Features',
  'subscription.feature': 'Feature',
  'subscription.coreLimits': 'Core Limits',
  'subscription.teamMembers': 'Team Members',
  'subscription.aiFeatures': 'AI Features',
  'subscription.duplicateDetect': 'Duplicate Detection',
  'subscription.aiReportsMonth': 'AI Reports/Month',
  'subscription.security': 'Security',
  'subscription.twoFactorAuth': 'Two-Factor Auth',
  'subscription.auditLogs': 'Audit Logs',
  'subscription.sso': 'SSO',
  'subscription.support': 'Support',
  'subscription.emailSupportLabel': 'Email Support',
  'subscription.dedicatedManager': 'Dedicated Manager',
  'subscription.faqTitle': 'Frequently Asked Questions',
  'subscription.faqSwitchQ': 'Can I switch plans anytime?',
  'subscription.faqSwitchA': 'Yes! You can upgrade or downgrade at any time. When upgrading, you\'ll be prorated for the remaining time. When downgrading, the new rate applies at the next billing cycle.',
  'subscription.faqDowngradeQ': 'What happens to my data if I downgrade?',
  'subscription.faqDowngradeA': 'Your data is safe. If you exceed the new plan\'s limits, you\'ll have a 30-day grace period to reduce usage or upgrade again.',
  'subscription.faqRefundQ': 'Do you offer refunds?',
  'subscription.faqRefundA': 'Yes, we offer a 30-day money-back guarantee for all paid plans. If you\'re not satisfied, contact us for a full refund.',
  'subscription.faqPaymentQ': 'What payment methods do you accept?',
  'subscription.faqPaymentA': 'We accept payments through Stripe (credit/debit cards). Enterprise plans can also be billed via invoice.',
  'subscription.faqTrialQ': 'Is there a free trial?',
  'subscription.faqTrialA': 'Yes! All paid plans come with a 14-day free trial. No credit card required to start.',
  'subscription.faqStorageQ': 'Can I add more storage later?',
  'subscription.faqStorageA': 'Absolutely! You can request storage add-ons for any plan at any time. Contact us for details.',
  'subscription.trialActive': 'Free Trial Active',
  'subscription.trialDaysLeft': 'days remaining',
  'subscription.upgradeNow': 'Upgrade Now',
  'subscription.trialAvailable': 'Try any plan free for 14 days!',
  'subscription.trialAvailableDesc': 'No credit card required. Experience all premium features risk-free.',
  'subscription.startTrial': 'Start Free Trial',
  'subscription.startFreeTrial': 'Start 14-Day Free Trial',
  'subscription.trialActiveLabel': 'Trial Active',
  'subscription.trialUsed': 'Trial Already Used',
  'subscription.trialExpired': 'Your trial has ended. Upgrade to keep premium features.',
  'subscription.cancelTrial': 'Cancel Trial',
  'subscription.cancelTrialConfirm': 'Are you sure you want to cancel your free trial? You will be downgraded to the Free plan immediately.',
  'subscription.ctaTitle': 'Ready to get started?',
  'subscription.ctaDesc': 'Join thousands of users managing their items with our platform.',
  'subscription.startFree': 'Start Free',
  'subscription.contactUs': 'Contact Us',
  'subscription.planTagline.FREE': 'Free forever',
  'subscription.planTagline.STARTER': 'Start collecting smarter',
  'subscription.planTagline.COLLECTOR': 'Most popular for collectors',
  'subscription.planTagline.PRO': 'Full-featured for pros',
  'subscription.planTagline.BUSINESS': 'Built for teams',
  'subscription.planTagline.ULTIMATE': 'No limits, no compromises',
  'subscription.planTagline.ENTERPRISE': "Let's talk",
  'subscription.planTagline.CUSTOM': 'Tailored to your needs',
  'subscription.planDesc.FREE': 'Get started — catalog up to 30 items with basic features.',
  'subscription.planDesc.STARTER': 'For hobbyists and small collectors getting serious.',
  'subscription.planDesc.COLLECTOR': 'For dedicated collectors who need more room and smarter tools.',
  'subscription.planDesc.PRO': 'For power users and professionals managing large inventories.',
  'subscription.planDesc.BUSINESS': 'For teams and organizations that need scale, security, and control.',
  'subscription.planDesc.ULTIMATE': 'The pinnacle — unlimited everything with premium support and customization.',
  'subscription.planDesc.ENTERPRISE': 'Tailored plan for large organizations with specific needs.',

  // Plan Highlights
  'subscription.planHighlight.FREE.0': 'Up to 30 items',
  'subscription.planHighlight.FREE.1': '300 MB storage',
  'subscription.planHighlight.FREE.2': '5 photos per item',
  'subscription.planHighlight.FREE.3': 'AI categorization',
  'subscription.planHighlight.FREE.4': 'Basic analytics',
  'subscription.planHighlight.FREE.5': 'Two-factor auth',
  'subscription.planHighlight.FREE.6': 'Basic analytics',
  'subscription.planHighlight.FREE.7': '10 AI messages/mo',
  'subscription.planHighlight.STARTER.0': 'Up to 250 items',
  'subscription.planHighlight.STARTER.1': '2 GB storage',
  'subscription.planHighlight.STARTER.2': '10 photos per item',
  'subscription.planHighlight.STARTER.3': 'AI categorization',
  'subscription.planHighlight.STARTER.4': 'Export your data',
  'subscription.planHighlight.STARTER.5': 'Comments',
  'subscription.planHighlight.COLLECTOR.0': 'Up to 1,000 items',
  'subscription.planHighlight.COLLECTOR.1': '10 GB storage',
  'subscription.planHighlight.COLLECTOR.2': '20 photos per item',
  'subscription.planHighlight.COLLECTOR.3': 'AI value prediction',
  'subscription.planHighlight.COLLECTOR.4': 'Duplicate detection',
  'subscription.planHighlight.COLLECTOR.5': 'Real-time sync',
  'subscription.planHighlight.COLLECTOR.6': '30-day audit logs',
  'subscription.planHighlight.PRO.0': 'Up to 10,000 items',
  'subscription.planHighlight.PRO.1': '50 GB storage',
  'subscription.planHighlight.PRO.2': '40 photos per item',
  'subscription.planHighlight.PRO.3': 'Unlimited AI reports',
  'subscription.planHighlight.PRO.4': 'Advanced analytics & reports',
  'subscription.planHighlight.PRO.5': 'API access (120 req/min)',
  'subscription.planHighlight.PRO.6': 'Priority support',
  'subscription.planHighlight.PRO.7': 'Team collaboration (10)',
  'subscription.planHighlight.BUSINESS.0': 'Unlimited items',
  'subscription.planHighlight.BUSINESS.1': '200 GB storage',
  'subscription.planHighlight.BUSINESS.2': '60 photos per item',
  'subscription.planHighlight.BUSINESS.3': 'Up to 25 team members',
  'subscription.planHighlight.BUSINESS.4': 'SSO / Single Sign-On',
  'subscription.planHighlight.BUSINESS.5': 'Custom branding',
  'subscription.planHighlight.BUSINESS.6': '365-day audit logs',
  'subscription.planHighlight.BUSINESS.7': 'Webhooks & API (300 req/min)',
  'subscription.planHighlight.BUSINESS.8': 'Priority support',
  'subscription.planHighlight.ULTIMATE.0': 'Unlimited everything',
  'subscription.planHighlight.ULTIMATE.1': '1 TB storage',
  'subscription.planHighlight.ULTIMATE.2': 'Unlimited photos per item',
  'subscription.planHighlight.ULTIMATE.3': 'Unlimited team members',
  'subscription.planHighlight.ULTIMATE.4': 'Dedicated account manager',
  'subscription.planHighlight.ULTIMATE.5': 'SLA guarantee',
  'subscription.planHighlight.ULTIMATE.6': 'White-label option',
  'subscription.planHighlight.ULTIMATE.7': 'Custom domain',
  'subscription.planHighlight.ULTIMATE.8': 'Unlimited API access',
  'subscription.planHighlight.ENTERPRISE.0': 'Unlimited everything',
  'subscription.planHighlight.ENTERPRISE.1': 'Custom storage',
  'subscription.planHighlight.ENTERPRISE.2': 'Dedicated account manager',
  'subscription.planHighlight.ENTERPRISE.3': 'SLA guarantee',
  'subscription.planHighlight.ENTERPRISE.4': 'White-label option',
  'subscription.planHighlight.ENTERPRISE.5': 'Custom domain',
  'subscription.planHighlight.ENTERPRISE.6': 'On-premise deployment',
  'subscription.planHighlight.ENTERPRISE.7': 'Custom integrations',
  'subscription.planHighlight.ENTERPRISE.8': 'Tailored onboarding',
  'subscription.planHighlight.CUSTOM.0': 'Unlimited everything',
  'subscription.planHighlight.CUSTOM.1': 'Custom storage',
  'subscription.planHighlight.CUSTOM.2': 'Dedicated account manager',
  'subscription.planHighlight.CUSTOM.3': 'SLA guarantee',
  'subscription.planHighlight.CUSTOM.4': 'White-label option',
  'subscription.planHighlight.CUSTOM.5': 'Custom domain',
  'subscription.planHighlight.CUSTOM.6': 'On-premise deployment',
  'subscription.planHighlight.CUSTOM.7': 'Custom integrations',
  'subscription.planHighlight.CUSTOM.8': 'Tailored onboarding',

  // ── Collections ──
  'collections.title': 'Collections',
  'collections.subtitle': 'Create custom groups and organize items your way',
  'collections.newCollection': 'New Collection',
  'collections.searchPlaceholder': 'Search collections...',
  'collections.customOrder': 'Custom Order',
  'collections.name': 'Name',
  'collections.recentlyUpdated': 'Recently Updated',
  'collections.mostItems': 'Most Items',
  'collections.collections': 'Collections',
  'collections.totalItems': 'Total Items',
  'collections.active': 'Active',
  'collections.items': 'item',
  'collections.itemsPlural': 'items',
  'collections.updated': 'Updated',
  'collections.noMatchingCollections': 'No matching collections',
  'collections.tryDifferentSearch': 'Try a different search term',
  'collections.noCollectionsYet': 'No collections yet',
  'collections.noCollectionsDesc': 'Create your first collection to start organizing items your way',
  'collections.createCollection': 'Create Collection',
  'collections.editCollection': 'Edit Collection',
  'collections.createNewCollection': 'Create New Collection',
  'collections.collectionName': 'Collection Name *',
  'collections.collectionNamePlaceholder': 'e.g., Vintage Watches, Rare Books...',
  'collections.description': 'Description',
  'collections.descriptionPlaceholder': "What's this collection about?",
  'collections.color': 'Color',
  'collections.saveChanges': 'Save Changes',
  'collections.cancel': 'Cancel',
  'collections.addItems': 'Add Items',
  'collections.addRemoveItems': 'Add / Remove Items',
  'collections.done': 'Done',
  'collections.itemsInCollection': '{count} item(s) in this collection',
  'collections.loadingItems': 'Loading items...',
  'collections.noItemsInCollection': 'No items in this collection',
  'collections.noItemsDesc': 'Click "Add Items" to start adding items from your library',
  'collections.noItemsFound': 'No items found',
  'collections.deleteConfirm': 'Delete this collection? Items will not be deleted.',
  'collections.loading': 'Loading collections...',
  'collections.icon': 'Icon',
  'collections.item': 'item',
  'collections.addItem': 'Add Item',
  'collections.itemName': 'Item name',
  'collections.itemNamePlaceholder': 'Item name...',
  'collections.itemType': 'Type',
  'collections.noItems': 'No items yet. Add your first item above.',
  'collections.type.document': 'Document',
  'collections.type.image': 'Image',
  'collections.type.link': 'Link',
  'collections.type.note': 'Note',
  'collections.type.video': 'Video',
  'collections.type.audio': 'Audio',
  'collections.type.spreadsheet': 'Spreadsheet',
  'collections.type.presentation': 'Presentation',
  'collections.type.report': 'Report',
  'collections.type.task': 'Task',
  'collections.type.invoice': 'Invoice',
  'collections.type.contract': 'Contract',
  'collections.type.email': 'Email',
  'collections.type.contact': 'Contact',
  'collections.type.blueprint': 'Blueprint',
  'collections.type.code': 'Code',

  // ── Duplicate Finder ──
  'duplicates.title': 'Duplicate Finder',
  'duplicates.subtitle': 'Upload a photo to find matching items in your collection and get value estimates',
  'duplicates.dropHere': 'Drop an image here or click to upload',
  'duplicates.supportedFormats': 'Supports JPG, PNG, GIF, WebP — up to 20MB',
  'duplicates.scanning': 'Scanning collection...',
  'duplicates.scanButton': 'Scan for Duplicates',
  'duplicates.aiAnalysis': 'AI Analysis',
  'duplicates.description': 'Description',
  'duplicates.category': 'Category',
  'duplicates.brand': 'Brand',
  'duplicates.model': 'Model',
  'duplicates.condition': 'Condition',
  'duplicates.estimatedValue': 'Estimated Market Value',
  'duplicates.low': 'Low',
  'duplicates.mid': 'Mid',
  'duplicates.high': 'High',
  'duplicates.matchesFound': '{count} Match(es) Found',
  'duplicates.noDuplicates': 'No Duplicates Found',
  'duplicates.noDuplicatesDesc': "This item doesn't appear to match anything in your collection. It might be a new item!",
  'duplicates.highMatch': 'High Match',
  'duplicates.possibleMatch': 'Possible Match',
  'duplicates.lowMatch': 'Low Match',
  'duplicates.estValue': 'Est. value:',
  'duplicates.selectImage': 'Please select an image file',

  // ── AI Assistant ──
  'ai.newChat': 'New Chat',
  'ai.loading': 'Loading...',
  'ai.noChats': 'No conversations yet',
  'ai.startChat': 'Start a new chat to begin',
  'ai.typeMessage': 'Type a message...',
  'ai.maxImages': 'Maximum 5 images per message',
  'ai.welcomeTitle': 'ELVOAQ AI Assistant',
  'ai.welcomeDesc': 'Ask me anything about your collection, item values, or organization tips.',
  'ai.deleteConfirm': 'Delete this conversation?',

  // ── Hidden Folders ──
  'hiddenFolders.title': 'Hidden Folders',
  'hiddenFolders.subtitle': 'Password-protected folders to keep your private items secure',
  'hiddenFolders.newFolder': 'New Folder',
  'hiddenFolders.loading': 'Loading folders...',
  'hiddenFolders.noFolders': 'No Hidden Folders Yet',
  'hiddenFolders.noFoldersDesc': 'Create a password-protected folder to keep your most private items secure.',
  'hiddenFolders.createFirst': 'Create Your First Folder',
  'hiddenFolders.open': 'Open',
  'hiddenFolders.items': 'items',
  'hiddenFolders.back': 'Back',
  'hiddenFolders.addItem': 'Add Item',
  'hiddenFolders.noItems': 'No items in this folder yet',
  'hiddenFolders.addFirstItem': 'Add Your First Item',
  'hiddenFolders.viewItem': 'View Item',
  'hiddenFolders.removeFromFolder': 'Remove from folder',
  'hiddenFolders.enterPassword': 'Enter Password',
  'hiddenFolders.passwordProtected': 'This folder is password-protected. Enter the password to view its contents.',
  'hiddenFolders.folderPassword': 'Folder password',
  'hiddenFolders.verifying': 'Verifying...',
  'hiddenFolders.viewFolder': 'View Folder',
  'hiddenFolders.createFolder': 'Create Hidden Folder',
  'hiddenFolders.folderName': 'Folder Name *',
  'hiddenFolders.folderNamePlaceholder': 'e.g., Valuables, Private Collection',
  'hiddenFolders.descriptionLabel': 'Description',
  'hiddenFolders.descriptionPlaceholder': 'Optional description...',
  'hiddenFolders.iconLabel': 'Icon',
  'hiddenFolders.colorLabel': 'Color',
  'hiddenFolders.password': 'Password *',
  'hiddenFolders.confirmPassword': 'Confirm Password *',
  'hiddenFolders.creating': 'Creating...',
  'hiddenFolders.editFolder': 'Edit Folder',
  'hiddenFolders.changeFolderPassword': 'Change Folder Password',
  'hiddenFolders.leaveEmptyKeep': 'Leave empty to keep the current password',
  'hiddenFolders.currentPassword': 'Current Password',
  'hiddenFolders.newPassword': 'New Password',
  'hiddenFolders.confirmNewPassword': 'Confirm New Password',
  'hiddenFolders.saveChanges': 'Save Changes',
  'hiddenFolders.deleteFolder': 'Delete Folder',
  'hiddenFolders.deleteConfirm': 'Are you sure you want to delete',
  'hiddenFolders.deleteDesc': '? This will remove the folder but not the items themselves.',
  'hiddenFolders.deletePasswordPrompt': 'Enter folder password to confirm',
  'hiddenFolders.cancel': 'Cancel',
  'hiddenFolders.addItemTo': 'Add Item to',
  'hiddenFolders.searchItems': 'Search items...',
  'hiddenFolders.enterPasswordToAdd': 'Enter folder password to add this item:',
  'hiddenFolders.confirm': 'Confirm',
  'hiddenFolders.alreadyAdded': 'Already added',
  'hiddenFolders.noResults': 'No items found',
  'hiddenFolders.allCategories': 'All Categories',
  'hiddenFolders.favoritesOnly': 'Favorites only',
  'hiddenFolders.favorites': 'Favorites',
  'hiddenFolders.passwordsNoMatch': 'Passwords do not match',
  'hiddenFolders.passwordMinLength': 'Password must be at least 4 characters',
  'hiddenFolders.folderCreated': 'Hidden folder created!',
  'hiddenFolders.itemAdded': 'Item added to hidden folder',
  'hiddenFolders.itemRemoved': 'Item removed from hidden folder',
  'hiddenFolders.folderUpdated': 'Folder updated successfully',
  'hiddenFolders.folderPasswordChanged': 'Folder password changed',
  'hiddenFolders.folderDeleted': 'Folder deleted successfully',
  'hiddenFolders.nameLabel': 'Name',
  'hiddenFolders.currentPasswordPlaceholder': 'Enter current password',
  'hiddenFolders.newPasswordPlaceholder': 'New password (min 4 chars)',
  'hiddenFolders.confirmPasswordPlaceholder': 'Re-enter new password',
  'hiddenFolders.folderPasswordPlaceholder': 'Folder password',
  'hiddenFolders.confirmFolderPasswordPlaceholder': 'Confirm password',
  'hiddenFolders.currentPasswordRequired': 'Current password is required',
  'hiddenFolders.newPasswordMinLength': 'New password must be at least 4 characters',
  'hiddenFolders.newPasswordsNoMatch': 'New passwords do not match',
  'hiddenFolders.deleteButton': 'Delete Folder',
  'hiddenFolders.addItemButton': 'Add Item',
  'hiddenFolders.nameAndPasswordRequired': 'Name and password are required',
  'hiddenFolders.enterFolderPassword': 'Enter the folder password',
  'hiddenFolders.wrongPassword': 'Wrong password',
  'hiddenFolders.failedToCreateFolder': 'Failed to create folder',
  'hiddenFolders.failedToAddItem': 'Failed to add item',
  'hiddenFolders.failedToRemoveItem': 'Failed to remove item',
  'hiddenFolders.failedToUpdateFolder': 'Failed to update folder',
  'hiddenFolders.failedToDeleteFolder': 'Failed to delete folder',
  'hiddenFolders.folderUpdatedAndPasswordChanged': 'Folder updated & password changed',

  // ── Data Transfer ──
  'dataTransfer.title': 'Import & Export',
  'dataTransfer.subtitle': 'Transfer your data in CSV or JSON format',
  'dataTransfer.export': 'Export',
  'dataTransfer.import': 'Import',
  'dataTransfer.exportTitle': 'Export Your Items',
  'dataTransfer.exportDesc': 'Download your item data in your preferred format.',
  'dataTransfer.format': 'Format:',
  'dataTransfer.fieldsToInclude': 'Fields to include:',
  'dataTransfer.selectAll': 'Select All',
  'dataTransfer.deselectAll': 'Deselect All',
  'dataTransfer.exporting': 'Exporting...',
  'dataTransfer.download': 'Download',
  'dataTransfer.importTitle': 'Import Items',
  'dataTransfer.importDesc': 'Upload a CSV or JSON file to import items into your collection.',
  'dataTransfer.importMode': 'Import Mode:',
  'dataTransfer.addToExisting': 'Add to existing',
  'dataTransfer.addToExistingDesc': 'New items will be added alongside your current items',
  'dataTransfer.overwriteAll': 'Overwrite all',
  'dataTransfer.overwriteAllDesc': 'Current items will be archived and replaced with imported data',
  'dataTransfer.overwriteWarning': 'Warning: This will archive all your current items. They can be restored from trash.',
  'dataTransfer.clickToSelect': 'Click to select a CSV or JSON file',
  'dataTransfer.supported': 'Supported: .csv, .json',
  'dataTransfer.preview': 'Preview',
  'dataTransfer.itemsFound': 'items found',
  'dataTransfer.showingOf': 'Showing 5 of {count} items',
  'dataTransfer.importing': 'Importing...',
  'dataTransfer.importItems': 'Import {count} Items',
  'dataTransfer.confirmOverwrite': 'Confirm Overwrite',
  'dataTransfer.overwriteBody1': 'You are about to overwrite all existing items with {count} imported items.',
  'dataTransfer.overwriteBody2': 'Your current items will be moved to trash and can be restored later.',
  'dataTransfer.yesOverwrite': 'Yes, Overwrite',
  'dataTransfer.selectOneField': 'Select at least one field to export',
  'dataTransfer.noDataToImport': 'No data to import',
  'dataTransfer.exportSuccess': 'Export downloaded as {format} file!',
  'dataTransfer.importSuccess': 'Successfully imported {count} items!',
  'dataTransfer.overwriteNote': '(Previous items were archived)',

  // ── Smart Hub ──
  'smartHub.loading': 'Loading Smart Hub...',
  'smartHub.title': 'Smart Hub',
  'smartHub.subtitle': 'AI-powered insights & management',
  'smartHub.analyzing': 'Analyzing...',
  'smartHub.aiAnalyze': 'AI Analyze',
  'smartHub.overview': 'Overview',
  'smartHub.reminders': 'Reminders',
  'smartHub.notes': 'Notes',
  'smartHub.insights': 'Insights',
  'smartHub.aiReport': 'AI Report',
  'smartHub.totalItems': 'Total Items',
  'smartHub.collectionValue': 'Collection Value',
  'smartHub.categories': 'Categories',
  'smartHub.activeReminders': 'Active Reminders',
  'smartHub.aiSummary': 'AI Summary',
  'smartHub.topCategories': 'Top Categories:',
  'smartHub.mostValuable': 'Most valuable:',
  'smartHub.summaryContains': 'Your collection contains {count} item(s).',
  'smartHub.summaryTotalValue': 'Total tracked value: {total} (avg: {avg}).',
  'smartHub.summaryTopCategory': 'Top category: {name} ({count} items).',
  'smartHub.summaryRecentItems': '{count} item(s) added this week.',
  'smartHub.summaryMostValuable': 'Most valuable: "{name}" ({value}).',
  'smartHub.summaryEmpty': 'Your collection is empty. Start by adding your first item!',
  'smartHub.overdueReminders': 'overdue reminder(s)',
  'smartHub.insightsAvailable': 'insight(s) available',
  'smartHub.pinnedNotes': 'pinned note(s)',
  'smartHub.all': 'All',
  'smartHub.active': 'Active',
  'smartHub.completed': 'Completed',
  'smartHub.addReminder': 'Add Reminder',
  'smartHub.editReminder': 'Edit Reminder',
  'smartHub.newReminder': 'New Reminder',
  'smartHub.reminderTitle': 'Reminder title',
  'smartHub.descriptionOptional': 'Description (optional)',
  'smartHub.custom': 'Custom',
  'smartHub.maintenance': 'Maintenance',
  'smartHub.review': 'Review',
  'smartHub.insurance': 'Insurance',
  'smartHub.warranty': 'Warranty',
  'smartHub.low': 'Low',
  'smartHub.medium': 'Medium',
  'smartHub.high': 'High',
  'smartHub.urgent': 'Urgent',
  'smartHub.unassigned': 'Unassigned',
  'smartHub.me': 'Me',
  'smartHub.assignHint': 'Assign to: "Me" means it will show on your list. Colleagues are your workspace members.',
  'smartHub.assignedTo': 'Assigned to',
  'smartHub.cancel': 'Cancel',
  'smartHub.saveChanges': 'Save Changes',
  'smartHub.createReminder': 'Create Reminder',
  'smartHub.noReminders': 'No reminders',
  'smartHub.noRemindersDesc': 'Create one or click "AI Analyze" to generate suggestions',
  'smartHub.allNotes': 'All Notes',
  'smartHub.pinned': 'Pinned',
  'smartHub.aiGenerated': 'AI Generated',
  'smartHub.addNote': 'Add Note',
  'smartHub.editNote': 'Edit Note',
  'smartHub.newNote': 'New Note',
  'smartHub.writeNote': 'Write your note...',
  'smartHub.addTag': 'Add tag...',
  'smartHub.add': 'Add',
  'smartHub.saveNote': 'Save Note',
  'smartHub.noNotes': 'No notes yet',
  'smartHub.noNotesDesc': 'Create a note to keep track of important information',
  'smartHub.noInsights': 'No active insights',
  'smartHub.noInsightsDesc': 'Click "AI Analyze" to scan your collection for recommendations',
  'smartHub.relatedItems': 'Related items:',
  'smartHub.confidence': 'Confidence:',
  'smartHub.generatingReport': 'Generating AI Report...',
  'smartHub.reportTitle': 'Collection Analysis Report',
  'smartHub.generated': 'Generated:',
  'smartHub.regenerate': 'Regenerate',
  'smartHub.items': 'Items',
  'smartHub.value': 'Value',
  'smartHub.avgValue': 'Avg Value',
  'smartHub.noReport': 'No report generated yet',
  'smartHub.generateReport': 'Generate AI Report',
  'smartHub.daysOverdue': '{count} days overdue',
  'smartHub.today': 'Today',
  'smartHub.tomorrow': 'Tomorrow',
  'smartHub.inDays': 'In {count} days',
  'smartHub.inWeeks': 'In {count} weeks',
  'smartHub.inMonths': 'In {count} months',
  'smartHub.lockedItem': 'Locked Item',
  'smartHub.markIncomplete': 'Mark as incomplete',
  'smartHub.markComplete': 'Mark as complete',
  'smartHub.editTitle': 'Edit',
  'smartHub.deleteTitle': 'Delete',
  'smartHub.unpin': 'Unpin',
  'smartHub.pinInsight': 'Pin',
  'smartHub.dismiss': 'Dismiss',
  'smartHub.missing': 'Missing:',
  'smartHub.member': 'Member',

  // Calendar
  'nav.calendar': 'Calendar',
  'calendar.title': 'Calendar',
  'calendar.today': 'Today',
  'calendar.day': 'Day',
  'calendar.week': 'Week',
  'calendar.month': 'Month',
  'calendar.newEvent': 'New Event',
  'calendar.editEvent': 'Edit Event',
  'calendar.eventTitle': 'Title',
  'calendar.titlePlaceholder': 'Add title...',
  'calendar.date': 'Date',
  'calendar.startTime': 'Start',
  'calendar.endTime': 'End',
  'calendar.allDay': 'All Day',
  'calendar.location': 'Location',
  'calendar.locationPlaceholder': 'Add location...',
  'calendar.description': 'Description',
  'calendar.descriptionPlaceholder': 'Add description...',
  'calendar.color': 'Color',
  'calendar.save': 'Save',
  'calendar.create': 'Create',
  'calendar.cancel': 'Cancel',
  'calendar.edit': 'Edit',
  'calendar.delete': 'Delete',
  'calendar.more': 'more',
  'calendar.mon': 'Mon',
  'calendar.tue': 'Tue',
  'calendar.wed': 'Wed',
  'calendar.thu': 'Thu',
  'calendar.fri': 'Fri',
  'calendar.sat': 'Sat',
  'calendar.sun': 'Sun',
  'calendar.january': 'January',
  'calendar.february': 'February',
  'calendar.march': 'March',
  'calendar.april': 'April',
  'calendar.may': 'May',
  'calendar.june': 'June',
  'calendar.july': 'July',
  'calendar.august': 'August',
  'calendar.september': 'September',
  'calendar.october': 'October',
  'calendar.november': 'November',
  'calendar.december': 'December',
  'calendar.year': 'Year',
  'calendar.deleteConfirm': 'Are you sure you want to delete this event?',
  'calendar.trash': 'Recently Deleted',
  'calendar.trashDesc': 'Events are permanently removed after 30 days',
  'calendar.trashEmpty': 'No deleted events',
  'calendar.trashEmptyDesc': 'Deleted events will appear here',
  'calendar.restore': 'Restore',
  'calendar.deleteForever': 'Delete forever',
  'calendar.loading': 'Loading...',
  'calendar.noEvents': 'No events found',
  'calendar.upcoming': 'Upcoming',
  'calendar.myCalendars': 'My Calendars',
  'calendar.addCategory': 'Add Category',
  'calendar.categoryName': 'Category name...',
  'calendar.category': 'Category',
  'calendar.sunday': 'Sunday',
  'calendar.monday': 'Monday',
  'calendar.tuesday': 'Tuesday',
  'calendar.wednesday': 'Wednesday',
  'calendar.thursday': 'Thursday',
  'calendar.friday': 'Friday',
  'calendar.saturday': 'Saturday',
  'calendar.cat.meetings': 'Meetings',
  'calendar.cat.deadlines': 'Deadlines',
  'calendar.cat.reminders': 'Reminders',
  'calendar.cat.reviews': 'Reviews',
  'calendar.cat.presentations': 'Presentations',
  'calendar.cat.training': 'Training',
  'calendar.cat.travel': 'Travel',
  'calendar.cat.holidays': 'Holidays',

  // ── User Management ──────────────────────────────────────────
  'userMgmt.empty.noWorkspaceTitle': 'No Workspace Selected',
  'userMgmt.empty.noWorkspaceDesc': 'Select or create a workspace to manage members',
  'userMgmt.empty.goToWorkspaces': 'Go to Workspaces',
  'userMgmt.header.badge': 'People & Teams',
  'userMgmt.header.description': 'Manage members, roles, teams, and access permissions',
  'userMgmt.header.invitePeople': 'Invite People',
  'userMgmt.header.convertToOrg': 'Convert to Organization',
  'userMgmt.header.transferOwnership': 'Transfer Ownership',
  'userMgmt.stats.members': 'Members',
  'userMgmt.stats.pending': 'Pending',
  'userMgmt.tabs.members': 'Members',
  'userMgmt.tabs.invitations': 'Invitations',
  'userMgmt.tabs.teams': 'Teams',
  'userMgmt.tabs.roleGuide': 'Role Guide',
  'userMgmt.tabs.suggestions': 'Suggestions',
  'userMgmt.members.searchPlaceholder': 'Search by name, email, or role...',
  'userMgmt.members.sortByRole': 'By Role',
  'userMgmt.members.sortByName': 'By Name',
  'userMgmt.members.sortByJoined': 'Recently Joined',
  'userMgmt.members.loading': 'Loading members...',
  'userMgmt.members.notFoundTitle': 'No members found',
  'userMgmt.members.noResults': 'No results for "{query}"',
  'userMgmt.members.noMembers': 'No members in this workspace',
  'userMgmt.members.unknownName': 'Unknown',
  'userMgmt.members.changeRoleTooltip': 'Change role',
  'userMgmt.members.removeMemberTooltip': 'Remove member',
  'userMgmt.invitations.title': 'Pending Invitations',
  'userMgmt.invitations.sendInvite': 'Send Invite',
  'userMgmt.invitations.loading': 'Loading invitations...',
  'userMgmt.invitations.emptyTitle': 'No Pending Invitations',
  'userMgmt.invitations.emptyDesc': 'Invite people to collaborate in your workspace',
  'userMgmt.invitations.inviteSomeone': 'Invite Someone',
  'userMgmt.invitations.expires': 'Expires',
  'userMgmt.invitations.revokeTooltip': 'Revoke',
  'userMgmt.teams.title': 'Teams',
  'userMgmt.teams.newTeam': 'New Team',
  'userMgmt.teams.loading': 'Loading teams...',
  'userMgmt.teams.emptyTitle': 'No Teams Yet',
  'userMgmt.teams.emptyDesc': 'Teams help organize members and manage item access',
  'userMgmt.teams.createFirst': 'Create First Team',
  'userMgmt.teams.noDescription': 'No description',
  'userMgmt.roles.title': 'Role Hierarchy & Permissions',
  'userMgmt.roles.description': 'Understand what each role can do in your workspace',
  'userMgmt.roles.memberCount': 'member',
  'userMgmt.roles.membersCount': 'members',
  'userMgmt.roles.permissionsHeading': 'Permissions',
  'userMgmt.perm.fullWorkspaceControl': 'Full workspace control',
  'userMgmt.perm.transferOwnership': 'Transfer ownership',
  'userMgmt.perm.deleteWorkspace': 'Delete workspace',
  'userMgmt.perm.manageBilling': 'Manage billing',
  'userMgmt.perm.allAdminPermissions': 'All admin permissions',
  'userMgmt.perm.manageSettings': 'Manage workspace settings',
  'userMgmt.perm.inviteRemoveMembers': 'Invite/remove members',
  'userMgmt.perm.assignRoles': 'Assign roles (up to Manager)',
  'userMgmt.perm.viewAllItems': 'View all items & analytics',
  'userMgmt.perm.configureAI': 'Configure AI features',
  'userMgmt.perm.createEditItems': 'Create & edit items',
  'userMgmt.perm.approveSuggestions': 'Approve suggestions',
  'userMgmt.perm.moderateComments': 'Moderate comments',
  'userMgmt.perm.useAI': 'Use AI features',
  'userMgmt.perm.createShareLinks': 'Create share links',
  'userMgmt.perm.cannotManageMembers': 'Cannot manage members',
  'userMgmt.perm.cannotChangeSettings': 'Cannot change settings',
  'userMgmt.perm.viewWorkspaceItems': 'View workspace items',
  'userMgmt.perm.suggestEdits': 'Suggest edits',
  'userMgmt.perm.postComments': 'Post comments',
  'userMgmt.perm.cannotEditItems': 'Cannot directly edit items',
  'userMgmt.perm.cannotCreateItems': 'Cannot create items',
  'userMgmt.perm.viewItems': 'View items',
  'userMgmt.perm.downloadFiles': 'Download files',
  'userMgmt.perm.viewComments': 'View comments',
  'userMgmt.perm.cannotEditOrComment': 'Cannot edit or comment',
  'userMgmt.perm.readOnlyAccess': 'Read-only access',
  'userMgmt.perm.cannotManageAnything': 'Cannot manage anything',
  'userMgmt.perm.limitedViewAccess': 'Limited view access',
  'userMgmt.perm.accessSharedItems': 'Access specific shared items',
  'userMgmt.perm.cannotSeeFullWorkspace': 'Cannot see full workspace',
  'userMgmt.perm.mostRestrictiveRole': 'Most restrictive role',
  'userMgmt.perm.noManagementRights': 'No management rights',
  'userMgmt.suggestions.loading': 'Loading suggestions...',
  'userMgmt.suggestions.emptyTitle': 'No Pending Suggestions',
  'userMgmt.suggestions.emptyDesc': 'Edit suggestions from Reviewers will appear here for your approval',
  'userMgmt.suggestions.currentLabel': 'Current',
  'userMgmt.suggestions.empty': '(empty)',
  'userMgmt.suggestions.suggestedLabel': 'Suggested',
  'userMgmt.suggestions.approve': 'Approve',
  'userMgmt.suggestions.reject': 'Reject',
  'userMgmt.inviteModal.title': 'Invite People',
  'userMgmt.inviteModal.emailLabel': 'Email Address',
  'userMgmt.inviteModal.emailPlaceholder': 'colleague@company.com',
  'userMgmt.inviteModal.roleLabel': 'Role',
  'userMgmt.inviteModal.sendInvitation': 'Send Invitation',
  'userMgmt.roleModal.title': 'Change Role',
  'userMgmt.roleModal.changingRoleFor': 'Changing role for',
  'userMgmt.roleModal.currentTag': 'Current',
  'userMgmt.transferModal.title': 'Transfer Ownership',
  'userMgmt.transferModal.warningTitle': 'This action cannot be undone',
  'userMgmt.transferModal.warningDesc': 'You will be demoted to Org Admin after transferring ownership.',
  'userMgmt.transferModal.newOwnerLabel': 'New Owner',
  'userMgmt.transferModal.selectMemberPlaceholder': 'Select a member...',
  'userMgmt.transferModal.transferBtn': 'Transfer Ownership',
  'userMgmt.teamModal.title': 'Create Team',
  'userMgmt.teamModal.nameLabel': 'Team Name',
  'userMgmt.teamModal.namePlaceholder': 'e.g., Marketing, Engineering',
  'userMgmt.teamModal.descLabel': 'Description (optional)',
  'userMgmt.teamModal.descPlaceholder': 'What does this team work on?',
  'userMgmt.teamModal.createBtn': 'Create Team',
  'userMgmt.convertModal.title': 'Convert to Organization',
  'userMgmt.convertModal.heroTitle': 'Upgrade to Organization',
  'userMgmt.convertModal.heroDesc': 'Convert your personal workspace to an organization to unlock team collaboration features.',
  'userMgmt.convertModal.featureInviteTitle': 'Invite Team Members',
  'userMgmt.convertModal.featureInviteDesc': 'Add colleagues and assign roles',
  'userMgmt.convertModal.featureRbacTitle': 'Role-Based Access',
  'userMgmt.convertModal.featureRbacDesc': 'Control who can view, edit, and manage',
  'userMgmt.convertModal.featureTeamsTitle': 'Teams & Groups',
  'userMgmt.convertModal.featureTeamsDesc': 'Organize members into teams',
  'userMgmt.convertModal.note': "All your existing data will be preserved. You'll become the Owner of the organization.",
  'userMgmt.convertModal.converting': 'Converting...',
  'userMgmt.convertModal.convertNow': 'Convert Now',
  'userMgmt.common.cancel': 'Cancel',
  'userMgmt.error.personalNoInvitations': 'Personal workspaces do not support invitations. Convert to Organization first.',
  'userMgmt.success.invitationSent': 'Invitation sent to {email}',
  'userMgmt.error.invitationFailed': 'Failed to send invitation',
  'userMgmt.success.convertedToOrg': 'Workspace converted to Organization! You can now invite members.',
  'userMgmt.error.convertFailed': 'Failed to convert workspace',
  'userMgmt.success.roleUpdated': 'Role updated successfully',
  'userMgmt.error.roleChangeFailed': 'Failed to change role',
  'userMgmt.success.memberRemoved': '{name} removed from workspace',
  'userMgmt.error.removeMemberFailed': 'Failed to remove member',
  'userMgmt.success.invitationRevoked': 'Invitation to {email} revoked',
  'userMgmt.error.revokeInvitationFailed': 'Failed to revoke invitation',
  'userMgmt.success.ownershipTransferred': 'Ownership transferred successfully',
  'userMgmt.error.transferOwnershipFailed': 'Failed to transfer ownership',
  'userMgmt.success.teamCreated': 'Team "{name}" created',
  'userMgmt.error.createTeamFailed': 'Failed to create team',
  'userMgmt.success.suggestionApproved': 'Suggestion approved',
  'userMgmt.success.suggestionRejected': 'Suggestion rejected',
  'userMgmt.error.reviewSuggestionFailed': 'Failed to review suggestion',
  'userMgmt.confirm.removeMember': 'Remove {name} from this workspace?',
  'userMgmt.confirm.revokeInvitation': 'Revoke invitation to {email}?',
  'userMgmt.confirm.transferOwnership': 'Transfer ownership? You will become an Org Admin. This cannot be undone.',
  'userMgmt.time.today': 'Today',
  'userMgmt.time.yesterday': 'Yesterday',
  'userMgmt.time.daysAgo': '{count}d ago',
  'userMgmt.time.monthsAgo': '{count}mo ago',
  'userMgmt.time.yearsAgo': '{count}y ago',

  // ── Missing Audit Trail keys ─────────────────────────────────
  'audit.statsLogins': 'logins & logouts',
  'audit.statsCrud': 'creates, updates, deletes',
  'audit.statsAlerts': 'requires attention',
  'audit.statsReverts': 'Reverts',
  'audit.statsRollbacks': 'rollbacks performed',
  'audit.allStatuses': 'All Statuses',
  'audit.statusSuccess': 'Success',
  'audit.statusFailed': 'Failed',
  'audit.allTime': 'All Time',
  'audit.pastHour': 'Past Hour',
  'audit.past24Hours': 'Past 24 Hours',
  'audit.past7Days': 'Past 7 Days',
  'audit.past30Days': 'Past 30 Days',
  'audit.reset': 'Reset',
  'audit.live': 'Live',
  'audit.liveMonitoring': 'Live monitoring',
  'audit.event': 'Event',
  'audit.description': 'Description',
  'audit.status': 'Status',
  'audit.view': 'View',
  'audit.twoFaVerified': '2FA Verified',

  // ═══ Homepage (Compliance-Focused SaaS) ═══
  // Legal Bar
  'hp.legal.gdpr': 'GDPR Compliant',
  'hp.legal.ccpa': 'CCPA Ready',
  'hp.legal.hipaa': 'HIPAA Compliant',
  'hp.legal.iso': 'ISO 27001',

  // Navigation
  'hp.nav.home': 'Home',
  'hp.nav.about': 'About',
  'hp.nav.features': 'Features',
  'hp.nav.security': 'Security',
  'hp.nav.compliance': 'Compliance',
  'hp.nav.pricing': 'Pricing',
  'hp.nav.contact': 'Contact',
  'hp.nav.login': 'Login',
  'hp.nav.signup': 'Sign Up',

  // Hero Section
  'hp.hero.badge': 'Enterprise Compliance Ready',
  'hp.hero.title': 'Secure Asset Management for Modern Businesses',
  'hp.hero.subtitle': 'ELVOAQ Vault & TimeCalc: AI-powered inventory management with enterprise-grade security, compliance, and audit trails. Trusted by businesses worldwide.',
  'hp.hero.ctaPrimary': 'Start Free Trial',
  'hp.hero.ctaSecondary': 'Watch Demo',
  'hp.hero.dashboardAlt': 'ELVOAQ Dashboard Interface',

  // Compliance Badges
  'hp.complianceBadges.title': 'Compliance & Security Standards',
  'hp.complianceBadges.gdpr.title': 'GDPR Compliant',
  'hp.complianceBadges.gdpr.desc': 'Full compliance with EU General Data Protection Regulation for European users.',
  'hp.complianceBadges.ccpa.title': 'CCPA Ready',
  'hp.complianceBadges.ccpa.desc': 'California Consumer Privacy Act compliance for US data protection.',
  'hp.complianceBadges.hipaa.title': 'HIPAA Compliant',
  'hp.complianceBadges.hipaa.desc': 'Healthcare data protection standards for medical and insurance industries.',
  'hp.complianceBadges.soc2.title': 'SOC 2 Type II',
  'hp.complianceBadges.soc2.desc': 'Audited security, availability, and confidentiality controls.',
  'hp.complianceBadges.iso.title': 'ISO 27001',
  'hp.complianceBadges.iso.desc': 'International standard for information security management systems.',
  'hp.complianceBadges.pci.title': 'PCI DSS',
  'hp.complianceBadges.pci.desc': 'Payment Card Industry Data Security Standard compliance.',

  // Features Section
  'hp.features.title': 'Powerful Features for Asset Management',
  'hp.features.ai.title': 'AI-Powered Intelligence',
  'hp.features.ai.desc': 'Advanced machine learning automatically categorizes items, detects duplicates, and predicts market values with precision.',
  'hp.features.duplicate.title': 'Duplicate Detection',
  'hp.features.duplicate.desc': 'Smart algorithms identify identical or similar items across your entire inventory, preventing costly mistakes.',
  'hp.features.valuation.title': 'Real-Time Valuation',
  'hp.features.valuation.desc': 'Live market data integration keeps your asset valuations accurate and up-to-date automatically.',
  'hp.features.gallery.title': 'Professional Galleries',
  'hp.features.gallery.desc': 'Create stunning visual catalogs with multiple high-resolution photos and professional presentation tools.',
  'hp.features.search.title': 'Advanced Search',
  'hp.features.search.desc': 'Lightning-fast search across all items with filters, tags, and full-text capabilities.',
  'hp.features.collaboration.title': 'Team Collaboration',
  'hp.features.collaboration.desc': 'Role-based access control and shared workspaces for seamless team collaboration and workflow management.',

  // Security Section
  'hp.security.title': 'Enterprise-Grade Security',
  'hp.security.subtitle': 'Your data is protected by military-grade encryption and comprehensive security measures.',
  'hp.security.encryption.title': 'End-to-End Encryption',
  'hp.security.encryption.desc': 'All data is encrypted in transit and at rest using AES-256 encryption standards.',
  'hp.security.backup.title': 'Automated Backups',
  'hp.security.backup.desc': 'Daily encrypted backups with point-in-time recovery ensure your data is always safe.',
  'hp.security.access.title': 'Access Control',
  'hp.security.access.desc': 'Multi-factor authentication and granular permissions protect against unauthorized access.',
  'hp.security.monitoring.title': '24/7 Monitoring',
  'hp.security.monitoring.desc': 'Continuous security monitoring and threat detection with instant alerts.',

  // Compliance Standards
  'hp.compliance.title': 'Compliance Standards',
  'hp.compliance.gdpr.title': 'GDPR',
  'hp.compliance.gdpr.desc': 'General Data Protection Regulation compliance for EU data protection requirements.',
  'hp.compliance.ccpa.title': 'CCPA',
  'hp.compliance.ccpa.desc': 'California Consumer Privacy Act compliance for US privacy regulations.',
  'hp.compliance.hipaa.title': 'HIPAA',
  'hp.compliance.hipaa.desc': 'Health Insurance Portability and Accountability Act for healthcare data protection.',

  // Pricing Section
  'hp.pricing.title': 'Choose Your Plan',
  'hp.pricing.starter.name': 'Starter',
  'hp.pricing.starter.price': '€9',
  'hp.pricing.starter.period': '/month',
  'hp.pricing.starter.desc': 'Perfect for individuals and small businesses getting started.',
  'hp.pricing.starter.feature1': 'Up to 1,000 items',
  'hp.pricing.starter.feature2': 'Basic AI categorization',
  'hp.pricing.starter.feature3': 'Mobile app access',
  'hp.pricing.starter.feature4': 'Email support',
  'hp.pricing.professional.name': 'Professional',
  'hp.pricing.professional.price': '€29',
  'hp.pricing.professional.period': '/month',
  'hp.pricing.professional.desc': 'Advanced features for growing businesses and teams.',
  'hp.pricing.professional.feature1': 'Up to 10,000 items',
  'hp.pricing.professional.feature2': 'Advanced AI features',
  'hp.pricing.professional.feature3': 'Team collaboration',
  'hp.pricing.professional.feature4': 'Priority support',
  'hp.pricing.professional.popular': 'Most Popular',
  'hp.pricing.enterprise.name': 'Enterprise',
  'hp.pricing.enterprise.price': 'Custom',
  'hp.pricing.enterprise.period': 'pricing',
  'hp.pricing.enterprise.desc': 'Tailored solutions for large organizations with specific requirements.',
  'hp.pricing.enterprise.feature1': 'Unlimited items',
  'hp.pricing.enterprise.feature2': 'Custom integrations',
  'hp.pricing.enterprise.feature3': 'Dedicated support',
  'hp.pricing.enterprise.feature4': 'SLA guarantee',
  'hp.pricing.cta': 'Get Started',

  // Legal Documents
  'hp.legal.title': 'Legal & Compliance',
  'hp.legal.dpa.title': 'Data Processing Agreement',
  'hp.legal.dpa.desc': 'GDPR-compliant data processing agreement for enterprise customers.',
  'hp.legal.dpa.link': 'Download DPA',
  'hp.legal.terms.title': 'Terms of Service',
  'hp.legal.terms.desc': 'Complete terms and conditions for using ELVOAQ services.',
  'hp.legal.terms.link': 'Read Terms',
  'hp.legal.privacy.title': 'Privacy Policy',
  'hp.legal.privacy.desc': 'How we collect, use, and protect your personal information.',
  'hp.legal.privacy.link': 'Privacy Policy',
  'hp.legal.cookies.title': 'Cookie Policy',
  'hp.legal.cookies.desc': 'Information about how we use cookies and tracking technologies.',
  'hp.legal.cookies.link': 'Cookie Policy',

  // Trust Guarantees
  'hp.trust.title': 'Why Choose ELVOAQ',
  'hp.trust.audit.title': 'Complete Audit Trails',
  'hp.trust.audit.desc': 'Every action is logged with timestamps and user attribution for full compliance.',
  'hp.trust.support.title': '24/7 Expert Support',
  'hp.trust.support.desc': 'Our compliance and security experts are here to help you succeed.',
  'hp.trust.uptime.title': '99.9% Uptime SLA',
  'hp.trust.uptime.desc': 'Enterprise-grade infrastructure ensures your business never stops.',
  'hp.trust.backup.title': 'Automated Backups',
  'hp.trust.backup.desc': 'Daily encrypted backups with 30-day retention for peace of mind.',

  // Contact Section
  'hp.contact.title': 'Get in Touch',
  'hp.contact.subtitle': 'Ready to secure your assets with ELVOAQ? Contact our team for a personalized demo.',
  'hp.contact.info.title': 'Contact Information',
  'hp.contact.info.email': 'Email',
  'hp.contact.info.emailValue': 'sales@elvoaq.com',
  'hp.contact.info.phone': 'Phone',
  'hp.contact.info.phoneValue': '+41 44 123 45 67',
  'hp.contact.info.address': 'Address',
  'hp.contact.info.addressValue': 'ELVOAQ AG, Zurich, Switzerland',
  'hp.contact.form.title': 'Send us a Message',
  'hp.contact.form.name': 'Full Name',
  'hp.contact.form.email': 'Email Address',
  'hp.contact.form.company': 'Company',
  'hp.contact.form.message': 'Message',
  'hp.contact.form.submit': 'Send Message',

  // Footer
  'hp.footer.company': 'Company',
  'hp.footer.about': 'About Us',
  'hp.footer.careers': 'Careers',
  'hp.footer.press': 'Press Kit',
  'hp.footer.blog': 'Blog',
  'hp.footer.products': 'Products',
  'hp.footer.vault': 'ELVOAQ Vault',
  'hp.footer.timecalc': 'TimeCalc',
  'hp.footer.integrations': 'Integrations',
  'hp.footer.resources': 'Resources',
  'hp.footer.docs': 'Documentation',
  'hp.footer.help': 'Help Center',
  'hp.footer.api': 'API Reference',
  'hp.footer.security': 'Security',
  'hp.footer.compliance': 'Compliance',
  'hp.footer.status': 'System Status',
  'hp.footer.contact': 'Contact',
  'hp.footer.support': 'Support',
  'hp.footer.sales': 'Sales',
  'hp.footer.partners': 'Partners',
  'hp.footer.social': 'Follow Us',
  'hp.footer.copyright': '© 2024 ELVOAQ AG. All rights reserved.',
  'hp.footer.privacy': 'Privacy Policy',
  'hp.footer.terms': 'Terms of Service',
  'hp.footer.cookies': 'Cookie Policy',

  // Cookie Consent
  'hp.cookie.message': 'We use cookies to enhance your experience and analyze our traffic. By continuing to use our site, you agree to our use of cookies.',
  'hp.cookie.accept': 'Accept All',
  'hp.cookie.decline': 'Decline',

  // ── Homepage component keys (mapped to match component usage) ──
  'hp.legal.compliant': 'ISO 27001 Certified',
  'hp.legal.soc2': 'SOC 2 Type II',
  'hp.legal.learnMore': 'Learn More',
  'hp.legal.subtitle': 'Transparent documentation for enterprise compliance requirements.',
  'hp.legal.download': 'Download',
  'hp.nav.getStarted': 'Get Started',
  'hp.hero.title1': 'Secure Asset Management',
  'hp.hero.title2': 'for Modern Businesses',
  'hp.hero.uptime': 'Uptime SLA',
  'hp.hero.compliant': 'Compliant',
  'hp.hero.support': 'Support',
  'hp.hero.items': 'Items Managed',
  'hp.hero.value': 'Portfolio Value',
  'hp.hero.ai': 'AI Accuracy',
  'hp.compliance.gdpr': 'Full EU data protection compliance',
  'hp.compliance.ccpa': 'California privacy law compliance',
  'hp.compliance.hipaa': 'Healthcare data protection standards',
  'hp.compliance.iso': 'International security management',
  'hp.compliance.aes': 'Military-grade AES-256 encryption',
  'hp.compliance.soc2': 'Audited security controls',
  'hp.compliance.section.title': 'Compliance Standards',
  'hp.compliance.section.subtitle': 'Built for the strictest regulatory requirements worldwide.',
  'hp.features.subtitle': 'Everything you need to manage, protect, and grow your asset portfolio.',
  'hp.features.dup.title': 'Duplicate Detection',
  'hp.features.dup.desc': 'Smart algorithms identify identical or similar items across your entire inventory.',
  'hp.features.val.title': 'Real-Time Valuation',
  'hp.features.val.desc': 'Live market data keeps your asset valuations accurate automatically.',
  'hp.features.collab.title': 'Team Collaboration',
  'hp.features.collab.desc': 'Role-based access and shared workspaces for seamless team workflows.',
  'hp.security.enc.title': 'End-to-End Encryption',
  'hp.security.enc.desc': 'All data encrypted in transit and at rest with AES-256 standards.',
  'hp.security.prot.title': 'Threat Protection',
  'hp.security.prot.desc': 'Real-time threat detection and automated incident response.',
  'hp.security.monitor.title': '24/7 Monitoring',
  'hp.security.monitor.desc': 'Continuous security monitoring with instant alerts and audit logging.',
  'hp.pricing.subtitle': 'Transparent pricing with no hidden fees. Scale as your business grows.',
  'hp.pricing.month': '/month',
  'hp.pricing.popular': 'Most Popular',
  'hp.pricing.starter.f1': 'Up to 1,000 items',
  'hp.pricing.starter.f2': 'Basic AI categorization',
  'hp.pricing.starter.f3': 'Mobile app access',
  'hp.pricing.starter.f4': 'Email support',
  'hp.pricing.professional.f1': 'Up to 10,000 items',
  'hp.pricing.professional.f2': 'Advanced AI features',
  'hp.pricing.professional.f3': 'Team collaboration',
  'hp.pricing.professional.f4': 'Priority support',
  'hp.pricing.enterprise.f1': 'Unlimited items',
  'hp.pricing.enterprise.f2': 'Custom integrations',
  'hp.pricing.enterprise.f3': 'Dedicated support',
  'hp.pricing.enterprise.f4': 'SLA guarantee',
  'hp.trust.subtitle': 'Trusted by thousands of businesses and individuals worldwide.',
  'hp.trust.partners.title': 'Trusted Partners',
  'hp.trust.partners.desc': 'Working with Fortune 500 companies and leading global enterprises.',
  'hp.trust.payment.title': 'Secure Payments',
  'hp.trust.payment.desc': 'Bank-level security for all payment processing and transactions.',
  'hp.trust.location.title': 'European Data Centers',
  'hp.trust.location.desc': 'All data stored in EU-certified, GDPR-compliant data centers.',
  'hp.contact.email': 'Email',
  'hp.contact.phone': 'Phone',
  'hp.contact.address': 'Address',
  'hp.contact.name': 'Your Name',
  'hp.contact.company': 'Company (Optional)',
  'hp.contact.message': 'Your Message',
  'hp.contact.send': 'Send Message',
  'hp.footer.desc': 'Enterprise-grade inventory management and time tracking with compliance at the core.',
  'hp.footer.product': 'Product',
  'hp.footer.features': 'Features',
  'hp.footer.pricing': 'Pricing',
  'hp.footer.rights': 'All rights reserved.',
  'hp.cookies.title': 'We use cookies',
  'hp.cookies.text': 'We use cookies to enhance your experience. By continuing, you agree to our',
  'hp.cookies.policy': 'Cookie Policy',
  'hp.cookies.essential': 'Essential Only',
  'hp.cookies.accept': 'Accept All',
};

/* ═══════════════════════════════════════════════════════════════
   TRANSLATION KEYS — German (Deutsch)
   ═══════════════════════════════════════════════════════════════ */

const de: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Übersicht',
  'nav.back': 'Zurück',
  'nav.library': 'Bibliothek',
  'nav.favorites': 'Favoriten',
  'nav.categories': 'Kategorien',
  'nav.workspaces': 'Arbeitsbereiche',
  'nav.collections': 'Sammlungen',
  'nav.notifications': 'Benachrichtigungen',
  'nav.settings': 'Einstellungen',
  'nav.history': 'Verlauf',
  'nav.trash': 'Papierkorb',
  'nav.users': 'Benutzer',
  'nav.usersRoles': 'Benutzer & Rollen',
  'nav.teams': 'Teams',
  'nav.feedback': 'Feedback',
  'nav.subscription': 'Abonnement',
  'nav.pricing': 'Preise',
  'nav.logout': 'Abmelden',
  'nav.auditTrail': 'Aktivitätsprotokoll',
  'nav.accounting': 'Buchhaltung',
  'nav.smartHub': 'Smart Hub',
  'nav.aiAssistant': 'KI-Assistent',
  'nav.duplicateFinder': 'Duplikatfinder',
  'nav.hiddenFolders': 'Versteckte Ordner',
  'nav.importExport': 'Import / Export',
  'nav.sectionMenu': 'Menü',
  'nav.sectionOther': 'Weitere',
  'nav.sectionBilling': 'Abrechnung',
  'nav.searchPlaceholder': 'Bereiche suchen…',
  'nav.searchNoResults': 'Keine Bereiche gefunden',
  'action.save': 'Speichern',
  'action.saveChanges': 'Änderungen speichern',
  'action.cancel': 'Abbrechen',
  'action.delete': 'Löschen',
  'action.edit': 'Bearbeiten',
  'action.add': 'Hinzufügen',
  'action.create': 'Erstellen',
  'action.search': 'Suchen',
  'action.filter': 'Filtern',
  'action.filters': 'Filter',
  'action.export': 'Exportieren',
  'action.import': 'Importieren',
  'action.refresh': 'Aktualisieren',
  'action.close': 'Schließen',
  'action.confirm': 'Bestätigen',
  'action.back': 'Zurück',
  'action.next': 'Weiter',
  'action.loading': 'Laden...',
  'action.saving': 'Speichern...',
  'action.selectAll': 'Alle auswählen',
  'action.deselectAll': 'Alle abwählen',
  'action.enableAll': 'Alle aktivieren',
  'action.disableAll': 'Alle deaktivieren',
  'action.view': 'Ansehen',
  'action.move': 'Verschieben',
  'action.restore': 'Wiederherstellen',
  'action.remove': 'Entfernen',
  'action.clear': 'Leeren',
  'action.clearAll': 'Alle leeren',
  'action.done': 'Fertig',
  'action.apply': 'Anwenden',
  'action.reset': 'Zurücksetzen',
  'action.resetFilters': 'Filter zurücksetzen',
  'action.upgrade': 'Upgrade',
  'action.download': 'Herunterladen',
  'action.upload': 'Hochladen',
  'action.submit': 'Absenden',
  'action.continue': 'Fortfahren',
  'action.getStarted': 'Loslegen',
  'action.learnMore': 'Mehr erfahren',
  'action.viewAll': 'Alle anzeigen',
  'action.showMore': 'Mehr anzeigen',
  'action.showLess': 'Weniger anzeigen',
  'action.hideStats': 'Statistiken ausblenden',
  'action.showStats': 'Statistiken anzeigen',
  'action.reply': 'Antworten',
  'action.post': 'Posten',
  'action.posting': 'Posten...',

  // Intervals
  'interval.day': 'Tag',
  'interval.week': 'Woche',
  'interval.month': 'Monat',
  'interval.quarter': 'Quartal',
  'interval.year': 'Jahr',

  // Sort Options
  'sort.newestFirst': 'Neueste zuerst',
  'sort.oldestFirst': 'Älteste zuerst',
  'sort.nameAZ': 'Name (A-Z)',
  'sort.nameZA': 'Name (Z-A)',
  'sort.highestValue': 'Höchster Wert',
  'sort.lowestValue': 'Niedrigster Wert',
  'sort.recentlyUpdated': 'Kürzlich aktualisiert',
  'sort.mostItems': 'Meiste Einträge',

  // Item Fields
  'item.name': 'Name',
  'item.namePlaceholder': 'Name eingeben',
  'item.description': 'Beschreibung',
  'item.descriptionPlaceholder': 'Beschreibung eingeben',
  'item.category': 'Kategorie',
  'item.selectCategory': 'Kategorie auswählen',
  'item.condition': 'Zustand',
  'item.location': 'Standort',
  'item.locationPlaceholder': 'Wo wird dieser Artikel aufbewahrt?',
  'item.purchasePrice': 'Kaufpreis',
  'item.purchaseDate': 'Kaufdatum',
  'item.estimatedValue': 'Geschätzter Wert',
  'item.aiValue': 'KI-Wert',
  'item.tags': 'Tags',
  'item.tagsPlaceholder': 'Tag hinzufügen und Enter drücken',
  'item.images': 'Bilder',
  'item.chooseImages': 'Bilder auswählen (max. 50)',
  'item.addMore': 'Mehr hinzufügen',
  'item.createdAt': 'Erstellt',
  'item.updatedAt': 'Aktualisiert',

  // Item Modal
  'itemModal.editItem': 'Artikel bearbeiten',
  'itemModal.addNewItem': 'Neuen Artikel hinzufügen',
  'itemModal.updateItem': 'Artikel aktualisieren',
  'itemModal.createItem': 'Artikel erstellen',
  'itemModal.saving': 'Speichern...',
  'itemModal.aiAnalyzing': 'KI analysiert...',
  'itemModal.aiSuggestions': 'KI-Vorschläge',
  'itemModal.purchasePlace': 'Kaufort',
  'itemModal.purchasePlacePlaceholder': 'Geschäft, Website oder Verkäufer',
  'itemModal.currency': 'Währung',
  'itemModal.purchaseDateTime': 'Kaufdatum & Uhrzeit',
  'itemModal.selectDate': 'Datum wählen',
  'itemModal.timeOptional': 'Uhrzeit (optional)',
  'itemModal.noTime': 'Keine Uhrzeit',
  'itemModal.timezone': 'Zeitzone',
  'itemModal.autoDetected': 'Automatisch erkannt',
  'itemModal.highMatch': 'Hohe Übereinstimmung',
  'itemModal.mediumMatch': 'Mittlere Übereinstimmung',
  'itemModal.lowMatch': 'Niedrige Übereinstimmung',
  'itemModal.add': 'Hinzufügen',

  // Conditions
  'condition.new': 'Neu',
  'condition.likeNew': 'Wie neu',
  'condition.excellent': 'Ausgezeichnet',
  'condition.good': 'Gut',
  'condition.fair': 'Befriedigend',
  'condition.poor': 'Schlecht',
  'condition.all': 'Alle Zustände',

  // Advanced Filters
  'filter.advancedFilters': 'Erweiterte Filter',
  'filter.condition': 'Zustand',
  'filter.conditionAll': 'Alle',
  'filter.valueRange': 'Wertbereich',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.purchaseDate': 'Kaufdatum',
  'filter.from': 'Von',
  'filter.to': 'Bis',
  'filter.aiValueTrend': 'KI-Werttrend',
  'filter.trendAll': 'Alle',
  'filter.appreciating': 'Steigend',
  'filter.stable': 'Stabil',
  'filter.depreciating': 'Fallend',
  'filter.locations': 'Standorte',
  'filter.tags': 'Tags',
  'filter.additionalOptions': 'Weitere Optionen',
  'filter.hasAIValue': 'Hat KI-Wertvorhersage',
  'filter.favoritesOnly': 'Nur Favoriten',
  'filter.sortBy': 'Sortieren nach',
  'filter.newestFirst': 'Neueste zuerst',
  'filter.oldestFirst': 'Älteste zuerst',
  'filter.nameAZ': 'Name (A-Z)',
  'filter.nameZA': 'Name (Z-A)',
  'filter.highestValue': 'Höchster Wert',
  'filter.lowestValue': 'Niedrigster Wert',
  'filter.bestCondition': 'Bester Zustand',
  'filter.worstCondition': 'Schlechtester Zustand',
  'filter.resetAll': 'Alles zurücksetzen',
  'filter.applyFilters': 'Filter anwenden',

  // Collections — delete confirm
  'collections.deleteConfirmDesc': 'Artikel in dieser Sammlung werden nicht gelöscht.',

  // Collections — library search
  'collections.addFromLibrary': 'Aus Bibliothek hinzufügen',
  'collections.searchLibraryPlaceholder': 'Artikel suchen...',
  'collections.noLibraryResults': 'Keine Artikel gefunden',
  'collections.orAddManually': 'oder manuell hinzufügen',
  'collections.searchTab': 'Suchen',
  'collections.browseAllTab': 'Alle durchsuchen',
  'collections.filterItems': 'Artikel filtern...',
  'collections.allCategories': 'Alle',
  'collections.allConditions': 'Alle Zustände',
  'collections.searchHint': 'Mindestens 2 Zeichen eingeben',
  'collections.itemsTab': 'Elemente',
  'collections.addItemsTab': 'Elemente hinzufügen',
  'collections.searchItems': 'Elemente suchen...',

  // Categories
  'categories.title': 'Kategorien',
  'categories.addCategory': 'Kategorie hinzufügen',
  'categories.editCategory': 'Kategorie bearbeiten',
  'categories.deleteCategory': 'Kategorie löschen',
  'categories.noCategories': 'Noch keine Kategorien',
  'categories.noCategoriesFound': 'Keine Kategorien gefunden',
  'categories.totalItems': 'Einträge gesamt',
  'categories.totalValue': 'Gesamtwert',
  'categories.avgValue': 'Durchschn. Wert',
  'categories.sortBy': 'Sortieren nach:',
  'categories.name': 'Name',
  'categories.items': 'Einträge',
  'categories.color': 'Farbe',
  'categories.icon': 'Symbol',
  'categories.subtitle': 'Organisieren Sie Ihre Einträge in Sammlungen',
  'categories.showStats': 'Statistik anzeigen',
  'categories.hideStats': 'Statistik ausblenden',
  'categories.newCategory': 'Neue Kategorie',
  'categories.categories': 'Kategorien',
  'categories.avgValuePerItem': 'Durchschn. Wert/Eintrag',
  'categories.mostValuable': 'Wertvollster',
  'categories.conditionDistribution': 'Zustandsverteilung',
  'categories.searchPlaceholder': 'Kategorien suchen...',
  'categories.itemCount': 'Anzahl Einträge',
  'categories.dateCreated': 'Erstellungsdatum',
  'categories.customizeDefault': 'Standardkategorie anpassen',
  'categories.createNew': 'Neue Kategorie erstellen',
  'categories.infoBanner': 'Sie können Standardkategorien an Ihre Bedürfnisse anpassen. Änderungen werden gespeichert.',
  'categories.categoryName': 'Kategoriename *',
  'categories.enterName': 'Kategorienamen eingeben',
  'categories.description': 'Beschreibung',
  'categories.describeCategory': 'Diese Kategorie beschreiben',
  'categories.searchIcons': 'Symbole suchen...',
  'categories.updateCategory': 'Kategorie aktualisieren',
  'categories.createCategory': 'Kategorie erstellen',
  'categories.cancel': 'Abbrechen',
  'categories.default': 'Standard',
  'categories.noDescription': 'Keine Beschreibung',
  'categories.avg': 'Durchschn.:',
  'categories.tryDifferent': 'Versuchen Sie einen anderen Suchbegriff',
  'categories.createToOrganize': 'Erstellen Sie Kategorien, um Ihre Einträge zu organisieren',
  'categories.recentItems': 'Neueste Einträge',
  'categories.viewAllInLibrary': 'Alle in Bibliothek anzeigen',
  'categories.loadingItems': 'Einträge werden geladen...',
  'categories.noItemsInCategory': 'Keine Einträge in dieser Kategorie.',
  'categories.cannotDeleteWithItems': 'Kategorie mit {count} Eintrag/Einträgen kann nicht gelöscht werden. Bitte verschieben oder löschen Sie die Einträge zuerst.',
  'categories.confirmDelete': 'Sind Sie sicher, dass Sie diese Kategorie löschen möchten?',
  'categories.confirmDeleteDefault': 'Dies ist eine Standardkategorie. Sind Sie sicher, dass Sie sie löschen möchten? Sie können sie jederzeit neu erstellen.',
  'categories.duplicateTitle': 'Kategorie duplizieren',
  'categories.loading': 'Kategorien werden geladen...',

  // Item Detail
  'itemDetail.photos': 'Fotos',
  'itemDetail.noImage': 'Kein Bild',
  'itemDetail.removeFromFavorites': 'Aus Favoriten entfernen',
  'itemDetail.addToFavorites': 'Zu Favoriten hinzufügen',
  'itemDetail.added': 'Hinzugefügt',
  'itemDetail.uncategorized': 'Nicht kategorisiert',
  'itemDetail.info': 'Info',
  'itemDetail.files': 'Dateien',
  'itemDetail.comments': 'Kommentare',
  'itemDetail.log': 'Protokoll',
  'itemDetail.description': 'Beschreibung',
  'itemDetail.noDescription': 'Keine Beschreibung vorhanden.',
  'itemDetail.location': 'Standort',
  'itemDetail.notSet': 'Nicht festgelegt',
  'itemDetail.purchasePlace': 'Kaufort',
  'itemDetail.purchased': 'Gekauft',
  'itemDetail.purchasePrice': 'Kaufpreis',
  'itemDetail.condition': 'Zustand',
  'itemDetail.tags': 'Tags',
  'itemDetail.aiPrediction': 'KI-Wertvorhersage',
  'itemDetail.highConfidence': 'Hohe Zuversicht',
  'itemDetail.mediumConfidence': 'Mittlere Zuversicht',
  'itemDetail.lowConfidence': 'Niedrige Zuversicht',
  'itemDetail.basisPurchase': 'Basierend auf Kaufpreis & Zustand',
  'itemDetail.basisCategory': 'Basierend auf Kategoriedurchschnitt',
  'itemDetail.basisAI': 'KI-Schätzung',
  'itemDetail.previousPrediction': 'Vorherige Vorhersage',
  'itemDetail.recalculate': 'Neu berechnen',
  'itemDetail.analyzing': 'Artikeldaten werden analysiert...',
  'itemDetail.tryAgain': 'Erneut versuchen',
  'itemDetail.aiEstimateDesc': 'Erhalten Sie eine KI-gestützte Wertschätzung basierend auf Artikeldaten, Kategorie und Zustand.',
  'itemDetail.predictValue': 'Wert vorhersagen',
  'itemDetail.itemInfo': 'Artikelinformationen',
  'itemDetail.created': 'Erstellt',
  'itemDetail.lastUpdated': 'Zuletzt aktualisiert',
  'itemDetail.itemId': 'Artikel-ID',
  'itemDetail.uploadFile': 'Datei hochladen',
  'itemDetail.uploading': 'Hochladen...',
  'itemDetail.deselectAll': 'Alle abwählen',
  'itemDetail.selectAll': 'Alle auswählen',
  'itemDetail.downloadSelected': '{count} Ausgewählte herunterladen',
  'itemDetail.noFiles': 'Noch keine Dateien angehängt',
  'itemDetail.addComment': 'Kommentar hinzufügen...',
  'itemDetail.posting': 'Wird gepostet...',
  'itemDetail.post': 'Posten',
  'itemDetail.loadingComments': 'Kommentare werden geladen...',
  'itemDetail.loadingFiles': 'Dateien werden geladen...',
  'itemDetail.loadingHistory': 'Verlauf wird geladen...',
  'itemDetail.edited': '(bearbeitet)',
  'itemDetail.editHistory': 'Bearbeitungsverlauf',
  'itemDetail.save': 'Speichern',
  'itemDetail.cancel': 'Abbrechen',
  'itemDetail.noComments': 'Noch keine Kommentare. Seien Sie der Erste!',
  'itemDetail.noHistory': 'Noch kein Verlauf für diesen Artikel verfügbar.',
  'itemDetail.changesTracked': 'Änderungen werden hier automatisch erfasst.',
  'itemDetail.itemCreated': 'Artikel erstellt',
  'itemDetail.updatedField': '{field} aktualisiert',
  'itemDetail.itemDeleted': 'Artikel gelöscht',
  'itemDetail.locked': 'Gesperrt',
  'itemDetail.changePin': 'PIN ändern',
  'itemDetail.removeLock': 'Sperre entfernen',
  'itemDetail.lockItem': 'Artikel sperren',
  'itemDetail.edit': 'Bearbeiten',
  'itemDetail.delete': 'Löschen',
  'itemDetail.conditionNew': 'Neu',
  'itemDetail.conditionLikeNew': 'Wie neu',
  'itemDetail.conditionExcellent': 'Ausgezeichnet',
  'itemDetail.conditionGood': 'Gut',
  'itemDetail.conditionFair': 'Befriedigend',
  'itemDetail.conditionPoor': 'Schlecht',
  'itemDetail.conditionNA': 'K.A.',
  'itemDetail.viewFullSize': 'Vollbild anzeigen',

  // Lock Modal
  'lockModal.lockItem': 'Artikel sperren',
  'lockModal.unlockItem': 'Artikel entsperren',
  'lockModal.verifyPassword': 'Passwort bestätigen',
  'lockModal.changeLockPassword': 'Sperrpasswort ändern',
  'lockModal.lockDesc': 'Legen Sie ein Passwort fest, um diesen Artikel zu schützen. Er wird ausgeblendet und erfordert das Passwort zum Anzeigen.',
  'lockModal.unlockDesc': 'Geben Sie das Passwort ein, um diesen Artikel dauerhaft zu entsperren.',
  'lockModal.verifyDesc': 'Geben Sie das Passwort ein, um diesen gesperrten Artikel anzuzeigen.',
  'lockModal.changeDesc': 'Ändern Sie das Sperrpasswort für diesen Artikel.',
  'lockModal.removeLock': 'Sperre entfernen',
  'lockModal.removeLockDesc': 'Die Sperre wird ohne Passwort entfernt.',
  'lockModal.currentPassword': 'Aktuelles Passwort',
  'lockModal.password': 'Passwort',
  'lockModal.newPassword': 'Neues Passwort',
  'lockModal.confirmPassword': 'Passwort bestätigen',
  'lockModal.confirmNewPassword': 'Neues Passwort bestätigen',
  'lockModal.choosePlaceholder': 'Passwort wählen (min. 4 Zeichen)',
  'lockModal.enterPlaceholder': 'Passwort eingeben',
  'lockModal.newPlaceholder': 'Neues Passwort (min. 4 Zeichen)',
  'lockModal.reenterPlaceholder': 'Passwort erneut eingeben',
  'lockModal.processing': 'Verarbeitung...',
  'lockModal.verify': 'Bestätigen',
  'lockModal.changePassword': 'Passwort ändern',
  'lockModal.minCharsError': 'Passwort muss mindestens 4 Zeichen haben',
  'lockModal.mismatchError': 'Passwörter stimmen nicht überein',
  'lockModal.newMinCharsError': 'Neues Passwort muss mindestens 4 Zeichen haben',
  'lockModal.newMismatchError': 'Neue Passwörter stimmen nicht überein',
  'lockModal.operationFailed': 'Vorgang fehlgeschlagen. Bitte versuchen Sie es erneut.',

  // Favorites
  'favorites.title': 'Favoriten',
  'favorites.noFavorites': 'Noch keine Favoriten',
  'favorites.noFavoritesDesc': 'Klicken Sie auf das Herzsymbol bei Einträgen, um sie zu Ihren Favoriten hinzuzufügen',
  'favorites.browseLibrary': 'Bibliothek durchsuchen',
  'favorites.shown': 'angezeigt',
  'favorites.averageValue': 'Durchschnittswert',
  'favorites.minPrice': 'Min. Preis',
  'favorites.maxPrice': 'Max. Preis',
  'favorites.location': 'Standort',
  'favorites.allLocations': 'Alle Standorte',
  'favorites.tag': 'Tag',
  'favorites.allTags': 'Alle Tags',
  'favorites.loading': 'Favoriten werden geladen...',
  'favorites.searchPlaceholder': 'Favoriten durchsuchen...',
  'favorites.filters': 'Filter',
  'favorites.toggleView': 'Ansicht wechseln',
  'favorites.export': 'Exportieren',
  'favorites.exportCSV': 'CSV exportieren',
  'favorites.items': 'Einträge',
  'favorites.totalValue': 'Gesamtwert',
  'favorites.average': 'Durchschnitt',
  'favorites.category': 'Kategorie',
  'favorites.condition': 'Zustand',
  'favorites.allCategories': 'Alle Kategorien',
  'favorites.allConditions': 'Alle Zustände',
  'favorites.reset': 'Zurücksetzen',
  'favorites.sortMostRecent': 'Neueste zuerst',
  'favorites.sortPrice': 'Preis',
  'favorites.sortName': 'Name',
  'favorites.sortCondition': 'Zustand',
  'favorites.sortLocation': 'Standort',
  'favorites.all': 'Alle',
  'favorites.remove': 'Entfernen',
  'favorites.removeFromFavorites': 'Aus Favoriten entfernen',
  'favorites.removeConfirm': '{count} Einträge aus Favoriten entfernen?',
  'favorites.noMatch': 'Keine Einträge entsprechen den Filtern',
  'favorites.noMatchDesc': 'Versuchen Sie, Ihre Filter oder Suchanfrage anzupassen',
  'favorites.addFromCollection': 'Markieren Sie Einträge als Favoriten in Ihrer Sammlung, um sie hier zu sehen',

  // Default Category Names
  'categoryName.Electronics': 'Elektronik',
  'categoryName.Furniture': 'Möbel',
  'categoryName.Books': 'Bücher',
  'categoryName.Clothing': 'Kleidung',
  'categoryName.Jewelry': 'Schmuck',
  'categoryName.Sports': 'Sport',
  'categoryName.Art & Collectibles': 'Kunst & Sammlerstücke',
  'categoryName.Tools': 'Werkzeuge',
  'categoryDesc.Electronics & gadgets': 'Elektronik & Gadgets',
  'categoryDesc.Furniture & decor': 'Möbel & Dekoration',
  'categoryDesc.Books & media': 'Bücher & Medien',
  'categoryDesc.Clothing & accessories': 'Kleidung & Accessoires',
  'categoryDesc.Jewelry & watches': 'Schmuck & Uhren',
  'categoryDesc.Sports & outdoors': 'Sport & Outdoor',
  'categoryDesc.Art & collectibles': 'Kunst & Sammlerstücke',
  'categoryDesc.Tools & equipment': 'Werkzeuge & Ausrüstung',

  // Unit of Measure
  'unit.title': 'Maßeinheit',
  'unit.type': 'Einheitentyp',
  'unit.subtype': 'Einheit',
  'unit.quantity': 'Menge',
  'unit.selectType': 'Typ auswählen...',
  'unit.selectUnit': 'Einheit auswählen...',
  'unit.enterQuantity': 'Menge eingeben...',
  'unit.customUnit': 'Eigener Einheitenname',
  'unit.type.weight': 'Gewicht',
  'unit.type.length': 'Länge',
  'unit.type.volume': 'Volumen',
  'unit.type.area': 'Fläche',
  'unit.type.quantity': 'Stückzahl',
  'unit.type.digital': 'Digitaler Speicher',
  'unit.type.time': 'Zeit',
  'unit.type.energy': 'Energie',
  'unit.type.temperature': 'Temperatur',
  'unit.type.speed': 'Geschwindigkeit',
  'unit.type.custom': 'Benutzerdefiniert',
  'unit.sub.mg': 'Milligramm (mg)',
  'unit.sub.g': 'Gramm (g)',
  'unit.sub.kg': 'Kilogramm (kg)',
  'unit.sub.t': 'Tonnen (t)',
  'unit.sub.oz': 'Unzen (oz)',
  'unit.sub.lb': 'Pfund (lb)',
  'unit.sub.mm': 'Millimeter (mm)',
  'unit.sub.cm': 'Zentimeter (cm)',
  'unit.sub.m': 'Meter (m)',
  'unit.sub.km': 'Kilometer (km)',
  'unit.sub.in': 'Zoll (in)',
  'unit.sub.ft': 'Fuß (ft)',
  'unit.sub.yd': 'Yard (yd)',
  'unit.sub.mi': 'Meilen (mi)',
  'unit.sub.ml': 'Milliliter (ml)',
  'unit.sub.cl': 'Zentiliter (cl)',
  'unit.sub.dl': 'Deziliter (dl)',
  'unit.sub.l': 'Liter (L)',
  'unit.sub.hl': 'Hektoliter (hL)',
  'unit.sub.floz': 'Flüssigunzen (fl oz)',
  'unit.sub.cup': 'Tassen',
  'unit.sub.pt': 'Pinten (pt)',
  'unit.sub.qt': 'Quart (qt)',
  'unit.sub.gal': 'Gallonen (gal)',
  'unit.sub.sqcm': 'Quadratzentimeter (cm²)',
  'unit.sub.sqm': 'Quadratmeter (m²)',
  'unit.sub.ha': 'Hektar (ha)',
  'unit.sub.sqkm': 'Quadratkilometer (km²)',
  'unit.sub.sqft': 'Quadratfuß (ft²)',
  'unit.sub.sqyd': 'Quadratyard (yd²)',
  'unit.sub.ac': 'Acres (ac)',
  'unit.sub.sqmi': 'Quadratmeilen (mi²)',
  'unit.sub.pcs': 'Stück (Stk)',
  'unit.sub.pairs': 'Paare',
  'unit.sub.sets': 'Sets',
  'unit.sub.packs': 'Packungen',
  'unit.sub.boxes': 'Kartons',
  'unit.sub.rolls': 'Rollen',
  'unit.sub.sheets': 'Blätter',
  'unit.sub.bottles': 'Flaschen',
  'unit.sub.cans': 'Dosen',
  'unit.sub.bags': 'Beutel',
  'unit.sub.dozens': 'Dutzend (Dtz)',
  'unit.sub.b': 'Bytes (B)',
  'unit.sub.kb': 'Kilobytes (KB)',
  'unit.sub.mb': 'Megabytes (MB)',
  'unit.sub.gb': 'Gigabytes (GB)',
  'unit.sub.tb': 'Terabytes (TB)',
  'unit.sub.sec': 'Sekunden (s)',
  'unit.sub.min': 'Minuten (min)',
  'unit.sub.hr': 'Stunden (h)',
  'unit.sub.day': 'Tage (d)',
  'unit.sub.wk': 'Wochen (Wo)',
  'unit.sub.mo': 'Monate (Mo)',
  'unit.sub.yr': 'Jahre (J)',
  'unit.sub.wh': 'Wattstunden (Wh)',
  'unit.sub.kwh': 'Kilowattstunden (kWh)',
  'unit.sub.cal': 'Kalorien (cal)',
  'unit.sub.kcal': 'Kilokalorien (kcal)',
  'unit.sub.j': 'Joule (J)',
  'unit.sub.kj': 'Kilojoule (kJ)',
  'unit.sub.c': 'Celsius (°C)',
  'unit.sub.f': 'Fahrenheit (°F)',
  'unit.sub.k': 'Kelvin (K)',
  'unit.sub.kmh': 'km/h',
  'unit.sub.mph': 'mph',
  'unit.sub.ms': 'm/s',
  'unit.sub.kn': 'Knoten (kn)',

  // Trash
  'trash.title': 'Papierkorb',
  'trash.noItems': 'Papierkorb ist leer',
  'trash.noItemsDesc': 'Gelöschte Einträge erscheinen hier',
  'trash.emptyTrash': 'Papierkorb leeren',
  'trash.restoreAll': 'Alle wiederherstellen',
  'trash.deleted': 'Gelöscht',
  'trash.expiresIn': 'Läuft ab in',
  'trash.days': 'Tagen',
  'trash.permanentDelete': 'Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.',
  'trash.loading': 'Papierkorb wird geladen...',
  'trash.items': '{count} Elemente',
  'trash.warning': 'Elemente im Papierkorb werden nach 30 Tagen endgültig gelöscht.',
  'trash.emptyTitle': 'Papierkorb ist leer',
  'trash.emptyDesc': 'Gelöschte Elemente erscheinen hier. Elemente werden nach 30 Tagen endgültig entfernt.',
  'trash.confirmDelete': 'Möchten Sie dieses Element wirklich endgültig löschen? Dies kann nicht rückgängig gemacht werden.',
  'trash.confirmEmpty': 'Möchten Sie wirklich ALLE Elemente im Papierkorb endgültig löschen? Dies kann nicht rückgängig gemacht werden.',
  'trash.unknown': 'Unbekannt',
  'trash.daysAgo': 'vor {count} Tagen',
  'trash.hoursAgo': 'vor {count} Stunden',
  'trash.justNow': 'Gerade eben',
  'trash.restore': 'Wiederherstellen',
  'trash.delete': 'Löschen',

  // Bibliothek
  'library.title': 'Bibliothek',
  'library.noItemsDesc': 'Fügen Sie Ihren ersten Artikel zur Bibliothek hinzu',
  'library.searchPlaceholder': 'Artikel suchen...',
  'library.addItem': 'Artikel hinzufügen',
  'library.allCategories': 'Alle Kategorien',
  'library.newestFirst': 'Neueste zuerst',
  'library.oldestFirst': 'Älteste zuerst',
  'library.nameAZ': 'Name (A-Z)',
  'library.nameZA': 'Name (Z-A)',
  'library.highestValue': 'Höchster Wert',
  'library.lowestValue': 'Niedrigster Wert',
  'library.advancedFilters': 'Erweiterte Filter',
  'library.gridView': 'Rasteransicht',
  'library.listView': 'Listenansicht',
  'library.edit': 'Bearbeiten',
  'library.move': 'Verschieben',
  'library.delete': 'Löschen',
  'library.value': 'Wert',
  'library.original': 'Original',
  'library.aiValue': 'KI-Wert',
  'library.noItems': 'Keine Artikel gefunden',
  'library.noItemsSearch': 'Versuchen Sie, Ihre Suche oder Filter anzupassen',
  'library.noItemsStart': 'Beginnen Sie mit dem Hinzufügen Ihres ersten Artikels',
  'library.addFirstItem': '+ Ersten Artikel hinzufügen',
  'library.moveItem': 'Artikel verschieben',
  'library.moving': 'Verschiebe:',
  'library.currentCategory': 'Aktuelle Kategorie:',
  'library.uncategorized': 'Unkategorisiert',
  'library.selectCategory': 'Neue Kategorie wählen:',
  'library.loading': 'Bibliothek wird geladen...',
  'library.confirmDelete': 'Sind Sie sicher, dass Sie diesen Artikel löschen möchten?',
  'library.moveFailed': 'Artikel konnte nicht verschoben werden',
  'library.lockedItem': 'Dieser Artikel ist gesperrt',

  // Dashboard
  'dashboard.analytics': 'Analytik',
  'dashboard.itemsTracked': 'Artikel verfolgt',
  'dashboard.categoriesLabel': 'Kategorien',
  'dashboard.customize': 'Anpassen',
  'dashboard.widgets': 'Widgets',
  'dashboard.save': 'Speichern',
  'dashboard.loading': 'Analytik wird geladen…',
  'dashboard.addWidget': 'Widget hinzufügen',
  'dashboard.totalItems': 'Gesamtartikel',
  'dashboard.portfolioValue': 'Portfoliowert',
  'dashboard.favorites': 'Favoriten',
  'dashboard.avgItemValue': 'Ø Artikelwert',
  'dashboard.newThisPeriod': 'neu in diesem Zeitraum',
  'dashboard.vsLastPeriod': 'gg. letztem Zeitraum',
  'dashboard.marked': 'markiert',
  'dashboard.perItem': 'pro Artikel',
  'dashboard.day': 'Tag',
  'dashboard.week': 'Woche',
  'dashboard.month': 'Monat',
  'dashboard.quarter': 'Quartal',
  'dashboard.year': 'Jahr',
  'dashboard.valueTrend': 'Wertentwicklung',
  'dashboard.itemsAdded': 'Hinzugefügte Artikel',
  'dashboard.conditionBreakdown': 'Zustandsverteilung',
  'dashboard.categoryDistribution': 'Kategorieverteilung',
  'dashboard.priceDistribution': 'Preisverteilung',
  'dashboard.mostValuable': 'Wertvollste Artikel',
  'dashboard.byLocation': 'Artikel nach Standort',
  'dashboard.cumulativeValue': 'Kumulierter Wert',
  'dashboard.favoritesOverTime': 'Favoriten im Zeitverlauf',
  'dashboard.topTags': 'Top Tags',
  'dashboard.purchaseSources': 'Kaufquellen',
  'dashboard.avgByCategory': 'Ø Wert nach Kategorie',
  'dashboard.valueTrendDesc': 'Gesamtwert pro Zeitraum hinzugefügt',
  'dashboard.itemsAddedDesc': 'Anzahl neuer Artikel pro Zeitraum',
  'dashboard.conditionDesc': 'Verteilung nach Artikelzustand',
  'dashboard.categoryDesc': 'Artikel nach Kategorie gruppiert',
  'dashboard.priceDistDesc': 'Verteilung der Artikelwerte über Preisbereiche',
  'dashboard.mostValuableDesc': 'Ihre wertvollsten Artikel',
  'dashboard.byLocationDesc': 'Artikel nach Lagerort gruppiert',
  'dashboard.cumulativeDesc': 'Laufende Summe des Portfoliowerts über die Zeit',
  'dashboard.favTrendDesc': 'Pro Zeitraum als Favorit markierte Artikel',
  'dashboard.topTagsDesc': 'Am häufigsten verwendete Tags',
  'dashboard.purchaseDesc': 'Wo Artikel gekauft wurden',
  'dashboard.avgCatDesc': 'Durchschnittlicher Artikelwert pro Kategorie',
  'dashboard.chooseChartType': 'Diagrammtyp wählen',
  'dashboard.configureData': 'Daten konfigurieren',
  'dashboard.previewCreate': 'Vorschau & Erstellen',
  'dashboard.stepType': '1. Typ',
  'dashboard.stepData': '2. Daten',
  'dashboard.stepPreview': '3. Vorschau',
  'dashboard.barChart': 'Balkendiagramm',
  'dashboard.pieChart': 'Kreisdiagramm',
  'dashboard.lineChart': 'Liniendiagramm',
  'dashboard.metricCard': 'Metrik-Karte',
  'dashboard.barChartDesc': 'Werte über Kategorien vergleichen',
  'dashboard.pieChartDesc': 'Anteile eines Ganzen zeigen',
  'dashboard.lineChartDesc': 'Trends über die Zeit verfolgen',
  'dashboard.metricCardDesc': 'Eine einzelne Kennzahl anzeigen',
  'dashboard.chartTitle': 'Diagrammtitel *',
  'dashboard.description': 'Beschreibung',
  'dashboard.value': 'Wert',
  'dashboard.dataPoints': 'Datenpunkte',
  'dashboard.addDataPoint': 'Datenpunkt hinzufügen',
  'dashboard.back': 'Zurück',
  'dashboard.nextConfigure': 'Weiter: Daten konfigurieren',
  'dashboard.nextPreview': 'Weiter: Vorschau',
  'dashboard.createChart': 'Diagramm erstellen',
  'dashboard.dashboardWidgets': 'Dashboard-Widgets',
  'dashboard.widgetHint': 'Sichtbarkeit umschalten · ziehen zum Neuordnen',

  // Abonnement-Extras
  'subscription.noWorkspaceError': 'Kein Arbeitsbereich ausgewählt. Bitte wählen Sie zuerst einen Arbeitsbereich.',
  'subscription.checkoutError': 'Checkout-Sitzung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
  'subscription.redirecting': 'Weiterleitung zu Stripe...',

  // Benachrichtigungseinstellungen
  'notifSettings.loading': 'Benachrichtigungseinstellungen werden geladen...',
  'notifSettings.title': 'Benachrichtigungseinstellungen',
  'notifSettings.subtitle': 'Steuern Sie, wann und wie Sie Benachrichtigungen erhalten',
  'notifSettings.disableAll': 'Alle deaktivieren',
  'notifSettings.enableAll': 'Alle aktivieren',
  'notifSettings.saving': 'Speichern...',
  'notifSettings.saveChanges': 'Änderungen speichern',
  'notifSettings.saved': 'Benachrichtigungseinstellungen gespeichert!',
  'notifSettings.saveFailed': 'Speichern der Einstellungen fehlgeschlagen',
  'notifSettings.typesTitle': 'Benachrichtigungstypen',
  'notifSettings.typesDesc': 'Wählen Sie, welche Benachrichtigungen Sie erhalten möchten.',
  'notifSettings.emailToggleTitle': 'E-Mail für diese Benachrichtigung senden',
  'notifSettings.itemUpdates': 'Artikelaktualisierungen',
  'notifSettings.itemUpdatesDesc': 'Benachrichtigung bei Aktualisierung beobachteter Artikel',
  'notifSettings.comments': 'Kommentare',
  'notifSettings.commentsDesc': 'Benachrichtigung wenn jemand Ihre Artikel kommentiert',
  'notifSettings.mentions': 'Erwähnungen',
  'notifSettings.mentionsDesc': 'Benachrichtigung wenn Sie in einem Kommentar erwähnt werden',
  'notifSettings.duplicateDetection': 'Duplikaterkennung',
  'notifSettings.duplicateDetectionDesc': 'Benachrichtigung wenn KI mögliche Duplikate erkennt',
  'notifSettings.valuePredictions': 'Wertvorhersagen',
  'notifSettings.valuePredictionsDesc': 'Benachrichtigung wenn KI Wertänderungen vorhersagt',
  'notifSettings.securityAlerts': 'Sicherheitswarnungen',
  'notifSettings.securityAlertsDesc': 'Wichtige Sicherheitsbenachrichtigungen (Anmeldeversuche, Passwortänderungen)',
  'notifSettings.emailPrefs': 'E-Mail-Einstellungen',
  'notifSettings.emailPrefsDesc': 'E-Mail-Benachrichtigungen verwalten',
  'notifSettings.weeklyDigest': 'Wöchentliche Zusammenfassung',
  'notifSettings.weeklyDigestDesc': 'Wöchentliche Aktivitätszusammenfassung Ihrer Arbeitsbereiche',
  'notifSettings.marketingEmails': 'Marketing-E-Mails',
  'notifSettings.marketingEmailsDesc': 'Produktupdates, Tipps und Werbeinhalte',
  'notifSettings.channels': 'Zustellungskanäle',
  'notifSettings.channelsDesc': 'Wählen Sie, wie Sie Benachrichtigungen erhalten möchten',
  'notifSettings.email': 'E-Mail',
  'notifSettings.emailDesc': 'Benachrichtigungen per E-Mail erhalten',
  'notifSettings.push': 'Push-Benachrichtigungen',
  'notifSettings.pushDesc': 'Push-Benachrichtigungen auf Ihren Geräten erhalten',
  'notifSettings.inApp': 'In-App-Benachrichtigungen',
  'notifSettings.inAppDesc': 'Benachrichtigungen in der App anzeigen',
  'notifSettings.quietHours': 'Ruhezeiten',
  'notifSettings.quietHoursDesc': 'Benachrichtigungen zu bestimmten Zeiten pausieren',
  'notifSettings.enableQuietHours': 'Ruhezeiten aktivieren',
  'notifSettings.quietHoursSilenced': 'Benachrichtigungen werden während der angegebenen Zeit stummgeschaltet',
  'notifSettings.startTime': 'Startzeit',
  'notifSettings.endTime': 'Endzeit',
  'notifSettings.timeTo': 'bis',
  'notifSettings.unsavedChanges': 'Sie haben ungespeicherte Änderungen',

  // Time
  'time.today': 'Heute',
  'time.yesterday': 'Gestern',
  'time.daysAgo': 'Tage her',
  'time.justNow': 'Gerade eben',
  'time.minutesAgo': 'Minuten her',
  'time.hoursAgo': 'Stunden her',

  // Settings
  'settings.title': 'Einstellungen',
  'settings.subtitle': 'Verwalten Sie Ihre Kontoeinstellungen und Sicherheit',
  'settings.general': 'Allgemein',
  'settings.appearance': 'Darstellung',
  'settings.security': 'Sicherheit',
  'settings.accessibility': 'Barrierefreiheit',
  'settings.privacy': 'Datenschutz',
  'settings.theme': 'Design',
  'settings.themeDesc': 'Zwischen hellem und dunklem Modus wechseln',
  'settings.language': 'Sprache',
  'settings.languageDesc': 'Wählen Sie Ihre bevorzugte Sprache',
  'settings.darkMode': 'Dunkler Modus',
  'settings.lightMode': 'Heller Modus',
  'settings.darkTheme': 'Dunkel',
  'settings.lightTheme': 'Hell',
  'settings.profile': 'Profilinformationen',
  'settings.profileDesc': 'Aktualisieren Sie Ihre persönlichen Daten',
  'settings.saved': 'Einstellungen gespeichert!',
  'settings.saveFailed': 'Speichern fehlgeschlagen',

  // Accounting & Assets
  'accounting.title': 'Anlagenbuchhaltung',
  'accounting.subtitle': 'Anlagevermögen, Abschreibungen und Zuordnungen verwalten',
  'accounting.assetRegister': 'Anlagenregister',
  'accounting.allItems': 'Alle Einträge',
  'accounting.transactions': 'Transaktionen',
  'accounting.createAsset': 'Anlage erstellen',
  'accounting.assetDetails': 'Anlagendetails',
  'accounting.trackedAssets': 'Erfasste Anlagen',
  'accounting.totalBookValue': 'Gesamt-Buchwert',
  'accounting.accumulatedDepreciation': 'Kumulierte Abschreibung',
  'accounting.acquisitionCost': 'Anschaffungskosten',
  'accounting.assetNumber': 'Anlagennummer',
  'accounting.assetClass': 'Anlagenklasse',
  'accounting.subClass': 'Unterklasse',
  'accounting.depreciationMethod': 'Abschreibungsmethode',
  'accounting.usefulLifeMonths': 'Nutzungsdauer (Monate)',
  'accounting.salvageValue': 'Restwert',
  'accounting.acquisitionDate': 'Anschaffungsdatum',
  'accounting.bookValue': 'Buchwert',
  'accounting.fairMarketValue': 'Marktwert',
  'accounting.status': 'Status',
  'accounting.active': 'Aktiv',
  'accounting.disposed': 'Veräußert',
  'accounting.writtenOff': 'Abgeschrieben',
  'accounting.heldForSale': 'Zum Verkauf',
  'accounting.impaired': 'Wertgemindert',
  'accounting.transferred': 'Übertragen',
  'accounting.straightLine': 'Linear',
  'accounting.decliningBalance': 'Degressiv',
  'accounting.doubleDeclining': 'Doppelt degressiv',
  'accounting.sumOfYears': 'Digitale Abschreibung',
  'accounting.unitsOfProduction': 'Leistungsabschreibung',
  'accounting.noDepreciation': 'Keine Abschreibung',
  'accounting.classBuildings': 'Gebäude',
  'accounting.classMachinery': 'Maschinen',
  'accounting.classEquipment': 'Ausrüstung',
  'accounting.classVehicles': 'Fahrzeuge',
  'accounting.classFurniture': 'Möbel',
  'accounting.classComputers': 'Computer',
  'accounting.classSoftware': 'Software',
  'accounting.classCollectibles': 'Sammlerstücke',
  'accounting.classArt': 'Kunst',
  'accounting.classJewelry': 'Schmuck',
  'accounting.classRealEstate': 'Immobilien',
  'accounting.classInvestments': 'Investitionen',
  'accounting.classOther': 'Sonstiges',
  'accounting.monthlyDepreciation': 'Monatliche Abschreibung',
  'accounting.yearlyDepreciation': 'Jährliche Abschreibung',
  'accounting.remainingLife': 'Restlaufzeit',
  'accounting.percentDepreciated': 'Abschreibungsgrad',
  'accounting.selectItem': 'Eintrag wählen',
  'accounting.selectMethod': 'Abschreibungsmethode wählen',
  'accounting.selectClass': 'Anlagenklasse wählen',
  'accounting.selectStatus': 'Status wählen',
  'accounting.recordImpairment': 'Wertminderung erfassen',
  'accounting.recordRevaluation': 'Neubewertung erfassen',
  'accounting.disposeAsset': 'Anlage veräußern',
  'accounting.impairmentAmount': 'Wertminderungsbetrag',
  'accounting.revaluedAmount': 'Neubewerteter Betrag',
  'accounting.disposalDate': 'Veräußerungsdatum',
  'accounting.disposalProceeds': 'Verkaufserlös',
  'accounting.notes': 'Notizen',
  'accounting.depreciationSchedule': 'Abschreibungsplan',
  'accounting.transactionHistory': 'Transaktionsverlauf',
  'accounting.noAssets': 'Keine Anlagen gefunden',
  'accounting.noItems': 'Keine Einträge gefunden',
  'accounting.noTransactions': 'Keine Transaktionen gefunden',
  'accounting.searchAssets': 'Anlagen suchen...',
  'accounting.searchItems': 'Einträge suchen...',
  'accounting.filterByStatus': 'Nach Status filtern',
  'accounting.filterByMethod': 'Nach Methode filtern',
  'accounting.filterByClass': 'Nach Klasse filtern',
  'accounting.filterByCategory': 'Nach Kategorie filtern',
  'accounting.showTracked': 'Nur erfasste',
  'accounting.showUntracked': 'Nur nicht erfasste',
  'accounting.sortBy': 'Sortieren nach',
  'accounting.sortByName': 'Name',
  'accounting.sortByCost': 'Kosten',
  'accounting.sortByBook': 'Buchwert',
  'accounting.sortByDepreciation': 'Abschreibung',
  'accounting.sortByDate': 'Datum',
  'accounting.page': 'Seite',
  'accounting.of': 'von',
  'accounting.showing': 'Zeige',
  'accounting.assets': 'Anlagen',
  'accounting.items': 'Einträge',
  'accounting.months': 'Monate',
  'accounting.years': 'Jahre',
  'accounting.assetCreated': 'Anlage erfolgreich erstellt',
  'accounting.assetUpdated': 'Anlage erfolgreich aktualisiert',
  'accounting.assetDisposed': 'Anlage erfolgreich veräußert',
  'accounting.impairmentRecorded': 'Wertminderung erfolgreich erfasst',
  'accounting.revaluationRecorded': 'Neubewertung erfolgreich erfasst',
  'accounting.costAllocation': 'Kostenzuordnung',
  'accounting.allocations': 'Zuordnungen',
  'accounting.department': 'Abteilung',
  'accounting.project': 'Projekt',
  'accounting.costCenter': 'Kostenstelle',
  'accounting.allocatedTo': 'Zugeordnet an',
  'accounting.allocationPercent': 'Zuordnung %',
  'accounting.addAllocation': 'Zuordnung hinzufügen',
  'accounting.removeAllocation': 'Zuordnung entfernen',
  'accounting.totalAllocation': 'Gesamt-Zuordnung',
  'accounting.mustBe100': 'Gesamt-Zuordnung muss 100% ergeben',
  'accounting.selectDepartment': 'Abteilung wählen',
  'accounting.selectProject': 'Projekt wählen',
  'accounting.selectCostCenter': 'Kostenstelle wählen',
  'accounting.noDepartments': 'Keine Abteilungen gefunden',
  'accounting.noProjects': 'Keine Projekte gefunden',
  'accounting.noCostCenters': 'Keine Kostenstellen gefunden',
  'accounting.createDepartment': 'Abteilung erstellen',
  'accounting.createProject': 'Projekt erstellen',
  'accounting.createCostCenter': 'Kostenstelle erstellen',
  'accounting.departmentName': 'Abteilungsname',
  'accounting.projectName': 'Projektname',
  'accounting.costCenterName': 'Kostenstellenname',
  'accounting.departmentCode': 'Abteilungscode',
  'accounting.projectCode': 'Projektcode',
  'accounting.costCenterCode': 'Kostenstellencode',
  'accounting.manager': 'Leiter',
  'accounting.budget': 'Budget',
  'accounting.startDate': 'Startdatum',
  'accounting.endDate': 'Enddatum',
  'accounting.allocationReport': 'Zuordnungsbericht',
  'accounting.byDepartment': 'Nach Abteilung',
  'accounting.byProject': 'Nach Projekt',
  'accounting.byCostCenter': 'Nach Kostenstelle',
  'accounting.exportReport': 'Bericht exportieren',
  'accounting.depreciationReport': 'Abschreibungsbericht',
  'accounting.assetValuation': 'Anlagenbewertung',
  'accounting.gainLoss': 'Gewinn/Verlust',

  // Accounting KPIs
  'accounting.kpi.trackedAssets': 'Nachverfasste Vermögenswerte',
  'accounting.kpi.itemsWithAccounting': 'Elemente mit Buchhaltung',
  'accounting.kpi.totalBookValue': 'Gesamter Buchwert',
  'accounting.kpi.currentNetValue': 'Aktueller Nettowert',
  'accounting.kpi.accDepreciation': 'Akkumulierte Abschreibung',
  'accounting.kpi.ofCost': 'von Kosten',
  'accounting.kpi.acquisitionCost': 'Anschaffungskosten',
  'accounting.kpi.originalInvestment': 'Ursprüngliche Investition',

  // Accounting Tabs
  'accounting.tabs.register': 'Anlagenregister',
  'accounting.tabs.items': 'Alle Elemente',
  'accounting.tabs.transactions': 'Transaktionen',
  'accounting.allStatus': 'Alle Status',
  'accounting.allMethods': 'Alle Methoden',
  'accounting.allClasses': 'Alle Klassen',
  'accounting.allCategories': 'Alle Kategorien',
  'accounting.allConditions': 'Alle Zustände',
  'accounting.allItemsFilter': 'Alle Artikel',
  'accounting.trackedOnly': 'Nur nachverfolgte',
  'accounting.untrackedOnly': 'Nur nicht nachverfolgte',
  'accounting.allTypes': 'Alle Typen',
  'accounting.itemCol': 'Artikel',
  'accounting.acquisitionCol': 'Anschaffung',
  'accounting.bookValueCol': 'Buchwert',
  'accounting.depreciationCol': 'Abschreibung',
  'accounting.methodCol': 'Methode',
  'accounting.statusCol': 'Status',
  'accounting.actionsCol': 'Aktionen',
  'accounting.categoryCol': 'Kategorie',
  'accounting.purchasePriceCol': 'Kaufpreis',
  'accounting.dateCol': 'Datum',
  'accounting.trackingCol': 'Nachverfolgung',
  'accounting.actionCol': 'Aktion',
  'accounting.noTrackedYet': 'Noch keine nachverfolgten Vermögenswerte',
  'accounting.noAssetsMatch': 'Keine Vermögenswerte entsprechen den Filtern',
  'accounting.browseItems': 'Artikel durchsuchen',
  'accounting.clearFilters': 'Filter löschen',
  'accounting.tracked': 'Nachverfolgt',
  'accounting.notTracked': 'Nicht nachverfolgt',
  'accounting.viewDetail': 'Ansehen',
  'accounting.trackItem': 'Nachverfolgen',
  'accounting.valueInfo': 'Wertinformationen',
  'accounting.depInfo': 'Abschreibung',
  'accounting.accDep': 'Kum. Abschreibung',
  'accounting.monthly': 'Monatlich',
  'accounting.remainingMonths': 'Verbleibend',
  'accounting.depPercent': 'Abgeschrieben',
  'accounting.recentTx': 'Neueste Transaktionen',
  'accounting.depreciate': 'Abschreiben',
  'accounting.impairmentAction': 'Wertminderung',
  'accounting.revalueAction': 'Neubewertung',
  'accounting.disposeAction': 'Entsorgen',
  'accounting.trackAsset': 'Vermögenswert erfassen',
  'accounting.cancel': 'Abbrechen',
  'accounting.creating': 'Wird erstellt…',
  'accounting.create': 'Vermögenswert erstellen',
  'accounting.confirmDisposal': 'Entsorgung bestätigen',
  'accounting.searchAssetsPlaceholder': 'Vermögenswerte suchen…',
  'accounting.searchItemsPlaceholder': 'Artikel suchen…',
  'accounting.searchTxPlaceholder': 'Transaktionen suchen…',
  'accounting.noItemsInLib': 'Keine Artikel gefunden',
  'accounting.noItemsMatch': 'Keine Artikel entsprechen den Filtern',
  'accounting.addItemsFirst': 'Fügen Sie zuerst Artikel zu Ihrer Bibliothek hinzu.',
  'accounting.noTxYet': 'Noch keine Transaktionen',
  'accounting.noTxMatch': 'Keine Transaktionen entsprechen den Filtern',
  'accounting.startDepreciation': 'Beginnen Sie mit der Nachverfolgung von Vermögenswerten und der Abschreibung.',
  'accounting.adjustTxFilter': 'Versuchen Sie, Ihre Suche oder Filter anzupassen.',
  'accounting.adjustFilters': 'Versuchen Sie, Ihre Suche oder Filter anzupassen.',
  'accounting.trackedAssetCount': 'Nachverfolgte Vermögenswerte ({count})',
  'accounting.filteredAssets': '{filtered} von {total} Vermögenswerten',
  'accounting.yourItemsCount': 'Ihre Artikel ({count})',
  'accounting.filteredItems': '{filtered} von {total} Artikeln',
  'accounting.txCount': 'Transaktionen ({count})',
  'accounting.filteredTx': '{filtered} von {total} Transaktionen',
  'accounting.unnamed': 'Unbenannt',
  'accounting.unclassified': 'Nicht klassifiziert',
  'accounting.unknown': 'Unbekannt',
  'accounting.txDateCol': 'Datum',
  'accounting.txAssetCol': 'Vermögenswert',
  'accounting.txTypeCol': 'Typ',
  'accounting.txDescCol': 'Beschreibung',
  'accounting.txAmountCol': 'Betrag',
  'accounting.bookValueAfterCol': 'Buchwert danach',
  'accounting.startTracking': 'Beginnen Sie mit dem Hinzufügen von Artikeln zu Ihrem Vermögensverzeichnis.',
  'accounting.tipAutoFill': 'Tipp: Verwenden Sie die Registerkarte „Alle Artikel" für einfacheres Tracking.',
  'accounting.itemIdLabel': 'Artikel-ID *',
  'accounting.pasteItemId': 'Artikel-ID einfügen',
  'accounting.selectClassOption': 'Klasse auswählen…',
  'accounting.impairmentDesc': 'Wertminderung reduziert den Buchwert, wenn der erzielbare Betrag unter dem Buchwert liegt.',
  'accounting.impairmentAmountLabel': 'Wertminderungsbetrag *',
  'accounting.reasonLabel': 'Grund',
  'accounting.reasonPlaceholder': 'Grund für die Wertminderung',
  'accounting.effectiveDateLabel': 'Gültigkeitsdatum',
  'accounting.revalDesc': 'Passt den Vermögenswert basierend auf professioneller Bewertung oder Markteinschätzung an.',
  'accounting.newAppraisedValue': 'Neuer Schätzwert *',
  'accounting.appraiserName': 'Gutachtername',
  'accounting.valuationMethodLabel': 'Bewertungsmethode',
  'accounting.valuationMethodPlaceholder': 'z.B. Marktvergleich',
  'accounting.notesLabel': 'Notizen',
  'accounting.optionalNotes': 'Optionale Notizen',
  'accounting.disposeDesc': 'Die Entsorgung entfernt den Vermögenswert aus der aktiven Nachverfolgung.',
  'accounting.disposalAmountLabel': 'Verkaufs- / Entsorgungsbetrag',
  'accounting.reasonSelect': 'Grund',
  'accounting.sold': 'Verkauft',
  'accounting.donated': 'Gespendet',
  'accounting.scrapped': 'Verschrottet',
  'accounting.stolenLost': 'Gestohlen / Verloren',
  'accounting.tradedIn': 'In Zahlung gegeben',
  'accounting.otherReason': 'Sonstiges',
  'accounting.optional': 'Optional',
  'accounting.expectedValue': 'Erwarteter Wert am Ende der Nutzungsdauer',

  // Workspaces
  'workspaces.title': 'Arbeitsbereiche',
  'workspaces.myWorkspaces': 'Meine Arbeitsbereiche',
  'workspaces.create': 'Arbeitsbereich erstellen',
  'workspaces.switchTo': 'Wechseln zu',
  'workspaces.settings': 'Arbeitsbereich-Einstellungen',
  'workspaces.members': 'Mitglieder',
  'workspaces.personal': 'Persönlich',
  'workspaces.organization': 'Organisation',
  'workspaces.name': 'Arbeitsbereichsname',
  'workspaces.description': 'Beschreibung',
  'workspaces.type': 'Typ',
  'workspaces.items': 'Elemente',
  'workspaces.totalItems': 'Gesamte Elemente',
  'workspaces.active': 'Aktiv',
  'workspaces.viewAll': 'Alle Arbeitsbereiche anzeigen',
  'workspaces.noWorkspaces': 'Noch keine Arbeitsbereiche',
  'workspaces.createFirst': 'Erstellen Sie Ihren ersten Arbeitsbereich',
  'workspaces.searchPlaceholder': 'Arbeitsbereiche durchsuchen...',
  'workspaces.sortByRecent': 'Neueste',
  'workspaces.sortByName': 'Name',
  'workspaces.sortByItems': 'Elemente',
  'workspaces.sortByMembers': 'Mitglieder',
  'workspaces.hub': 'Arbeitsbereich Hub',
  'workspaces.manageSubtitle': 'Verwalten, organisieren und zusammenarbeiten über alle Arbeitsbereiche',
  'workspaces.new': 'Neuer Arbeitsbereich',
  'workspaces.organizations': 'Organisationen',
  'workspaces.loading': 'Arbeitsbereiche werden geladen...',
  'workspaces.welcome': 'Willkommen im Arbeitsbereich Hub',
  'workspaces.welcomeSubtitle': 'Erstellen Sie Ihren ersten Arbeitsbereich.',
  'workspaces.personalDesc': 'Privater Arbeitsbereich nur für Sie',
  'workspaces.orgDesc': 'Zusammenarbeit mit Ihrem Team',
  'workspaces.enterName': 'Arbeitsbereich-Name eingeben...',
  'workspaces.creating': 'Wird erstellt...',
  'workspaces.getStarted': 'Loslegen',
  'workspaces.activeWorkspace': 'Aktiver Arbeitsbereich',
  'workspaces.switchToWorkspace': 'Zum Arbeitsbereich wechseln',
  'workspaces.openDashboard': 'Dashboard öffnen',
  'workspaces.createNew': 'Neuen Arbeitsbereich erstellen',
  'workspaces.personalOrOrg': 'Persönlich oder Organisation',
  'workspaces.noFound': 'Keine Arbeitsbereiche gefunden',
  'workspaces.clearSearch': 'Suche löschen',
  'workspaces.edit': 'Arbeitsbereich bearbeiten',
  'workspaces.delete': 'Arbeitsbereich löschen',
  'workspaces.cannotBeUndone': 'Diese Aktion kann nicht rückgängig gemacht werden',
  'workspaces.deletePermanently': 'Dauerhaft löschen',
  'workspaces.cancel': 'Abbrechen',
  'workspaces.save': 'Änderungen speichern',
  'workspaces.continue': 'Weiter',
  'workspaces.back': 'Zurück',
  'workspaces.typeStep': 'Typ',
  'workspaces.detailsStep': 'Details',
  'workspaces.reviewStep': 'Überprüfung',
  'workspaces.chooseType': 'Wählen Sie den Typ des Arbeitsbereichs',
  'workspaces.personalWorkspace': 'Persönlicher Arbeitsbereich',
  'workspaces.configurePersonal': 'Konfigurieren Sie Ihren persönlichen Arbeitsbereich',
  'workspaces.configureOrg': 'Konfigurieren Sie Ihre Organisation',
  'workspaces.reviewAndCreate': 'Überprüfen und erstellen',
  'workspaces.whatYouGet': 'Was Sie erhalten:',
  'workspaces.nameRequired': 'Arbeitsbereich-Name *',
  'workspaces.descLabel': 'Beschreibung',
  'workspaces.whatFor': 'Wofür ist dieser Arbeitsbereich?',
  'workspaces.privateSecure': 'Privat & Sicher',
  'workspaces.fullItemMgmt': 'Volle Artikelverwaltung',
  'workspaces.personalAnalytics': 'Persönliche Analysen',
  'workspaces.teamCollaboration': 'Team-Zusammenarbeit',
  'workspaces.roleBasedAccess': 'Rollenbasierte Zugriffskontrolle',
  'workspaces.orgDashboard': 'Organisations-Dashboard',
  'workspaces.inviteMembers': 'Mitglieder einladen',
  'workspaces.unlimitedItems': 'Unbegrenzte Artikel',
  'workspaces.private': 'Privat',
  'workspaces.itemMgmtCategories': 'Artikelverwaltung mit Kategorien',
  'workspaces.aiCategorization': 'KI-gestützte Kategorisierung',
  'workspaces.analyticsDashboard': 'Analyse-Dashboard',
  'workspaces.teamInvitations': 'Team-Einladungen',
  'workspaces.rbac': 'Rollenbasierte Zugriffskontrolle',
  'workspaces.auditTrail': 'Organisations-Audit-Trail',
  'workspaces.untitled': 'Ohne Titel',
  'workspaces.createWorkspace': 'Arbeitsbereich erstellen',
  'workspaces.switchThis': 'Hierhin wechseln',
  'workspaces.viewOrg': 'Organisation anzeigen',
  'workspaces.convertToOrg': 'In Organisation umwandeln',
  'workspaces.star': 'Favorit',
  'workspaces.unstar': 'Favorit entfernen',
  'workspaces.grid': 'Raster',
  'workspaces.list': 'Liste',

  // Workspace Settings
  'wsSettings.loading': 'Arbeitsbereich wird geladen...',
  'wsSettings.notFound': 'Arbeitsbereich nicht gefunden',
  'wsSettings.backToWorkspaces': 'Zurück zu Arbeitsbereichen',
  'wsSettings.savedSuccess': 'Einstellungen erfolgreich gespeichert',
  'wsSettings.inviteSent': 'Einladung erfolgreich gesendet',
  'wsSettings.roleUpdated': 'Rolle erfolgreich aktualisiert',
  'wsSettings.confirmRemoveMember': 'Möchten Sie dieses Mitglied wirklich entfernen?',
  'wsSettings.memberRemoved': 'Mitglied erfolgreich entfernt',
  'wsSettings.inviteRevoked': 'Einladung widerrufen',
  'wsSettings.ownershipTransferred': 'Eigentum erfolgreich übertragen',
  'wsSettings.typeNameToConfirm': 'Bitte geben Sie den Namen des Arbeitsbereichs zur Bestätigung ein',
  'wsSettings.convertedSuccess': 'Arbeitsbereich in Organisation umgewandelt!',
  'wsSettings.organization': 'Organisation',
  'wsSettings.personalWorkspace': 'Persönlicher Arbeitsbereich',
  'wsSettings.owner': 'Besitzer',
  'wsSettings.general': 'Allgemein',
  'wsSettings.members': 'Mitglieder',
  'wsSettings.convertTab': 'Konvertieren',
  'wsSettings.dangerZone': 'Gefahrenzone',
  'wsSettings.generalSettings': 'Allgemeine Einstellungen',
  'wsSettings.workspaceName': 'Name des Arbeitsbereichs',
  'wsSettings.enterName': 'Name eingeben',
  'wsSettings.description': 'Beschreibung',
  'wsSettings.descriptionPlaceholder': 'Beschreiben Sie diesen Arbeitsbereich...',
  'wsSettings.workspaceId': 'Arbeitsbereich-ID',
  'wsSettings.created': 'Erstellt',
  'wsSettings.items': 'Elemente',
  'wsSettings.categories': 'Kategorien',
  'wsSettings.saving': 'Speichern...',
  'wsSettings.saveChanges': 'Änderungen speichern',
  'wsSettings.inviteMember': 'Mitglied einladen',
  'wsSettings.emailPlaceholder': 'E-Mail-Adresse',
  'wsSettings.sendInvite': 'Einladung senden',
  'wsSettings.pendingInvitations': 'Ausstehende Einladungen',
  'wsSettings.revoke': 'Widerrufen',
  'wsSettings.currentMembers': 'Aktuelle Mitglieder',
  'wsSettings.loadingMembers': 'Mitglieder werden geladen...',
  'wsSettings.remove': 'Entfernen',
  'wsSettings.convertToOrg': 'In Organisation umwandeln',
  'wsSettings.upgradeTitle': 'Auf Organisation upgraden',
  'wsSettings.upgradeDesc': 'Schalten Sie Kollaborationsfunktionen frei, indem Sie Ihren persönlichen Arbeitsbereich in eine Organisation umwandeln.',
  'wsSettings.featInvite': 'Mitglieder einladen',
  'wsSettings.featInviteDesc': 'Teammitglieder hinzufügen und zusammenarbeiten',
  'wsSettings.featRBAC': 'Rollenbasierter Zugriff',
  'wsSettings.featRBACDesc': 'Rollen zuweisen und Berechtigungen steuern',
  'wsSettings.featTeams': 'Teams & Gruppen',
  'wsSettings.featTeamsDesc': 'Mitglieder in Teams organisieren',
  'wsSettings.convertNote': 'Diese Aktion ist unwiderruflich. Ihre Daten und Einstellungen bleiben erhalten.',
  'wsSettings.convertNow': 'Jetzt konvertieren',
  'wsSettings.transferOwnership': 'Eigentum übertragen',
  'wsSettings.transferDesc': 'Übertragen Sie diesen Arbeitsbereich an ein anderes Mitglied.',
  'wsSettings.transfer': 'Übertragen',
  'wsSettings.deleteWorkspace': 'Arbeitsbereich löschen',
  'wsSettings.deleteDesc': 'Diesen Arbeitsbereich und alle Daten dauerhaft löschen.',
  'wsSettings.delete': 'Löschen',
  'wsSettings.cannotBeUndone': 'Diese Aktion kann nicht rückgängig gemacht werden!',
  'wsSettings.transferWarn': 'Sie verlieren den Besitzerzugang und werden ein normales Mitglied.',
  'wsSettings.newOwner': 'Neuer Besitzer',
  'wsSettings.selectMember': 'Mitglied auswählen',
  'wsSettings.cancel': 'Abbrechen',
  'wsSettings.transferring': 'Übertragen...',
  'wsSettings.deleteWarning': 'Dies löscht den Arbeitsbereich dauerhaft',
  'wsSettings.typeToConfirm': 'Geben Sie den Namen zur Bestätigung ein:',
  'wsSettings.deleting': 'Löschen...',
  'wsSettings.deletePermanently': 'Dauerhaft löschen',
  'wsSettings.converting': 'Konvertieren...',

  // Notifications
  'notifications.title': 'Benachrichtigungen',
  'notifications.markAsRead': 'Als gelesen markieren',
  'notifications.markAllAsRead': 'Alle als gelesen markieren',
  'notifications.delete': 'Löschen',
  'notifications.deleteAll': 'Alle löschen',
  'notifications.clearAll': 'Alle löschen',
  'notifications.unread': 'Ungelesen',
  'notifications.all': 'Alle',
  'notifications.noNotifications': 'Keine Benachrichtigungen',
  'notifications.noUnread': 'Keine ungelesenen Benachrichtigungen',
  'notifications.today': 'Heute',
  'notifications.yesterday': 'Gestern',
  'notifications.older': 'Älter',
  'notifications.justNow': 'Gerade eben',
  'notifications.minutesAgo': 'vor {count}m',
  'notifications.hoursAgo': 'vor {count}h',
  'notifications.daysAgo': 'vor {count}d',
  'notifications.search': 'Benachrichtigungen durchsuchen...',
  'notifications.settings': 'Benachrichtigungseinstellungen',
  'notifications.types.itemUpdate': 'Element-Updates',
  'notifications.types.comment': 'Kommentare',
  'notifications.types.aiSuggestion': 'KI-Vorschläge',
  'notifications.types.duplicate': 'Duplikate',
  'notifications.types.valueChanged': 'Wertänderungen',
  'notifications.types.security': 'Sicherheit',
  'notifications.types.storage': 'Speicher',
  'notifications.types.mention': 'Erwähnungen',
  'notifications.types.share': 'Geteilt',
  'notifications.types.system': 'System',
  'notifications.loading': 'Benachrichtigungen werden geladen...',
  'notifications.noMatching': 'Keine passenden Benachrichtigungen',
  'notifications.tryAdjusting': 'Versuchen Sie, Ihre Filter anzupassen',
  'notifications.deleteConfirm': 'Alle Benachrichtigungen löschen? Dies kann nicht rückgängig gemacht werden.',
  'notifications.markAllRead': 'Alle als gelesen markieren',
  'notifications.markRead': 'Als gelesen markieren',
  'notifications.markUnread': 'Als ungelesen markieren',
  'notifications.allCaughtUp': 'Sie sind auf dem neuesten Stand!',
  'notifications.showAll': 'Alle anzeigen',
  'notifications.empty': 'Keine Benachrichtigungen',
  'notifications.viewAll': 'Alle Benachrichtigungen anzeigen',
  'notifications.clickToView': 'Klicken zum Anzeigen',
  'notifications.moreNotifications': '+{count} weitere Benachrichtigungen',

  // Two-Factor Authentication
  'twofa.title': 'Zwei-Faktor-Authentifizierung',
  'twofa.subtitle': 'Fügen Sie Ihrem Konto eine zusätzliche Sicherheitsebene hinzu',
  'twofa.enabled': '2FA aktiviert',
  'twofa.disabled': '2FA deaktiviert',
  'twofa.enable': '2FA aktivieren',
  'twofa.disable': '2FA deaktivieren',
  'twofa.status': 'Status',
  'twofa.setup': 'Einrichten',
  'twofa.setupInstructions': 'Scannen Sie den QR-Code mit Ihrer Authenticator-App',
  'twofa.manualEntry': 'Oder geben Sie diesen Code manuell ein',
  'twofa.verificationCode': 'Bestätigungscode',
  'twofa.enterCode': '6-stelligen Code eingeben',
  'twofa.verify': 'Verifizieren',
  'twofa.backupCodes': 'Backup-Codes',
  'twofa.backupCodesInfo': 'Bewahren Sie diese Codes an einem sicheren Ort auf',
  'twofa.backupCodesRemaining': '{count} Backup-Codes übrig',
  'twofa.regenerateBackupCodes': 'Backup-Codes neu generieren',
  'twofa.downloadBackupCodes': 'Codes herunterladen',
  'twofa.copyCode': 'Code kopieren',
  'twofa.copied': 'Kopiert!',
  'twofa.setupSuccess': 'Zwei-Faktor-Authentifizierung erfolgreich aktiviert',
  'twofa.disableSuccess': 'Zwei-Faktor-Authentifizierung deaktiviert',
  'twofa.invalidCode': 'Ungültiger Bestätigungscode',
  'twofa.required': '2FA-Code erforderlich',
  'twofa.step1': 'Schritt 1: Authenticator-App installieren',
  'twofa.step2': 'Schritt 2: QR-Code scannen',
  'twofa.step3': 'Schritt 3: Verifizieren',
  'twofa.apps': 'Empfohlene Apps: Google Authenticator, Authy, Microsoft Authenticator',
  'twofa.loading': '2FA-Einstellungen werden geladen...',
  'twofa.whyEnable': 'Warum 2FA aktivieren?',
  'twofa.protectsAccount': 'Schützt Ihr Konto auch bei kompromittiertem Passwort',
  'twofa.timeBasedCodes': 'Verwendet zeitbasierte Codes von Authenticator-Apps',
  'twofa.backupAccess': 'Backup-Codes für den Notfallzugang',
  'twofa.enableTwoFactor': 'Zwei-Faktor-Authentifizierung aktivieren',
  'twofa.accountProtected': 'Ihr Konto ist durch 2FA geschützt',
  'twofa.scanQrCode': 'Schritt 1: QR-Code scannen',
  'twofa.scanQrDesc': 'Scannen Sie den QR-Code mit Ihrer Authenticator-App',
  'twofa.saveBackupCodes': 'Schritt 2: Backup-Codes speichern',
  'twofa.saveBackupDesc': 'Speichern Sie diese Codes an einem sicheren Ort.',
  'twofa.verifyStep': 'Schritt 3: Verifizieren',
  'twofa.verifyDesc': 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein',
  'twofa.cantScan': 'Können Sie nicht scannen? Geben Sie diesen Code manuell ein:',
  'twofa.verifyAndEnable': 'Verifizieren & Aktivieren',
  'twofa.cancelSetup': 'Einrichtung abbrechen',
  'twofa.disableConfirm': 'Zwei-Faktor-Authentifizierung deaktivieren?',
  'twofa.lessSecure': 'Ihr Konto wird ohne 2FA weniger sicher sein.',
  'twofa.enterCodeConfirm': 'Geben Sie einen Bestätigungscode ein:',
  'twofa.codeOrBackup': 'Code oder Backup-Code',
  'twofa.regenerateTitle': 'Backup-Codes neu generieren',
  'twofa.regenerateWarning': 'Dies macht alle bestehenden Backup-Codes ungültig.',
  'twofa.generateNew': 'Neue Codes generieren',
  'twofa.newCodes': 'Neue Backup-Codes',
  'twofa.saveNewCodes': 'Speichern Sie diese Codes. Ihre alten Codes sind ungültig.',
  'twofa.download': 'Herunterladen',
  'twofa.done': 'Fertig',
  'twofa.twofaEnabled': '2FA Aktiviert!',
  'twofa.saveBeforeClose': 'Stellen Sie sicher, dass Sie Ihre Backup-Codes gespeichert haben.',
  'twofa.savedMyCodes': 'Ich habe meine Codes gespeichert',

  // Misc
  'misc.noData': 'Keine Daten verfügbar',
  'misc.error': 'Ein Fehler ist aufgetreten',
  'misc.success': 'Erfolg',
  'misc.to': 'bis',
  'misc.yes': 'Ja',
  'misc.no': 'Nein',

  // Audit Trail
  'audit.title': 'Aktivitätsprotokoll',
  'audit.subtitle': 'Verfolgen Sie alle Änderungen und Aktivitäten in Ihrem Konto',
  'audit.tableView': 'Tabellenansicht',
  'audit.timelineView': 'Zeitachse',
  'audit.dashboardView': 'Organisations-Dashboard',
  'audit.filters': 'Filter',
  'audit.exportCsv': 'CSV exportieren',
  'audit.search': 'Nach Benutzer, Aktion oder Ressource suchen...',
  'audit.totalEvents': 'Ereignisse gesamt',
  'audit.eventsToday': 'Ereignisse heute',
  'audit.activeUsers': 'Aktive Benutzer',
  'audit.topActions': 'Top-Aktionen',
  'audit.actionType': 'Aktionstyp',
  'audit.allActions': 'Alle Aktionen',
  'audit.resourceType': 'Ressourcentyp',
  'audit.allResources': 'Alle Ressourcen',
  'audit.startDate': 'Startdatum',
  'audit.endDate': 'Enddatum',
  'audit.clear': 'Löschen',
  'audit.time': 'Zeit',
  'audit.user': 'Benutzer',
  'audit.action': 'Aktion',
  'audit.resource': 'Ressource',
  'audit.details': 'Details',
  'audit.source': 'Quelle',
  'audit.noLogs': 'Keine Protokolleinträge gefunden',
  'audit.emptyState': 'Aktivitäten werden hier angezeigt, sobald Aktionen durchgeführt werden',
  'audit.loading': 'Laden...',
  'audit.loadingTimeline': 'Zeitachse wird geladen...',
  'audit.loadingDashboard': 'Organisations-Dashboard wird geladen...',
  'audit.page': 'Seite {current} von {total}',
  'audit.events': 'Ereignisse',
  'audit.actions': 'Aktionen',
  'audit.system': 'System',
  'audit.unknown': 'Unbekannt',
  'audit.empty': '(leer)',
  'audit.revert': 'Änderung rückgängig machen',
  'audit.reverting': 'Wird rückgängig gemacht...',
  'audit.revertToPrevious': 'Auf vorherigen Wert zurücksetzen',
  'audit.logDetails': 'Protokolldetails',
  'audit.eventInfo': 'Ereignisinformationen',
  'audit.timestamp': 'Zeitstempel',
  'audit.resourceId': 'Ressourcen-ID',
  'audit.resourceName': 'Ressourcenname',
  'audit.userInfo': 'Benutzerinformationen',
  'audit.userId': 'Benutzer-ID',
  'audit.role': 'Rolle',
  'audit.fieldChange': 'Feldänderung',
  'audit.oldValue': 'Alter Wert',
  'audit.newValue': 'Neuer Wert',
  'audit.techDetails': 'Technische Details',
  'audit.ipAddress': 'IP-Adresse',
  'audit.device': 'Gerät',
  'audit.userAgent': 'User-Agent',
  'audit.metadata': 'Zusätzliche Metadaten',
  'audit.revertChange': 'Änderung rückgängig machen',
  'audit.dashboardTitle': 'Organisations-Audit-Dashboard',
  'audit.lastDays': 'Letzte {days} Tage',
  'audit.today': 'Heute',
  'audit.mostActiveUsers': 'Aktivste Benutzer',
  'audit.recentSecurity': 'Aktuelle Sicherheitsereignisse',
  'audit.actionDistribution': 'Aktionsverteilung',
  'audit.noData': 'Organisations-Dashboard-Daten sind nicht verfügbar',
  'audit.minutesAgo': 'vor {count}m',
  'audit.hoursAgo': 'vor {count}h',
  'audit.daysAgo': 'vor {count}T',
  'audit.justNow': 'gerade eben',
  'audit.group.items': 'Artikel',
  'audit.group.imagesFiles': 'Bilder & Dateien',
  'audit.group.comments': 'Kommentare',
  'audit.group.categories': 'Kategorien',
  'audit.group.auth': 'Authentifizierung',
  'audit.group.security': 'Sicherheit',
  'audit.group.members': 'Mitglieder & Einladungen',
  'audit.group.ai': 'KI & Automatisierung',
  'audit.group.notifications': 'Benachrichtigungen & System',
  'audit.group.bulk': 'Massenaktionen & Export',
  'audit.action.itemCreated': 'Artikel erstellt',
  'audit.action.itemUpdated': 'Artikel aktualisiert',
  'audit.action.itemDeleted': 'Artikel gelöscht',
  'audit.action.itemRestored': 'Artikel wiederhergestellt',
  'audit.action.favoriteAdded': 'Favorit hinzugefügt',
  'audit.action.favoriteRemoved': 'Favorit entfernt',
  'audit.action.imageUploaded': 'Bild hochgeladen',
  'audit.action.imageDeleted': 'Bild gelöscht',
  'audit.action.fileUploaded': 'Datei hochgeladen',
  'audit.action.fileDeleted': 'Datei gelöscht',
  'audit.action.commentAdded': 'Kommentar hinzugefügt',
  'audit.action.commentEdited': 'Kommentar bearbeitet',
  'audit.action.commentDeleted': 'Kommentar gelöscht',
  'audit.action.categoryCreated': 'Kategorie erstellt',
  'audit.action.categoryUpdated': 'Kategorie aktualisiert',
  'audit.action.categoryDeleted': 'Kategorie gelöscht',
  'audit.action.login': 'Anmeldung',
  'audit.action.logout': 'Abmeldung',
  'audit.action.registration': 'Registrierung',
  'audit.action.loginFailed': 'Anmeldung fehlgeschlagen',
  'audit.action.passwordChanged': 'Passwort geändert',
  'audit.action.passwordReset': 'Passwort-Zurücksetzung angefordert',
  'audit.action.profileUpdated': 'Profil aktualisiert',
  'audit.action.twoFaEnabled': '2FA aktiviert',
  'audit.action.twoFaDisabled': '2FA deaktiviert',
  'audit.action.accountDeleted': 'Konto gelöscht',
  'audit.action.unauthorizedAccess': 'Unerlaubter Zugriff',
  'audit.action.roleChanged': 'Rolle geändert',
  'audit.action.permissionChanged': 'Berechtigung geändert',
  'audit.action.invitationSent': 'Einladung gesendet',
  'audit.action.memberRemoved': 'Mitglied entfernt',
  'audit.action.aiCategorization': 'KI-Kategorisierung',
  'audit.action.aiDuplicate': 'KI-Duplikaterkennung',
  'audit.action.aiValue': 'KI-Wertvorhersage',
  'audit.action.aiAccepted': 'KI-Vorschlag akzeptiert',
  'audit.action.aiRejected': 'KI-Vorschlag abgelehnt',
  'audit.action.notificationSent': 'Benachrichtigung gesendet',
  'audit.action.systemConfig': 'Systemkonfiguration geändert',
  'audit.action.bulkExport': 'Massenexport',
  'audit.action.bulkImport': 'Massenimport',
  'audit.action.bulkDelete': 'Massenlöschung',
  'audit.resource.items': 'Artikel',
  'audit.resource.categories': 'Kategorien',
  'audit.resource.users': 'Benutzer',
  'audit.resource.organization': 'Organisation',
  'audit.resource.comments': 'Kommentare',
  'audit.resource.images': 'Bilder',
  'audit.resource.files': 'Dateien',
  'audit.resource.notifications': 'Benachrichtigungen',
  'audit.resource.system': 'System',

  // Auth Pages
  'auth.welcomeBack': 'Willkommen zurück',
  'auth.signInSubtitle': 'Melden Sie sich an, um zu Ihrem Workspace fortzufahren',
  'auth.email': 'E-Mail-Adresse',
  'auth.emailPlaceholder': 'ihre@email.com',
  'auth.password': 'Passwort',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signIn': 'Anmelden',
  'auth.signingIn': 'Anmeldung...',
  'auth.forgotPassword': 'Passwort vergessen?',
  'auth.noAccount': 'Noch kein Konto?',
  'auth.createOne': 'Konto erstellen',
  'auth.orContinueWith': 'oder',
  'auth.rememberMe': 'Angemeldet bleiben',
  'auth.secureLogin': 'Sichere & verschlüsselte Anmeldung',
  'auth.benefit1': 'Intelligente Artikelverwaltung',
  'auth.benefit2': 'Echtzeit-Zusammenarbeit',
  'auth.benefit3': 'Erweiterte Analysen',
  'auth.emailRequired': 'E-Mail und Passwort sind erforderlich',
  'auth.loginFailed': 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',

  'auth.createAccount': 'Konto erstellen',
  'auth.signUpSubtitle': 'Starten Sie noch heute Ihre Reise mit uns',
  'auth.firstName': 'Vorname',
  'auth.firstNamePlaceholder': 'Max',
  'auth.lastName': 'Nachname',
  'auth.lastNamePlaceholder': 'Mustermann',
  'auth.confirmPassword': 'Passwort bestätigen',
  'auth.signUp': 'Konto erstellen',
  'auth.signingUp': 'Konto wird erstellt...',
  'auth.hasAccount': 'Bereits ein Konto?',
  'auth.signInLink': 'Anmelden',
  'auth.passwordMismatch': 'Passwörter stimmen nicht überein',
  'auth.passwordTooShort': 'Passwort muss mindestens 8 Zeichen lang sein',
  'auth.fillAllFields': 'Bitte füllen Sie alle Pflichtfelder aus',
  'auth.signupFailed': 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
  'auth.emailAlreadyExists': 'Ein Konto mit dieser E-Mail-Adresse existiert bereits. Bitte melden Sie sich an oder verwenden Sie eine andere E-Mail.',
  'auth.networkError': 'Verbindung zum Server nicht möglich. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
  'auth.signupSuccess': 'Konto erstellt! Überprüfen Sie Ihre E-Mail zur Verifizierung.',
  'auth.joinCommunity': 'Treten Sie unserer Community bei',
  'auth.benefitSecure': 'Sicher & verschlüsselt',
  'auth.benefitVerified': 'E-Mail-verifizierte Konten',
  'auth.benefitSmart': 'Intelligente Kategorisierung',
  'auth.agreeTerms': 'Mit der Registrierung stimmen Sie unseren',
  'auth.termsOfService': 'Nutzungsbedingungen',
  'auth.and': 'und',
  'auth.privacyPolicy': 'Datenschutzrichtlinie',
  'auth.passwordStrength': 'Passwortstärke',
  'auth.weak': 'Schwach',
  'auth.fair': 'Mäßig',
  'auth.good': 'Gut',
  'auth.strong': 'Stark',

  'auth.forgotPasswordTitle': 'Passwort vergessen?',
  'auth.forgotPasswordSubtitle': 'Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Reset-Link',
  'auth.sendResetLink': 'Reset-Link senden',
  'auth.sending': 'Wird gesendet...',
  'auth.resetLinkSent': 'Passwort-Reset-Link wurde an Ihre E-Mail gesendet',
  'auth.backToLogin': 'Zurück zur Anmeldung',
  'auth.resetYourPassword': 'Passwort zurücksetzen',
  'auth.resetDescription': 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen',
  'auth.emailRequired2': 'E-Mail ist erforderlich',
  'auth.sendFailed': 'Reset-E-Mail konnte nicht gesendet werden',
  'auth.checkInbox': 'Überprüfen Sie Ihren Posteingang',
  'auth.resetEmailSentDesc': 'Wir haben einen Passwort-Reset-Link an Ihre E-Mail gesendet. Bitte überprüfen Sie Ihren Posteingang und Spam-Ordner.',

  'auth.resetPasswordTitle': 'Passwort zurücksetzen',
  'auth.resetPasswordSubtitle': 'Geben Sie Ihr neues Passwort ein',
  'auth.newPassword': 'Neues Passwort',
  'auth.confirmNewPassword': 'Neues Passwort bestätigen',
  'auth.resetPassword': 'Passwort zurücksetzen',
  'auth.resetting': 'Wird zurückgesetzt...',
  'auth.resetSuccess': 'Passwort erfolgreich zurückgesetzt! Weiterleitung...',
  'auth.resetFailed': 'Passwort-Reset fehlgeschlagen',
  'auth.invalidResetLink': 'Ungültiger Reset-Link',
  'auth.invalidResetDesc': 'Dieser Passwort-Reset-Link ist ungültig oder abgelaufen',
  'auth.requestNewLink': 'Neuen Link anfordern',
  'auth.noTokenProvided': 'Kein Reset-Token vorhanden',
  'auth.createNewPassword': 'Neues Passwort erstellen',
  'auth.createNewPasswordDesc': 'Wählen Sie ein starkes Passwort für Ihr Konto',
  'auth.tryAgain': 'Erneut versuchen',

  'auth.verifyEmail': 'E-Mail verifizieren',
  'auth.verifyEmailDesc': 'Wir haben einen Verifizierungslink an Ihre E-Mail gesendet',
  'auth.checkYourInbox': 'Überprüfen Sie Ihren Posteingang',
  'auth.weSentVerification': 'Wir haben einen Verifizierungslink gesendet an',
  'auth.checkSpam': 'Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu verifizieren. Falls nicht sichtbar, überprüfen Sie Ihren Spam-Ordner.',
  'auth.resendVerification': 'Verifizierungs-E-Mail erneut senden',
  'auth.resendSuccess': 'Verifizierungs-E-Mail erneut gesendet! Überprüfen Sie Ihren Posteingang.',
  'auth.alreadyVerified': 'Bereits verifiziert? Anmelden',
  'auth.verifying': 'E-Mail wird verifiziert',
  'auth.verifyingDesc': 'Bitte warten Sie, während wir Ihre E-Mail bestätigen...',
  'auth.emailVerified': 'E-Mail verifiziert!',
  'auth.verifiedSuccess': 'E-Mail erfolgreich verifiziert! Weiterleitung zum Dashboard...',
  'auth.verificationFailed': 'Verifizierung fehlgeschlagen',
  'auth.resendFailed': 'Verifizierungs-E-Mail konnte nicht erneut gesendet werden',
  'auth.goToLogin': 'Zur Anmeldung',
  'auth.goToDashboard': 'Zum Dashboard',
  'auth.enterEmailResend': 'E-Mail eingeben zum erneut Senden',
  'auth.verificationSent': 'Verifizierungs-E-Mail gesendet! Überprüfen Sie Ihren Posteingang.',

  // Support
  'auth.needHelp': 'Benötigen Sie Hilfe?',
  'auth.contactSupport': 'Support kontaktieren',
  'auth.supportEmail': 'Senden Sie uns eine E-Mail',
  'auth.supportSubject': 'ELVOAQ Supportanfrage',
  'auth.supportBody': 'Hallo,\n\nIch benötige Hilfe mit meinem Konto.\n\nBitte beschreiben Sie Ihr Anliegen:',
  'auth.havingProblems': 'Probleme?',
  'auth.reachOutToUs': 'Kontaktieren Sie uns',
  'auth.contactUsForHelp': 'Kontaktieren Sie uns für Hilfe mit Ihrem Konto',

  // Sidebar
  'sidebar.collapse': 'Einklappen',
  'sidebar.collapseSidebar': 'Seitenleiste einklappen',
  'sidebar.expandSidebar': 'Seitenleiste ausklappen',

  // User Dropdown
  'dropdown.settings': 'Einstellungen',
  'dropdown.subscription': 'Abonnement',
  'dropdown.logout': 'Abmelden',
  'dropdown.openProfile': 'Profil öffnen',

  // Settings — Profile
  'settings.profileTitle': 'Profilinformationen',
  'role.OWNER': 'Eigentümer',
  'role.ADMIN': 'Super-Admin',
  'role.MEMBER': 'Mitglied',
  'role.LEAD': 'Lead',
  'role.VIEWER': 'Betrachter',
  'settings.firstName': 'Vorname',
  'settings.lastName': 'Nachname',
  'settings.firstNamePlaceholder': 'Geben Sie Ihren Vornamen ein',
  'settings.lastNamePlaceholder': 'Geben Sie Ihren Nachnamen ein',
  'settings.emailAddress': 'E-Mail-Adresse',
  'settings.changeEmail': 'Ändern',
  'settings.newEmailAddress': 'Neue E-Mail-Adresse',
  'settings.enterNewEmail': 'Neue E-Mail-Adresse eingeben',
  'settings.currentPassword': 'Aktuelles Passwort',
  'settings.currentPasswordConfirm': 'Geben Sie Ihr aktuelles Passwort zur Bestätigung ein',
  'settings.updatingEmail': 'Aktualisierung...',
  'settings.updateEmail': 'E-Mail aktualisieren',
  'settings.bio': 'Biografie',
  'settings.bioPlaceholder': 'Erzählen Sie etwas über sich...',
  'settings.defaultCurrency': 'Standardwährung',
  'settings.currencyHint': 'Diese Währung wird für die Anzeige umgerechneter Werte in der gesamten App verwendet.',
  'settings.savingChanges': 'Speichern...',

  // Settings — Security
  'settings.securityTitle': 'Sicherheitseinstellungen',
  'settings.changePassword': 'Passwort ändern',
  'settings.enterCurrentPassword': 'Aktuelles Passwort eingeben',
  'settings.newPassword': 'Neues Passwort',
  'settings.enterNewPassword': 'Neues Passwort eingeben',
  'settings.confirmPassword': 'Passwort bestätigen',
  'settings.confirmNewPassword': 'Neues Passwort bestätigen',
  'settings.updatingPassword': 'Aktualisierung...',

  // Settings — Notifications
  'settings.notifPreferences': 'Benachrichtigungseinstellungen',
  'settings.notifTypes': 'Benachrichtigungstypen',
  'settings.notifItemUpdates': 'Artikelaktualisierungen',
  'settings.notifItemUpdatesDesc': 'Benachrichtigung bei Erstellung, Aktualisierung oder Löschung von Artikeln',
  'settings.notifComments': 'Kommentare',
  'settings.notifCommentsDesc': 'Benachrichtigungen für neue Kommentare zu Ihren Artikeln',
  'settings.notifMentions': 'Erwähnungen',
  'settings.notifMentionsDesc': 'Benachrichtigung wenn jemand Sie erwähnt',
  'settings.notifDuplicates': 'Duplikaterkennung',
  'settings.notifDuplicatesDesc': 'KI-gestützte Warnungen bei potenziellen Duplikaten',
  'settings.notifValuePredictions': 'Wertvorhersagen',
  'settings.notifValuePredictionsDesc': 'Benachrichtigungen über Wertänderungen und Vorhersagen',
  'settings.notifSecurityAlerts': 'Sicherheitswarnungen',
  'settings.notifSecurityAlertsDesc': 'Wichtige sicherheitsbezogene Benachrichtigungen',
  'settings.notifEmailPrefs': 'E-Mail-Einstellungen',
  'settings.notifWeeklyDigest': 'Wochenzusammenfassung',
  'settings.notifWeeklyDigestDesc': 'Wöchentliche Aktivitätszusammenfassung erhalten',
  'settings.notifMarketing': 'Marketing-E-Mails',
  'settings.notifMarketingDesc': 'Updates zu neuen Funktionen und Aktionen',
  'settings.notifChannels': 'Zustellkanäle',
  'settings.notifEmail': 'E-Mail-Benachrichtigungen',
  'settings.notifEmailDesc': 'Benachrichtigungen per E-Mail erhalten',
  'settings.notifPush': 'Push-Benachrichtigungen',
  'settings.notifPushDesc': 'Browser- und mobile Push-Benachrichtigungen',
  'settings.notifInApp': 'In-App-Benachrichtigungen',
  'settings.notifInAppDesc': 'Benachrichtigungen in der Anwendung anzeigen',
  'settings.notifQuietHours': 'Ruhezeiten',
  'settings.notifEnableQuietHours': 'Ruhezeiten aktivieren',
  'settings.notifQuietHoursDesc': 'Nicht-kritische Benachrichtigungen während bestimmter Stunden pausieren',
  'settings.notifStartTime': 'Startzeit',
  'settings.notifEndTime': 'Endzeit',
  'settings.notifSaving': 'Speichern...',
  'settings.notifSaveBtn': 'Benachrichtigungseinstellungen speichern',
  'settings.loadingNotifications': 'Benachrichtigungseinstellungen laden...',

  // Settings — Accessibility
  'settings.accessibilityTitle': 'Barrierefreiheit & Anzeige',
  'settings.accessibilityDesc': 'Passen Sie Animationen und Interaktionen für Ihren Komfort und Ihr Gerät an.',
  'settings.motionAnimations': 'Bewegung & Animationen',
  'settings.reduceMotion': 'Bewegung reduzieren',
  'settings.reduceMotionDesc': 'Übergänge, Hover-Animationen und dekorative Bewegungseffekte in der gesamten App deaktivieren',
  'settings.reduceMotionHint': 'Respektiert auch automatisch die "Bewegung reduzieren"-Einstellung Ihres Betriebssystems.',
  'settings.touchMobile': 'Touch & Mobil',
  'settings.touchFriendly': 'Touch-freundlicher Modus',
  'settings.touchFriendlyDesc': 'Hover-abhängige Interaktionen durch Tipp-/Klick-Alternativen ersetzen. Vergrößert Schaltflächen und Umschalter für einfachere Touch-Bedienung.',
  'settings.touchFriendlyHint': 'Empfohlen bei Verwendung eines Tablets oder Touchscreen-Geräts.',
  'settings.mobileWarningTitle': 'Hinweis zur mobilen Nutzung',
  'settings.mobileWarningText': 'ELVOAQ Vault ist für Desktop- und Tablet-Bildschirme optimiert. Auf kleineren Mobilgeräten können einige Funktionen eingeschränkt sein oder Scrollen erfordern. Für das beste Erlebnis empfehlen wir einen Desktop-Browser oder ein Tablet.',
  'settings.mobileWarningDarkHint': 'Tipp: Der Dunkelmodus reduziert die Augenbelastung auf mobilen Bildschirmen.',
  'settings.mobileWarningLightHint': 'Tipp: Der Hellmodus kann die Lesbarkeit in hellen Umgebungen verbessern.',

  // Settings — Privacy
  'settings.privacyTitle': 'Datenschutz & Daten',
  'settings.privacyTab': 'Datenschutz',
  'settings.yourData': 'Ihre Daten',
  'settings.yourDataDesc': 'Ihre persönlichen Informationen sind geschützt und sicher gespeichert. Wir geben Ihre Daten niemals an Dritte weiter.',
  'settings.dangerZone': '⚠️ Gefahrenzone',
  'settings.dangerZoneDesc': 'Das Löschen Ihres Kontos ist permanent und kann nicht rückgängig gemacht werden. Alle Ihre Artikel, Daten und Einstellungen werden dauerhaft gelöscht.',
  'settings.deleteAccount': 'Mein Konto löschen',
  'settings.deleteConfirmTitle': '⚠️ Sind Sie absolut sicher?',
  'settings.deleteConfirmDesc': 'Diese Aktion kann nicht rückgängig gemacht werden. Bitte geben Sie Ihr Passwort ein, um die Kontolöschung zu bestätigen.',
  'settings.enterPassword': 'Geben Sie Ihr Passwort ein',
  'settings.deleting': 'Lösche...',
  'settings.confirmDeleteBtn': 'Ja, mein Konto löschen',
  'settings.privacyPolicy': 'Datenschutzrichtlinie',
  'settings.privacyPolicyDesc': 'Lesen Sie unsere umfassende Datenschutzrichtlinie und Nutzungsbedingungen.',
  'settings.viewPrivacyPolicy': 'Datenschutzrichtlinie anzeigen',

  // Settings — AI
  'settings.aiTitle': 'KI-Einstellungen',
  'settings.aiTab': 'KI-Anbieter',
  'settings.aboutTab': 'Über',
  // Tutorial
  'tutorial.tourBadge': 'Plattform-Tour',
  'tutorial.back': 'Zurück',
  'tutorial.next': 'Weiter',
  'tutorial.skip': 'Tour überspringen',
  'tutorial.getStarted': 'Loslegen',
  'tutorial.welcome.title': 'Willkommen bei ELVOAQ',
  'tutorial.welcome.desc': 'ELVOAQ ist Ihre All-in-one-Plattform für digitales Inventar. Verwalten, verfolgen und sichern Sie alle Ihre wertvollen Gegenstände an einem Ort.',
  'tutorial.library.title': 'Ihre Artikelbibliothek',
  'tutorial.library.desc': 'Die Bibliothek ist das Herzstück von ELVOAQ. Fügen Sie Artikel mit Fotos, Beschreibungen, Preisen und Zuständen hinzu. Leistungsstarke Suche und Filter helfen Ihnen sofort.',
  'tutorial.categories.title': 'Kategorien',
  'tutorial.categories.desc': 'Organisieren Sie Artikel in benutzerdefinierte Kategorien mit Farben und Symbolen. Gesamtwert und Artikelanzahl pro Kategorie auf einen Blick.',
  'tutorial.favorites.title': 'Favoriten',
  'tutorial.favorites.desc': 'Markieren Sie Ihre wichtigsten Artikel als Favoriten für schnellen Zugriff. Sortieren, filtern und exportieren Sie Ihre Favoritenliste jederzeit als CSV.',
  'tutorial.collections.title': 'Sammlungen',
  'tutorial.collections.desc': 'Gruppieren Sie verwandte Artikel in benannten Sammlungen — ein Reiseset, ein Heimbüro oder ein Versicherungspaket. Artikel aus Ihrer Bibliothek mit einem Klick hinzufügen.',
  'tutorial.accounting.title': 'Buchhaltung & Vermögenswerte',
  'tutorial.accounting.desc': 'Verfolgen Sie Abschreibungen, verwalten Sie Anlagendaten und überwachen Sie den Finanzwert Ihres Inventars mit mehreren Abschreibungsmethoden.',
  'tutorial.ai.title': 'KI-gestützte Funktionen',
  'tutorial.ai.desc': 'Lassen Sie KI für Sie arbeiten. Erhalten Sie Wertvorhersagen, erkennen Sie Duplikate automatisch, chatten Sie mit dem KI-Assistenten und setzen Sie intelligente Erinnerungen.',
  'tutorial.security.title': 'Sicherheit & Datenschutz',
  'tutorial.security.desc': 'Sperren Sie einzelne Artikel mit einem Passwort, erstellen Sie versteckte private Ordner, aktivieren Sie die Zwei-Faktor-Authentifizierung und verwalten Sie Zugriffsrollen.',
  'tutorial.calendar.title': 'Kalender & Erinnerungen',
  'tutorial.calendar.desc': 'Planen Sie Wartungserinnerungen, Garantieablaufdaten, Versicherungsverlängerungen und benutzerdefinierte Ereignisse — alles mit Ihren Inventarartikeln verknüpft.',
  'tutorial.settings.title': 'Alles personalisieren',
  'tutorial.settings.desc': 'Wechseln Sie zwischen dunklem und hellem Modus, wählen Sie Ihre Sprache, legen Sie Ihre Standardwährung fest und verwalten Sie alle Einstellungen.',
  'tutorial.audit.title': 'Audit-Protokoll',
  'tutorial.audit.desc': 'Überwachen Sie jede Aktion auf der Plattform in Echtzeit. Sehen Sie, wer was wann und von wo aus geändert hat \u2014 mit vollständigen Details, IP-Verfolgung und der Möglichkeit, Änderungen rückgängig zu machen.',
  'tutorial.clickHint': 'Klicken Sie hier, um zu diesem Bereich zu navigieren',
  'tutorial.done.title': 'Sie sind startklar!',
  'tutorial.done.desc': 'Sie kennen jetzt die Grundlagen von ELVOAQ. Fügen Sie Ihren ersten Artikel zur Bibliothek hinzu. Sie können diese Tour jederzeit unter Einstellungen → Über wiederholen.',
  'tutorial.startTour': 'Tour starten',
  'tutorial.exploreOwn': 'Ich erkunde selbst',
  'tutorial.replayTour': 'Tour wiederholen',
  // About / Version
  'about.version': 'App-Version',
  'about.releaseDate': 'Veröffentlichungsdatum',
  'about.changelog': 'Änderungsprotokoll',
  'about.newFeatures': 'Neue Funktionen',
  'about.replayTutorial': 'Tutorial wiederholen',
  'about.replayTutorialDesc': 'Interaktive Plattform-Tour erneut anzeigen',
  'about.autoLogout': 'Automatischer Abmeldung',
  'about.autoLogoutDesc': 'Nach einer Zeit der Inaktivität automatisch abmelden',
  'about.timeout15': '15 Minuten',
  'about.timeout30': '30 Minuten',
  'about.timeout60': '1 Stunde',
  'about.timeout120': '2 Stunden',
  'about.timeoutNever': 'Nie (nicht empfohlen)',
  'about.privacyPolicy': 'Datenschutzrichtlinie',
  'about.termsOfService': 'Nutzungsbedingungen',
  'about.viewPrivacy': 'Datenschutzrichtlinie anzeigen',
  'about.viewTerms': 'Nutzungsbedingungen anzeigen',
  'about.legalDocuments': 'Rechtliche Dokumente',
  // CAPTCHA
  'auth.captchaQuestion': 'Sicherheitscheck: Was ist {a} + {b}?',
  'auth.captchaPlaceholder': 'Antwort eingeben',
  'auth.captchaError': 'Falsche Antwort. Bitte erneut versuchen.',
  'settings.aiDescription': 'ELVOAQ KI ist konfiguriert und aktiv. Sie können alle KI-gestützten Funktionen nutzen, einschließlich Assistent, Duplikaterkennung, Wertvorhersage und mehr.',
  'settings.aiActive': 'ELVOAQ KI Aktiv',
  'settings.aiActiveDesc': 'Angetrieben durch unsere fortschrittliche KI. Alle Funktionen sind plattformweit einsatzbereit.',
  'settings.aiFeatures': 'Verfügbare KI-Funktionen:',
  'settings.aiAssistant': 'KI-Assistent',
  'settings.aiAssistantDesc': 'Hilfe bei der Verwaltung Ihres Inventars',
  'settings.aiDuplicateDetection': 'Duplikaterkennung',
  'settings.aiDuplicateDetectionDesc': 'Automatisch ähnliche Artikel finden',
  'settings.aiValuePrediction': 'Wertvorhersage',
  'settings.aiValuePredictionDesc': 'Artikelwerte im Zeitverlauf schätzen',
  'settings.aiSmartCategorization': 'Intelligente Kategorisierung',
  'settings.aiSmartCategorizationDesc': 'Kategorien automatisch vorschlagen',

  // Feedback Modal
  'feedback.title': 'Feedback senden',
  'feedback.successTitle': 'Feedback gesendet!',
  'feedback.successMessage': 'Vielen Dank! Ihr Feedback wurde erfolgreich an unser Team übermittelt.',
  'feedback.typeLabel': 'Typ',
  'feedback.titleLabel': 'Titel *',
  'feedback.titlePlaceholder': 'Kurze Zusammenfassung Ihres Feedbacks',
  'feedback.descriptionLabel': 'Beschreibung *',
  'feedback.descriptionPlaceholder': 'Bitte geben Sie detaillierte Informationen an...',
  'feedback.categoryLabel': 'Kategorie *',
  'feedback.categoryPlaceholder': 'Kategorie auswählen...',
  'feedback.attachmentLabel': 'Anhang (Optional)',
  'feedback.uploadBtn': 'Screenshot oder Video hochladen',
  'feedback.maxFileSize': 'Max 5MB. Unterstützt: JPG, PNG, GIF, WebP, MP4, WebM',
  'feedback.systemInfo': '📊 Systeminformationen (automatisch erfasst)',
  'feedback.submitBtn': 'Feedback absenden',
  'feedback.submitting': 'Wird gesendet...',
  'feedback.bugReport': 'Fehlerbericht',
  'feedback.bugReportDesc': 'Einen Fehler melden',
  'feedback.featureRequest': 'Funktionswunsch',
  'feedback.featureRequestDesc': 'Eine neue Funktion vorschlagen',
  'feedback.improvement': 'Verbesserung',
  'feedback.improvementDesc': 'Eine Verbesserung vorschlagen',
  'feedback.question': 'Frage',
  'feedback.questionDesc': 'Eine Frage stellen',
  'feedback.complaint': 'Beschwerde',
  'feedback.complaintDesc': 'Ein Problem oder Anliegen melden',
  'feedback.other': 'Sonstiges',
  'feedback.otherDesc': 'Allgemeines Feedback',
  'feedback.fileTooLarge': 'Dateigröße muss kleiner als 5MB sein',
  'feedback.invalidFileType': 'Nur Bilder (JPG, PNG, GIF, WebP) und Videos (MP4, WebM) sind erlaubt',
  'feedback.titleDescRequired': 'Titel und Beschreibung sind erforderlich',
  'feedback.categoryRequired': 'Bitte wählen Sie eine Kategorie',
  'feedback.remove': 'Entfernen',
  'feedback.cancel': 'Abbrechen',
  'feedback.display': 'Anzeige',
  'feedback.browser': 'Browser',
  'feedback.locationLang': 'Standort & Sprache',
  'feedback.pageContext': 'Seitenkontext',
  'feedback.screenResolution': 'Bildschirmauflösung',
  'feedback.viewportSize': 'Ansichtsgröße',
  'feedback.colorDepth': 'Farbtiefe',
  'feedback.userAgent': 'User Agent',
  'feedback.device': 'Gerät',
  'feedback.os': 'Betriebssystem',
  'feedback.cookiesEnabled': 'Cookies aktiviert',
  'feedback.language': 'Sprache',
  'feedback.timezone': 'Zeitzone',
  'feedback.currentUrl': 'Aktuelle URL',
  'feedback.referrer': 'Referrer',
  'feedback.onlineStatus': 'Online-Status',
  'feedback.errorSubmit': 'Feedback konnte nicht gesendet werden',

  // Feedback Categories
  'feedback.category.Dashboard': 'Dashboard',
  'feedback.category.Items & Library': 'Artikel & Bibliothek',
  'feedback.category.Categories': 'Kategorien',
  'feedback.category.Notifications': 'Benachrichtigungen',
  'feedback.category.Settings': 'Einstellungen',
  'feedback.category.Workspaces & Teams': 'Arbeitsbereiche & Teams',
  'feedback.category.Authentication': 'Authentifizierung',
  'feedback.category.Performance': 'Leistung',
  'feedback.category.User Interface': 'Benutzeroberfläche',
  'feedback.category.Mobile Experience': 'Mobile Erfahrung',
  'feedback.category.Data & Analytics': 'Daten & Analysen',
  'feedback.category.Other': 'Sonstiges',

  // Subscription / Plans
  'subscription.title': 'Abonnement',
  'subscription.overview': 'Übersicht',
  'subscription.plans': 'Pläne',
  'subscription.storage': 'Speicher',
  'subscription.billing': 'Abrechnung',
  'subscription.header': 'Abonnement & Pläne',
  'subscription.headerDesc': 'Verwalten Sie Ihren Plan, Speicher und Abrechnung an einem Ort',
  'subscription.loading': 'Abonnement wird geladen...',
  'subscription.currentPlan': 'Aktueller Plan',
  'subscription.upgrade': 'Upgrade',
  'subscription.usageOverview': 'Nutzungsübersicht',
  'subscription.items': 'Artikel',
  'subscription.categories': 'Kategorien',
  'subscription.workspaces': 'Arbeitsbereiche',
  'subscription.aiMessages': 'KI-Nachrichten',
  'subscription.imagesPerItem': 'Bilder / Artikel',
  'subscription.max': 'max',
  'subscription.yourPlanFeatures': 'Ihre Plan-Funktionen',
  'subscription.unlimited': 'Unbegrenzt',
  'subscription.itemsLabel': 'Artikel',
  'subscription.customCategories': 'benutzerdefinierte Kategorien',
  'subscription.storageLabel': 'Speicher',
  'subscription.imagesPerItemLabel': 'Bilder pro Artikel',
  'subscription.workspacesLabel': 'Arbeitsbereich(e)',
  'subscription.aiMessagesMonth': 'KI-Nachrichten/Monat',
  'subscription.aiCategorization': 'KI-Kategorisierung',
  'subscription.valuePrediction': 'Wertvorhersage',
  'subscription.advancedAnalytics': 'Erweiterte Analysen',
  'subscription.prioritySupport': 'Prioritäts-Support',
  'subscription.compareAllPlans': 'Alle Pläne vergleichen',
  'subscription.storageWarningPrefix': 'Sie verwenden',
  'subscription.storageWarningSuffix': 'Ihres Speichers. Erwägen Sie ein Upgrade oder zusätzlichen Speicher.',
  'subscription.manageStorage': 'Speicher verwalten',
  'subscription.monthly': 'Monatlich',
  'subscription.yearly': 'Jährlich',
  'subscription.save20': '20% sparen',
  'subscription.mostPopular': 'Am beliebtesten',
  'subscription.currentPlanLabel': 'Aktueller Plan',
  'subscription.free': 'Kostenlos',
  'subscription.custom': 'Individuell',
  'subscription.contactSales': 'Vertrieb kontaktieren',
  'subscription.switch': 'Wechseln',
  'subscription.viewFullPricing': 'Vollständige Preisübersicht anzeigen',
  'subscription.storageBreakdown': 'Speicher-Aufschlüsselung',
  'subscription.images': 'Bilder',
  'subscription.documents': 'Dokumente',
  'subscription.metadata': 'Metadaten',
  'subscription.perMonth': '/Mo',
  'subscription.perYear': '/Jahr',
  'subscription.savePerYear': '€{amount}/Jahr sparen',
  'subscription.used': 'Verwendet',
  'subscription.freeSpace': 'Frei',
  'subscription.total': 'Gesamt',
  'subscription.needMoreSpace': 'Mehr Platz benötigt?',
  'subscription.addExtraStorage': 'Fügen Sie Ihrem Plan zusätzlichen Speicher hinzu',
  'subscription.addStorage': 'Speicher hinzufügen',
  'subscription.saveTilde20': '~20% sparen',
  'subscription.save20Approx': '~20% sparen',
  'subscription.billingSummary': 'Abrechnungsübersicht',
  'subscription.billingCycle': 'Abrechnungszyklus',
  'subscription.yearlyCost': 'Jährliche Kosten',
  'subscription.monthlyCost': 'Monatliche Kosten',
  'subscription.nextBillingDate': 'Nächstes Abrechnungsdatum',
  'subscription.paymentMethod': 'Zahlungsmethode',
  'subscription.naFree': 'N/A — Kostenloser Plan',
  'subscription.manageKofi': 'Auf Ko-fi verwalten',
  'subscription.manageStripe': 'Abonnement verwalten',
  'subscription.downloadInvoice': 'Rechnung herunterladen',
  'subscription.cancelSubscription': 'Abonnement kündigen',
  'subscription.cancelDesc': 'Um Ihr Abonnement zu kündigen, verwalten Sie es über Stripe. Ihr Zugang bleibt bis zum Ende des aktuellen Abrechnungszeitraums bestehen.',
  'subscription.cancelOnKofi': 'Auf Ko-fi kündigen',
  'subscription.cancelStripe': 'Abonnement kündigen',
  'subscription.paymentHistory': 'Zahlungsverlauf',
  'subscription.noPayments': 'Noch kein Zahlungsverlauf',
  'subscription.noPaymentsDesc': 'Ihr Zahlungsverlauf erscheint hier nach Ihrer ersten Zahlung',
  'subscription.needHelpBilling': 'Hilfe bei der Abrechnung?',
  'subscription.contactBilling': 'Kontaktieren Sie uns unter',
  'subscription.billingQuestions': 'bei Fragen zur Abrechnung.',
  'subscription.securePayments': 'Sichere Zahlungen',
  'subscription.securePaymentsDesc': 'Alle Zahlungen werden sicher über Stripe verarbeitet. Wir speichern nie Ihre Zahlungsdaten.',
  'subscription.upgradeTo': 'Upgrade auf',
  'subscription.customPricing': 'Individuelle Preisgestaltung',
  'subscription.whatsIncluded': 'Was enthalten ist:',
  'subscription.save20Yearly': '20% sparen mit jährlicher Abrechnung',
  'subscription.subscribeKofi': 'Auf Ko-fi abonnieren',
  'subscription.subscribeStripe': 'Mit Stripe abonnieren',
  'subscription.afterPayment': 'Nach der Zahlung wird Ihr Plan innerhalb von 24 Stunden aktualisiert.',
  'subscription.afterPaymentStripe': 'Sie werden zur sicheren Stripe-Kasse weitergeleitet. Ihr Plan wird automatisch aktiviert.',
  'subscription.secureVia': 'Sichere Zahlung über Ko-fi. Jederzeit kündbar.',
  'subscription.secureViaStripe': 'Sichere Zahlungen über Stripe. Jederzeit kündbar.',
  'subscription.contactForAddon': 'Kontaktieren Sie uns',
  'subscription.contactSalesDesc': 'Wir melden uns innerhalb von 24 Stunden mit einem individuellen Angebot.',
  'subscription.compareFeatures': 'Alle Funktionen vergleichen',
  'subscription.feature': 'Funktion',
  'subscription.coreLimits': 'Kernlimits',
  'subscription.teamMembers': 'Teammitglieder',
  'subscription.aiFeatures': 'KI-Funktionen',
  'subscription.duplicateDetect': 'Duplikaterkennung',
  'subscription.aiReportsMonth': 'KI-Berichte/Monat',
  'subscription.security': 'Sicherheit',
  'subscription.twoFactorAuth': 'Zwei-Faktor-Auth',
  'subscription.auditLogs': 'Audit-Protokolle',
  'subscription.sso': 'SSO',
  'subscription.support': 'Support',
  'subscription.emailSupportLabel': 'E-Mail-Support',
  'subscription.dedicatedManager': 'Dedizierter Manager',
  'subscription.faqTitle': 'Häufig gestellte Fragen',
  'subscription.faqSwitchQ': 'Kann ich jederzeit den Plan wechseln?',
  'subscription.faqSwitchA': 'Ja! Sie können jederzeit upgraden oder downgraden. Beim Upgrade wird der Restbetrag anteilig berechnet. Beim Downgrade gilt der neue Tarif ab dem nächsten Abrechnungszyklus.',
  'subscription.faqDowngradeQ': 'Was passiert mit meinen Daten bei einem Downgrade?',
  'subscription.faqDowngradeA': 'Ihre Daten sind sicher. Wenn Sie die Limits des neuen Plans überschreiten, haben Sie 30 Tage Zeit, die Nutzung zu reduzieren oder wieder zu upgraden.',
  'subscription.faqRefundQ': 'Bieten Sie Rückerstattungen an?',
  'subscription.faqRefundA': 'Ja, wir bieten eine 30-tägige Geld-zurück-Garantie für alle kostenpflichtigen Pläne. Kontaktieren Sie uns für eine vollständige Rückerstattung.',
  'subscription.faqPaymentQ': 'Welche Zahlungsmethoden akzeptieren Sie?',
  'subscription.faqPaymentA': 'Wir akzeptieren Zahlungen über Stripe (Kredit-/Debitkarten). Enterprise-Pläne können auch per Rechnung bezahlt werden.',
  'subscription.faqTrialQ': 'Gibt es eine kostenlose Testversion?',
  'subscription.faqTrialA': 'Ja! Alle kostenpflichtigen Pläne beinhalten eine 14-tägige kostenlose Testversion. Keine Kreditkarte erforderlich.',
  'subscription.faqStorageQ': 'Kann ich später mehr Speicher hinzufügen?',
  'subscription.faqStorageA': 'Natürlich! Sie können jederzeit Speicher-Add-ons anfordern. Kontaktieren Sie uns für Details.',
  'subscription.trialActive': 'Kostenlose Testversion aktiv',
  'subscription.trialDaysLeft': 'Tage verbleibend',
  'subscription.upgradeNow': 'Jetzt upgraden',
  'subscription.trialAvailable': 'Teste jeden Plan 14 Tage kostenlos!',
  'subscription.trialAvailableDesc': 'Keine Kreditkarte erforderlich. Teste alle Premium-Funktionen risikofrei.',
  'subscription.startTrial': 'Testversion starten',
  'subscription.startFreeTrial': '14-Tage Testversion starten',
  'subscription.trialActiveLabel': 'Testversion aktiv',
  'subscription.trialUsed': 'Testversion bereits genutzt',
  'subscription.trialExpired': 'Deine Testversion ist abgelaufen. Upgrade, um Premium-Funktionen zu behalten.',
  'subscription.cancelTrial': 'Testversion abbrechen',
  'subscription.cancelTrialConfirm': 'Sind Sie sicher, dass Sie Ihre kostenlose Testversion abbrechen möchten? Sie werden sofort auf den kostenlosen Plan herabgestuft.',
  'subscription.ctaTitle': 'Bereit loszulegen?',
  'subscription.ctaDesc': 'Schließen Sie sich Tausenden von Nutzern an, die ihre Gegenstände mit unserer Plattform verwalten.',
  'subscription.startFree': 'Kostenlos starten',
  'subscription.contactUs': 'Kontakt',
  'subscription.planTagline.FREE': 'Für immer kostenlos',
  'subscription.planTagline.STARTER': 'Smarter sammeln starten',
  'subscription.planTagline.COLLECTOR': 'Am beliebtesten für Sammler',
  'subscription.planTagline.PRO': 'Voll ausgestattet für Profis',
  'subscription.planTagline.BUSINESS': 'Für Teams entwickelt',
  'subscription.planTagline.ULTIMATE': 'Keine Grenzen, keine Kompromisse',
  'subscription.planTagline.ENTERPRISE': 'Sprechen wir darüber',
  'subscription.planTagline.CUSTOM': 'Auf Ihre Bedürfnisse zugeschnitten',
  'subscription.planDesc.FREE': 'Starten Sie — katalogisieren Sie bis zu 30 Artikel mit grundlegenden Funktionen.',
  'subscription.planDesc.STARTER': 'Für Hobbys und kleine Sammler, die es ernst meinen.',
  'subscription.planDesc.COLLECTOR': 'Für engagierte Sammler, die mehr Platz und bessere Werkzeuge benötigen.',
  'subscription.planDesc.PRO': 'Für Power-User und Profis, die große Bestände verwalten.',
  'subscription.planDesc.BUSINESS': 'Für Teams und Organisationen, die Skalierung, Sicherheit und Kontrolle benötigen.',
  'subscription.planDesc.ULTIMATE': 'Der Gipfel — unbegrenzt alles mit Premium-Support und Anpassung.',
  'subscription.planDesc.ENTERPRISE': 'Maßgeschneiderter Plan für große Organisationen mit spezifischen Anforderungen.',

  // Plan-Highlights
  'subscription.planHighlight.FREE.0': 'Bis zu 30 Artikel',
  'subscription.planHighlight.FREE.1': '300 MB Speicher',
  'subscription.planHighlight.FREE.2': '5 Fotos pro Artikel',
  'subscription.planHighlight.FREE.3': 'KI-Kategorisierung',
  'subscription.planHighlight.FREE.4': 'Grundlegende Analysen',
  'subscription.planHighlight.FREE.5': 'Zwei-Faktor-Auth.',
  'subscription.planHighlight.FREE.6': 'Grundlegende Analysen',
  'subscription.planHighlight.FREE.7': '10 KI-Nachrichten/Monat',
  'subscription.planHighlight.STARTER.0': 'Bis zu 250 Artikel',
  'subscription.planHighlight.STARTER.1': '2 GB Speicher',
  'subscription.planHighlight.STARTER.2': '10 Fotos pro Artikel',
  'subscription.planHighlight.STARTER.3': 'KI-Kategorisierung',
  'subscription.planHighlight.STARTER.4': 'Daten exportieren',
  'subscription.planHighlight.STARTER.5': 'Kommentare',
  'subscription.planHighlight.COLLECTOR.0': 'Bis zu 1.000 Artikel',
  'subscription.planHighlight.COLLECTOR.1': '10 GB Speicher',
  'subscription.planHighlight.COLLECTOR.2': '20 Fotos pro Artikel',
  'subscription.planHighlight.COLLECTOR.3': 'KI-Wertvorhersage',
  'subscription.planHighlight.COLLECTOR.4': 'Duplikaterkennung',
  'subscription.planHighlight.COLLECTOR.5': 'Echtzeit-Sync',
  'subscription.planHighlight.COLLECTOR.6': '30-Tage-Audit-Logs',
  'subscription.planHighlight.PRO.0': 'Bis zu 10.000 Artikel',
  'subscription.planHighlight.PRO.1': '50 GB Speicher',
  'subscription.planHighlight.PRO.2': '40 Fotos pro Artikel',
  'subscription.planHighlight.PRO.3': 'Unbegrenzte KI-Berichte',
  'subscription.planHighlight.PRO.4': 'Erweiterte Analysen & Berichte',
  'subscription.planHighlight.PRO.5': 'API-Zugriff (120 Anf./Min.)',
  'subscription.planHighlight.PRO.6': 'Prioritäts-Support',
  'subscription.planHighlight.PRO.7': 'Team-Zusammenarbeit (10)',
  'subscription.planHighlight.BUSINESS.0': 'Unbegrenzte Artikel',
  'subscription.planHighlight.BUSINESS.1': '200 GB Speicher',
  'subscription.planHighlight.BUSINESS.2': '60 Fotos pro Artikel',
  'subscription.planHighlight.BUSINESS.3': 'Bis zu 25 Teammitglieder',
  'subscription.planHighlight.BUSINESS.4': 'SSO / Single Sign-On',
  'subscription.planHighlight.BUSINESS.5': 'Eigenes Branding',
  'subscription.planHighlight.BUSINESS.6': '365-Tage-Audit-Logs',
  'subscription.planHighlight.BUSINESS.7': 'Webhooks & API (300 Anf./Min.)',
  'subscription.planHighlight.BUSINESS.8': 'Prioritäts-Support',
  'subscription.planHighlight.ULTIMATE.0': 'Alles unbegrenzt',
  'subscription.planHighlight.ULTIMATE.1': '1 TB Speicher',
  'subscription.planHighlight.ULTIMATE.2': 'Unbegrenzte Fotos pro Artikel',
  'subscription.planHighlight.ULTIMATE.3': 'Unbegrenzte Teammitglieder',
  'subscription.planHighlight.ULTIMATE.4': 'Dedizierter Account-Manager',
  'subscription.planHighlight.ULTIMATE.5': 'SLA-Garantie',
  'subscription.planHighlight.ULTIMATE.6': 'White-Label-Option',
  'subscription.planHighlight.ULTIMATE.7': 'Eigene Domain',
  'subscription.planHighlight.ULTIMATE.8': 'Unbegrenzter API-Zugriff',
  'subscription.planHighlight.ENTERPRISE.0': 'Alles unbegrenzt',
  'subscription.planHighlight.ENTERPRISE.1': 'Individueller Speicher',
  'subscription.planHighlight.ENTERPRISE.2': 'Dedizierter Account-Manager',
  'subscription.planHighlight.ENTERPRISE.3': 'SLA-Garantie',
  'subscription.planHighlight.ENTERPRISE.4': 'White-Label-Option',
  'subscription.planHighlight.ENTERPRISE.5': 'Eigene Domain',
  'subscription.planHighlight.ENTERPRISE.6': 'On-Premise-Bereitstellung',
  'subscription.planHighlight.ENTERPRISE.7': 'Individuelle Integrationen',
  'subscription.planHighlight.ENTERPRISE.8': 'Maßgeschneidertes Onboarding',
  'subscription.planHighlight.CUSTOM.0': 'Alles unbegrenzt',
  'subscription.planHighlight.CUSTOM.1': 'Individueller Speicher',
  'subscription.planHighlight.CUSTOM.2': 'Dedizierter Account-Manager',
  'subscription.planHighlight.CUSTOM.3': 'SLA-Garantie',
  'subscription.planHighlight.CUSTOM.4': 'White-Label-Option',
  'subscription.planHighlight.CUSTOM.5': 'Eigene Domain',
  'subscription.planHighlight.CUSTOM.6': 'On-Premise-Bereitstellung',
  'subscription.planHighlight.CUSTOM.7': 'Individuelle Integrationen',
  'subscription.planHighlight.CUSTOM.8': 'Maßgeschneidertes Onboarding',

  // ── Collections ──
  'collections.title': 'Sammlungen',
  'collections.subtitle': 'Erstellen Sie benutzerdefinierte Gruppen und organisieren Sie Artikel nach Belieben',
  'collections.newCollection': 'Neue Sammlung',
  'collections.searchPlaceholder': 'Sammlungen suchen...',
  'collections.customOrder': 'Benutzerdefinierte Reihenfolge',
  'collections.name': 'Name',
  'collections.recentlyUpdated': 'Kürzlich aktualisiert',
  'collections.mostItems': 'Meiste Artikel',
  'collections.collections': 'Sammlungen',
  'collections.totalItems': 'Artikel gesamt',
  'collections.active': 'Aktiv',
  'collections.items': 'Artikel',
  'collections.itemsPlural': 'Artikel',
  'collections.updated': 'Aktualisiert',
  'collections.noMatchingCollections': 'Keine passenden Sammlungen',
  'collections.tryDifferentSearch': 'Versuchen Sie einen anderen Suchbegriff',
  'collections.noCollectionsYet': 'Noch keine Sammlungen',
  'collections.noCollectionsDesc': 'Erstellen Sie Ihre erste Sammlung, um Artikel nach Ihren Wünschen zu organisieren',
  'collections.createCollection': 'Sammlung erstellen',
  'collections.editCollection': 'Sammlung bearbeiten',
  'collections.createNewCollection': 'Neue Sammlung erstellen',
  'collections.collectionName': 'Sammlungsname *',
  'collections.collectionNamePlaceholder': 'z.B., Vintage Uhren, Seltene Bücher...',
  'collections.description': 'Beschreibung',
  'collections.descriptionPlaceholder': 'Worum geht es in dieser Sammlung?',
  'collections.color': 'Farbe',
  'collections.saveChanges': 'Änderungen speichern',
  'collections.cancel': 'Abbrechen',
  'collections.addItems': 'Artikel hinzufügen',
  'collections.addRemoveItems': 'Artikel hinzufügen / entfernen',
  'collections.done': 'Fertig',
  'collections.itemsInCollection': '{count} Artikel in dieser Sammlung',
  'collections.loadingItems': 'Artikel werden geladen...',
  'collections.noItemsInCollection': 'Keine Artikel in dieser Sammlung',
  'collections.noItemsDesc': 'Klicken Sie auf "Artikel hinzufügen", um Artikel aus Ihrer Bibliothek hinzuzufügen',
  'collections.noItemsFound': 'Keine Artikel gefunden',
  'collections.deleteConfirm': 'Diese Sammlung löschen? Artikel werden nicht gelöscht.',
  'collections.loading': 'Sammlungen werden geladen...',
  'collections.icon': 'Symbol',
  'collections.item': 'Element',
  'collections.addItem': 'Element hinzufügen',
  'collections.itemName': 'Elementname',
  'collections.itemNamePlaceholder': 'Elementname...',
  'collections.itemType': 'Typ',
  'collections.noItems': 'Noch keine Elemente. Fügen Sie Ihr erstes Element oben hinzu.',
  'collections.type.document': 'Dokument',
  'collections.type.image': 'Bild',
  'collections.type.link': 'Link',
  'collections.type.note': 'Notiz',
  'collections.type.video': 'Video',
  'collections.type.audio': 'Audio',
  'collections.type.spreadsheet': 'Tabelle',
  'collections.type.presentation': 'Präsentation',
  'collections.type.report': 'Bericht',
  'collections.type.task': 'Aufgabe',
  'collections.type.invoice': 'Rechnung',
  'collections.type.contract': 'Vertrag',
  'collections.type.email': 'E-Mail',
  'collections.type.contact': 'Kontakt',
  'collections.type.blueprint': 'Entwurf',
  'collections.type.code': 'Code',

  // ── Duplicate Finder ──
  'duplicates.title': 'Duplikat-Finder',
  'duplicates.subtitle': 'Laden Sie ein Foto hoch, um passende Artikel in Ihrer Sammlung zu finden und Wertschätzungen zu erhalten',
  'duplicates.dropHere': 'Bild hierher ziehen oder klicken zum Hochladen',
  'duplicates.supportedFormats': 'Unterstützt JPG, PNG, GIF, WebP — bis zu 20MB',
  'duplicates.scanning': 'Sammlung wird gescannt...',
  'duplicates.scanButton': 'Nach Duplikaten suchen',
  'duplicates.aiAnalysis': 'KI-Analyse',
  'duplicates.description': 'Beschreibung',
  'duplicates.category': 'Kategorie',
  'duplicates.brand': 'Marke',
  'duplicates.model': 'Modell',
  'duplicates.condition': 'Zustand',
  'duplicates.estimatedValue': 'Geschätzter Marktwert',
  'duplicates.low': 'Niedrig',
  'duplicates.mid': 'Mittel',
  'duplicates.high': 'Hoch',
  'duplicates.matchesFound': '{count} Treffer gefunden',
  'duplicates.noDuplicates': 'Keine Duplikate gefunden',
  'duplicates.noDuplicatesDesc': 'Dieser Artikel scheint mit nichts in Ihrer Sammlung übereinzustimmen. Es könnte ein neuer Artikel sein!',
  'duplicates.highMatch': 'Hohe Übereinstimmung',
  'duplicates.possibleMatch': 'Mögliche Übereinstimmung',
  'duplicates.lowMatch': 'Geringe Übereinstimmung',
  'duplicates.estValue': 'Geschätzter Wert:',
  'duplicates.selectImage': 'Bitte wählen Sie eine Bilddatei',

  // ── AI Assistant ──
  'ai.newChat': 'Neuer Chat',
  'ai.loading': 'Laden...',
  'ai.noChats': 'Noch keine Gespräche',
  'ai.startChat': 'Starten Sie einen neuen Chat',
  'ai.typeMessage': 'Nachricht eingeben...',
  'ai.maxImages': 'Maximal 5 Bilder pro Nachricht',
  'ai.welcomeTitle': 'ELVOAQ KI-Assistent',
  'ai.welcomeDesc': 'Fragen Sie mich alles über Ihre Sammlung, Artikelwerte oder Organisationstipps.',
  'ai.deleteConfirm': 'Dieses Gespräch löschen?',

  // ── Hidden Folders ──
  'hiddenFolders.title': 'Versteckte Ordner',
  'hiddenFolders.subtitle': 'Passwortgeschützte Ordner zum Schutz Ihrer privaten Artikel',
  'hiddenFolders.newFolder': 'Neuer Ordner',
  'hiddenFolders.loading': 'Ordner werden geladen...',
  'hiddenFolders.noFolders': 'Noch keine versteckten Ordner',
  'hiddenFolders.noFoldersDesc': 'Erstellen Sie einen passwortgeschützten Ordner, um Ihre privatesten Artikel sicher aufzubewahren.',
  'hiddenFolders.createFirst': 'Ersten Ordner erstellen',
  'hiddenFolders.open': 'Öffnen',
  'hiddenFolders.items': 'Artikel',
  'hiddenFolders.back': 'Zurück',
  'hiddenFolders.addItem': 'Artikel hinzufügen',
  'hiddenFolders.noItems': 'Noch keine Artikel in diesem Ordner',
  'hiddenFolders.addFirstItem': 'Ersten Artikel hinzufügen',
  'hiddenFolders.viewItem': 'Artikel anzeigen',
  'hiddenFolders.removeFromFolder': 'Aus Ordner entfernen',
  'hiddenFolders.enterPassword': 'Passwort eingeben',
  'hiddenFolders.passwordProtected': 'Dieser Ordner ist passwortgeschützt. Geben Sie das Passwort ein, um den Inhalt anzuzeigen.',
  'hiddenFolders.folderPassword': 'Ordnerpasswort',
  'hiddenFolders.verifying': 'Überprüfung...',
  'hiddenFolders.viewFolder': 'Ordner anzeigen',
  'hiddenFolders.createFolder': 'Versteckten Ordner erstellen',
  'hiddenFolders.folderName': 'Ordnername *',
  'hiddenFolders.folderNamePlaceholder': 'z.B., Wertgegenstände, Private Sammlung',
  'hiddenFolders.descriptionLabel': 'Beschreibung',
  'hiddenFolders.descriptionPlaceholder': 'Optionale Beschreibung...',
  'hiddenFolders.iconLabel': 'Symbol',
  'hiddenFolders.colorLabel': 'Farbe',
  'hiddenFolders.password': 'Passwort *',
  'hiddenFolders.confirmPassword': 'Passwort bestätigen *',
  'hiddenFolders.creating': 'Wird erstellt...',
  'hiddenFolders.editFolder': 'Ordner bearbeiten',
  'hiddenFolders.changeFolderPassword': 'Ordnerpasswort ändern',
  'hiddenFolders.leaveEmptyKeep': 'Leer lassen, um das aktuelle Passwort beizubehalten',
  'hiddenFolders.currentPassword': 'Aktuelles Passwort',
  'hiddenFolders.newPassword': 'Neues Passwort',
  'hiddenFolders.confirmNewPassword': 'Neues Passwort bestätigen',
  'hiddenFolders.saveChanges': 'Änderungen speichern',
  'hiddenFolders.deleteFolder': 'Ordner löschen',
  'hiddenFolders.deleteConfirm': 'Möchten Sie wirklich löschen',
  'hiddenFolders.deleteDesc': '? Der Ordner wird entfernt, aber die Artikel bleiben erhalten.',
  'hiddenFolders.deletePasswordPrompt': 'Ordnerpasswort zur Bestätigung eingeben',
  'hiddenFolders.cancel': 'Abbrechen',
  'hiddenFolders.addItemTo': 'Artikel hinzufügen zu',
  'hiddenFolders.searchItems': 'Artikel suchen...',
  'hiddenFolders.enterPasswordToAdd': 'Ordnerpasswort eingeben, um diesen Artikel hinzuzufügen:',
  'hiddenFolders.confirm': 'Bestätigen',
  'hiddenFolders.alreadyAdded': 'Bereits hinzugefügt',
  'hiddenFolders.noResults': 'Keine Artikel gefunden',
  'hiddenFolders.allCategories': 'Alle Kategorien',
  'hiddenFolders.favoritesOnly': 'Nur Favoriten',
  'hiddenFolders.favorites': 'Favoriten',
  'hiddenFolders.passwordsNoMatch': 'Passwörter stimmen nicht überein',
  'hiddenFolders.passwordMinLength': 'Passwort muss mindestens 4 Zeichen lang sein',
  'hiddenFolders.folderCreated': 'Versteckter Ordner erstellt!',
  'hiddenFolders.itemAdded': 'Artikel zum versteckten Ordner hinzugefügt',
  'hiddenFolders.itemRemoved': 'Artikel aus verstecktem Ordner entfernt',
  'hiddenFolders.folderUpdated': 'Ordner erfolgreich aktualisiert',
  'hiddenFolders.folderPasswordChanged': 'Ordnerpasswort geändert',
  'hiddenFolders.folderDeleted': 'Ordner erfolgreich gelöscht',
  'hiddenFolders.nameLabel': 'Name',
  'hiddenFolders.currentPasswordPlaceholder': 'Aktuelles Passwort eingeben',
  'hiddenFolders.newPasswordPlaceholder': 'Neues Passwort (mind. 4 Zeichen)',
  'hiddenFolders.confirmPasswordPlaceholder': 'Neues Passwort erneut eingeben',
  'hiddenFolders.folderPasswordPlaceholder': 'Ordnerpasswort',
  'hiddenFolders.confirmFolderPasswordPlaceholder': 'Passwort bestätigen',
  'hiddenFolders.currentPasswordRequired': 'Aktuelles Passwort ist erforderlich',
  'hiddenFolders.newPasswordMinLength': 'Neues Passwort muss mindestens 4 Zeichen lang sein',
  'hiddenFolders.newPasswordsNoMatch': 'Neue Passwörter stimmen nicht überein',
  'hiddenFolders.deleteButton': 'Ordner löschen',
  'hiddenFolders.addItemButton': 'Artikel hinzufügen',
  'hiddenFolders.nameAndPasswordRequired': 'Name und Passwort sind erforderlich',
  'hiddenFolders.enterFolderPassword': 'Ordnerpasswort eingeben',
  'hiddenFolders.wrongPassword': 'Falsches Passwort',
  'hiddenFolders.failedToCreateFolder': 'Ordner konnte nicht erstellt werden',
  'hiddenFolders.failedToAddItem': 'Artikel konnte nicht hinzugefügt werden',
  'hiddenFolders.failedToRemoveItem': 'Artikel konnte nicht entfernt werden',
  'hiddenFolders.failedToUpdateFolder': 'Ordner konnte nicht aktualisiert werden',
  'hiddenFolders.failedToDeleteFolder': 'Ordner konnte nicht gelöscht werden',
  'hiddenFolders.folderUpdatedAndPasswordChanged': 'Ordner aktualisiert & Passwort geändert',

  // ── Data Transfer ──
  'dataTransfer.title': 'Import & Export',
  'dataTransfer.subtitle': 'Übertragen Sie Ihre Daten im CSV- oder JSON-Format',
  'dataTransfer.export': 'Export',
  'dataTransfer.import': 'Import',
  'dataTransfer.exportTitle': 'Ihre Artikel exportieren',
  'dataTransfer.exportDesc': 'Laden Sie Ihre Artikeldaten in Ihrem bevorzugten Format herunter.',
  'dataTransfer.format': 'Format:',
  'dataTransfer.fieldsToInclude': 'Einzuschließende Felder:',
  'dataTransfer.selectAll': 'Alle auswählen',
  'dataTransfer.deselectAll': 'Alle abwählen',
  'dataTransfer.exporting': 'Wird exportiert...',
  'dataTransfer.download': 'Herunterladen',
  'dataTransfer.importTitle': 'Artikel importieren',
  'dataTransfer.importDesc': 'Laden Sie eine CSV- oder JSON-Datei hoch, um Artikel zu importieren.',
  'dataTransfer.importMode': 'Importmodus:',
  'dataTransfer.addToExisting': 'Zu bestehenden hinzufügen',
  'dataTransfer.addToExistingDesc': 'Neue Artikel werden neben Ihren aktuellen Artikeln hinzugefügt',
  'dataTransfer.overwriteAll': 'Alle überschreiben',
  'dataTransfer.overwriteAllDesc': 'Aktuelle Artikel werden archiviert und durch importierte Daten ersetzt',
  'dataTransfer.overwriteWarning': 'Warnung: Dies archiviert alle Ihre aktuellen Artikel. Sie können aus dem Papierkorb wiederhergestellt werden.',
  'dataTransfer.clickToSelect': 'Klicken Sie, um eine CSV- oder JSON-Datei auszuwählen',
  'dataTransfer.supported': 'Unterstützt: .csv, .json',
  'dataTransfer.preview': 'Vorschau',
  'dataTransfer.itemsFound': 'Artikel gefunden',
  'dataTransfer.showingOf': '5 von {count} Artikeln angezeigt',
  'dataTransfer.importing': 'Wird importiert...',
  'dataTransfer.importItems': '{count} Artikel importieren',
  'dataTransfer.confirmOverwrite': 'Überschreiben bestätigen',
  'dataTransfer.overwriteBody1': 'Sie werden alle bestehenden Artikel mit {count} importierten Artikeln überschreiben.',
  'dataTransfer.overwriteBody2': 'Ihre aktuellen Artikel werden in den Papierkorb verschoben und können später wiederhergestellt werden.',
  'dataTransfer.yesOverwrite': 'Ja, überschreiben',
  'dataTransfer.selectOneField': 'Wählen Sie mindestens ein Feld zum Exportieren',
  'dataTransfer.noDataToImport': 'Keine Daten zum Importieren',
  'dataTransfer.exportSuccess': 'Export als {format}-Datei heruntergeladen!',
  'dataTransfer.importSuccess': '{count} Artikel erfolgreich importiert!',
  'dataTransfer.overwriteNote': '(Vorherige Artikel wurden archiviert)',

  // ── Smart Hub ──
  'smartHub.loading': 'Smart Hub wird geladen...',
  'smartHub.title': 'Smart Hub',
  'smartHub.subtitle': 'KI-gestützte Einblicke & Verwaltung',
  'smartHub.analyzing': 'Analysiere...',
  'smartHub.aiAnalyze': 'KI-Analyse',
  'smartHub.overview': 'Übersicht',
  'smartHub.reminders': 'Erinnerungen',
  'smartHub.notes': 'Notizen',
  'smartHub.insights': 'Einblicke',
  'smartHub.aiReport': 'KI-Bericht',
  'smartHub.totalItems': 'Gesamtanzahl',
  'smartHub.collectionValue': 'Sammlungswert',
  'smartHub.categories': 'Kategorien',
  'smartHub.activeReminders': 'Aktive Erinnerungen',
  'smartHub.aiSummary': 'KI-Zusammenfassung',
  'smartHub.topCategories': 'Top-Kategorien:',
  'smartHub.mostValuable': 'Wertvollstes:',
  'smartHub.summaryContains': 'Ihre Sammlung enthält {count} Artikel.',
  'smartHub.summaryTotalValue': 'Gesamtwert: {total} (Durchschn.: {avg}).',
  'smartHub.summaryTopCategory': 'Top-Kategorie: {name} ({count} Artikel).',
  'smartHub.summaryRecentItems': '{count} Artikel diese Woche hinzugefügt.',
  'smartHub.summaryMostValuable': 'Wertvollstes: "{name}" ({value}).',
  'smartHub.summaryEmpty': 'Ihre Sammlung ist leer. Fügen Sie Ihren ersten Artikel hinzu!',
  'smartHub.overdueReminders': 'überfällige Erinnerung(en)',
  'smartHub.insightsAvailable': 'Einblick(e) verfügbar',
  'smartHub.pinnedNotes': 'angeheftete Notiz(en)',
  'smartHub.all': 'Alle',
  'smartHub.active': 'Aktiv',
  'smartHub.completed': 'Erledigt',
  'smartHub.addReminder': 'Erinnerung hinzufügen',
  'smartHub.editReminder': 'Erinnerung bearbeiten',
  'smartHub.newReminder': 'Neue Erinnerung',
  'smartHub.reminderTitle': 'Erinnerungstitel',
  'smartHub.descriptionOptional': 'Beschreibung (optional)',
  'smartHub.custom': 'Benutzerdefiniert',
  'smartHub.maintenance': 'Wartung',
  'smartHub.review': 'Überprüfung',
  'smartHub.insurance': 'Versicherung',
  'smartHub.warranty': 'Garantie',
  'smartHub.low': 'Niedrig',
  'smartHub.medium': 'Mittel',
  'smartHub.high': 'Hoch',
  'smartHub.urgent': 'Dringend',
  'smartHub.unassigned': 'Nicht zugewiesen',
  'smartHub.me': 'Ich',
  'smartHub.assignHint': 'Zuweisen an: "Ich" bedeutet, dass es auf Ihrer Liste erscheint. Kollegen sind Ihre Workspace-Mitglieder.',
  'smartHub.assignedTo': 'Zugewiesen an',
  'smartHub.cancel': 'Abbrechen',
  'smartHub.saveChanges': 'Änderungen speichern',
  'smartHub.createReminder': 'Erinnerung erstellen',
  'smartHub.noReminders': 'Keine Erinnerungen',
  'smartHub.noRemindersDesc': 'Erstellen Sie eine oder klicken Sie auf "KI-Analyse" für Vorschläge',
  'smartHub.allNotes': 'Alle Notizen',
  'smartHub.pinned': 'Angeheftet',
  'smartHub.aiGenerated': 'KI-generiert',
  'smartHub.addNote': 'Notiz hinzufügen',
  'smartHub.editNote': 'Notiz bearbeiten',
  'smartHub.newNote': 'Neue Notiz',
  'smartHub.writeNote': 'Schreiben Sie Ihre Notiz...',
  'smartHub.addTag': 'Tag hinzufügen...',
  'smartHub.add': 'Hinzufügen',
  'smartHub.saveNote': 'Notiz speichern',
  'smartHub.noNotes': 'Noch keine Notizen',
  'smartHub.noNotesDesc': 'Erstellen Sie eine Notiz, um wichtige Informationen festzuhalten',
  'smartHub.noInsights': 'Keine aktiven Einblicke',
  'smartHub.noInsightsDesc': 'Klicken Sie auf "KI-Analyse", um Ihre Sammlung nach Empfehlungen zu durchsuchen',
  'smartHub.relatedItems': 'Verwandte Artikel:',
  'smartHub.confidence': 'Konfidenz:',
  'smartHub.generatingReport': 'KI-Bericht wird erstellt...',
  'smartHub.reportTitle': 'Sammlungsanalysebericht',
  'smartHub.generated': 'Erstellt:',
  'smartHub.regenerate': 'Neu generieren',
  'smartHub.items': 'Artikel',
  'smartHub.value': 'Wert',
  'smartHub.avgValue': 'Durchschn. Wert',
  'smartHub.noReport': 'Noch kein Bericht erstellt',
  'smartHub.generateReport': 'KI-Bericht erstellen',
  'smartHub.daysOverdue': '{count} Tage überfällig',
  'smartHub.today': 'Heute',
  'smartHub.tomorrow': 'Morgen',
  'smartHub.inDays': 'In {count} Tagen',
  'smartHub.inWeeks': 'In {count} Wochen',
  'smartHub.inMonths': 'In {count} Monaten',
  'smartHub.lockedItem': 'Gesperrter Artikel',
  'smartHub.markIncomplete': 'Als unvollständig markieren',
  'smartHub.markComplete': 'Als erledigt markieren',
  'smartHub.editTitle': 'Bearbeiten',
  'smartHub.deleteTitle': 'Löschen',
  'smartHub.unpin': 'Lösen',
  'smartHub.pinInsight': 'Anheften',
  'smartHub.dismiss': 'Verwerfen',
  'smartHub.missing': 'Fehlt:',
  'smartHub.member': 'Mitglied',

  // Calendar
  'nav.calendar': 'Kalender',
  'calendar.title': 'Kalender',
  'calendar.today': 'Heute',
  'calendar.day': 'Tag',
  'calendar.week': 'Woche',
  'calendar.month': 'Monat',
  'calendar.newEvent': 'Neues Ereignis',
  'calendar.editEvent': 'Ereignis bearbeiten',
  'calendar.eventTitle': 'Titel',
  'calendar.titlePlaceholder': 'Titel hinzufügen...',
  'calendar.date': 'Datum',
  'calendar.startTime': 'Beginn',
  'calendar.endTime': 'Ende',
  'calendar.allDay': 'Ganztägig',
  'calendar.location': 'Ort',
  'calendar.locationPlaceholder': 'Ort hinzufügen...',
  'calendar.description': 'Beschreibung',
  'calendar.descriptionPlaceholder': 'Beschreibung hinzufügen...',
  'calendar.color': 'Farbe',
  'calendar.save': 'Speichern',
  'calendar.create': 'Erstellen',
  'calendar.cancel': 'Abbrechen',
  'calendar.edit': 'Bearbeiten',
  'calendar.delete': 'Löschen',
  'calendar.more': 'mehr',
  'calendar.mon': 'Mo',
  'calendar.tue': 'Di',
  'calendar.wed': 'Mi',
  'calendar.thu': 'Do',
  'calendar.fri': 'Fr',
  'calendar.sat': 'Sa',
  'calendar.sun': 'So',
  'calendar.january': 'Januar',
  'calendar.february': 'Februar',
  'calendar.march': 'März',
  'calendar.april': 'April',
  'calendar.may': 'Mai',
  'calendar.june': 'Juni',
  'calendar.july': 'Juli',
  'calendar.august': 'August',
  'calendar.september': 'September',
  'calendar.october': 'Oktober',
  'calendar.november': 'November',
  'calendar.december': 'Dezember',
  'calendar.year': 'Jahr',
  'calendar.deleteConfirm': 'Möchten Sie dieses Ereignis wirklich löschen?',
  'calendar.trash': 'Zuletzt gelöscht',
  'calendar.trashDesc': 'Ereignisse werden nach 30 Tagen dauerhaft gelöscht',
  'calendar.trashEmpty': 'Keine gelöschten Ereignisse',
  'calendar.trashEmptyDesc': 'Gelöschte Ereignisse erscheinen hier',
  'calendar.restore': 'Wiederherstellen',
  'calendar.deleteForever': 'Endgültig löschen',
  'calendar.loading': 'Laden...',
  'calendar.searchEvents': 'Ereignisse suchen...',
  'calendar.noEvents': 'Keine Ereignisse gefunden',
  'calendar.upcoming': 'Anstehend',
  'calendar.myCalendars': 'Meine Kalender',
  'calendar.addCategory': 'Kategorie hinzufügen',
  'calendar.categoryName': 'Kategoriename...',
  'calendar.category': 'Kategorie',
  'calendar.sunday': 'Sonntag',
  'calendar.monday': 'Montag',
  'calendar.tuesday': 'Dienstag',
  'calendar.wednesday': 'Mittwoch',
  'calendar.thursday': 'Donnerstag',
  'calendar.friday': 'Freitag',
  'calendar.saturday': 'Samstag',
  'calendar.cat.meetings': 'Meetings',
  'calendar.cat.deadlines': 'Fristen',
  'calendar.cat.reminders': 'Erinnerungen',
  'calendar.cat.reviews': 'Reviews',
  'calendar.cat.presentations': 'Präsentationen',
  'calendar.cat.training': 'Schulungen',
  'calendar.cat.travel': 'Reisen',
  'calendar.cat.holidays': 'Feiertage',

  // ── User Management (DE) ──────────────────────────────────────
  'userMgmt.empty.noWorkspaceTitle': 'Kein Arbeitsbereich ausgewählt',
  'userMgmt.empty.noWorkspaceDesc': 'Wählen Sie einen Arbeitsbereich aus oder erstellen Sie einen, um Mitglieder zu verwalten',
  'userMgmt.empty.goToWorkspaces': 'Zu Arbeitsbereichen',
  'userMgmt.header.badge': 'Personen & Teams',
  'userMgmt.header.description': 'Mitglieder, Rollen, Teams und Zugriffsberechtigungen verwalten',
  'userMgmt.header.invitePeople': 'Personen einladen',
  'userMgmt.header.convertToOrg': 'In Organisation umwandeln',
  'userMgmt.header.transferOwnership': 'Eigentum übertragen',
  'userMgmt.stats.members': 'Mitglieder',
  'userMgmt.stats.pending': 'Ausstehend',
  'userMgmt.tabs.members': 'Mitglieder',
  'userMgmt.tabs.invitations': 'Einladungen',
  'userMgmt.tabs.teams': 'Teams',
  'userMgmt.tabs.roleGuide': 'Rollenleitfaden',
  'userMgmt.tabs.suggestions': 'Vorschläge',
  'userMgmt.members.searchPlaceholder': 'Nach Name, E-Mail oder Rolle suchen...',
  'userMgmt.members.sortByRole': 'Nach Rolle',
  'userMgmt.members.sortByName': 'Nach Name',
  'userMgmt.members.sortByJoined': 'Kürzlich beigetreten',
  'userMgmt.members.loading': 'Mitglieder werden geladen...',
  'userMgmt.members.notFoundTitle': 'Keine Mitglieder gefunden',
  'userMgmt.members.noResults': 'Keine Ergebnisse für "{query}"',
  'userMgmt.members.noMembers': 'Keine Mitglieder in diesem Arbeitsbereich',
  'userMgmt.members.unknownName': 'Unbekannt',
  'userMgmt.members.changeRoleTooltip': 'Rolle ändern',
  'userMgmt.members.removeMemberTooltip': 'Mitglied entfernen',
  'userMgmt.invitations.title': 'Ausstehende Einladungen',
  'userMgmt.invitations.sendInvite': 'Einladung senden',
  'userMgmt.invitations.loading': 'Einladungen werden geladen...',
  'userMgmt.invitations.emptyTitle': 'Keine ausstehenden Einladungen',
  'userMgmt.invitations.emptyDesc': 'Laden Sie Personen ein, um in Ihrem Arbeitsbereich zusammenzuarbeiten',
  'userMgmt.invitations.inviteSomeone': 'Jemanden einladen',
  'userMgmt.invitations.expires': 'Läuft ab',
  'userMgmt.invitations.revokeTooltip': 'Widerrufen',
  'userMgmt.teams.title': 'Teams',
  'userMgmt.teams.newTeam': 'Neues Team',
  'userMgmt.teams.loading': 'Teams werden geladen...',
  'userMgmt.teams.emptyTitle': 'Noch keine Teams',
  'userMgmt.teams.emptyDesc': 'Teams helfen bei der Organisation von Mitgliedern und der Verwaltung des Elementzugriffs',
  'userMgmt.teams.createFirst': 'Erstes Team erstellen',
  'userMgmt.teams.noDescription': 'Keine Beschreibung',
  'userMgmt.roles.title': 'Rollenhierarchie & Berechtigungen',
  'userMgmt.roles.description': 'Erfahren Sie, was jede Rolle in Ihrem Arbeitsbereich tun kann',
  'userMgmt.roles.memberCount': 'Mitglied',
  'userMgmt.roles.membersCount': 'Mitglieder',
  'userMgmt.roles.permissionsHeading': 'Berechtigungen',
  'userMgmt.perm.fullWorkspaceControl': 'Volle Kontrolle über den Arbeitsbereich',
  'userMgmt.perm.transferOwnership': 'Eigentum übertragen',
  'userMgmt.perm.deleteWorkspace': 'Arbeitsbereich löschen',
  'userMgmt.perm.manageBilling': 'Abrechnung verwalten',
  'userMgmt.perm.allAdminPermissions': 'Alle Admin-Berechtigungen',
  'userMgmt.perm.manageSettings': 'Arbeitsbereich-Einstellungen verwalten',
  'userMgmt.perm.inviteRemoveMembers': 'Mitglieder einladen/entfernen',
  'userMgmt.perm.assignRoles': 'Rollen zuweisen (bis Manager)',
  'userMgmt.perm.viewAllItems': 'Alle Elemente & Analysen anzeigen',
  'userMgmt.perm.configureAI': 'KI-Funktionen konfigurieren',
  'userMgmt.perm.createEditItems': 'Elemente erstellen & bearbeiten',
  'userMgmt.perm.approveSuggestions': 'Vorschläge genehmigen',
  'userMgmt.perm.moderateComments': 'Kommentare moderieren',
  'userMgmt.perm.useAI': 'KI-Funktionen nutzen',
  'userMgmt.perm.createShareLinks': 'Freigabelinks erstellen',
  'userMgmt.perm.cannotManageMembers': 'Kann keine Mitglieder verwalten',
  'userMgmt.perm.cannotChangeSettings': 'Kann keine Einstellungen ändern',
  'userMgmt.perm.viewWorkspaceItems': 'Arbeitsbereich-Elemente anzeigen',
  'userMgmt.perm.suggestEdits': 'Änderungen vorschlagen',
  'userMgmt.perm.postComments': 'Kommentare schreiben',
  'userMgmt.perm.cannotEditItems': 'Kann Elemente nicht direkt bearbeiten',
  'userMgmt.perm.cannotCreateItems': 'Kann keine Elemente erstellen',
  'userMgmt.perm.viewItems': 'Elemente anzeigen',
  'userMgmt.perm.downloadFiles': 'Dateien herunterladen',
  'userMgmt.perm.viewComments': 'Kommentare anzeigen',
  'userMgmt.perm.cannotEditOrComment': 'Kann nicht bearbeiten oder kommentieren',
  'userMgmt.perm.readOnlyAccess': 'Nur-Lese-Zugriff',
  'userMgmt.perm.cannotManageAnything': 'Kann nichts verwalten',
  'userMgmt.perm.limitedViewAccess': 'Eingeschränkter Lesezugriff',
  'userMgmt.perm.accessSharedItems': 'Zugriff auf bestimmte freigegebene Elemente',
  'userMgmt.perm.cannotSeeFullWorkspace': 'Kann den gesamten Arbeitsbereich nicht sehen',
  'userMgmt.perm.mostRestrictiveRole': 'Restriktivste Rolle',
  'userMgmt.perm.noManagementRights': 'Keine Verwaltungsrechte',
  'userMgmt.suggestions.loading': 'Vorschläge werden geladen...',
  'userMgmt.suggestions.emptyTitle': 'Keine ausstehenden Vorschläge',
  'userMgmt.suggestions.emptyDesc': 'Änderungsvorschläge von Reviewern erscheinen hier zur Genehmigung',
  'userMgmt.suggestions.currentLabel': 'Aktuell',
  'userMgmt.suggestions.empty': '(leer)',
  'userMgmt.suggestions.suggestedLabel': 'Vorgeschlagen',
  'userMgmt.suggestions.approve': 'Genehmigen',
  'userMgmt.suggestions.reject': 'Ablehnen',
  'userMgmt.inviteModal.title': 'Personen einladen',
  'userMgmt.inviteModal.emailLabel': 'E-Mail-Adresse',
  'userMgmt.inviteModal.emailPlaceholder': 'kollege@firma.de',
  'userMgmt.inviteModal.roleLabel': 'Rolle',
  'userMgmt.inviteModal.sendInvitation': 'Einladung senden',
  'userMgmt.roleModal.title': 'Rolle ändern',
  'userMgmt.roleModal.changingRoleFor': 'Rolle ändern für',
  'userMgmt.roleModal.currentTag': 'Aktuell',
  'userMgmt.transferModal.title': 'Eigentum übertragen',
  'userMgmt.transferModal.warningTitle': 'Diese Aktion kann nicht rückgängig gemacht werden',
  'userMgmt.transferModal.warningDesc': 'Sie werden nach der Übertragung zum Org-Admin herabgestuft.',
  'userMgmt.transferModal.newOwnerLabel': 'Neuer Eigentümer',
  'userMgmt.transferModal.selectMemberPlaceholder': 'Mitglied auswählen...',
  'userMgmt.transferModal.transferBtn': 'Eigentum übertragen',
  'userMgmt.teamModal.title': 'Team erstellen',
  'userMgmt.teamModal.nameLabel': 'Teamname',
  'userMgmt.teamModal.namePlaceholder': 'z.B. Marketing, Entwicklung',
  'userMgmt.teamModal.descLabel': 'Beschreibung (optional)',
  'userMgmt.teamModal.descPlaceholder': 'Woran arbeitet dieses Team?',
  'userMgmt.teamModal.createBtn': 'Team erstellen',
  'userMgmt.convertModal.title': 'In Organisation umwandeln',
  'userMgmt.convertModal.heroTitle': 'Auf Organisation upgraden',
  'userMgmt.convertModal.heroDesc': 'Wandeln Sie Ihren persönlichen Arbeitsbereich in eine Organisation um, um Team-Zusammenarbeit freizuschalten.',
  'userMgmt.convertModal.featureInviteTitle': 'Teammitglieder einladen',
  'userMgmt.convertModal.featureInviteDesc': 'Kollegen hinzufügen und Rollen zuweisen',
  'userMgmt.convertModal.featureRbacTitle': 'Rollenbasierter Zugriff',
  'userMgmt.convertModal.featureRbacDesc': 'Steuern Sie, wer anzeigen, bearbeiten und verwalten kann',
  'userMgmt.convertModal.featureTeamsTitle': 'Teams & Gruppen',
  'userMgmt.convertModal.featureTeamsDesc': 'Mitglieder in Teams organisieren',
  'userMgmt.convertModal.note': 'Alle Ihre bestehenden Daten bleiben erhalten. Sie werden zum Eigentümer der Organisation.',
  'userMgmt.convertModal.converting': 'Wird umgewandelt...',
  'userMgmt.convertModal.convertNow': 'Jetzt umwandeln',
  'userMgmt.common.cancel': 'Abbrechen',
  'userMgmt.error.personalNoInvitations': 'Persönliche Arbeitsbereiche unterstützen keine Einladungen. Zuerst in Organisation umwandeln.',
  'userMgmt.success.invitationSent': 'Einladung gesendet an {email}',
  'userMgmt.error.invitationFailed': 'Einladung konnte nicht gesendet werden',
  'userMgmt.success.convertedToOrg': 'Arbeitsbereich in Organisation umgewandelt! Sie können jetzt Mitglieder einladen.',
  'userMgmt.error.convertFailed': 'Umwandlung fehlgeschlagen',
  'userMgmt.success.roleUpdated': 'Rolle erfolgreich aktualisiert',
  'userMgmt.error.roleChangeFailed': 'Rollenänderung fehlgeschlagen',
  'userMgmt.success.memberRemoved': '{name} aus dem Arbeitsbereich entfernt',
  'userMgmt.error.removeMemberFailed': 'Mitglied konnte nicht entfernt werden',
  'userMgmt.success.invitationRevoked': 'Einladung an {email} widerrufen',
  'userMgmt.error.revokeInvitationFailed': 'Einladung konnte nicht widerrufen werden',
  'userMgmt.success.ownershipTransferred': 'Eigentum erfolgreich übertragen',
  'userMgmt.error.transferOwnershipFailed': 'Eigentumübertragung fehlgeschlagen',
  'userMgmt.success.teamCreated': 'Team „{name}" erstellt',
  'userMgmt.error.createTeamFailed': 'Team konnte nicht erstellt werden',
  'userMgmt.success.suggestionApproved': 'Vorschlag genehmigt',
  'userMgmt.success.suggestionRejected': 'Vorschlag abgelehnt',
  'userMgmt.error.reviewSuggestionFailed': 'Vorschlag konnte nicht überprüft werden',
  'userMgmt.confirm.removeMember': '{name} aus diesem Arbeitsbereich entfernen?',
  'userMgmt.confirm.revokeInvitation': 'Einladung an {email} widerrufen?',
  'userMgmt.confirm.transferOwnership': 'Eigentum übertragen? Sie werden zum Org-Admin. Dies kann nicht rückgängig gemacht werden.',
  'userMgmt.time.today': 'Heute',
  'userMgmt.time.yesterday': 'Gestern',
  'userMgmt.time.daysAgo': 'vor {count} T.',
  'userMgmt.time.monthsAgo': 'vor {count} Mon.',
  'userMgmt.time.yearsAgo': 'vor {count} J.',
  'audit.statsLogins': 'An- & Abmeldungen',
  'audit.statsCrud': 'Erstellen, Aktualisieren, Löschen',
  'audit.statsAlerts': 'erfordert Aufmerksamkeit',
  'audit.statsReverts': 'Rückgängig',
  'audit.statsRollbacks': 'durchgeführte Rollbacks',
  'audit.allStatuses': 'Alle Status',
  'audit.statusSuccess': 'Erfolgreich',
  'audit.statusFailed': 'Fehlgeschlagen',
  'audit.allTime': 'Gesamte Zeit',
  'audit.pastHour': 'Letzte Stunde',
  'audit.past24Hours': 'Letzte 24 Stunden',
  'audit.past7Days': 'Letzte 7 Tage',
  'audit.past30Days': 'Letzte 30 Tage',
  'audit.reset': 'Zurücksetzen',
  'audit.live': 'Live',
  'audit.liveMonitoring': 'Live-Überwachung',
  'audit.event': 'Ereignis',
  'audit.description': 'Beschreibung',
  'audit.status': 'Status',
  'audit.view': 'Anzeigen',
  'audit.twoFaVerified': '2FA verifiziert',

  // ═══ Homepage (GSAP) ═══
  'hp.legal.gdpr': 'DSGVO konform',
  'hp.legal.ccpa': 'CCPA bereit',
  'hp.legal.hipaa': 'HIPAA konform',
  'hp.legal.iso': 'ISO 27001',

  // Navigation
  'hp.nav.home': 'Startseite',
  'hp.nav.about': 'Über uns',
  'hp.nav.features': 'Funktionen',
  'hp.nav.security': 'Sicherheit',
  'hp.nav.compliance': 'Compliance',
  'hp.nav.pricing': 'Preise',
  'hp.nav.contact': 'Kontakt',
  'hp.nav.login': 'Anmelden',
  'hp.nav.signup': 'Registrieren',

  // Hero Section
  'hp.hero.badge': 'Enterprise Compliance bereit',
  'hp.hero.title': 'Sicheres Asset-Management für moderne Unternehmen',
  'hp.hero.subtitle': 'ELVOAQ Vault & TimeCalc: KI-gestütztes Inventar-Management mit Enterprise-Sicherheit, Compliance und Audit-Trails. Vertraut von Unternehmen weltweit.',
  'hp.hero.ctaPrimary': 'Kostenlose Testversion starten',
  'hp.hero.ctaSecondary': 'Demo ansehen',
  'hp.hero.dashboardAlt': 'ELVOAQ Dashboard Oberfläche',

  // Compliance Badges
  'hp.complianceBadges.title': 'Compliance & Sicherheitsstandards',
  'hp.complianceBadges.gdpr.title': 'DSGVO konform',
  'hp.complianceBadges.gdpr.desc': 'Vollständige Compliance mit der EU-Datenschutz-Grundverordnung für europäische Nutzer.',
  'hp.complianceBadges.ccpa.title': 'CCPA bereit',
  'hp.complianceBadges.ccpa.desc': 'California Consumer Privacy Act Compliance für US-Datenschutzvorschriften.',
  'hp.complianceBadges.hipaa.title': 'HIPAA konform',
  'hp.complianceBadges.hipaa.desc': 'Healthcare-Datenschutzstandards für medizinische und Versicherungsbranchen.',
  'hp.complianceBadges.soc2.title': 'SOC 2 Typ II',
  'hp.complianceBadges.soc2.desc': 'Geprüfte Sicherheits-, Verfügbarkeits- und Vertraulichkeitskontrollen.',
  'hp.complianceBadges.iso.title': 'ISO 27001',
  'hp.complianceBadges.iso.desc': 'Internationaler Standard für Informationssicherheits-Managementsysteme.',
  'hp.complianceBadges.pci.title': 'PCI DSS',
  'hp.complianceBadges.pci.desc': 'Payment Card Industry Data Security Standard Compliance.',

  // Features Section
  'hp.features.title': 'Leistungsstarke Funktionen für Asset-Management',
  'hp.features.ai.title': 'KI-gestützte Intelligenz',
  'hp.features.ai.desc': 'Fortgeschrittene maschinelle Lernalgorithmen kategorisieren Artikel automatisch, erkennen Duplikate und prognostizieren Marktwerte mit Präzision.',
  'hp.features.duplicate.title': 'Duplikaterkennung',
  'hp.features.duplicate.desc': 'Intelligente Algorithmen identifizieren identische oder ähnliche Artikel in Ihrem gesamten Inventar und verhindern kostspielige Fehler.',
  'hp.features.valuation.title': 'Echtzeit-Bewertung',
  'hp.features.valuation.desc': 'Live-Marktdaten-Integration hält Ihre Asset-Bewertungen genau und immer auf dem neuesten Stand.',
  'hp.features.gallery.title': 'Professionelle Galerien',
  'hp.features.gallery.desc': 'Erstellen Sie beeindruckende visuelle Kataloge mit mehreren hochauflösenden Fotos und professionellen Präsentationstools.',
  'hp.features.search.title': 'Erweiterte Suche',
  'hp.features.search.desc': 'Blitzschnelle Suche über alle Artikel mit Filtern, Tags und Volltext-Funktionen.',
  'hp.features.collaboration.title': 'Team-Zusammenarbeit',
  'hp.features.collaboration.desc': 'Rollenbasierte Zugriffskontrolle und gemeinsame Arbeitsbereiche für nahtlose Team-Zusammenarbeit und Workflow-Management.',

  // Security Section
  'hp.security.title': 'Enterprise-Sicherheit',
  'hp.security.subtitle': 'Ihre Daten sind durch militärische Verschlüsselung und umfassende Sicherheitsmaßnahmen geschützt.',
  'hp.security.encryption.title': 'Ende-zu-Ende-Verschlüsselung',
  'hp.security.encryption.desc': 'Alle Daten werden in Transit und im Ruhezustand mit AES-256-Verschlüsselungsstandards verschlüsselt.',
  'hp.security.backup.title': 'Automatisierte Backups',
  'hp.security.backup.desc': 'Tägliche verschlüsselte Backups mit Point-in-Time-Wiederherstellung sorgen dafür, dass Ihre Daten immer sicher sind.',
  'hp.security.access.title': 'Zugriffskontrolle',
  'hp.security.access.desc': 'Multi-Faktor-Authentifizierung und granulare Berechtigungen schützen vor unbefugtem Zugriff.',
  'hp.security.monitoring.title': '24/7 Überwachung',
  'hp.security.monitoring.desc': 'Kontinuierliche Sicherheitsüberwachung und Bedrohungserkennung mit sofortigen Benachrichtigungen.',

  // Compliance Standards
  'hp.compliance.title': 'Compliance-Standards',
  'hp.compliance.gdpr.title': 'DSGVO',
  'hp.compliance.gdpr.desc': 'Datenschutz-Grundverordnung Compliance für EU-Datenschutzvorschriften.',
  'hp.compliance.ccpa.title': 'CCPA',
  'hp.compliance.ccpa.desc': 'California Consumer Privacy Act Compliance für US-Datenschutzvorschriften.',
  'hp.compliance.hipaa.title': 'HIPAA',
  'hp.compliance.hipaa.desc': 'Health Insurance Portability and Accountability Act für Healthcare-Datenschutz.',

  // Pricing Section
  'hp.pricing.title': 'Wählen Sie Ihren Plan',
  'hp.pricing.starter.name': 'Starter',
  'hp.pricing.starter.price': '9',
  'hp.pricing.starter.period': '/Monat',
  'hp.pricing.starter.desc': 'Perfekt für Einzelpersonen und kleine Unternehmen, die gerade beginnen.',
  'hp.pricing.starter.feature1': 'Bis zu 1.000 Artikel',
  'hp.pricing.starter.feature2': 'Basis-KI-Kategorisierung',
  'hp.pricing.starter.feature3': 'Mobile App Zugriff',
  'hp.pricing.starter.feature4': 'E-Mail-Support',
  'hp.pricing.professional.name': 'Professional',
  'hp.pricing.professional.price': '29',
  'hp.pricing.professional.period': '/Monat',
  'hp.pricing.professional.desc': 'Erweiterte Funktionen für wachsende Unternehmen und Teams.',
  'hp.pricing.professional.feature1': 'Bis zu 10.000 Artikel',
  'hp.pricing.professional.feature2': 'Erweiterte KI-Funktionen',
  'hp.pricing.professional.feature3': 'Team-Zusammenarbeit',
  'hp.pricing.professional.feature4': 'Prioritäts-Support',
  'hp.pricing.professional.popular': 'Am beliebtesten',
  'hp.pricing.enterprise.name': 'Enterprise',
  'hp.pricing.enterprise.price': 'Individuell',
  'hp.pricing.enterprise.period': 'Preisgestaltung',
  'hp.pricing.enterprise.desc': 'Maßgeschneiderte Lösungen für große Organisationen mit spezifischen Anforderungen.',
  'hp.pricing.enterprise.feature1': 'Unbegrenzte Artikel',
  'hp.pricing.enterprise.feature2': 'Benutzerdefinierte Integrationen',
  'hp.pricing.enterprise.feature3': 'Dedizierter Support',
  'hp.pricing.enterprise.feature4': 'SLA-Garantie',
  'hp.pricing.cta': 'Jetzt starten',

  // Legal Documents
  'hp.legal.title': 'Rechtliches & Compliance',
  'hp.legal.dpa.title': 'Datenverarbeitungsvereinbarung',
  'hp.legal.dpa.desc': 'DSGVO-konforme Datenverarbeitungsvereinbarung für Enterprise-Kunden.',
  'hp.legal.dpa.link': 'DPA herunterladen',
  'hp.legal.terms.title': 'Nutzungsbedingungen',
  'hp.legal.terms.desc': 'Vollständige Nutzungsbedingungen für die Nutzung von ELVOAQ-Diensten.',
  'hp.legal.terms.link': 'Bedingungen lesen',
  'hp.legal.privacy.title': 'Datenschutzrichtlinie',
  'hp.legal.privacy.desc': 'Wie wir Ihre persönlichen Informationen sammeln, verwenden und schützen.',
  'hp.legal.privacy.link': 'Datenschutzrichtlinie',
  'hp.legal.cookies.title': 'Cookie-Richtlinie',
  'hp.legal.cookies.desc': 'Informationen darüber, wie wir Cookies und Tracking-Technologien verwenden.',
  'hp.legal.cookies.link': 'Cookie-Richtlinie',

  // Trust Guarantees
  'hp.trust.title': 'Warum ELVOAQ wählen',
  'hp.trust.audit.title': 'Vollständige Audit-Trails',
  'hp.trust.audit.desc': 'Jede Aktion wird mit Zeitstempeln und Benutzerzuordnung für vollständige Compliance protokolliert.',
  'hp.trust.support.title': '24/7 Experten-Support',
  'hp.trust.support.desc': 'Unsere Compliance- und Sicherheitsexperten sind da, um Ihnen zum Erfolg zu verhelfen.',
  'hp.trust.uptime.title': '99.9% Uptime-SLA',
  'hp.trust.uptime.desc': 'Enterprise-Infrastruktur sorgt dafür, dass Ihr Geschäft niemals stillsteht.',
  'hp.trust.backup.title': 'Automatisierte Backups',
  'hp.trust.backup.desc': 'Tägliche verschlüsselte Backups mit 30-tägiger Aufbewahrung für Seelenfrieden.',

  // Contact Section
  'hp.contact.title': 'Kontakt aufnehmen',
  'hp.contact.subtitle': 'Bereit, Ihre Assets mit ELVOAQ zu sichern? Kontaktieren Sie unser Team für eine personalisierte Demo.',
  'hp.contact.info.title': 'Kontaktinformationen',
  'hp.contact.info.email': 'E-Mail',
  'hp.contact.info.emailValue': 'sales@elvoaq.com',
  'hp.contact.info.phone': 'Telefon',
  'hp.contact.info.phoneValue': '+41 44 123 45 67',
  'hp.contact.info.address': 'Adresse',
  'hp.contact.info.addressValue': 'ELVOAQ AG, Zürich, Schweiz',
  'hp.contact.form.title': 'Schreiben Sie uns',
  'hp.contact.form.name': 'Vollständiger Name',
  'hp.contact.form.email': 'E-Mail-Adresse',
  'hp.contact.form.company': 'Unternehmen',
  'hp.contact.form.message': 'Nachricht',
  'hp.contact.form.submit': 'Nachricht senden',

  // Footer
  'hp.footer.company': 'Unternehmen',
  'hp.footer.about': 'Über uns',
  'hp.footer.careers': 'Karriere',
  'hp.footer.press': 'Pressemitteilungen',
  'hp.footer.blog': 'Blog',
  'hp.footer.products': 'Produkte',
  'hp.footer.vault': 'ELVOAQ Vault',
  'hp.footer.timecalc': 'TimeCalc',
  'hp.footer.integrations': 'Integrationen',
  'hp.footer.resources': 'Ressourcen',
  'hp.footer.docs': 'Dokumentation',
  'hp.footer.help': 'Hilfecenter',
  'hp.footer.api': 'API-Referenz',
  'hp.footer.security': 'Sicherheit',
  'hp.footer.compliance': 'Compliance',
  'hp.footer.status': 'Systemstatus',
  'hp.footer.contact': 'Kontakt',
  'hp.footer.support': 'Support',
  'hp.footer.sales': 'Vertrieb',
  'hp.footer.partners': 'Partner',
  'hp.footer.social': 'Folgen Sie uns',
  'hp.footer.copyright': '© 2024 ELVOAQ AG. Alle Rechte vorbehalten.',
  'hp.footer.privacy': 'Datenschutzrichtlinie',
  'hp.footer.terms': 'Nutzungsbedingungen',
  'hp.footer.cookies': 'Cookie-Richtlinie',

  // Cookie Consent
  'hp.cookie.message': 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und unseren Traffic zu analysieren. Durch die weitere Nutzung unserer Website stimmen Sie unserer Verwendung von Cookies zu.',
  'hp.cookie.accept': 'Alle akzeptieren',
  'hp.cookie.decline': 'Ablehnen',
};

/* ═══════════════════════════════════════════════════════════════
   TRANSLATION KEYS — Czech (Čeština)
   ═══════════════════════════════════════════════════════════════ */

const cs: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Přehled',
  'nav.back': 'Zpět',
  'nav.library': 'Knihovna',
  'nav.favorites': 'Oblíbené',
  'nav.categories': 'Kategorie',
  'nav.workspaces': 'Pracovní prostory',
  'nav.collections': 'Kolekce',
  'nav.notifications': 'Oznámení',
  'nav.settings': 'Nastavení',
  'nav.history': 'Historie',
  'nav.trash': 'Koš',
  'nav.users': 'Uživatelé',
  'nav.usersRoles': 'Uživatelé a role',
  'nav.teams': 'Týmy',
  'nav.feedback': 'Zpětná vazba',
  'nav.subscription': 'Předplatné',
  'nav.pricing': 'Ceník',
  'nav.logout': 'Odhlásit se',
  'nav.auditTrail': 'Protokol aktivit',
  'nav.accounting': 'Účetnictví',
  'nav.smartHub': 'Smart Hub',
  'nav.aiAssistant': 'AI Asistent',
  'nav.duplicateFinder': 'Hledání duplikátů',
  'nav.hiddenFolders': 'Skryté složky',
  'nav.importExport': 'Import / Export',
  'nav.sectionMenu': 'Menu',
  'nav.sectionOther': 'Ostatní',
  'nav.sectionBilling': 'Fakturace',
  'nav.searchPlaceholder': 'Hledat sekce…',
  'nav.searchNoResults': 'Nic nebylo nalezeno',
  'action.save': 'Uložit',
  'action.saveChanges': 'Uložit změny',
  'action.cancel': 'Zrušit',
  'action.delete': 'Smazat',
  'action.edit': 'Upravit',
  'action.add': 'Přidat',
  'action.create': 'Vytvořit',
  'action.search': 'Hledat',
  'action.filter': 'Filtrovat',
  'action.filters': 'Filtry',
  'action.export': 'Exportovat',
  'action.import': 'Importovat',
  'action.refresh': 'Obnovit',
  'action.close': 'Zavřít',
  'action.confirm': 'Potvrdit',
  'action.back': 'Zpět',
  'action.next': 'Další',
  'action.loading': 'Načítání...',
  'action.saving': 'Ukládání...',
  'action.selectAll': 'Vybrat vše',
  'action.deselectAll': 'Zrušit výběr',
  'action.enableAll': 'Povolit vše',
  'action.disableAll': 'Zakázat vše',
  'action.view': 'Zobrazit',
  'action.move': 'Přesunout',
  'action.restore': 'Obnovit',
  'action.remove': 'Odstranit',
  'action.clear': 'Vymazat',
  'action.clearAll': 'Vymazat vše',
  'action.done': 'Hotovo',
  'action.apply': 'Použít',
  'action.reset': 'Resetovat',
  'action.resetFilters': 'Resetovat filtry',
  'action.upgrade': 'Upgradovat',
  'action.download': 'Stáhnout',
  'action.upload': 'Nahrát',
  'action.submit': 'Odeslat',
  'action.continue': 'Pokračovat',
  'action.getStarted': 'Začít',
  'action.learnMore': 'Zjistit více',
  'action.viewAll': 'Zobrazit vše',
  'action.showMore': 'Zobrazit více',
  'action.showLess': 'Zobrazit méně',
  'action.hideStats': 'Skrýt statistiky',
  'action.showStats': 'Zobrazit statistiky',
  'action.reply': 'Odpovědět',
  'action.post': 'Odeslat',
  'action.posting': 'Odesílání...',

  // Intervals
  'interval.day': 'Den',
  'interval.week': 'Týden',
  'interval.month': 'Měsíc',
  'interval.quarter': 'Čtvrtletí',
  'interval.year': 'Rok',

  // Sort Options
  'sort.newestFirst': 'Nejnovější první',
  'sort.oldestFirst': 'Nejstarší první',
  'sort.nameAZ': 'Název (A-Z)',
  'sort.nameZA': 'Název (Z-A)',
  'sort.highestValue': 'Nejvyšší hodnota',
  'sort.lowestValue': 'Nejnižší hodnota',
  'sort.recentlyUpdated': 'Nedávno aktualizované',
  'sort.mostItems': 'Nejvíce položek',

  // Item Fields
  'item.name': 'Název',
  'item.namePlaceholder': 'Zadejte název',
  'item.description': 'Popis',
  'item.descriptionPlaceholder': 'Zadejte popis',
  'item.category': 'Kategorie',
  'item.selectCategory': 'Vyberte kategorii',
  'item.condition': 'Stav',
  'item.location': 'Umístění',
  'item.locationPlaceholder': 'Kde je tato položka uložena?',
  'item.purchasePrice': 'Kupní cena',
  'item.purchaseDate': 'Datum nákupu',
  'item.estimatedValue': 'Odhadovaná hodnota',
  'item.aiValue': 'AI hodnota',
  'item.tags': 'Štítky',
  'item.tagsPlaceholder': 'Přidejte štítek a stiskněte Enter',
  'item.images': 'Obrázky',
  'item.chooseImages': 'Vyberte obrázky (max. 50)',
  'item.addMore': 'Přidat další',
  'item.createdAt': 'Vytvořeno',
  'item.updatedAt': 'Aktualizováno',

  // Item Modal
  'itemModal.editItem': 'Upravit položku',
  'itemModal.addNewItem': 'Přidat novou položku',
  'itemModal.updateItem': 'Aktualizovat položku',
  'itemModal.createItem': 'Vytvořit položku',
  'itemModal.saving': 'Ukládání...',
  'itemModal.aiAnalyzing': 'AI analyzuje...',
  'itemModal.aiSuggestions': 'Návrhy AI',
  'itemModal.purchasePlace': 'Místo nákupu',
  'itemModal.purchasePlacePlaceholder': 'Obchod, web nebo prodejce',
  'itemModal.currency': 'Měna',
  'itemModal.purchaseDateTime': 'Datum a čas nákupu',
  'itemModal.selectDate': 'Vyberte datum',
  'itemModal.timeOptional': 'Čas (volitelný)',
  'itemModal.noTime': 'Bez času',
  'itemModal.timezone': 'Časové pásmo',
  'itemModal.autoDetected': 'Automaticky rozpoznáno',
  'itemModal.highMatch': 'Vysoká shoda',
  'itemModal.mediumMatch': 'Střední shoda',
  'itemModal.lowMatch': 'Nízká shoda',
  'itemModal.add': 'Přidat',

  // Conditions
  'condition.new': 'Nový',
  'condition.likeNew': 'Jako nový',
  'condition.excellent': 'Výborný',
  'condition.good': 'Dobrý',
  'condition.fair': 'Uspokojivý',
  'condition.poor': 'Špatný',
  'condition.all': 'Všechny stavy',

  // Advanced Filters
  'filter.advancedFilters': 'Rozšířené filtry',
  'filter.condition': 'Stav',
  'filter.conditionAll': 'Vše',
  'filter.valueRange': 'Rozsah hodnot',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.purchaseDate': 'Datum nákupu',
  'filter.from': 'Od',
  'filter.to': 'Do',
  'filter.aiValueTrend': 'AI trend hodnoty',
  'filter.trendAll': 'Vše',
  'filter.appreciating': 'Rostoucí',
  'filter.stable': 'Stabilní',
  'filter.depreciating': 'Klesající',
  'filter.locations': 'Umístění',
  'filter.tags': 'Štítky',
  'filter.additionalOptions': 'Další možnosti',
  'filter.hasAIValue': 'Má AI predikci hodnoty',
  'filter.favoritesOnly': 'Pouze oblíbené',
  'filter.sortBy': 'Řadit podle',
  'filter.newestFirst': 'Nejnovější',
  'filter.oldestFirst': 'Nejstarší',
  'filter.nameAZ': 'Název (A-Z)',
  'filter.nameZA': 'Název (Z-A)',
  'filter.highestValue': 'Nejvyšší hodnota',
  'filter.lowestValue': 'Nejnižší hodnota',
  'filter.bestCondition': 'Nejlepší stav',
  'filter.worstCondition': 'Nejhorší stav',
  'filter.resetAll': 'Obnovit vše',
  'filter.applyFilters': 'Použít filtry',

  // Collections — delete confirm
  'collections.deleteConfirmDesc': 'Položky v této kolekci nebudou smazány.',

  // Collections — library search
  'collections.addFromLibrary': 'Přidat z knihovny',
  'collections.searchLibraryPlaceholder': 'Hledat položky...',
  'collections.noLibraryResults': 'Žádné položky nenalezeny',
  'collections.orAddManually': 'nebo přidat ručně',
  'collections.searchTab': 'Hledat',
  'collections.browseAllTab': 'Procházet vše',
  'collections.filterItems': 'Filtrovat položky...',
  'collections.allCategories': 'Vše',
  'collections.allConditions': 'Všechny stavy',
  'collections.searchHint': 'Zadejte alespoň 2 znaky pro hledání',
  'collections.itemsTab': 'Položky',
  'collections.addItemsTab': 'Přidat položky',
  'collections.searchItems': 'Hledat položky...',

  // Categories
  'categories.title': 'Kategorie',
  'categories.addCategory': 'Přidat kategorii',
  'categories.editCategory': 'Upravit kategorii',
  'categories.deleteCategory': 'Smazat kategorii',
  'categories.noCategories': 'Zatím žádné kategorie',
  'categories.noCategoriesFound': 'Žádné kategorie nenalezeny',
  'categories.totalItems': 'Celkem položek',
  'categories.totalValue': 'Celková hodnota',
  'categories.avgValue': 'Prům. hodnota',
  'categories.sortBy': 'Řadit podle:',
  'categories.name': 'Název',
  'categories.items': 'Položky',
  'categories.color': 'Barva',
  'categories.icon': 'Ikona',
  'categories.subtitle': 'Organizujte své položky do kolekcí',
  'categories.showStats': 'Zobrazit statistiky',
  'categories.hideStats': 'Skrýt statistiky',
  'categories.newCategory': 'Nová kategorie',
  'categories.categories': 'Kategorie',
  'categories.avgValuePerItem': 'Prům. hodnota/položku',
  'categories.mostValuable': 'Nejcennější',
  'categories.conditionDistribution': 'Rozložení stavu',
  'categories.searchPlaceholder': 'Hledat kategorie...',
  'categories.itemCount': 'Počet položek',
  'categories.dateCreated': 'Datum vytvoření',
  'categories.customizeDefault': 'Upravit výchozí kategorii',
  'categories.createNew': 'Vytvořit novou kategorii',
  'categories.infoBanner': 'Výchozí kategorie můžete přizpůsobit svým potřebám. Změny budou uloženy.',
  'categories.categoryName': 'Název kategorie *',
  'categories.enterName': 'Zadejte název kategorie',
  'categories.description': 'Popis',
  'categories.describeCategory': 'Popište tuto kategorii',
  'categories.searchIcons': 'Hledat ikony...',
  'categories.updateCategory': 'Aktualizovat kategorii',
  'categories.createCategory': 'Vytvořit kategorii',
  'categories.cancel': 'Zrušit',
  'categories.default': 'Výchozí',
  'categories.noDescription': 'Bez popisu',
  'categories.avg': 'Prům.:',
  'categories.tryDifferent': 'Zkuste jiný hledaný výraz',
  'categories.createToOrganize': 'Vytvořte kategorie pro organizaci svých položek',
  'categories.recentItems': 'Nedávné položky',
  'categories.viewAllInLibrary': 'Zobrazit vše v knihovně',
  'categories.loadingItems': 'Načítání položek...',
  'categories.noItemsInCategory': 'V této kategorii nejsou žádné položky.',
  'categories.cannotDeleteWithItems': 'Nelze smazat kategorii s {count} položkou/položkami. Nejprve je přesuňte nebo smažte.',
  'categories.confirmDelete': 'Opravdu chcete smazat tuto kategorii?',
  'categories.confirmDeleteDefault': 'Toto je výchozí kategorie. Opravdu ji chcete smazat? Můžete ji kdykoliv znovu vytvořit.',
  'categories.duplicateTitle': 'Duplikovat kategorii',
  'categories.loading': 'Načítání kategorií...',

  // Item Detail
  'itemDetail.photos': 'fotky',
  'itemDetail.noImage': 'Bez obrázku',
  'itemDetail.removeFromFavorites': 'Odebrat z oblíbených',
  'itemDetail.addToFavorites': 'Přidat do oblíbených',
  'itemDetail.added': 'Přidáno',
  'itemDetail.uncategorized': 'Bez kategorie',
  'itemDetail.info': 'Info',
  'itemDetail.files': 'Soubory',
  'itemDetail.comments': 'Komentáře',
  'itemDetail.log': 'Protokol',
  'itemDetail.description': 'Popis',
  'itemDetail.noDescription': 'Popis nebyl zadán.',
  'itemDetail.location': 'Místo',
  'itemDetail.notSet': 'Nenastaveno',
  'itemDetail.purchasePlace': 'Místo nákupu',
  'itemDetail.purchased': 'Zakoupeno',
  'itemDetail.purchasePrice': 'Nákupní cena',
  'itemDetail.condition': 'Stav',
  'itemDetail.tags': 'Štítky',
  'itemDetail.aiPrediction': 'AI odhad hodnoty',
  'itemDetail.highConfidence': 'Vysoká spolehlivost',
  'itemDetail.mediumConfidence': 'Střední spolehlivost',
  'itemDetail.lowConfidence': 'Nízká spolehlivost',
  'itemDetail.basisPurchase': 'Na základě nákupní ceny a stavu',
  'itemDetail.basisCategory': 'Na základě průměru kategorie',
  'itemDetail.basisAI': 'AI odhad',
  'itemDetail.previousPrediction': 'Předchozí odhad',
  'itemDetail.recalculate': 'Přepočítat',
  'itemDetail.analyzing': 'Analyzuji data položky...',
  'itemDetail.tryAgain': 'Zkusit znovu',
  'itemDetail.aiEstimateDesc': 'Získejte AI odhad hodnoty na základě dat položky, kategorie a stavu.',
  'itemDetail.predictValue': 'Odhadnout hodnotu',
  'itemDetail.itemInfo': 'Informace o položce',
  'itemDetail.created': 'Vytvořeno',
  'itemDetail.lastUpdated': 'Naposledy aktualizováno',
  'itemDetail.itemId': 'ID položky',
  'itemDetail.uploadFile': 'Nahrát soubor',
  'itemDetail.uploading': 'Nahrávání...',
  'itemDetail.deselectAll': 'Zrušit výběr',
  'itemDetail.selectAll': 'Vybrat vše',
  'itemDetail.downloadSelected': 'Stáhnout {count} vybraných',
  'itemDetail.noFiles': 'Zatím žádné přiložené soubory',
  'itemDetail.addComment': 'Přidat komentář...',
  'itemDetail.posting': 'Odesílání...',
  'itemDetail.post': 'Odeslat',
  'itemDetail.loadingComments': 'Načítání komentářů...',
  'itemDetail.loadingFiles': 'Načítání souborů...',
  'itemDetail.loadingHistory': 'Načítání historie...',
  'itemDetail.edited': '(upraveno)',
  'itemDetail.editHistory': 'Historie úprav',
  'itemDetail.save': 'Uložit',
  'itemDetail.cancel': 'Zrušit',
  'itemDetail.noComments': 'Zatím žádné komentáře. Buďte první!',
  'itemDetail.noHistory': 'Pro tuto položku zatím není k dispozici žádná historie.',
  'itemDetail.changesTracked': 'Změny budou automaticky sledovány.',
  'itemDetail.itemCreated': 'Položka vytvořena',
  'itemDetail.updatedField': '{field} aktualizováno',
  'itemDetail.itemDeleted': 'Položka smazána',
  'itemDetail.locked': 'Zamčeno',
  'itemDetail.changePin': 'Změnit PIN',
  'itemDetail.removeLock': 'Odstranit zámek',
  'itemDetail.lockItem': 'Zamknout položku',
  'itemDetail.edit': 'Upravit',
  'itemDetail.delete': 'Smazat',
  'itemDetail.conditionNew': 'Nový',
  'itemDetail.conditionLikeNew': 'Jako nový',
  'itemDetail.conditionExcellent': 'Výborný',
  'itemDetail.conditionGood': 'Dobrý',
  'itemDetail.conditionFair': 'Uspokojivý',
  'itemDetail.conditionPoor': 'Špatný',
  'itemDetail.conditionNA': 'Neuveden',
  'itemDetail.viewFullSize': 'Zobrazit v plné velikosti',

  // Lock Modal
  'lockModal.lockItem': 'Zamknout položku',
  'lockModal.unlockItem': 'Odemknout položku',
  'lockModal.verifyPassword': 'Ověřit heslo',
  'lockModal.changeLockPassword': 'Změnit heslo zámku',
  'lockModal.lockDesc': 'Nastavte heslo pro ochranu této položky. Bude skrytá a bude vyžadovat heslo k zobrazení.',
  'lockModal.unlockDesc': 'Zadejte heslo pro trvalé odemknutí této položky.',
  'lockModal.verifyDesc': 'Zadejte heslo pro zobrazení této zamknuté položky.',
  'lockModal.changeDesc': 'Změňte heslo zámku pro tuto položku.',
  'lockModal.removeLock': 'Odstranit zámek',
  'lockModal.removeLockDesc': 'Zámek bude odstraněn bez nutnosti zadání hesla.',
  'lockModal.currentPassword': 'Aktuální heslo',
  'lockModal.password': 'Heslo',
  'lockModal.newPassword': 'Nové heslo',
  'lockModal.confirmPassword': 'Potvrdit heslo',
  'lockModal.confirmNewPassword': 'Potvrdit nové heslo',
  'lockModal.choosePlaceholder': 'Zvolte heslo (min. 4 znaky)',
  'lockModal.enterPlaceholder': 'Zadejte heslo',
  'lockModal.newPlaceholder': 'Nové heslo (min. 4 znaky)',
  'lockModal.reenterPlaceholder': 'Zadejte heslo znovu',
  'lockModal.processing': 'Zpracování...',
  'lockModal.verify': 'Ověřit',
  'lockModal.changePassword': 'Změnit heslo',
  'lockModal.minCharsError': 'Heslo musí mít alespoň 4 znaky',
  'lockModal.mismatchError': 'Hesla se neshodují',
  'lockModal.newMinCharsError': 'Nové heslo musí mít alespoň 4 znaky',
  'lockModal.newMismatchError': 'Nová hesla se neshodují',
  'lockModal.operationFailed': 'Operace se nezdařila. Zkuste to prosím znovu.',

  // Favorites
  'favorites.title': 'Oblíbené',
  'favorites.noFavorites': 'Zatím žádné oblíbené',
  'favorites.noFavoritesDesc': 'Klikněte na ikonu srdce u položek pro přidání do oblíbených',
  'favorites.browseLibrary': 'Procházet knihovnu',
  'favorites.shown': 'zobrazeno',
  'favorites.averageValue': 'Průměrná hodnota',
  'favorites.minPrice': 'Min. cena',
  'favorites.maxPrice': 'Max. cena',
  'favorites.location': 'Umístění',
  'favorites.allLocations': 'Všechna umístění',
  'favorites.tag': 'Štítek',
  'favorites.allTags': 'Všechny štítky',
  'favorites.loading': 'Načítání oblíbených...',
  'favorites.searchPlaceholder': 'Hledat v oblíbených...',
  'favorites.filters': 'Filtry',
  'favorites.toggleView': 'Přepnout zobrazení',
  'favorites.export': 'Exportovat',
  'favorites.exportCSV': 'Exportovat CSV',
  'favorites.items': 'Položky',
  'favorites.totalValue': 'Celková hodnota',
  'favorites.average': 'Průměr',
  'favorites.category': 'Kategorie',
  'favorites.condition': 'Stav',
  'favorites.allCategories': 'Všechny kategorie',
  'favorites.allConditions': 'Všechny stavy',
  'favorites.reset': 'Resetovat',
  'favorites.sortMostRecent': 'Nejnovější',
  'favorites.sortPrice': 'Cena',
  'favorites.sortName': 'Název',
  'favorites.sortCondition': 'Stav',
  'favorites.sortLocation': 'Umístění',
  'favorites.all': 'Vše',
  'favorites.remove': 'Odebrat',
  'favorites.removeFromFavorites': 'Odebrat z oblíbených',
  'favorites.removeConfirm': 'Odebrat {count} položek z oblíbených?',
  'favorites.noMatch': 'Žádné položky neodpovídají filtrům',
  'favorites.noMatchDesc': 'Zkuste upravit filtry nebo vyhledávací dotaz',
  'favorites.addFromCollection': 'Označte položky jako oblíbené ve své sbírce, abyste je zde viděli',

  // Default Category Names
  'categoryName.Electronics': 'Elektronika',
  'categoryName.Furniture': 'Nábytek',
  'categoryName.Books': 'Knihy',
  'categoryName.Clothing': 'Oblečení',
  'categoryName.Jewelry': 'Šperky',
  'categoryName.Sports': 'Sport',
  'categoryName.Art & Collectibles': 'Umění & Sběratelství',
  'categoryName.Tools': 'Nářadí',
  'categoryDesc.Electronics & gadgets': 'Elektronika & gadgety',
  'categoryDesc.Furniture & decor': 'Nábytek & dekorace',
  'categoryDesc.Books & media': 'Knihy & média',
  'categoryDesc.Clothing & accessories': 'Oblečení & doplňky',
  'categoryDesc.Jewelry & watches': 'Šperky & hodinky',
  'categoryDesc.Sports & outdoors': 'Sport & outdoor',
  'categoryDesc.Art & collectibles': 'Umění & sběratelství',
  'categoryDesc.Tools & equipment': 'Nářadí & vybavení',

  // Unit of Measure
  'unit.title': 'Měrná jednotka',
  'unit.type': 'Typ jednotky',
  'unit.subtype': 'Jednotka',
  'unit.quantity': 'Množství',
  'unit.selectType': 'Vyberte typ...',
  'unit.selectUnit': 'Vyberte jednotku...',
  'unit.enterQuantity': 'Zadejte množství...',
  'unit.customUnit': 'Vlastní název jednotky',
  'unit.type.weight': 'Hmotnost',
  'unit.type.length': 'Délka',
  'unit.type.volume': 'Objem',
  'unit.type.area': 'Plocha',
  'unit.type.quantity': 'Množství',
  'unit.type.digital': 'Digitální úložiště',
  'unit.type.time': 'Čas',
  'unit.type.energy': 'Energie',
  'unit.type.temperature': 'Teplota',
  'unit.type.speed': 'Rychlost',
  'unit.type.custom': 'Vlastní',
  'unit.sub.mg': 'Miligramy (mg)',
  'unit.sub.g': 'Gramy (g)',
  'unit.sub.kg': 'Kilogramy (kg)',
  'unit.sub.t': 'Tuny (t)',
  'unit.sub.oz': 'Unce (oz)',
  'unit.sub.lb': 'Libry (lb)',
  'unit.sub.mm': 'Milimetry (mm)',
  'unit.sub.cm': 'Centimetry (cm)',
  'unit.sub.m': 'Metry (m)',
  'unit.sub.km': 'Kilometry (km)',
  'unit.sub.in': 'Palce (in)',
  'unit.sub.ft': 'Stopy (ft)',
  'unit.sub.yd': 'Yardy (yd)',
  'unit.sub.mi': 'Míle (mi)',
  'unit.sub.ml': 'Mililitry (ml)',
  'unit.sub.cl': 'Centilitry (cl)',
  'unit.sub.dl': 'Decilitry (dl)',
  'unit.sub.l': 'Litry (L)',
  'unit.sub.hl': 'Hektolitry (hL)',
  'unit.sub.floz': 'Tekuté unce (fl oz)',
  'unit.sub.cup': 'Hrnky',
  'unit.sub.pt': 'Pinty (pt)',
  'unit.sub.qt': 'Kvarty (qt)',
  'unit.sub.gal': 'Galony (gal)',
  'unit.sub.sqcm': 'Čtverečné cm (cm²)',
  'unit.sub.sqm': 'Čtverečné metry (m²)',
  'unit.sub.ha': 'Hektary (ha)',
  'unit.sub.sqkm': 'Čtverečné km (km²)',
  'unit.sub.sqft': 'Čtverečné stopy (ft²)',
  'unit.sub.sqyd': 'Čtverečné yardy (yd²)',
  'unit.sub.ac': 'Akry (ac)',
  'unit.sub.sqmi': 'Čtverečné míle (mi²)',
  'unit.sub.pcs': 'Kusy (ks)',
  'unit.sub.pairs': 'Páry',
  'unit.sub.sets': 'Sady',
  'unit.sub.packs': 'Balení',
  'unit.sub.boxes': 'Krabice',
  'unit.sub.rolls': 'Role',
  'unit.sub.sheets': 'Listy',
  'unit.sub.bottles': 'Lahve',
  'unit.sub.cans': 'Plechovky',
  'unit.sub.bags': 'Tašky',
  'unit.sub.dozens': 'Tucty (tuc)',
  'unit.sub.b': 'Bajty (B)',
  'unit.sub.kb': 'Kilobajty (KB)',
  'unit.sub.mb': 'Megabajty (MB)',
  'unit.sub.gb': 'Gigabajty (GB)',
  'unit.sub.tb': 'Terabajty (TB)',
  'unit.sub.sec': 'Sekundy (s)',
  'unit.sub.min': 'Minuty (min)',
  'unit.sub.hr': 'Hodiny (h)',
  'unit.sub.day': 'Dny (d)',
  'unit.sub.wk': 'Týdny (týd)',
  'unit.sub.mo': 'Měsíce (měs)',
  'unit.sub.yr': 'Roky (r)',
  'unit.sub.wh': 'Watthodiny (Wh)',
  'unit.sub.kwh': 'Kilowatthodiny (kWh)',
  'unit.sub.cal': 'Kalorie (cal)',
  'unit.sub.kcal': 'Kilokalorie (kcal)',
  'unit.sub.j': 'Jouly (J)',
  'unit.sub.kj': 'Kilojouly (kJ)',
  'unit.sub.c': 'Celsius (°C)',
  'unit.sub.f': 'Fahrenheit (°F)',
  'unit.sub.k': 'Kelvin (K)',
  'unit.sub.kmh': 'km/h',
  'unit.sub.mph': 'mph',
  'unit.sub.ms': 'm/s',
  'unit.sub.kn': 'Uzly (kn)',

  // Trash
  'trash.title': 'Koš',
  'trash.noItems': 'Koš je prázdný',
  'trash.noItemsDesc': 'Smazané položky se zobrazí zde',
  'trash.emptyTrash': 'Vyprázdnit koš',
  'trash.restoreAll': 'Obnovit vše',
  'trash.deleted': 'Smazáno',
  'trash.expiresIn': 'Vyprší za',
  'trash.days': 'dní',
  'trash.permanentDelete': 'Opravdu? Tuto akci nelze vrátit zpět.',
  'trash.loading': 'Načítání koše...',
  'trash.items': '{count} položek',
  'trash.warning': 'Položky v koši budou po 30 dnech trvale smazány.',
  'trash.emptyTitle': 'Koš je prázdný',
  'trash.emptyDesc': 'Smazané položky se zobrazí zde. Položky jsou trvale odstraněny po 30 dnech.',
  'trash.confirmDelete': 'Opravdu chcete trvale smazat tuto položku? Toto nelze vrátit zpět.',
  'trash.confirmEmpty': 'Opravdu chcete trvale smazat VŠECHNY položky v koši? Toto nelze vrátit zpět.',
  'trash.unknown': 'Neznámé',
  'trash.daysAgo': 'před {count} dny',
  'trash.hoursAgo': 'před {count} hodinami',
  'trash.justNow': 'Právě teď',
  'trash.restore': 'Obnovit',
  'trash.delete': 'Smazat',

  // Knihovna
  'library.title': 'Knihovna',
  'library.noItemsDesc': 'Začněte přidáním první položky do knihovny',
  'library.searchPlaceholder': 'Hledat položky...',
  'library.addItem': 'Přidat položku',
  'library.allCategories': 'Všechny kategorie',
  'library.newestFirst': 'Nejnovější',
  'library.oldestFirst': 'Nejstarší',
  'library.nameAZ': 'Název (A-Z)',
  'library.nameZA': 'Název (Z-A)',
  'library.highestValue': 'Nejvyšší hodnota',
  'library.lowestValue': 'Nejnižší hodnota',
  'library.advancedFilters': 'Pokročilé filtry',
  'library.gridView': 'Mřížka',
  'library.listView': 'Seznam',
  'library.edit': 'Upravit',
  'library.move': 'Přesunout',
  'library.delete': 'Smazat',
  'library.value': 'Hodnota',
  'library.original': 'Originál',
  'library.aiValue': 'AI Hodnota',
  'library.noItems': 'Žádné položky nenalezeny',
  'library.noItemsSearch': 'Zkuste upravit hledání nebo filtry',
  'library.noItemsStart': 'Začněte přidáním první položky',
  'library.addFirstItem': '+ Přidat první položku',
  'library.moveItem': 'Přesunout položku',
  'library.moving': 'Přesouvá se:',
  'library.currentCategory': 'Aktuální kategorie:',
  'library.uncategorized': 'Nezařazeno',
  'library.selectCategory': 'Vyberte novou kategorii:',
  'library.loading': 'Načítání knihovny...',
  'library.confirmDelete': 'Opravdu chcete smazat tuto položku?',
  'library.moveFailed': 'Přesun položky se nezdařil',
  'library.lockedItem': 'Tato položka je zamčená',

  // Dashboard
  'dashboard.analytics': 'Analytika',
  'dashboard.itemsTracked': 'sledovaných položek',
  'dashboard.categoriesLabel': 'kategorií',
  'dashboard.customize': 'Přizpůsobit',
  'dashboard.widgets': 'Widgety',
  'dashboard.save': 'Uložit',
  'dashboard.loading': 'Načítání analytiky…',
  'dashboard.addWidget': 'Přidat widget',
  'dashboard.totalItems': 'Celkem položek',
  'dashboard.portfolioValue': 'Hodnota portfolia',
  'dashboard.favorites': 'Oblíbené',
  'dashboard.avgItemValue': 'Prům. hodnota položky',
  'dashboard.newThisPeriod': 'nových v tomto období',
  'dashboard.vsLastPeriod': 'oproti minulému období',
  'dashboard.marked': 'označeno',
  'dashboard.perItem': 'na položku',
  'dashboard.day': 'Den',
  'dashboard.week': 'Týden',
  'dashboard.month': 'Měsíc',
  'dashboard.quarter': 'Čtvrtletí',
  'dashboard.year': 'Rok',
  'dashboard.valueTrend': 'Vývoj hodnoty',
  'dashboard.itemsAdded': 'Přidané položky',
  'dashboard.conditionBreakdown': 'Rozdělení podle stavu',
  'dashboard.categoryDistribution': 'Rozdělení podle kategorií',
  'dashboard.priceDistribution': 'Rozdělení podle cen',
  'dashboard.mostValuable': 'Nejhodnotnější položky',
  'dashboard.byLocation': 'Položky podle umístění',
  'dashboard.cumulativeValue': 'Kumulativní hodnota',
  'dashboard.favoritesOverTime': 'Oblíbené v čase',
  'dashboard.topTags': 'Top štítky',
  'dashboard.purchaseSources': 'Zdroje nákupů',
  'dashboard.avgByCategory': 'Prům. hodnota podle kategorií',
  'dashboard.valueTrendDesc': 'Celková přidaná hodnota za období',
  'dashboard.itemsAddedDesc': 'Počet nových položek za období',
  'dashboard.conditionDesc': 'Rozdělení podle stavu položek',
  'dashboard.categoryDesc': 'Položky seskupené podle kategorií',
  'dashboard.priceDistDesc': 'Jak jsou hodnoty položek rozloženy přes cenové rozsahy',
  'dashboard.mostValuableDesc': 'Vaše nejhodnotnější položky',
  'dashboard.byLocationDesc': 'Položky seskupené podle umístění',
  'dashboard.cumulativeDesc': 'Průběžný součet hodnoty portfolia v čase',
  'dashboard.favTrendDesc': 'Oblíbené položky přidané za období',
  'dashboard.topTagsDesc': 'Nejčastěji používané štítky',
  'dashboard.purchaseDesc': 'Kde byly položky zakoupeny',
  'dashboard.avgCatDesc': 'Průměrná hodnota položky podle kategorií',
  'dashboard.chooseChartType': 'Zvolte typ grafu',
  'dashboard.configureData': 'Konfigurovat data',
  'dashboard.previewCreate': 'Náhled a vytvoření',
  'dashboard.stepType': '1. Typ',
  'dashboard.stepData': '2. Data',
  'dashboard.stepPreview': '3. Náhled',
  'dashboard.barChart': 'Sloupcový graf',
  'dashboard.pieChart': 'Koláčový graf',
  'dashboard.lineChart': 'Čárový graf',
  'dashboard.metricCard': 'Metrika',
  'dashboard.barChartDesc': 'Porovnání hodnot napříč kategoriemi',
  'dashboard.pieChartDesc': 'Zobrazení poměrů celku',
  'dashboard.lineChartDesc': 'Sledování trendů v čase',
  'dashboard.metricCardDesc': 'Zobrazení jedné klíčové metriky',
  'dashboard.chartTitle': 'Název grafu *',
  'dashboard.description': 'Popis',
  'dashboard.value': 'Hodnota',
  'dashboard.dataPoints': 'Datové body',
  'dashboard.addDataPoint': 'Přidat datový bod',
  'dashboard.back': 'Zpět',
  'dashboard.nextConfigure': 'Další: Konfigurovat data',
  'dashboard.nextPreview': 'Další: Náhled',
  'dashboard.createChart': 'Vytvořit graf',
  'dashboard.dashboardWidgets': 'Widgety dashboardu',
  'dashboard.widgetHint': 'Přepnout viditelnost · přetáhněte pro změnu pořadí',

  // Předplatné navíc
  'subscription.noWorkspaceError': 'Není vybrán žádný pracovní prostor. Nejprve vyberte pracovní prostor.',
  'subscription.checkoutError': 'Nepodařilo se vytvořit platební relaci. Zkuste to prosím znovu.',
  'subscription.redirecting': 'Přesměrování na Stripe...',

  // Nastavení oznámení
  'notifSettings.loading': 'Načítání nastavení oznámení...',
  'notifSettings.title': 'Nastavení oznámení',
  'notifSettings.subtitle': 'Nastavte kdy a jak dostáváte oznámení',
  'notifSettings.disableAll': 'Zakázat vše',
  'notifSettings.enableAll': 'Povolit vše',
  'notifSettings.saving': 'Ukládání...',
  'notifSettings.saveChanges': 'Uložit změny',
  'notifSettings.saved': 'Nastavení oznámení uloženo!',
  'notifSettings.saveFailed': 'Uložení nastavení se nezdařilo',
  'notifSettings.typesTitle': 'Typy oznámení',
  'notifSettings.typesDesc': 'Vyberte, která oznámení chcete dostávat.',
  'notifSettings.emailToggleTitle': 'Odeslat e-mail pro toto oznámení',
  'notifSettings.itemUpdates': 'Aktualizace položek',
  'notifSettings.itemUpdatesDesc': 'Oznámení při aktualizaci sledovaných položek',
  'notifSettings.comments': 'Komentáře',
  'notifSettings.commentsDesc': 'Oznámení když někdo komentuje vaše položky',
  'notifSettings.mentions': 'Zmínky',
  'notifSettings.mentionsDesc': 'Oznámení když vás někdo zmíní v komentáři',
  'notifSettings.duplicateDetection': 'Detekce duplikátů',
  'notifSettings.duplicateDetectionDesc': 'Oznámení když AI detekuje možné duplikáty',
  'notifSettings.valuePredictions': 'Predikce hodnoty',
  'notifSettings.valuePredictionsDesc': 'Oznámení když AI předpovídá změny hodnoty',
  'notifSettings.securityAlerts': 'Bezpečnostní upozornění',
  'notifSettings.securityAlertsDesc': 'Důležitá bezpečnostní oznámení (pokusy o přihlášení, změny hesla)',
  'notifSettings.emailPrefs': 'E-mailové předvolby',
  'notifSettings.emailPrefsDesc': 'Spravovat e-mailová oznámení',
  'notifSettings.weeklyDigest': 'Týdenní shrnutí',
  'notifSettings.weeklyDigestDesc': 'Týdenní shrnutí aktivity vašich pracovních prostorů',
  'notifSettings.marketingEmails': 'Marketingové e-maily',
  'notifSettings.marketingEmailsDesc': 'Aktualizace produktů, tipy a propagační obsah',
  'notifSettings.channels': 'Doručovací kanály',
  'notifSettings.channelsDesc': 'Zvolte jak chcete dostávat oznámení',
  'notifSettings.email': 'E-mail',
  'notifSettings.emailDesc': 'Dostávat oznámení e-mailem',
  'notifSettings.push': 'Push oznámení',
  'notifSettings.pushDesc': 'Dostávat push oznámení na zařízeních',
  'notifSettings.inApp': 'Oznámení v aplikaci',
  'notifSettings.inAppDesc': 'Zobrazit oznámení v aplikaci',
  'notifSettings.quietHours': 'Klidové hodiny',
  'notifSettings.quietHoursDesc': 'Pozastavit oznámení v určitých hodinách',
  'notifSettings.enableQuietHours': 'Povolit klidové hodiny',
  'notifSettings.quietHoursSilenced': 'Oznámení budou během určené doby ztlumena',
  'notifSettings.startTime': 'Čas začátku',
  'notifSettings.endTime': 'Čas konce',
  'notifSettings.timeTo': 'do',
  'notifSettings.unsavedChanges': 'Máte neuložené změny',

  // Time
  'time.today': 'Dnes',
  'time.yesterday': 'Včera',
  'time.daysAgo': 'dní zpět',
  'time.justNow': 'právě teď',
  'time.minutesAgo': 'minut zpět',
  'time.hoursAgo': 'hodin zpět',

  // Settings
  'settings.title': 'Nastavení',
  'settings.subtitle': 'Spravujte nastavení účtu a zabezpečení',
  'settings.general': 'Obecné',
  'settings.appearance': 'Vzhled',
  'settings.security': 'Zabezpečení',
  'settings.accessibility': 'Přístupnost',
  'settings.privacy': 'Soukromí',
  'settings.theme': 'Motiv',
  'settings.themeDesc': 'Přepnout mezi světlým a tmavým režimem',
  'settings.language': 'Jazyk',
  'settings.languageDesc': 'Vyberte si preferovaný jazyk',
  'settings.darkMode': 'Tmavý režim',
  'settings.lightMode': 'Světlý režim',
  'settings.darkTheme': 'Tmavý',
  'settings.lightTheme': 'Světlý',
  'settings.profile': 'Informace o profilu',
  'settings.profileDesc': 'Aktualizujte své osobní údaje',
  'settings.saved': 'Nastavení uloženo!',
  'settings.saveFailed': 'Uložení se nezdařilo',

  // Accounting & Assets
  'accounting.title': 'Účetnictví majetku',
  'accounting.subtitle': 'Správa dlouhodobého majetku, odpisů a alokací',
  'accounting.assetRegister': 'Evidence majetku',
  'accounting.allItems': 'Všechny položky',
  'accounting.transactions': 'Transakce',
  'accounting.createAsset': 'Vytvořit majetek',
  'accounting.assetDetails': 'Detaily majetku',
  'accounting.trackedAssets': 'Sledovaný majetek',
  'accounting.totalBookValue': 'Celková účetní hodnota',
  'accounting.accumulatedDepreciation': 'Kumulované odpisy',
  'accounting.acquisitionCost': 'Pořizovací cena',
  'accounting.assetNumber': 'Číslo majetku',
  'accounting.assetClass': 'Třída majetku',
  'accounting.subClass': 'Podtřída',
  'accounting.depreciationMethod': 'Metoda odpisování',
  'accounting.usefulLifeMonths': 'Doba použitelnosti (měsíce)',
  'accounting.salvageValue': 'Zůstatková hodnota',
  'accounting.acquisitionDate': 'Datum pořízení',
  'accounting.bookValue': 'Účetní hodnota',
  'accounting.fairMarketValue': 'Tržní hodnota',
  'accounting.status': 'Stav',
  'accounting.active': 'Aktivní',
  'accounting.disposed': 'Vyřazeno',
  'accounting.writtenOff': 'Odepsáno',
  'accounting.heldForSale': 'K prodeji',
  'accounting.impaired': 'Znehodnoceno',
  'accounting.transferred': 'Převedeno',
  'accounting.straightLine': 'Rovnoměrné',
  'accounting.decliningBalance': 'Zrychlené',
  'accounting.doubleDeclining': 'Dvojitě zrychlené',
  'accounting.sumOfYears': 'Součet čísel',
  'accounting.unitsOfProduction': 'Výkonové',
  'accounting.noDepreciation': 'Bez odpisů',
  'accounting.classBuildings': 'Budovy',
  'accounting.classMachinery': 'Stroje',
  'accounting.classEquipment': 'Vybavení',
  'accounting.classVehicles': 'Vozidla',
  'accounting.classFurniture': 'Nábytek',
  'accounting.classComputers': 'Počítače',
  'accounting.classSoftware': 'Software',
  'accounting.classCollectibles': 'Sběratelství',
  'accounting.classArt': 'Umění',
  'accounting.classJewelry': 'Šperky',
  'accounting.classRealEstate': 'Nemovitosti',
  'accounting.classInvestments': 'Investice',
  'accounting.classOther': 'Ostatní',
  'accounting.monthlyDepreciation': 'Měsíční odpis',
  'accounting.yearlyDepreciation': 'Roční odpis',
  'accounting.remainingLife': 'Zbývající doba',
  'accounting.percentDepreciated': 'Procento odepsáno',
  'accounting.selectItem': 'Vyberte položku',
  'accounting.selectMethod': 'Vyberte metodu odpisování',
  'accounting.selectClass': 'Vyberte třídu majetku',
  'accounting.selectStatus': 'Vyberte stav',
  'accounting.recordImpairment': 'Zaznamenat znehodnocení',
  'accounting.recordRevaluation': 'Zaznamenat přecenění',
  'accounting.disposeAsset': 'Vyřadit majetek',
  'accounting.impairmentAmount': 'Částka znehodnocení',
  'accounting.revaluedAmount': 'Přeceněná částka',
  'accounting.disposalDate': 'Datum vyřazení',
  'accounting.disposalProceeds': 'Výnos z prodeje',
  'accounting.notes': 'Poznámky',
  'accounting.depreciationSchedule': 'Odpisový plán',
  'accounting.transactionHistory': 'Historie transakcí',
  'accounting.noAssets': 'Žádný majetek nenalezen',
  'accounting.noItems': 'Žádné položky nenalezeny',
  'accounting.noTransactions': 'Žádné transakce nenalezeny',
  'accounting.searchAssets': 'Hledat majetek...',
  'accounting.searchItems': 'Hledat položky...',
  'accounting.filterByStatus': 'Filtrovat podle stavu',
  'accounting.filterByMethod': 'Filtrovat podle metody',
  'accounting.filterByClass': 'Filtrovat podle třídy',
  'accounting.filterByCategory': 'Filtrovat podle kategorie',
  'accounting.showTracked': 'Pouze sledované',
  'accounting.showUntracked': 'Pouze nesledované',
  'accounting.sortBy': 'Řadit podle',
  'accounting.sortByName': 'Název',
  'accounting.sortByCost': 'Cena',
  'accounting.sortByBook': 'Účetní hodnota',
  'accounting.sortByDepreciation': 'Odpisy',
  'accounting.sortByDate': 'Datum',
  'accounting.page': 'Strana',
  'accounting.of': 'z',
  'accounting.showing': 'Zobrazeno',
  'accounting.assets': 'majetek',
  'accounting.items': 'položky',
  'accounting.months': 'měsíce',
  'accounting.years': 'roky',
  'accounting.assetCreated': 'Majetek úspěšně vytvořen',
  'accounting.assetUpdated': 'Majetek úspěšně aktualizován',
  'accounting.assetDisposed': 'Majetek úspěšně vyřazen',
  'accounting.impairmentRecorded': 'Znehodnocení úspěšně zaznamenáno',
  'accounting.revaluationRecorded': 'Přecenění úspěšně zaznamenáno',
  'accounting.costAllocation': 'Přiřazení nákladů',
  'accounting.allocations': 'Přiřazení',
  'accounting.department': 'Oddělení',
  'accounting.project': 'Projekt',
  'accounting.costCenter': 'Nákladové středisko',
  'accounting.allocatedTo': 'Přiřazeno k',
  'accounting.allocationPercent': 'Přiřazení %',
  'accounting.addAllocation': 'Přidat přiřazení',
  'accounting.removeAllocation': 'Odebrat přiřazení',
  'accounting.totalAllocation': 'Celkové přiřazení',
  'accounting.mustBe100': 'Celkové přiřazení musí být 100%',
  'accounting.selectDepartment': 'Vyberte oddělení',
  'accounting.selectProject': 'Vyberte projekt',
  'accounting.selectCostCenter': 'Vyberte nákladové středisko',
  'accounting.noDepartments': 'Žádná oddělení nenalezena',
  'accounting.noProjects': 'Žádné projekty nenalezeny',
  'accounting.noCostCenters': 'Žádná nákladová střediska nenalezena',
  'accounting.createDepartment': 'Vytvořit oddělení',
  'accounting.createProject': 'Vytvořit projekt',
  'accounting.createCostCenter': 'Vytvořit nákladové středisko',
  'accounting.departmentName': 'Název oddělení',
  'accounting.projectName': 'Název projektu',
  'accounting.costCenterName': 'Název střediska',
  'accounting.departmentCode': 'Kód oddělení',
  'accounting.projectCode': 'Kód projektu',
  'accounting.costCenterCode': 'Kód střediska',
  'accounting.manager': 'Manažer',
  'accounting.budget': 'Rozpočet',
  'accounting.startDate': 'Datum zahájení',
  'accounting.endDate': 'Datum ukončení',
  'accounting.allocationReport': 'Report přiřazení',
  'accounting.byDepartment': 'Podle oddělení',
  'accounting.byProject': 'Podle projektu',
  'accounting.byCostCenter': 'Podle střediska',
  'accounting.exportReport': 'Exportovat report',
  'accounting.depreciationReport': 'Report odpisů',
  'accounting.assetValuation': 'Ocenění majetku',
  'accounting.gainLoss': 'Zisk/Ztráta',

  // Accounting KPIs
  'accounting.kpi.trackedAssets': 'Sledované prostředky',
  'accounting.kpi.itemsWithAccounting': 'Položky s účetnictvím',
  'accounting.kpi.totalBookValue': 'Celková knihovní hodnota',
  'accounting.kpi.currentNetValue': 'Aktuální čistá hodnota',
  'accounting.kpi.accDepreciation': 'Akumulované odpisy',
  'accounting.kpi.ofCost': 'z nákladů',
  'accounting.kpi.acquisitionCost': 'Pořizovací cena',
  'accounting.kpi.originalInvestment': 'Původní investice',

  // Accounting Tabs
  'accounting.tabs.register': 'Register majetku',
  'accounting.tabs.items': 'Všechny položky',
  'accounting.tabs.transactions': 'Transakce',
  'accounting.allStatus': 'Všechny stavy',
  'accounting.allMethods': 'Všechny metody',
  'accounting.allClasses': 'Všechny třídy',
  'accounting.allCategories': 'Všechny kategorie',
  'accounting.allConditions': 'Všechny stavy',
  'accounting.allItemsFilter': 'Všechny položky',
  'accounting.trackedOnly': 'Pouze sledované',
  'accounting.untrackedOnly': 'Pouze nesledované',
  'accounting.allTypes': 'Všechny typy',
  'accounting.itemCol': 'Položka',
  'accounting.acquisitionCol': 'Pořízení',
  'accounting.bookValueCol': 'Účetní hodnota',
  'accounting.depreciationCol': 'Odpisy',
  'accounting.methodCol': 'Metoda',
  'accounting.statusCol': 'Stav',
  'accounting.actionsCol': 'Akce',
  'accounting.categoryCol': 'Kategorie',
  'accounting.purchasePriceCol': 'Nákupní cena',
  'accounting.dateCol': 'Datum',
  'accounting.trackingCol': 'Sledování',
  'accounting.actionCol': 'Akce',
  'accounting.noTrackedYet': 'Zatím žádná sledovaná aktiva',
  'accounting.noAssetsMatch': 'Žádná aktiva neodpovídají filtrům',
  'accounting.browseItems': 'Procházet položky',
  'accounting.clearFilters': 'Vymazat filtry',
  'accounting.tracked': 'Sledováno',
  'accounting.notTracked': 'Nesledováno',
  'accounting.viewDetail': 'Zobrazit',
  'accounting.trackItem': 'Sledovat',
  'accounting.valueInfo': 'Informace o hodnotě',
  'accounting.depInfo': 'Odpisy',
  'accounting.accDep': 'Kum. odpisy',
  'accounting.monthly': 'Měsíčně',
  'accounting.remainingMonths': 'Zbývající',
  'accounting.depPercent': 'Odepsáno',
  'accounting.recentTx': 'Nedávné transakce',
  'accounting.depreciate': 'Odepsat',
  'accounting.impairmentAction': 'Znehodnocení',
  'accounting.revalueAction': 'Přecenění',
  'accounting.disposeAction': 'Vyřadit',
  'accounting.trackAsset': 'Sledovat aktivum',
  'accounting.cancel': 'Zrušit',
  'accounting.creating': 'Vytváření…',
  'accounting.create': 'Vytvořit aktivum',
  'accounting.confirmDisposal': 'Potvrdit vyřazení',
  'accounting.searchAssetsPlaceholder': 'Hledat aktiva…',
  'accounting.searchItemsPlaceholder': 'Hledat položky…',
  'accounting.searchTxPlaceholder': 'Hledat transakce…',
  'accounting.noItemsInLib': 'Žádné položky nenalezeny',
  'accounting.noItemsMatch': 'Žádné položky neodpovídají filtrům',
  'accounting.addItemsFirst': 'Nejprve přidejte položky do knihovny.',
  'accounting.noTxYet': 'Zatím žádné transakce',
  'accounting.noTxMatch': 'Žádné transakce neodpovídají filtrům',
  'accounting.startDepreciation': 'Začněte sledovat aktiva a zpracovávat odpisy.',
  'accounting.adjustTxFilter': 'Zkuste upravit vyhledávání nebo filtr.',
  'accounting.adjustFilters': 'Zkuste upravit vyhledávání nebo filtry.',
  'accounting.trackedAssetCount': 'Sledovaná aktiva ({count})',
  'accounting.filteredAssets': '{filtered} z {total} aktiv',
  'accounting.yourItemsCount': 'Vaše položky ({count})',
  'accounting.filteredItems': '{filtered} z {total} položek',
  'accounting.txCount': 'Transakce ({count})',
  'accounting.filteredTx': '{filtered} z {total} transakcí',
  'accounting.unnamed': 'Nepojmenováno',
  'accounting.unclassified': 'Neklasifikováno',
  'accounting.unknown': 'Neznámé',
  'accounting.txDateCol': 'Datum',
  'accounting.txAssetCol': 'Aktivum',
  'accounting.txTypeCol': 'Typ',
  'accounting.txDescCol': 'Popis',
  'accounting.txAmountCol': 'Částka',
  'accounting.bookValueAfterCol': 'Účetní hodnota po',
  'accounting.startTracking': 'Začněte přidáváním položek do registru aktiv.',
  'accounting.tipAutoFill': 'Tip: Použijte záložku „Všechny položky" pro snazší sledování.',
  'accounting.itemIdLabel': 'ID položky *',
  'accounting.pasteItemId': 'Vložte ID položky',
  'accounting.selectClassOption': 'Vyberte třídu…',
  'accounting.impairmentDesc': 'Znehodnocení snižuje účetní hodnotu, když zpětně získatelná částka klesne pod účetní hodnotu.',
  'accounting.impairmentAmountLabel': 'Částka znehodnocení *',
  'accounting.reasonLabel': 'Důvod',
  'accounting.reasonPlaceholder': 'Důvod znehodnocení',
  'accounting.effectiveDateLabel': 'Datum účinnosti',
  'accounting.revalDesc': 'Upraví hodnotu aktiva na základě profesionálního ocenění nebo tržního posouzení.',
  'accounting.newAppraisedValue': 'Nová odhadní hodnota *',
  'accounting.appraiserName': 'Jméno odhadce',
  'accounting.valuationMethodLabel': 'Metoda ocenění',
  'accounting.valuationMethodPlaceholder': 'např. Tržní srovnání',
  'accounting.notesLabel': 'Poznámky',
  'accounting.optionalNotes': 'Volitelné poznámky',
  'accounting.disposeDesc': 'Vyřazení odstraní aktivum z aktivního sledování.',
  'accounting.disposalAmountLabel': 'Částka prodeje / vyřazení',
  'accounting.reasonSelect': 'Důvod',
  'accounting.sold': 'Prodáno',
  'accounting.donated': 'Darováno',
  'accounting.scrapped': 'Sešrotováno',
  'accounting.stolenLost': 'Ukradeno / Ztraceno',
  'accounting.tradedIn': 'Vyměněno',
  'accounting.otherReason': 'Jiné',
  'accounting.optional': 'Volitelné',
  'accounting.expectedValue': 'Očekávaná hodnota na konci doby použitelnosti',

  // Workspaces
  'workspaces.title': 'Pracovní prostory',
  'workspaces.myWorkspaces': 'Moje pracovní prostory',
  'workspaces.create': 'Vytvořit pracovní prostor',
  'workspaces.switchTo': 'Přepnout na',
  'workspaces.settings': 'Nastavení pracovního prostoru',
  'workspaces.members': 'Členové',
  'workspaces.personal': 'Osobní',
  'workspaces.organization': 'Organizace',
  'workspaces.name': 'Název pracovního prostoru',
  'workspaces.description': 'Popis',
  'workspaces.type': 'Typ',
  'workspaces.items': 'položky',
  'workspaces.totalItems': 'Celkový počet položek',
  'workspaces.active': 'Aktivní',
  'workspaces.viewAll': 'Zobrazit všechny pracovní prostory',
  'workspaces.noWorkspaces': 'Zatím žádné pracovní prostory',
  'workspaces.createFirst': 'Vytvořte svůj první pracovní prostor',
  'workspaces.searchPlaceholder': 'Hledat pracovní prostory...',
  'workspaces.sortByRecent': 'Nedávné',
  'workspaces.sortByName': 'Název',
  'workspaces.sortByItems': 'Položky',
  'workspaces.sortByMembers': 'Členové',
  'workspaces.hub': 'Centrum pracovních prostorů',
  'workspaces.manageSubtitle': 'Spravujte, organizujte a spolupracujte napříč všemi pracovními prostory',
  'workspaces.new': 'Nový pracovní prostor',
  'workspaces.organizations': 'Organizace',
  'workspaces.loading': 'Načítání pracovních prostorů...',
  'workspaces.welcome': 'Vítejte v centru pracovních prostorů',
  'workspaces.welcomeSubtitle': 'Vytvořte svůj první pracovní prostor.',
  'workspaces.personalDesc': 'Soukromý pracovní prostor jen pro vás',
  'workspaces.orgDesc': 'Spolupracujte se svým týmem',
  'workspaces.enterName': 'Zadejte název pracovního prostoru...',
  'workspaces.creating': 'Vytváření...',
  'workspaces.getStarted': 'Začít',
  'workspaces.activeWorkspace': 'Aktivní pracovní prostor',
  'workspaces.switchToWorkspace': 'Přepnout na pracovní prostor',
  'workspaces.openDashboard': 'Otevřít dashboard',
  'workspaces.createNew': 'Vytvořit nový pracovní prostor',
  'workspaces.personalOrOrg': 'Osobní nebo organizace',
  'workspaces.noFound': 'Žádné pracovní prostory nenalezeny',
  'workspaces.clearSearch': 'Vymazat hledání',
  'workspaces.edit': 'Upravit pracovní prostor',
  'workspaces.delete': 'Smazat pracovní prostor',
  'workspaces.cannotBeUndone': 'Tuto akci nelze vrátit zpět',
  'workspaces.deletePermanently': 'Smazat trvale',
  'workspaces.cancel': 'Zrušit',
  'workspaces.save': 'Uložit změny',
  'workspaces.continue': 'Pokračovat',
  'workspaces.back': 'Zpět',
  'workspaces.typeStep': 'Typ',
  'workspaces.detailsStep': 'Podrobnosti',
  'workspaces.reviewStep': 'Kontrola',
  'workspaces.chooseType': 'Vyberte typ pracovního prostoru',
  'workspaces.personalWorkspace': 'Osobní pracovní prostor',
  'workspaces.configurePersonal': 'Nastavte svůj osobní pracovní prostor',
  'workspaces.configureOrg': 'Nastavte svou organizaci',
  'workspaces.reviewAndCreate': 'Zkontrolujte a vytvořte',
  'workspaces.whatYouGet': 'Co získáte:',
  'workspaces.nameRequired': 'Název pracovního prostoru *',
  'workspaces.descLabel': 'Popis',
  'workspaces.whatFor': 'K čemu je tento pracovní prostor?',
  'workspaces.privateSecure': 'Soukromé a bezpečné',
  'workspaces.fullItemMgmt': 'Kompletní správa položek',
  'workspaces.personalAnalytics': 'Osobní analytika',
  'workspaces.teamCollaboration': 'Týmová spolupráce',
  'workspaces.roleBasedAccess': 'Řízení přístupu na základě rolí',
  'workspaces.orgDashboard': 'Dashboard organizace',
  'workspaces.inviteMembers': 'Pozvat členy',
  'workspaces.unlimitedItems': 'Neomezené položky',
  'workspaces.private': 'Soukromé',
  'workspaces.itemMgmtCategories': 'Správa položek s kategoriemi',
  'workspaces.aiCategorization': 'AI kategorizace',
  'workspaces.analyticsDashboard': 'Analytický dashboard',
  'workspaces.teamInvitations': 'Pozvánky členů týmu',
  'workspaces.rbac': 'Řízení přístupu na základě rolí',
  'workspaces.auditTrail': 'Auditní stopa organizace',
  'workspaces.untitled': 'Bez názvu',
  'workspaces.createWorkspace': 'Vytvořit pracovní prostor',
  'workspaces.switchThis': 'Přepnout sem',
  'workspaces.viewOrg': 'Zobrazit org.',
  'workspaces.convertToOrg': 'Převést na org.',
  'workspaces.star': 'Oblíbené',
  'workspaces.unstar': 'Odebrat z oblíbených',
  'workspaces.grid': 'Mřížka',
  'workspaces.list': 'Seznam',

  // Workspace Settings
  'wsSettings.loading': 'Načítání pracovního prostoru...',
  'wsSettings.notFound': 'Pracovní prostor nenalezen',
  'wsSettings.backToWorkspaces': 'Zpět na pracovní prostory',
  'wsSettings.savedSuccess': 'Nastavení úspěšně uloženo',
  'wsSettings.inviteSent': 'Pozvánka úspěšně odeslána',
  'wsSettings.roleUpdated': 'Role úspěšně aktualizována',
  'wsSettings.confirmRemoveMember': 'Opravdu chcete odebrat tohoto člena?',
  'wsSettings.memberRemoved': 'Člen úspěšně odebrán',
  'wsSettings.inviteRevoked': 'Pozvánka zrušena',
  'wsSettings.ownershipTransferred': 'Vlastnictví úspěšně převedeno',
  'wsSettings.typeNameToConfirm': 'Zadejte název pracovního prostoru pro potvrzení',
  'wsSettings.convertedSuccess': 'Pracovní prostor převeden na organizaci!',
  'wsSettings.organization': 'Organizace',
  'wsSettings.personalWorkspace': 'Osobní pracovní prostor',
  'wsSettings.owner': 'Vlastník',
  'wsSettings.general': 'Obecné',
  'wsSettings.members': 'Členové',
  'wsSettings.convertTab': 'Konverze',
  'wsSettings.dangerZone': 'Nebezpečná zóna',
  'wsSettings.generalSettings': 'Obecná nastavení',
  'wsSettings.workspaceName': 'Název pracovního prostoru',
  'wsSettings.enterName': 'Zadejte název',
  'wsSettings.description': 'Popis',
  'wsSettings.descriptionPlaceholder': 'Popište tento pracovní prostor...',
  'wsSettings.workspaceId': 'ID pracovního prostoru',
  'wsSettings.created': 'Vytvořeno',
  'wsSettings.items': 'Položky',
  'wsSettings.categories': 'Kategorie',
  'wsSettings.saving': 'Ukládání...',
  'wsSettings.saveChanges': 'Uložit změny',
  'wsSettings.inviteMember': 'Pozvat člena',
  'wsSettings.emailPlaceholder': 'E-mailová adresa',
  'wsSettings.sendInvite': 'Odeslat pozvánku',
  'wsSettings.pendingInvitations': 'Čekající pozvánky',
  'wsSettings.revoke': 'Zrušit',
  'wsSettings.currentMembers': 'Aktuální členové',
  'wsSettings.loadingMembers': 'Načítání členů...',
  'wsSettings.remove': 'Odebrat',
  'wsSettings.convertToOrg': 'Převést na organizaci',
  'wsSettings.upgradeTitle': 'Povýšit na organizaci',
  'wsSettings.upgradeDesc': 'Odemkněte funkce pro spolupráci převedením osobního prostoru na organizaci.',
  'wsSettings.featInvite': 'Pozvat členy',
  'wsSettings.featInviteDesc': 'Přidejte členy týmu a spolupracujte',
  'wsSettings.featRBAC': 'Přístup podle rolí',
  'wsSettings.featRBACDesc': 'Přiřaďte role a spravujte oprávnění',
  'wsSettings.featTeams': 'Týmy a skupiny',
  'wsSettings.featTeamsDesc': 'Organizujte členy do týmů',
  'wsSettings.convertNote': 'Tato akce je nevratná. Vaše data a nastavení budou zachována.',
  'wsSettings.convertNow': 'Převést nyní',
  'wsSettings.transferOwnership': 'Převést vlastnictví',
  'wsSettings.transferDesc': 'Převeďte tento prostor na jiného člena.',
  'wsSettings.transfer': 'Převést',
  'wsSettings.deleteWorkspace': 'Smazat pracovní prostor',
  'wsSettings.deleteDesc': 'Trvale smazat tento prostor a všechna data.',
  'wsSettings.delete': 'Smazat',
  'wsSettings.cannotBeUndone': 'Tuto akci nelze vrátit zpět!',
  'wsSettings.transferWarn': 'Ztratíte přístup vlastníka a stanete se běžným členem.',
  'wsSettings.newOwner': 'Nový vlastník',
  'wsSettings.selectMember': 'Vyberte člena',
  'wsSettings.cancel': 'Zrušit',
  'wsSettings.transferring': 'Převádění...',
  'wsSettings.deleteWarning': 'Toto trvale smaže pracovní prostor',
  'wsSettings.typeToConfirm': 'Pro potvrzení zadejte název:',
  'wsSettings.deleting': 'Mazání...',
  'wsSettings.deletePermanently': 'Smazat trvale',
  'wsSettings.converting': 'Převádění...',

  // Notifications
  'notifications.title': 'Oznámení',
  'notifications.markAsRead': 'Označit jako přečtené',
  'notifications.markAllAsRead': 'Označit vše jako přečtené',
  'notifications.delete': 'Smazat',
  'notifications.deleteAll': 'Smazat vše',
  'notifications.clearAll': 'Vymazat vše',
  'notifications.unread': 'Nepřečtené',
  'notifications.all': 'Vše',
  'notifications.noNotifications': 'Žádná oznámení',
  'notifications.noUnread': 'Žádná nepřečtená oznámení',
  'notifications.today': 'Dnes',
  'notifications.yesterday': 'Včera',
  'notifications.older': 'Starší',
  'notifications.justNow': 'Právě teď',
  'notifications.minutesAgo': 'před {count}m',
  'notifications.hoursAgo': 'před {count}h',
  'notifications.daysAgo': 'před {count}d',
  'notifications.search': 'Hledat oznámení...',
  'notifications.settings': 'Nastavení oznámení',
  'notifications.types.itemUpdate': 'Aktualizace položek',
  'notifications.types.comment': 'Komentáře',
  'notifications.types.aiSuggestion': 'Návrhy AI',
  'notifications.types.duplicate': 'Duplikáty',
  'notifications.types.valueChanged': 'Změny hodnot',
  'notifications.types.security': 'Zabezpečení',
  'notifications.types.storage': 'Úložiště',
  'notifications.types.mention': 'Zmínky',
  'notifications.types.share': 'Sdíleno',
  'notifications.types.system': 'Systém',
  'notifications.loading': 'Načítání oznámení...',
  'notifications.noMatching': 'Žádná odpovídající oznámení',
  'notifications.tryAdjusting': 'Zkuste upravit filtry',
  'notifications.deleteConfirm': 'Smazat všechna oznámení? Tuto akci nelze vrátit.',
  'notifications.markAllRead': 'Označit vše jako přečtené',
  'notifications.markRead': 'Označit jako přečtené',
  'notifications.markUnread': 'Označit jako nepřečtené',
  'notifications.allCaughtUp': 'Vše je přečteno!',
  'notifications.showAll': 'Zobrazit vše',
  'notifications.empty': 'Žádná oznámení',
  'notifications.viewAll': 'Zobrazit všechna oznámení',
  'notifications.clickToView': 'Klikněte pro zobrazení',
  'notifications.moreNotifications': '+{count} dalších oznámení',

  // Two-Factor Authentication
  'twofa.title': 'Dvoufaktorové ověření',
  'twofa.subtitle': 'Přidejte svému účtu další vrstvu zabezpečení',
  'twofa.enabled': '2FA povoleno',
  'twofa.disabled': '2FA zakázáno',
  'twofa.enable': 'Povolit 2FA',
  'twofa.disable': 'Zakázat 2FA',
  'twofa.status': 'Stav',
  'twofa.setup': 'Nastavení',
  'twofa.setupInstructions': 'Naskenujte QR kód pomocí aplikace autentikátoru',
  'twofa.manualEntry': 'Nebo zadejte tento kód ručně',
  'twofa.verificationCode': 'Ověřovací kód',
  'twofa.enterCode': 'Zadejte 6místný kód',
  'twofa.verify': 'Ověřit',
  'twofa.backupCodes': 'Záložní kódy',
  'twofa.backupCodesInfo': 'Uložte tyto kódy na bezpečné místo',
  'twofa.backupCodesRemaining': 'Zbývá {count} záložních kódů',
  'twofa.regenerateBackupCodes': 'Regenerovat záložní kódy',
  'twofa.downloadBackupCodes': 'Stáhnout kódy',
  'twofa.copyCode': 'Kopírovat kód',
  'twofa.copied': 'Zkopírováno!',
  'twofa.setupSuccess': 'Dvoufaktorové ověření bylo úspěšně povoleno',
  'twofa.disableSuccess': 'Dvoufaktorové ověření zakázáno',
  'twofa.invalidCode': 'Neplatný ověřovací kód',
  'twofa.required': 'Vyžadován 2FA kód',
  'twofa.step1': 'Krok 1: Nainstalujte aplikaci autentikátoru',
  'twofa.step2': 'Krok 2: Naskenujte QR kód',
  'twofa.step3': 'Krok 3: Ověřte',
  'twofa.apps': 'Doporučené aplikace: Google Authenticator, Authy, Microsoft Authenticator',
  'twofa.loading': 'Načítání nastavení 2FA...',
  'twofa.whyEnable': 'Proč aktivovat 2FA?',
  'twofa.protectsAccount': 'Chrání váš účet i při prozrazení hesla',
  'twofa.timeBasedCodes': 'Používá časové kódy z autentizačních aplikací',
  'twofa.backupAccess': 'Záložní kódy pro nouzový přístup',
  'twofa.enableTwoFactor': 'Aktivovat dvoufaktorovou autentizaci',
  'twofa.accountProtected': 'Váš účet je chráněn 2FA',
  'twofa.scanQrCode': 'Krok 1: Naskenujte QR kód',
  'twofa.scanQrDesc': 'Naskenujte QR kód pomocí autentizační aplikace',
  'twofa.saveBackupCodes': 'Krok 2: Uložte záložní kódy',
  'twofa.saveBackupDesc': 'Uložte tyto kódy na bezpečné místo.',
  'twofa.verifyStep': 'Krok 3: Ověřte',
  'twofa.verifyDesc': 'Zadejte 6místný kód z autentizační aplikace',
  'twofa.cantScan': 'Nemůžete skenovat? Zadejte kód ručně:',
  'twofa.verifyAndEnable': 'Ověřit a aktivovat',
  'twofa.cancelSetup': 'Zrušit nastavení',
  'twofa.disableConfirm': 'Deaktivovat dvoufaktorovou autentizaci?',
  'twofa.lessSecure': 'Váš účet bude bez 2FA méně bezpečný.',
  'twofa.enterCodeConfirm': 'Pro potvrzení zadejte ověřovací kód:',
  'twofa.codeOrBackup': 'Kód nebo záložní kód',
  'twofa.regenerateTitle': 'Přegenerovat záložní kódy',
  'twofa.regenerateWarning': 'Tím se zneplatní všechny stávající záložní kódy.',
  'twofa.generateNew': 'Vygenerovat nové kódy',
  'twofa.newCodes': 'Nové záložní kódy',
  'twofa.saveNewCodes': 'Uložte tyto kódy. Staré kódy jsou neplatné.',
  'twofa.download': 'Stáhnout',
  'twofa.done': 'Hotovo',
  'twofa.twofaEnabled': '2FA Aktivováno!',
  'twofa.saveBeforeClose': 'Před zavřením se ujistěte, že máte uložené záložní kódy.',
  'twofa.savedMyCodes': 'Uložil jsem si kódy',

  // Misc
  'misc.noData': 'Žádná data',
  'misc.error': 'Došlo k chybě',
  'misc.success': 'Úspěch',
  'misc.to': 'do',
  'misc.yes': 'Ano',
  'misc.no': 'Ne',

  // Audit Trail
  'audit.title': 'Protokol aktivit',
  'audit.subtitle': 'Sledujte všechny změny a aktivity ve vašem účtu',
  'audit.tableView': 'Tabulka',
  'audit.timelineView': 'Časová osa',
  'audit.dashboardView': 'Dashboard organizace',
  'audit.filters': 'Filtry',
  'audit.exportCsv': 'Exportovat CSV',
  'audit.search': 'Hledat podle uživatele, akce nebo zdroje...',
  'audit.totalEvents': 'Celkem událostí',
  'audit.eventsToday': 'Události dnes',
  'audit.activeUsers': 'Aktivní uživatelé',
  'audit.topActions': 'Nejčastější akce',
  'audit.actionType': 'Typ akce',
  'audit.allActions': 'Všechny akce',
  'audit.resourceType': 'Typ zdroje',
  'audit.allResources': 'Všechny zdroje',
  'audit.startDate': 'Datum od',
  'audit.endDate': 'Datum do',
  'audit.clear': 'Vymazat',
  'audit.time': 'Čas',
  'audit.user': 'Uživatel',
  'audit.action': 'Akce',
  'audit.resource': 'Zdroj',
  'audit.details': 'Podrobnosti',
  'audit.source': 'Zdroj',
  'audit.noLogs': 'Žádné záznamy nenalezeny',
  'audit.emptyState': 'Aktivity se zde zobrazí, jakmile budou provedeny akce',
  'audit.loading': 'Načítání...',
  'audit.loadingTimeline': 'Načítání časové osy...',
  'audit.loadingDashboard': 'Načítání dashboardu organizace...',
  'audit.page': 'Stránka {current} z {total}',
  'audit.events': 'událostí',
  'audit.actions': 'akcí',
  'audit.system': 'Systém',
  'audit.unknown': 'Neznámý',
  'audit.empty': '(prázdné)',
  'audit.revert': 'Vrátit tuto změnu',
  'audit.reverting': 'Vracení...',
  'audit.revertToPrevious': 'Vrátit na předchozí hodnotu',
  'audit.logDetails': 'Podrobnosti záznamu',
  'audit.eventInfo': 'Informace o události',
  'audit.timestamp': 'Časové razítko',
  'audit.resourceId': 'ID zdroje',
  'audit.resourceName': 'Název zdroje',
  'audit.userInfo': 'Informace o uživateli',
  'audit.userId': 'ID uživatele',
  'audit.role': 'Role',
  'audit.fieldChange': 'Změna pole',
  'audit.oldValue': 'Stará hodnota',
  'audit.newValue': 'Nová hodnota',
  'audit.techDetails': 'Technické podrobnosti',
  'audit.ipAddress': 'IP adresa',
  'audit.device': 'Zařízení',
  'audit.userAgent': 'User Agent',
  'audit.metadata': 'Další metadata',
  'audit.revertChange': 'Vrátit změnu',
  'audit.dashboardTitle': 'Dashboard auditu organizace',
  'audit.lastDays': 'Posledních {days} dní',
  'audit.today': 'Dnes',
  'audit.mostActiveUsers': 'Nejaktivnější uživatelé',
  'audit.recentSecurity': 'Nedávné bezpečnostní události',
  'audit.actionDistribution': 'Rozložení akcí',
  'audit.noData': 'Data dashboardu organizace nejsou k dispozici',
  'audit.minutesAgo': 'před {count}m',
  'audit.hoursAgo': 'před {count}h',
  'audit.daysAgo': 'před {count}d',
  'audit.justNow': 'právě teď',
  'audit.group.items': 'Položky',
  'audit.group.imagesFiles': 'Obrázky a soubory',
  'audit.group.comments': 'Komentáře',
  'audit.group.categories': 'Kategorie',
  'audit.group.auth': 'Autentizace',
  'audit.group.security': 'Zabezpečení',
  'audit.group.members': 'Členové a pozvánky',
  'audit.group.ai': 'AI a automatizace',
  'audit.group.notifications': 'Oznámení a systém',
  'audit.group.bulk': 'Hromadné akce a export',
  'audit.action.itemCreated': 'Položka vytvořena',
  'audit.action.itemUpdated': 'Položka aktualizována',
  'audit.action.itemDeleted': 'Položka smazána',
  'audit.action.itemRestored': 'Položka obnovena',
  'audit.action.favoriteAdded': 'Přidáno do oblíbených',
  'audit.action.favoriteRemoved': 'Odebráno z oblíbených',
  'audit.action.imageUploaded': 'Obrázek nahrán',
  'audit.action.imageDeleted': 'Obrázek smazán',
  'audit.action.fileUploaded': 'Soubor nahrán',
  'audit.action.fileDeleted': 'Soubor smazán',
  'audit.action.commentAdded': 'Komentář přidán',
  'audit.action.commentEdited': 'Komentář upraven',
  'audit.action.commentDeleted': 'Komentář smazán',
  'audit.action.categoryCreated': 'Kategorie vytvořena',
  'audit.action.categoryUpdated': 'Kategorie aktualizována',
  'audit.action.categoryDeleted': 'Kategorie smazána',
  'audit.action.login': 'Přihlášení',
  'audit.action.logout': 'Odhlášení',
  'audit.action.registration': 'Registrace',
  'audit.action.loginFailed': 'Přihlášení selhalo',
  'audit.action.passwordChanged': 'Heslo změněno',
  'audit.action.passwordReset': 'Požadavek na reset hesla',
  'audit.action.profileUpdated': 'Profil aktualizován',
  'audit.action.twoFaEnabled': '2FA povoleno',
  'audit.action.twoFaDisabled': '2FA zakázáno',
  'audit.action.accountDeleted': 'Účet smazán',
  'audit.action.unauthorizedAccess': 'Neautorizovaný přístup',
  'audit.action.roleChanged': 'Role změněna',
  'audit.action.permissionChanged': 'Oprávnění změněno',
  'audit.action.invitationSent': 'Pozvánka odeslána',
  'audit.action.memberRemoved': 'Člen odebrán',
  'audit.action.aiCategorization': 'AI kategorizace',
  'audit.action.aiDuplicate': 'AI detekce duplikátů',
  'audit.action.aiValue': 'AI predikce hodnoty',
  'audit.action.aiAccepted': 'AI návrh přijat',
  'audit.action.aiRejected': 'AI návrh odmítnut',
  'audit.action.notificationSent': 'Oznámení odesláno',
  'audit.action.systemConfig': 'Konfigurace systému změněna',
  'audit.action.bulkExport': 'Hromadný export',
  'audit.action.bulkImport': 'Hromadný import',
  'audit.action.bulkDelete': 'Hromadné smazání',
  'audit.resource.items': 'Položky',
  'audit.resource.categories': 'Kategorie',
  'audit.resource.users': 'Uživatelé',
  'audit.resource.organization': 'Organizace',
  'audit.resource.comments': 'Komentáře',
  'audit.resource.images': 'Obrázky',
  'audit.resource.files': 'Soubory',
  'audit.resource.notifications': 'Oznámení',
  'audit.resource.system': 'Systém',

  // Auth Pages
  'auth.welcomeBack': 'Vítejte zpět',
  'auth.signInSubtitle': 'Přihlaste se a pokračujte do svého pracovního prostoru',
  'auth.email': 'E-mailová adresa',
  'auth.emailPlaceholder': 'vas@email.com',
  'auth.password': 'Heslo',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signIn': 'Přihlásit se',
  'auth.signingIn': 'Přihlašování...',
  'auth.forgotPassword': 'Zapomněli jste heslo?',
  'auth.noAccount': 'Nemáte účet?',
  'auth.createOne': 'Vytvořit účet',
  'auth.orContinueWith': 'nebo',
  'auth.rememberMe': 'Zapamatovat si mě',
  'auth.secureLogin': 'Bezpečné a šifrované přihlášení',
  'auth.benefit1': 'Chytrá správa položek',
  'auth.benefit2': 'Spolupráce v reálném čase',
  'auth.benefit3': 'Pokročilé analýzy',
  'auth.emailRequired': 'E-mail a heslo jsou povinné',
  'auth.loginFailed': 'Přihlášení se nezdařilo. Zkuste to prosím znovu.',

  'auth.createAccount': 'Vytvořit účet',
  'auth.signUpSubtitle': 'Začněte svou cestu s námi ještě dnes',
  'auth.firstName': 'Jméno',
  'auth.firstNamePlaceholder': 'Jan',
  'auth.lastName': 'Příjmení',
  'auth.lastNamePlaceholder': 'Novák',
  'auth.confirmPassword': 'Potvrdit heslo',
  'auth.signUp': 'Vytvořit účet',
  'auth.signingUp': 'Vytváření účtu...',
  'auth.hasAccount': 'Již máte účet?',
  'auth.signInLink': 'Přihlásit se',
  'auth.passwordMismatch': 'Hesla se neshodují',
  'auth.passwordTooShort': 'Heslo musí mít alespoň 8 znaků',
  'auth.fillAllFields': 'Vyplňte prosím všechna povinná pole',
  'auth.signupFailed': 'Registrace se nezdařila. Zkuste to prosím znovu.',
  'auth.emailAlreadyExists': 'Účet s touto e-mailovou adresou již existuje. Přihlaste se nebo použijte jinou e-mailovou adresu.',
  'auth.networkError': 'Nelze se připojit k serveru. Zkontrolujte prosím připojení k internetu a zkuste to znovu.',
  'auth.signupSuccess': 'Účet vytvořen! Zkontrolujte e-mail pro ověření.',
  'auth.joinCommunity': 'Připojte se k naší komunitě',
  'auth.benefitSecure': 'Bezpečné a šifrované',
  'auth.benefitVerified': 'E-mailem ověřené účty',
  'auth.benefitSmart': 'Chytrá kategorizace',
  'auth.agreeTerms': 'Registrací souhlasíte s našimi',
  'auth.termsOfService': 'Podmínky služby',
  'auth.and': 'a',
  'auth.privacyPolicy': 'Zásady ochrany osobních údajů',
  'auth.passwordStrength': 'Síla hesla',
  'auth.weak': 'Slabé',
  'auth.fair': 'Průměrné',
  'auth.good': 'Dobré',
  'auth.strong': 'Silné',

  'auth.forgotPasswordTitle': 'Zapomněli jste heslo?',
  'auth.forgotPasswordSubtitle': 'Zadejte svůj e-mail a my vám zašleme odkaz pro obnovení',
  'auth.sendResetLink': 'Odeslat odkaz',
  'auth.sending': 'Odesílání...',
  'auth.resetLinkSent': 'Odkaz pro obnovení hesla byl odeslán na váš e-mail',
  'auth.backToLogin': 'Zpět na přihlášení',
  'auth.resetYourPassword': 'Obnovte své heslo',
  'auth.resetDescription': 'Zadejte svou e-mailovou adresu a my vám zašleme odkaz pro obnovení hesla',
  'auth.emailRequired2': 'E-mail je povinný',
  'auth.sendFailed': 'Nepodařilo se odeslat e-mail pro obnovení',
  'auth.checkInbox': 'Zkontrolujte svou schránku',
  'auth.resetEmailSentDesc': 'Odeslali jsme odkaz pro obnovení hesla na váš e-mail. Zkontrolujte svou schránku a složku se spamem.',

  'auth.resetPasswordTitle': 'Obnovit heslo',
  'auth.resetPasswordSubtitle': 'Zadejte své nové heslo',
  'auth.newPassword': 'Nové heslo',
  'auth.confirmNewPassword': 'Potvrdit nové heslo',
  'auth.resetPassword': 'Obnovit heslo',
  'auth.resetting': 'Obnovování...',
  'auth.resetSuccess': 'Heslo úspěšně obnoveno! Přesměrování...',
  'auth.resetFailed': 'Obnovení hesla se nezdařilo',
  'auth.invalidResetLink': 'Neplatný odkaz',
  'auth.invalidResetDesc': 'Tento odkaz pro obnovení hesla je neplatný nebo vypršel',
  'auth.requestNewLink': 'Vyžádat nový odkaz',
  'auth.noTokenProvided': 'Nebyl poskytnut žádný token',
  'auth.createNewPassword': 'Vytvořte nové heslo',
  'auth.createNewPasswordDesc': 'Zvolte silné heslo pro svůj účet',
  'auth.tryAgain': 'Zkusit znovu',

  'auth.verifyEmail': 'Ověřte svůj e-mail',
  'auth.verifyEmailDesc': 'Odeslali jsme ověřovací odkaz na váš e-mail',
  'auth.checkYourInbox': 'Zkontrolujte svou schránku',
  'auth.weSentVerification': 'Odeslali jsme ověřovací odkaz na',
  'auth.checkSpam': 'Klikněte na odkaz v e-mailu pro ověření účtu. Pokud ho nevidíte, zkontrolujte složku se spamem.',
  'auth.resendVerification': 'Znovu odeslat ověřovací e-mail',
  'auth.resendSuccess': 'Ověřovací e-mail znovu odeslán! Zkontrolujte svou schránku.',
  'auth.alreadyVerified': 'Již ověřeno? Přihlásit se',
  'auth.verifying': 'Ověřování e-mailu',
  'auth.verifyingDesc': 'Počkejte prosím, než potvrdíme vaši e-mailovou adresu...',
  'auth.emailVerified': 'E-mail ověřen!',
  'auth.verifiedSuccess': 'E-mail úspěšně ověřen! Přesměrování na dashboard...',
  'auth.verificationFailed': 'Ověření se nezdařilo',
  'auth.resendFailed': 'Nepodařilo se znovu odeslat ověřovací e-mail',
  'auth.goToLogin': 'Přejít na přihlášení',
  'auth.goToDashboard': 'Přejít na dashboard',
  'auth.enterEmailResend': 'Zadejte e-mail pro opětovné odeslání',
  'auth.verificationSent': 'Ověřovací e-mail odeslán! Zkontrolujte svou schránku.',

  // Support
  'auth.needHelp': 'Potřebujete pomoc?',
  'auth.contactSupport': 'Kontaktujte podporu',
  'auth.havingProblems': 'Máte problémy?',
  'auth.reachOutToUs': 'Kontaktujte nás',
  'auth.contactUsForHelp': 'Kontaktujte nás pro pomoc s vaším účtem',
  'auth.supportEmail': 'Pošlete nám e-mail',
  'auth.supportSubject': 'ELVOAQ žádost o podporu',
  'auth.supportBody': 'Ahoj,\n\nPotřebuji pomoc se svým účtem.\n\nProsím popište svůj problém:',

  // Sidebar
  'sidebar.collapse': 'Sbalit',
  'sidebar.collapseSidebar': 'Sbalit postranní panel',
  'sidebar.expandSidebar': 'Rozbalit postranní panel',

  // User Dropdown
  'dropdown.settings': 'Nastavení',
  'dropdown.subscription': 'Předplatné',
  'dropdown.logout': 'Odhlásit se',
  'dropdown.openProfile': 'Otevřít profil',

  // Settings — Profile
  'settings.profileTitle': 'Informace o profilu',
  'role.OWNER': 'Vlastník',
  'role.ADMIN': 'Super admin',
  'role.MEMBER': 'Člen',
  'role.LEAD': 'Lead',
  'role.VIEWER': 'Prohlížeč',
  'settings.firstName': 'Jméno',
  'settings.lastName': 'Příjmení',
  'settings.firstNamePlaceholder': 'Zadejte své jméno',
  'settings.lastNamePlaceholder': 'Zadejte své příjmení',
  'settings.emailAddress': 'E-mailová adresa',
  'settings.changeEmail': 'Změnit',
  'settings.newEmailAddress': 'Nová e-mailová adresa',
  'settings.enterNewEmail': 'Zadejte novou e-mailovou adresu',
  'settings.currentPassword': 'Aktuální heslo',
  'settings.currentPasswordConfirm': 'Zadejte aktuální heslo pro potvrzení',
  'settings.updatingEmail': 'Aktualizace...',
  'settings.updateEmail': 'Aktualizovat e-mail',
  'settings.bio': 'O mně',
  'settings.bioPlaceholder': 'Řekněte nám něco o sobě...',
  'settings.defaultCurrency': 'Výchozí měna',
  'settings.currencyHint': 'Tato měna bude použita pro zobrazení přepočtených hodnot v celé aplikaci.',
  'settings.savingChanges': 'Ukládání...',

  // Settings — Security
  'settings.securityTitle': 'Nastavení zabezpečení',
  'settings.changePassword': 'Změnit heslo',
  'settings.enterCurrentPassword': 'Zadejte aktuální heslo',
  'settings.newPassword': 'Nové heslo',
  'settings.enterNewPassword': 'Zadejte nové heslo',
  'settings.confirmPassword': 'Potvrdit heslo',
  'settings.confirmNewPassword': 'Potvrďte nové heslo',
  'settings.updatingPassword': 'Aktualizace...',

  // Settings — Notifications
  'settings.notifPreferences': 'Nastavení oznámení',
  'settings.notifTypes': 'Typy oznámení',
  'settings.notifItemUpdates': 'Aktualizace položek',
  'settings.notifItemUpdatesDesc': 'Upozornění při vytvoření, aktualizaci nebo smazání položek',
  'settings.notifComments': 'Komentáře',
  'settings.notifCommentsDesc': 'Oznámení o nových komentářích k vašim položkám',
  'settings.notifMentions': 'Zmínky',
  'settings.notifMentionsDesc': 'Upozornění když vás někdo zmíní',
  'settings.notifDuplicates': 'Detekce duplikátů',
  'settings.notifDuplicatesDesc': 'AI upozornění při nalezení potenciálních duplikátů',
  'settings.notifValuePredictions': 'Predikce hodnot',
  'settings.notifValuePredictionsDesc': 'Oznámení o změnách hodnot a predikcích',
  'settings.notifSecurityAlerts': 'Bezpečnostní upozornění',
  'settings.notifSecurityAlertsDesc': 'Důležitá bezpečnostní oznámení',
  'settings.notifEmailPrefs': 'E-mailové preference',
  'settings.notifWeeklyDigest': 'Týdenní přehled',
  'settings.notifWeeklyDigestDesc': 'Týdenní souhrn aktivit',
  'settings.notifMarketing': 'Marketingové e-maily',
  'settings.notifMarketingDesc': 'Novinky o funkcích a akcích',
  'settings.notifChannels': 'Kanály doručení',
  'settings.notifEmail': 'E-mailová oznámení',
  'settings.notifEmailDesc': 'Přijímat oznámení e-mailem',
  'settings.notifPush': 'Push oznámení',
  'settings.notifPushDesc': 'Prohlížečová a mobilní push oznámení',
  'settings.notifInApp': 'Oznámení v aplikaci',
  'settings.notifInAppDesc': 'Zobrazovat oznámení uvnitř aplikace',
  'settings.notifQuietHours': 'Klidové hodiny',
  'settings.notifEnableQuietHours': 'Povolit klidové hodiny',
  'settings.notifQuietHoursDesc': 'Pozastavit nekritická oznámení během určitých hodin',
  'settings.notifStartTime': 'Čas začátku',
  'settings.notifEndTime': 'Čas konce',
  'settings.notifSaving': 'Ukládání...',
  'settings.notifSaveBtn': 'Uložit nastavení oznámení',
  'settings.loadingNotifications': 'Načítání nastavení oznámení...',

  // Settings — Accessibility
  'settings.accessibilityTitle': 'Přístupnost & Zobrazení',
  'settings.accessibilityDesc': 'Přizpůsobte animace a interakce pro váš komfort a zařízení.',
  'settings.motionAnimations': 'Pohyb & Animace',
  'settings.reduceMotion': 'Omezit pohyb',
  'settings.reduceMotionDesc': 'Vypnout přechody, hover animace a dekorativní pohybové efekty v celé aplikaci',
  'settings.reduceMotionHint': 'Automaticky respektuje také nastavení "Omezit pohyb" vašeho operačního systému.',
  'settings.touchMobile': 'Dotyk & Mobil',
  'settings.touchFriendly': 'Režim pro dotykové ovládání',
  'settings.touchFriendlyDesc': 'Nahradit hover interakce alternativami na kliknutí. Zvětší tlačítka a přepínače pro snadnější dotykové ovládání.',
  'settings.touchFriendlyHint': 'Doporučeno při použití tabletu nebo dotykového zařízení.',
  'settings.mobileWarningTitle': 'Upozornění k mobilnímu použití',
  'settings.mobileWarningText': 'ELVOAQ Vault je optimalizován pro desktopové a tabletové obrazovky. Na menších mobilních zařízeních mohou být některé funkce omezené nebo vyžadovat posuvání. Pro nejlepší zážitek doporučujeme použití desktopového prohlížeče nebo tabletu.',
  'settings.mobileWarningDarkHint': 'Tip: Tmavý režim snižuje namáhání očí na mobilních obrazovkách.',
  'settings.mobileWarningLightHint': 'Tip: Světlý režim může zlepšit čitelnost v jasném prostředí.',

  // Settings — Privacy
  'settings.privacyTitle': 'Ochrana soukromí & Data',
  'settings.privacyTab': 'Soukromí',
  'settings.yourData': 'Vaše data',
  'settings.yourDataDesc': 'Vaše osobní údaje jsou chráněny a bezpečně uloženy. Vaše data nikdy nesdílíme s třetími stranami.',
  'settings.dangerZone': '⚠️ Nebezpečná zóna',
  'settings.dangerZoneDesc': 'Smazání účtu je trvalé a nelze jej vrátit zpět. Všechny vaše položky, data a nastavení budou trvale smazány.',
  'settings.deleteAccount': 'Smazat můj účet',
  'settings.deleteConfirmTitle': '⚠️ Jste si naprosto jistí?',
  'settings.deleteConfirmDesc': 'Tuto akci nelze vrátit zpět. Zadejte prosím své heslo pro potvrzení smazání účtu.',
  'settings.enterPassword': 'Zadejte své heslo',
  'settings.deleting': 'Mazání...',
  'settings.confirmDeleteBtn': 'Ano, smazat můj účet',
  'settings.privacyPolicy': 'Zásady ochrany soukromí',
  'settings.privacyPolicyDesc': 'Přečtěte si naše zásady ochrany soukromí a podmínky služby.',
  'settings.viewPrivacyPolicy': 'Zobrazit zásady ochrany soukromí',

  // Settings — AI
  'settings.aiTitle': 'Nastavení AI',
  'settings.aiTab': 'AI Poskytovatel',
  'settings.aboutTab': 'O aplikaci',
  // Tutorial
  'tutorial.tourBadge': 'Prohlídka platformy',
  'tutorial.back': 'Zpět',
  'tutorial.next': 'Další',
  'tutorial.skip': 'Přeskočit prohlídku',
  'tutorial.getStarted': 'Začít',
  'tutorial.welcome.title': 'Vítejte v ELVOAQ',
  'tutorial.welcome.desc': 'ELVOAQ je vaše platforma vše v jednom pro digitální inventuru. Spravujte, sledujte a zabezpečte všechny své cenné předměty na jednom místě.',
  'tutorial.library.title': 'Vaše knihovna položek',
  'tutorial.library.desc': 'Knihovna je srdcem ELVOAQ. Přidávejte položky s fotografiemi, popisy, cenami a stavy. Výkonné vyhledávání a filtry vám pomohou okamžitě najít cokoliv.',
  'tutorial.categories.title': 'Kategorie',
  'tutorial.categories.desc': 'Organizujte položky do vlastních kategorií s barvami a ikonami. Celková hodnota a počet položek na kategorii na jeden pohled.',
  'tutorial.favorites.title': 'Oblíbené',
  'tutorial.favorites.desc': 'Označte nejdůležitější položky jako oblíbené pro rychlý přístup. Kdykoliv exportujte svůj seznam oblíbených jako CSV.',
  'tutorial.collections.title': 'Kolekce',
  'tutorial.collections.desc': 'Seskupte související položky do pojmenovaných kolekcí — cestovní sada, domácí kancelář nebo pojistný balíček. Přidejte libovolnou položku z knihovny jedním kliknutím.',
  'tutorial.accounting.title': 'Účetnictví a aktiva',
  'tutorial.accounting.desc': 'Sledujte odpisy, spravujte záznamy aktiv a monitorujte finanční hodnotu svého inventáře v průběhu času.',
  'tutorial.ai.title': 'Funkce s podporou AI',
  'tutorial.ai.desc': 'Nechte AI pracovat pro vás. Získejte předpovědi hodnot, automaticky odhalte duplikáty, chatujte s AI asistentem a nastavte chytré připomínky.',
  'tutorial.security.title': 'Zabezpečení a soukromí',
  'tutorial.security.desc': 'Uzamkněte jednotlivé položky heslem, vytvořte skryté soukromé složky, povolte dvoufaktorové ověření a spravujte role přístupu.',
  'tutorial.calendar.title': 'Kalendář a připomínky',
  'tutorial.calendar.desc': 'Plánujte připomínky na údržbu, upozornění na vypršení záruky, obnovy pojištění a vlastní události — vše propojeno s vašimi položkami.',
  'tutorial.settings.title': 'Přizpůsobte vše',
  'tutorial.settings.desc': 'Přepínejte mezi tmavým a světlým režimem, vyberte jazyk, nastavte výchozí měnu a spravujte všechna svá nastavení.',
  'tutorial.audit.title': 'Audit log',
  'tutorial.audit.desc': 'Sledujte každou akci na platformě v reálném čase. Zjistěte, kdo co změnil, kdy a odkud \u2014 s úplnými detaily, sledováním IP adres a možností vrátit změny.',
  'tutorial.clickHint': 'Klikněte sem pro přechod do této sekce',
  'tutorial.done.title': 'Vše je připraveno!',
  'tutorial.done.desc': 'Nyní znáte všechny základy ELVOAQ. Začněte přidáním své první položky do knihovny. Tuto prohlídku můžete kdykoliv zopakovat v Nastavení → O aplikaci.',
  'tutorial.startTour': 'Spustit prohlídku',
  'tutorial.exploreOwn': 'Prozkoumám sám',
  'tutorial.replayTour': 'Zopakovat prohlídku',
  // About / Version
  'about.version': 'Verze aplikace',
  'about.releaseDate': 'Datum vydání',
  'about.changelog': 'Historie změn',
  'about.newFeatures': 'Nové funkce',
  'about.replayTutorial': 'Zopakovat tutoriál',
  'about.replayTutorialDesc': 'Znovu zobrazit interaktivní prohlídku platformy',
  'about.autoLogout': 'Automatické odhlášení',
  'about.autoLogoutDesc': 'Automaticky se odhlásit po období nečinnosti',
  'about.timeout15': '15 minut',
  'about.timeout30': '30 minut',
  'about.timeout60': '1 hodina',
  'about.timeout120': '2 hodiny',
  'about.timeoutNever': 'Nikdy (nedoporučeno)',
  'about.privacyPolicy': 'Zásady ochrany osobních údajů',
  'about.termsOfService': 'Podmínky použití',
  'about.viewPrivacy': 'Zobrazit zásady ochrany osobních údajů',
  'about.viewTerms': 'Zobrazit podmínky použití',
  'about.legalDocuments': 'Právní dokumenty',
  // CAPTCHA
  'auth.captchaQuestion': 'Bezpečnostní kontrola: Co je {a} + {b}?',
  'auth.captchaPlaceholder': 'Zadejte odpověď',
  'auth.captchaError': 'Nesprávná odpověď. Zkuste to prosím znovu.',
  'settings.aiDescription': 'ELVOAQ AI je nakonfigurována a aktivní. Můžete využívat všechny AI funkce včetně asistenta, detekce duplikátů, predikce hodnot a dalších.',
  'settings.aiActive': 'ELVOAQ AI Aktivní',
  'settings.aiActiveDesc': 'Poháněno naší pokročilou AI. Všechny funkce jsou připraveny k použití na celé platformě.',
  'settings.aiFeatures': 'Dostupné AI funkce:',
  'settings.aiAssistant': 'AI Asistent',
  'settings.aiAssistantDesc': 'Pomoc se správou vašeho inventáře',
  'settings.aiDuplicateDetection': 'Detekce duplikátů',
  'settings.aiDuplicateDetectionDesc': 'Automaticky najít podobné položky',
  'settings.aiValuePrediction': 'Predikce hodnot',
  'settings.aiValuePredictionDesc': 'Odhad hodnoty položek v čase',
  'settings.aiSmartCategorization': 'Inteligentní kategorizace',
  'settings.aiSmartCategorizationDesc': 'Automaticky navrhovat kategorie',

  // Feedback Modal
  'feedback.title': 'Odeslat zpětnou vazbu',
  'feedback.successTitle': 'Zpětná vazba odeslána!',
  'feedback.successMessage': 'Děkujeme! Vaše zpětná vazba byla úspěšně doručena našemu týmu.',
  'feedback.typeLabel': 'Typ',
  'feedback.titleLabel': 'Název *',
  'feedback.titlePlaceholder': 'Stručný souhrn vaší zpětné vazby',
  'feedback.descriptionLabel': 'Popis *',
  'feedback.descriptionPlaceholder': 'Uveďte prosím podrobné informace...',
  'feedback.categoryLabel': 'Kategorie *',
  'feedback.categoryPlaceholder': 'Vyberte kategorii...',
  'feedback.attachmentLabel': 'Příloha (Volitelné)',
  'feedback.uploadBtn': 'Nahrát snímek obrazovky nebo video',
  'feedback.maxFileSize': 'Max 5MB. Podporováno: JPG, PNG, GIF, WebP, MP4, WebM',
  'feedback.systemInfo': '📊 Informace o systému (automaticky zachyceny)',
  'feedback.submitBtn': 'Odeslat zpětnou vazbu',
  'feedback.submitting': 'Odesílání...',
  'feedback.bugReport': 'Hlášení chyby',
  'feedback.bugReportDesc': 'Nahlásit chybu',
  'feedback.featureRequest': 'Žádost o funkci',
  'feedback.featureRequestDesc': 'Navrhnout novou funkci',
  'feedback.improvement': 'Vylepšení',
  'feedback.improvementDesc': 'Navrhnout vylepšení',
  'feedback.question': 'Otázka',
  'feedback.questionDesc': 'Položit otázku',
  'feedback.complaint': 'Stížnost',
  'feedback.complaintDesc': 'Nahlásit problém nebo obavu',
  'feedback.other': 'Ostatní',
  'feedback.otherDesc': 'Obecná zpětná vazba',
  'feedback.fileTooLarge': 'Velikost souboru musí být menší než 5MB',
  'feedback.invalidFileType': 'Povoleny jsou pouze obrázky (JPG, PNG, GIF, WebP) a videa (MP4, WebM)',
  'feedback.titleDescRequired': 'Název a popis jsou povinné',
  'feedback.categoryRequired': 'Vyberte prosím kategorii',
  'feedback.remove': 'Odebrat',
  'feedback.cancel': 'Zrušit',
  'feedback.display': 'Zobrazení',
  'feedback.browser': 'Prohlížeč',
  'feedback.locationLang': 'Poloha & Jazyk',
  'feedback.pageContext': 'Kontext stránky',
  'feedback.screenResolution': 'Rozlišení obrazovky',
  'feedback.viewportSize': 'Velikost okna',
  'feedback.colorDepth': 'Barevná hloubka',
  'feedback.userAgent': 'User Agent',
  'feedback.device': 'Zařízení',
  'feedback.os': 'Operační systém',
  'feedback.cookiesEnabled': 'Cookies povoleny',
  'feedback.language': 'Jazyk',
  'feedback.timezone': 'Časové pásmo',
  'feedback.currentUrl': 'Aktuální URL',
  'feedback.referrer': 'Referrer',
  'feedback.onlineStatus': 'Stav připojení',
  'feedback.errorSubmit': 'Nepodařilo se odeslat zpětnou vazbu',

  // Feedback Categories
  'feedback.category.Dashboard': 'Nástěnka',
  'feedback.category.Items & Library': 'Položky & Knihovna',
  'feedback.category.Categories': 'Kategorie',
  'feedback.category.Notifications': 'Oznámení',
  'feedback.category.Settings': 'Nastavení',
  'feedback.category.Workspaces & Teams': 'Pracovní prostory & Týmy',
  'feedback.category.Authentication': 'Autentizace',
  'feedback.category.Performance': 'Výkon',
  'feedback.category.User Interface': 'Uživatelské rozhraní',
  'feedback.category.Mobile Experience': 'Mobilní zkušenost',
  'feedback.category.Data & Analytics': 'Data & Analytika',
  'feedback.category.Other': 'Ostatní',

  // Subscription / Plans
  'subscription.title': 'Předplatné',
  'subscription.overview': 'Přehled',
  'subscription.plans': 'Plány',
  'subscription.storage': 'Úložiště',
  'subscription.billing': 'Fakturace',
  'subscription.header': 'Předplatné & Plány',
  'subscription.headerDesc': 'Spravujte svůj plán, úložiště a fakturaci na jednom místě',
  'subscription.loading': 'Načítání předplatného...',
  'subscription.currentPlan': 'Aktuální plán',
  'subscription.upgrade': 'Upgrade',
  'subscription.usageOverview': 'Přehled využití',
  'subscription.items': 'Položky',
  'subscription.categories': 'Kategorie',
  'subscription.workspaces': 'Pracovní prostory',
  'subscription.aiMessages': 'AI zprávy',
  'subscription.imagesPerItem': 'Obrázky / Položka',
  'subscription.max': 'max',
  'subscription.yourPlanFeatures': 'Funkce vašeho plánu',
  'subscription.unlimited': 'Neomezeně',
  'subscription.itemsLabel': 'položek',
  'subscription.customCategories': 'vlastních kategorií',
  'subscription.storageLabel': 'úložiště',
  'subscription.imagesPerItemLabel': 'obrázků na položku',
  'subscription.workspacesLabel': 'pracovní(ch) prostor(ů)',
  'subscription.aiMessagesMonth': 'AI zpráv/měsíc',
  'subscription.aiCategorization': 'AI kategorizace',
  'subscription.valuePrediction': 'Predikce hodnoty',
  'subscription.advancedAnalytics': 'Pokročilé analýzy',
  'subscription.prioritySupport': 'Prioritní podpora',
  'subscription.compareAllPlans': 'Porovnat všechny plány',
  'subscription.storageWarningPrefix': 'Využíváte',
  'subscription.storageWarningSuffix': 'vašeho úložiště. Zvažte upgrade nebo přidání úložiště.',
  'subscription.manageStorage': 'Spravovat úložiště',
  'subscription.monthly': 'Měsíčně',
  'subscription.yearly': 'Ročně',
  'subscription.save20': 'Ušetřete 20%',
  'subscription.mostPopular': 'Nejoblíbenější',
  'subscription.currentPlanLabel': 'Aktuální plán',
  'subscription.free': 'Zdarma',
  'subscription.custom': 'Vlastní',
  'subscription.contactSales': 'Kontaktovat obchod',
  'subscription.switch': 'Přepnout',
  'subscription.viewFullPricing': 'Zobrazit kompletní ceník',
  'subscription.storageBreakdown': 'Rozložení úložiště',
  'subscription.images': 'Obrázky',
  'subscription.documents': 'Dokumenty',
  'subscription.metadata': 'Metadata',
  'subscription.perMonth': '/měs',
  'subscription.perYear': '/rok',
  'subscription.savePerYear': 'Ušetřete €{amount}/rok',
  'subscription.used': 'Použito',
  'subscription.freeSpace': 'Volné',
  'subscription.total': 'Celkem',
  'subscription.needMoreSpace': 'Potřebujete více místa?',
  'subscription.addExtraStorage': 'Přidejte k plánu další úložiště',
  'subscription.addStorage': 'Přidat úložiště',
  'subscription.saveTilde20': 'Ušetřete ~20%',
  'subscription.save20Approx': 'Ušetřete ~20%',
  'subscription.billingSummary': 'Přehled fakturace',
  'subscription.billingCycle': 'Fakturační cyklus',
  'subscription.yearlyCost': 'Roční cena',
  'subscription.monthlyCost': 'Měsíční cena',
  'subscription.nextBillingDate': 'Další datum fakturace',
  'subscription.paymentMethod': 'Platební metoda',
  'subscription.naFree': 'N/A — Bezplatný plán',
  'subscription.manageKofi': 'Spravovat na Ko-fi',
  'subscription.manageStripe': 'Spravovat předplatné',
  'subscription.downloadInvoice': 'Stáhnout fakturu',
  'subscription.cancelSubscription': 'Zrušit předplatné',
  'subscription.cancelDesc': 'Pro zrušení předplatného ho spravujte přes Stripe. Váš přístup bude pokračovat do konce aktuálního fakturačního období.',
  'subscription.cancelOnKofi': 'Zrušit na Ko-fi',
  'subscription.cancelStripe': 'Zrušit předplatné',
  'subscription.paymentHistory': 'Historie plateb',
  'subscription.noPayments': 'Zatím žádná historie plateb',
  'subscription.noPaymentsDesc': 'Vaše historie plateb se zde zobrazí po první platbě',
  'subscription.needHelpBilling': 'Potřebujete pomoc s fakturací?',
  'subscription.contactBilling': 'Kontaktujte nás na',
  'subscription.billingQuestions': 'pro jakékoli dotazy k fakturaci.',
  'subscription.securePayments': 'Bezpečné platby',
  'subscription.securePaymentsDesc': 'Všechny platby jsou bezpečně zpracovány přes Stripe. Nikdy neukládáme vaše platební údaje.',
  'subscription.upgradeTo': 'Upgrade na',
  'subscription.customPricing': 'Individuální ceny',
  'subscription.whatsIncluded': 'Co je zahrnuto:',
  'subscription.save20Yearly': 'Ušetřete 20% s roční fakturací',
  'subscription.subscribeKofi': 'Předplatit na Ko-fi',
  'subscription.subscribeStripe': 'Předplatit přes Stripe',
  'subscription.afterPayment': 'Po platbě bude váš plán aktualizován do 24 hodin.',
  'subscription.afterPaymentStripe': 'Budete přesměrováni na bezpečnou platbu Stripe. Váš plán bude aktivován automaticky.',
  'subscription.secureVia': 'Bezpečná platba přes Ko-fi. Kdykoli zrušitelné.',
  'subscription.secureViaStripe': 'Bezpečné platby zpracované přes Stripe. Kdykoli zrušitelné.',
  'subscription.contactForAddon': 'Kontaktujte nás',
  'subscription.contactSalesDesc': 'Ozveme se vám do 24 hodin s individuální nabídkou.',
  'subscription.compareFeatures': 'Porovnat všechny funkce',
  'subscription.feature': 'Funkce',
  'subscription.coreLimits': 'Základní limity',
  'subscription.teamMembers': 'Členové týmu',
  'subscription.aiFeatures': 'AI funkce',
  'subscription.duplicateDetect': 'Detekce duplikátů',
  'subscription.aiReportsMonth': 'AI reporty/měsíc',
  'subscription.security': 'Zabezpečení',
  'subscription.twoFactorAuth': 'Dvoufaktorové ověření',
  'subscription.auditLogs': 'Audit logy',
  'subscription.sso': 'SSO',
  'subscription.support': 'Podpora',
  'subscription.emailSupportLabel': 'E-mailová podpora',
  'subscription.dedicatedManager': 'Dedikovaný manažer',
  'subscription.faqTitle': 'Často kladené otázky',
  'subscription.faqSwitchQ': 'Mohu kdykoli změnit plán?',
  'subscription.faqSwitchA': 'Ano! Můžete kdykoli upgradovat nebo downgradovat. Při upgradu bude zbývající částka poměrně přepočítána. Při downgradu se nová sazba uplatní v dalším fakturačním cyklu.',
  'subscription.faqDowngradeQ': 'Co se stane s mými daty při downgradu?',
  'subscription.faqDowngradeA': 'Vaše data jsou v bezpečí. Pokud překročíte limity nového plánu, budete mít 30 dní na snížení využití nebo opětovný upgrade.',
  'subscription.faqRefundQ': 'Nabízíte vrácení peněz?',
  'subscription.faqRefundA': 'Ano, nabízíme 30denní záruku vrácení peněz pro všechny placené plány. Kontaktujte nás pro plné vrácení.',
  'subscription.faqPaymentQ': 'Jaké platební metody přijímáte?',
  'subscription.faqPaymentA': 'Přijímáme platby přes Stripe (kreditní/debetní karty). Enterprise plány lze fakturovat i fakturou.',
  'subscription.faqTrialQ': 'Je k dispozici bezplatná zkušební verze?',
  'subscription.faqTrialA': 'Ano! Všechny placené plány zahrnují 14denní bezplatnou zkušební verzi. Kreditní karta není vyžadována.',
  'subscription.faqStorageQ': 'Mohu přidat úložiště později?',
  'subscription.faqStorageA': 'Samozřejmě! Doplňky úložiště si můžete kdykoli vyžádat. Kontaktujte nás pro podrobnosti.',
  'subscription.trialActive': 'Zkušební verze aktivní',
  'subscription.trialDaysLeft': 'dní zbývá',
  'subscription.upgradeNow': 'Upgradovat nyní',
  'subscription.trialAvailable': 'Vyzkoušejte jakýkoli plán zdarma na 14 dní!',
  'subscription.trialAvailableDesc': 'Kreditní karta není potřeba. Vyzkoušejte všechny prémiové funkce bez rizika.',
  'subscription.startTrial': 'Zahájit zkušební verzi',
  'subscription.startFreeTrial': 'Zahájit 14denní zkušební verzi',
  'subscription.trialActiveLabel': 'Zkušební verze',
  'subscription.trialUsed': 'Zkušební verze již využita',
  'subscription.trialExpired': 'Vaše zkušební verze skončila. Upgradujte pro zachování prémiových funkcí.',
  'subscription.cancelTrial': 'Zrušit zkušební verzi',
  'subscription.cancelTrialConfirm': 'Opravdu chcete zrušit svou bezplatnou zkušební verzi? Budete okamžitě přepnuti na bezplatný plán.',
  'subscription.ctaTitle': 'Připraveni začít?',
  'subscription.ctaDesc': 'Připojte se k tisícům uživatelů spravujících své předměty naší platformou.',
  'subscription.startFree': 'Začít zdarma',
  'subscription.contactUs': 'Kontaktovat nás',
  'subscription.planTagline.FREE': 'Navždy zdarma',
  'subscription.planTagline.STARTER': 'Začněte sbírat chytřeji',
  'subscription.planTagline.COLLECTOR': 'Nejoblíbenější pro sběratele',
  'subscription.planTagline.PRO': 'Plně vybavené pro profesionály',
  'subscription.planTagline.BUSINESS': 'Navrženo pro týmy',
  'subscription.planTagline.ULTIMATE': 'Bez limitů, bez kompromisů',
  'subscription.planTagline.ENTERPRISE': 'Pojďme si promluvit',
  'subscription.planTagline.CUSTOM': 'Přizpůsobeno vašim potřebám',
  'subscription.planDesc.FREE': 'Začněte — katalogizujte až 30 položek se základními funkcemi.',
  'subscription.planDesc.STARTER': 'Pro nadšence a malé sběratele, kteří to myslí vážně.',
  'subscription.planDesc.COLLECTOR': 'Pro oddané sběratele, kteří potřebují více prostoru a chytřejší nástroje.',
  'subscription.planDesc.PRO': 'Pro pokročilé uživatele a profesionály spravující velké inventáře.',
  'subscription.planDesc.BUSINESS': 'Pro týmy a organizace, které potřebují škálování, bezpečnost a kontrolu.',
  'subscription.planDesc.ULTIMATE': 'Vrchol — neomezené vše s prémiovou podporou a přizpůsobením.',
  'subscription.planDesc.ENTERPRISE': 'Plán na míru pro velké organizace se specifickými potřebami.',

  // Přednosti plánu
  'subscription.planHighlight.FREE.0': 'Až 30 položek',
  'subscription.planHighlight.FREE.1': '300 MB úložiště',
  'subscription.planHighlight.FREE.2': '5 fotek na položku',
  'subscription.planHighlight.FREE.3': 'AI kategorizace',
  'subscription.planHighlight.FREE.4': 'Základní analytika',
  'subscription.planHighlight.FREE.5': 'Dvoufaktorové ověření',
  'subscription.planHighlight.FREE.6': 'Základní analytika',
  'subscription.planHighlight.FREE.7': '10 AI zpráv/měsíc',
  'subscription.planHighlight.STARTER.0': 'Až 250 položek',
  'subscription.planHighlight.STARTER.1': '2 GB úložiště',
  'subscription.planHighlight.STARTER.2': '10 fotek na položku',
  'subscription.planHighlight.STARTER.3': 'AI kategorizace',
  'subscription.planHighlight.STARTER.4': 'Export dat',
  'subscription.planHighlight.STARTER.5': 'Komentáře',
  'subscription.planHighlight.COLLECTOR.0': 'Až 1 000 položek',
  'subscription.planHighlight.COLLECTOR.1': '10 GB úložiště',
  'subscription.planHighlight.COLLECTOR.2': '20 fotek na položku',
  'subscription.planHighlight.COLLECTOR.3': 'AI predikce hodnoty',
  'subscription.planHighlight.COLLECTOR.4': 'Detekce duplikátů',
  'subscription.planHighlight.COLLECTOR.5': 'Synchronizace v reálném čase',
  'subscription.planHighlight.COLLECTOR.6': '30denní audit logy',
  'subscription.planHighlight.PRO.0': 'Až 10 000 položek',
  'subscription.planHighlight.PRO.1': '50 GB úložiště',
  'subscription.planHighlight.PRO.2': '40 fotek na položku',
  'subscription.planHighlight.PRO.3': 'Neomezené AI reporty',
  'subscription.planHighlight.PRO.4': 'Pokročilá analytika a reporty',
  'subscription.planHighlight.PRO.5': 'API přístup (120 pož./min)',
  'subscription.planHighlight.PRO.6': 'Prioritní podpora',
  'subscription.planHighlight.PRO.7': 'Týmová spolupráce (10)',
  'subscription.planHighlight.BUSINESS.0': 'Neomezené položky',
  'subscription.planHighlight.BUSINESS.1': '200 GB úložiště',
  'subscription.planHighlight.BUSINESS.2': '60 fotek na položku',
  'subscription.planHighlight.BUSINESS.3': 'Až 25 členů týmu',
  'subscription.planHighlight.BUSINESS.4': 'SSO / Single Sign-On',
  'subscription.planHighlight.BUSINESS.5': 'Vlastní branding',
  'subscription.planHighlight.BUSINESS.6': '365denní audit logy',
  'subscription.planHighlight.BUSINESS.7': 'Webhooky & API (300 pož./min)',
  'subscription.planHighlight.BUSINESS.8': 'Prioritní podpora',
  'subscription.planHighlight.ULTIMATE.0': 'Vše neomezeně',
  'subscription.planHighlight.ULTIMATE.1': '1 TB úložiště',
  'subscription.planHighlight.ULTIMATE.2': 'Neomezené fotky na položku',
  'subscription.planHighlight.ULTIMATE.3': 'Neomezení členové týmu',
  'subscription.planHighlight.ULTIMATE.4': 'Osobní account manažer',
  'subscription.planHighlight.ULTIMATE.5': 'SLA garance',
  'subscription.planHighlight.ULTIMATE.6': 'White-label možnost',
  'subscription.planHighlight.ULTIMATE.7': 'Vlastní doména',
  'subscription.planHighlight.ULTIMATE.8': 'Neomezený API přístup',
  'subscription.planHighlight.ENTERPRISE.0': 'Vše neomezeně',
  'subscription.planHighlight.ENTERPRISE.1': 'Vlastní úložiště',
  'subscription.planHighlight.ENTERPRISE.2': 'Osobní account manažer',
  'subscription.planHighlight.ENTERPRISE.3': 'SLA garance',
  'subscription.planHighlight.ENTERPRISE.4': 'White-label možnost',
  'subscription.planHighlight.ENTERPRISE.5': 'Vlastní doména',
  'subscription.planHighlight.ENTERPRISE.6': 'On-premise nasazení',
  'subscription.planHighlight.ENTERPRISE.7': 'Vlastní integrace',
  'subscription.planHighlight.ENTERPRISE.8': 'Onboarding na míru',
  'subscription.planHighlight.CUSTOM.0': 'Vše neomezeně',
  'subscription.planHighlight.CUSTOM.1': 'Vlastní úložiště',
  'subscription.planHighlight.CUSTOM.2': 'Osobní account manažer',
  'subscription.planHighlight.CUSTOM.3': 'SLA garance',
  'subscription.planHighlight.CUSTOM.4': 'White-label možnost',
  'subscription.planHighlight.CUSTOM.5': 'Vlastní doména',
  'subscription.planHighlight.CUSTOM.6': 'On-premise nasazení',
  'subscription.planHighlight.CUSTOM.7': 'Vlastní integrace',
  'subscription.planHighlight.CUSTOM.8': 'Onboarding na míru',

  // ── Collections ──
  'collections.title': 'Kolekce',
  'collections.subtitle': 'Vytvořte vlastní skupiny a organizujte položky podle svého',
  'collections.newCollection': 'Nová kolekce',
  'collections.searchPlaceholder': 'Hledat kolekce...',
  'collections.customOrder': 'Vlastní pořadí',
  'collections.name': 'Název',
  'collections.recentlyUpdated': 'Naposledy aktualizované',
  'collections.mostItems': 'Nejvíce položek',
  'collections.collections': 'Kolekce',
  'collections.totalItems': 'Celkem položek',
  'collections.active': 'Aktivní',
  'collections.items': 'položka',
  'collections.itemsPlural': 'položek',
  'collections.updated': 'Aktualizováno',
  'collections.noMatchingCollections': 'Žádné odpovídající kolekce',
  'collections.tryDifferentSearch': 'Zkuste jiný hledaný výraz',
  'collections.noCollectionsYet': 'Zatím žádné kolekce',
  'collections.noCollectionsDesc': 'Vytvořte svou první kolekci a začněte organizovat položky po svém',
  'collections.createCollection': 'Vytvořit kolekci',
  'collections.editCollection': 'Upravit kolekci',
  'collections.createNewCollection': 'Vytvořit novou kolekci',
  'collections.collectionName': 'Název kolekce *',
  'collections.collectionNamePlaceholder': 'např. Vintage hodinky, Vzácné knihy...',
  'collections.description': 'Popis',
  'collections.descriptionPlaceholder': 'O čem je tato kolekce?',
  'collections.color': 'Barva',
  'collections.saveChanges': 'Uložit změny',
  'collections.cancel': 'Zrušit',
  'collections.addItems': 'Přidat položky',
  'collections.addRemoveItems': 'Přidat / odebrat položky',
  'collections.done': 'Hotovo',
  'collections.itemsInCollection': '{count} položek v této kolekci',
  'collections.loadingItems': 'Načítání položek...',
  'collections.noItemsInCollection': 'V této kolekci nejsou žádné položky',
  'collections.noItemsDesc': 'Klikněte na "Přidat položky" pro přidání položek z vaší knihovny',
  'collections.noItemsFound': 'Žádné položky nenalezeny',
  'collections.deleteConfirm': 'Smazat tuto kolekci? Položky nebudou smazány.',
  'collections.loading': 'Načítání kolekcí...',
  'collections.icon': 'Ikona',
  'collections.item': 'položka',
  'collections.addItem': 'Přidat položku',
  'collections.itemName': 'Název položky',
  'collections.itemNamePlaceholder': 'Název položky...',
  'collections.itemType': 'Typ',
  'collections.noItems': 'Zatím žádné položky. Přidejte první položku výše.',
  'collections.type.document': 'Dokument',
  'collections.type.image': 'Obrázek',
  'collections.type.link': 'Odkaz',
  'collections.type.note': 'Poznámka',
  'collections.type.video': 'Video',
  'collections.type.audio': 'Audio',
  'collections.type.spreadsheet': 'Tabulka',
  'collections.type.presentation': 'Prezentace',
  'collections.type.report': 'Zpráva',
  'collections.type.task': 'Úkol',
  'collections.type.invoice': 'Faktura',
  'collections.type.contract': 'Smlouva',
  'collections.type.email': 'E-mail',
  'collections.type.contact': 'Kontakt',
  'collections.type.blueprint': 'Plán',
  'collections.type.code': 'Kód',

  // ── Duplicate Finder ──
  'duplicates.title': 'Hledač duplikátů',
  'duplicates.subtitle': 'Nahrajte fotografii pro nalezení odpovídajících položek ve vaší sbírce a odhad hodnoty',
  'duplicates.dropHere': 'Přetáhněte obrázek sem nebo klikněte pro nahrání',
  'duplicates.supportedFormats': 'Podporuje JPG, PNG, GIF, WebP — do 20MB',
  'duplicates.scanning': 'Prohledávání sbírky...',
  'duplicates.scanButton': 'Hledat duplikáty',
  'duplicates.aiAnalysis': 'AI analýza',
  'duplicates.description': 'Popis',
  'duplicates.category': 'Kategorie',
  'duplicates.brand': 'Značka',
  'duplicates.model': 'Model',
  'duplicates.condition': 'Stav',
  'duplicates.estimatedValue': 'Odhadovaná tržní hodnota',
  'duplicates.low': 'Nízká',
  'duplicates.mid': 'Střední',
  'duplicates.high': 'Vysoká',
  'duplicates.matchesFound': '{count} shoda/shod nalezeno',
  'duplicates.noDuplicates': 'Žádné duplikáty nenalezeny',
  'duplicates.noDuplicatesDesc': 'Tato položka se zdá, že neodpovídá ničemu ve vaší sbírce. Může to být nová položka!',
  'duplicates.highMatch': 'Vysoká shoda',
  'duplicates.possibleMatch': 'Možná shoda',
  'duplicates.lowMatch': 'Nízká shoda',
  'duplicates.estValue': 'Odhadovaná hodnota:',
  'duplicates.selectImage': 'Prosím vyberte soubor obrázku',

  // ── AI Assistant ──
  'ai.newChat': 'Nový chat',
  'ai.loading': 'Načítání...',
  'ai.noChats': 'Zatím žádné konverzace',
  'ai.startChat': 'Začněte nový chat',
  'ai.typeMessage': 'Napište zprávu...',
  'ai.maxImages': 'Maximálně 5 obrázků na zprávu',
  'ai.welcomeTitle': 'ELVOAQ AI Asistent',
  'ai.welcomeDesc': 'Zeptejte se mě na cokoli o vaší sbírce, hodnotách položek nebo tipech na organizaci.',
  'ai.deleteConfirm': 'Smazat tuto konverzaci?',

  // ── Hidden Folders ──
  'hiddenFolders.title': 'Skryté složky',
  'hiddenFolders.subtitle': 'Složky chráněné heslem pro zabezpečení vašich soukromých položek',
  'hiddenFolders.newFolder': 'Nová složka',
  'hiddenFolders.loading': 'Načítání složek...',
  'hiddenFolders.noFolders': 'Zatím žádné skryté složky',
  'hiddenFolders.noFoldersDesc': 'Vytvořte složku chráněnou heslem pro zabezpečení vašich nejsoukromějších položek.',
  'hiddenFolders.createFirst': 'Vytvořit první složku',
  'hiddenFolders.open': 'Otevřít',
  'hiddenFolders.items': 'položek',
  'hiddenFolders.back': 'Zpět',
  'hiddenFolders.addItem': 'Přidat položku',
  'hiddenFolders.noItems': 'V této složce zatím nejsou žádné položky',
  'hiddenFolders.addFirstItem': 'Přidat první položku',
  'hiddenFolders.viewItem': 'Zobrazit položku',
  'hiddenFolders.removeFromFolder': 'Odebrat ze složky',
  'hiddenFolders.enterPassword': 'Zadejte heslo',
  'hiddenFolders.passwordProtected': 'Tato složka je chráněná heslem. Zadejte heslo pro zobrazení obsahu.',
  'hiddenFolders.folderPassword': 'Heslo složky',
  'hiddenFolders.verifying': 'Ověřování...',
  'hiddenFolders.viewFolder': 'Zobrazit složku',
  'hiddenFolders.createFolder': 'Vytvořit skrytou složku',
  'hiddenFolders.folderName': 'Název složky *',
  'hiddenFolders.folderNamePlaceholder': 'např. Cennosti, Soukromá sbírka',
  'hiddenFolders.descriptionLabel': 'Popis',
  'hiddenFolders.descriptionPlaceholder': 'Volitelný popis...',
  'hiddenFolders.iconLabel': 'Ikona',
  'hiddenFolders.colorLabel': 'Barva',
  'hiddenFolders.password': 'Heslo *',
  'hiddenFolders.confirmPassword': 'Potvrdit heslo *',
  'hiddenFolders.creating': 'Vytváření...',
  'hiddenFolders.editFolder': 'Upravit složku',
  'hiddenFolders.changeFolderPassword': 'Změnit heslo složky',
  'hiddenFolders.leaveEmptyKeep': 'Ponechte prázdné pro zachování aktuálního hesla',
  'hiddenFolders.currentPassword': 'Aktuální heslo',
  'hiddenFolders.newPassword': 'Nové heslo',
  'hiddenFolders.confirmNewPassword': 'Potvrdit nové heslo',
  'hiddenFolders.saveChanges': 'Uložit změny',
  'hiddenFolders.deleteFolder': 'Smazat složku',
  'hiddenFolders.deleteConfirm': 'Opravdu chcete smazat',
  'hiddenFolders.deleteDesc': '? Složka bude odstraněna, ale položky zůstanou zachovány.',
  'hiddenFolders.deletePasswordPrompt': 'Zadejte heslo složky pro potvrzení',
  'hiddenFolders.cancel': 'Zrušit',
  'hiddenFolders.addItemTo': 'Přidat položku do',
  'hiddenFolders.searchItems': 'Hledat položky...',
  'hiddenFolders.enterPasswordToAdd': 'Zadejte heslo složky pro přidání této položky:',
  'hiddenFolders.confirm': 'Potvrdit',
  'hiddenFolders.alreadyAdded': 'Již přidáno',
  'hiddenFolders.noResults': 'Žádné položky nenalezeny',
  'hiddenFolders.allCategories': 'Všechny kategorie',
  'hiddenFolders.favoritesOnly': 'Pouze oblíbené',
  'hiddenFolders.favorites': 'Oblíbené',
  'hiddenFolders.passwordsNoMatch': 'Hesla se neshodují',
  'hiddenFolders.passwordMinLength': 'Heslo musí mít alespoň 4 znaky',
  'hiddenFolders.folderCreated': 'Skrytá složka vytvořena!',
  'hiddenFolders.itemAdded': 'Položka přidána do skryté složky',
  'hiddenFolders.itemRemoved': 'Položka odebrána ze skryté složky',
  'hiddenFolders.folderUpdated': 'Složka úspěšně aktualizována',
  'hiddenFolders.folderPasswordChanged': 'Heslo složky změněno',
  'hiddenFolders.folderDeleted': 'Složka úspěšně smazána',
  'hiddenFolders.nameLabel': 'Název',
  'hiddenFolders.currentPasswordPlaceholder': 'Zadejte aktuální heslo',
  'hiddenFolders.newPasswordPlaceholder': 'Nové heslo (min 4 znaky)',
  'hiddenFolders.confirmPasswordPlaceholder': 'Zadejte nové heslo znovu',
  'hiddenFolders.folderPasswordPlaceholder': 'Heslo složky',
  'hiddenFolders.confirmFolderPasswordPlaceholder': 'Potvrďte heslo',
  'hiddenFolders.currentPasswordRequired': 'Aktuální heslo je vyžadováno',
  'hiddenFolders.newPasswordMinLength': 'Nové heslo musí mít alespoň 4 znaky',
  'hiddenFolders.newPasswordsNoMatch': 'Nová hesla se neshodují',
  'hiddenFolders.deleteButton': 'Smazat složku',
  'hiddenFolders.addItemButton': 'Přidat položku',
  'hiddenFolders.nameAndPasswordRequired': 'Název a heslo jsou povinné',
  'hiddenFolders.enterFolderPassword': 'Zadejte heslo složky',
  'hiddenFolders.wrongPassword': 'Špatné heslo',
  'hiddenFolders.failedToCreateFolder': 'Nepodařilo se vytvořit složku',
  'hiddenFolders.failedToAddItem': 'Nepodařilo se přidat položku',
  'hiddenFolders.failedToRemoveItem': 'Nepodařilo se odebrat položku',
  'hiddenFolders.failedToUpdateFolder': 'Nepodařilo se aktualizovat složku',
  'hiddenFolders.failedToDeleteFolder': 'Nepodařilo se smazat složku',
  'hiddenFolders.folderUpdatedAndPasswordChanged': 'Složka aktualizována & heslo změněno',

  // ── Data Transfer ──
  'dataTransfer.title': 'Import & Export',
  'dataTransfer.subtitle': 'Přeneste svá data ve formátu CSV nebo JSON',
  'dataTransfer.export': 'Export',
  'dataTransfer.import': 'Import',
  'dataTransfer.exportTitle': 'Exportovat vaše položky',
  'dataTransfer.exportDesc': 'Stáhněte si data položek ve vámi preferovaném formátu.',
  'dataTransfer.format': 'Formát:',
  'dataTransfer.fieldsToInclude': 'Pole k zahrnutí:',
  'dataTransfer.selectAll': 'Vybrat vše',
  'dataTransfer.deselectAll': 'Zrušit výběr',
  'dataTransfer.exporting': 'Exportování...',
  'dataTransfer.download': 'Stáhnout',
  'dataTransfer.importTitle': 'Importovat položky',
  'dataTransfer.importDesc': 'Nahrajte soubor CSV nebo JSON pro import položek do vaší sbírky.',
  'dataTransfer.importMode': 'Režim importu:',
  'dataTransfer.addToExisting': 'Přidat k existujícím',
  'dataTransfer.addToExistingDesc': 'Nové položky budou přidány vedle vašich aktuálních položek',
  'dataTransfer.overwriteAll': 'Přepsat vše',
  'dataTransfer.overwriteAllDesc': 'Aktuální položky budou archivovány a nahrazeny importovanými daty',
  'dataTransfer.overwriteWarning': 'Upozornění: Toto archivuje všechny vaše aktuální položky. Lze je obnovit z koše.',
  'dataTransfer.clickToSelect': 'Klikněte pro výběr souboru CSV nebo JSON',
  'dataTransfer.supported': 'Podporováno: .csv, .json',
  'dataTransfer.preview': 'Náhled',
  'dataTransfer.itemsFound': 'položek nalezeno',
  'dataTransfer.showingOf': 'Zobrazeno 5 z {count} položek',
  'dataTransfer.importing': 'Importování...',
  'dataTransfer.importItems': 'Importovat {count} položek',
  'dataTransfer.confirmOverwrite': 'Potvrdit přepsání',
  'dataTransfer.overwriteBody1': 'Chystáte se přepsat všechny existující položky {count} importovanými položkami.',
  'dataTransfer.overwriteBody2': 'Vaše aktuální položky budou přesunuty do koše a lze je později obnovit.',
  'dataTransfer.yesOverwrite': 'Ano, přepsat',
  'dataTransfer.selectOneField': 'Vyberte alespoň jedno pole pro export',
  'dataTransfer.noDataToImport': 'Žádná data k importu',
  'dataTransfer.exportSuccess': 'Export stažen jako {format} soubor!',
  'dataTransfer.importSuccess': 'Úspěšně importováno {count} položek!',
  'dataTransfer.overwriteNote': '(Předchozí položky byly archivovány)',

  // ── Smart Hub ──
  'smartHub.loading': 'Načítání Smart Hub...',
  'smartHub.title': 'Smart Hub',
  'smartHub.subtitle': 'Přehledy a správa s umělou inteligencí',
  'smartHub.analyzing': 'Analyzuji...',
  'smartHub.aiAnalyze': 'AI Analýza',
  'smartHub.overview': 'Přehled',
  'smartHub.reminders': 'Připomínky',
  'smartHub.notes': 'Poznámky',
  'smartHub.insights': 'Postřehy',
  'smartHub.aiReport': 'AI Report',
  'smartHub.totalItems': 'Celkem položek',
  'smartHub.collectionValue': 'Hodnota sbírky',
  'smartHub.categories': 'Kategorie',
  'smartHub.activeReminders': 'Aktivní připomínky',
  'smartHub.aiSummary': 'AI Shrnutí',
  'smartHub.topCategories': 'Top kategorie:',
  'smartHub.mostValuable': 'Nejcennější:',
  'smartHub.summaryContains': 'Vaše sbírka obsahuje {count} položek.',
  'smartHub.summaryTotalValue': 'Celková hodnota: {total} (průměr: {avg}).',
  'smartHub.summaryTopCategory': 'Top kategorie: {name} ({count} položek).',
  'smartHub.summaryRecentItems': '{count} položek přidáno tento týden.',
  'smartHub.summaryMostValuable': 'Nejcennější: "{name}" ({value}).',
  'smartHub.summaryEmpty': 'Vaše sbírka je prázdná. Začněte přidáním první položky!',
  'smartHub.overdueReminders': 'zpožděných připomínek',
  'smartHub.insightsAvailable': 'dostupných postřehů',
  'smartHub.pinnedNotes': 'připnutých poznámek',
  'smartHub.all': 'Vše',
  'smartHub.active': 'Aktivní',
  'smartHub.completed': 'Dokončené',
  'smartHub.addReminder': 'Přidat připomínku',
  'smartHub.editReminder': 'Upravit připomínku',
  'smartHub.newReminder': 'Nová připomínka',
  'smartHub.reminderTitle': 'Název připomínky',
  'smartHub.descriptionOptional': 'Popis (volitelný)',
  'smartHub.custom': 'Vlastní',
  'smartHub.maintenance': 'Údržba',
  'smartHub.review': 'Kontrola',
  'smartHub.insurance': 'Pojištění',
  'smartHub.warranty': 'Záruka',
  'smartHub.low': 'Nízká',
  'smartHub.medium': 'Střední',
  'smartHub.high': 'Vysoká',
  'smartHub.urgent': 'Urgentní',
  'smartHub.unassigned': 'Nepřiřazeno',
  'smartHub.me': 'Já',
  'smartHub.assignHint': 'Přiřadit: "Já" znamená, že se zobrazí na vašem seznamu. Kolegové jsou členové vašeho workspace.',
  'smartHub.assignedTo': 'Přiřazeno',
  'smartHub.cancel': 'Zrušit',
  'smartHub.saveChanges': 'Uložit změny',
  'smartHub.createReminder': 'Vytvořit připomínku',
  'smartHub.noReminders': 'Žádné připomínky',
  'smartHub.noRemindersDesc': 'Vytvořte jednu nebo klikněte na "AI Analýza" pro vygenerování návrhů',
  'smartHub.allNotes': 'Všechny poznámky',
  'smartHub.pinned': 'Připnuté',
  'smartHub.aiGenerated': 'Generované AI',
  'smartHub.addNote': 'Přidat poznámku',
  'smartHub.editNote': 'Upravit poznámku',
  'smartHub.newNote': 'Nová poznámka',
  'smartHub.writeNote': 'Napište svou poznámku...',
  'smartHub.addTag': 'Přidat štítek...',
  'smartHub.add': 'Přidat',
  'smartHub.saveNote': 'Uložit poznámku',
  'smartHub.noNotes': 'Zatím žádné poznámky',
  'smartHub.noNotesDesc': 'Vytvořte poznámku pro sledování důležitých informací',
  'smartHub.noInsights': 'Žádné aktivní postřehy',
  'smartHub.noInsightsDesc': 'Klikněte na "AI Analýza" pro prohledání vaší sbírky a doporučení',
  'smartHub.relatedItems': 'Související položky:',
  'smartHub.confidence': 'Spolehlivost:',
  'smartHub.generatingReport': 'Generování AI reportu...',
  'smartHub.reportTitle': 'Analytický report sbírky',
  'smartHub.generated': 'Vygenerováno:',
  'smartHub.regenerate': 'Přegenerovat',
  'smartHub.items': 'Položky',
  'smartHub.value': 'Hodnota',
  'smartHub.avgValue': 'Prům. hodnota',
  'smartHub.noReport': 'Zatím nebyl vygenerován žádný report',
  'smartHub.generateReport': 'Vygenerovat AI report',
  'smartHub.daysOverdue': '{count} dní po termínu',
  'smartHub.today': 'Dnes',
  'smartHub.tomorrow': 'Zítra',
  'smartHub.inDays': 'Za {count} dní',
  'smartHub.inWeeks': 'Za {count} týdnů',
  'smartHub.inMonths': 'Za {count} měsíců',
  'smartHub.lockedItem': 'Zamknutá položka',
  'smartHub.markIncomplete': 'Označit jako nedokončené',
  'smartHub.markComplete': 'Označit jako dokončené',
  'smartHub.editTitle': 'Upravit',
  'smartHub.deleteTitle': 'Smazat',
  'smartHub.unpin': 'Odepnout',
  'smartHub.pinInsight': 'Připnout',
  'smartHub.dismiss': 'Zavřít',
  'smartHub.missing': 'Chybí:',
  'smartHub.member': 'Člen',

  // Calendar
  'nav.calendar': 'Kalendář',
  'calendar.title': 'Kalendář',
  'calendar.today': 'Dnes',
  'calendar.day': 'Den',
  'calendar.week': 'Týden',
  'calendar.month': 'Měsíc',
  'calendar.newEvent': 'Nová událost',
  'calendar.editEvent': 'Upravit událost',
  'calendar.eventTitle': 'Název',
  'calendar.titlePlaceholder': 'Přidat název...',
  'calendar.date': 'Datum',
  'calendar.startTime': 'Začátek',
  'calendar.endTime': 'Konec',
  'calendar.allDay': 'Celý den',
  'calendar.location': 'Místo',
  'calendar.locationPlaceholder': 'Přidat místo...',
  'calendar.description': 'Popis',
  'calendar.descriptionPlaceholder': 'Přidat popis...',
  'calendar.color': 'Barva',
  'calendar.save': 'Uložit',
  'calendar.create': 'Vytvořit',
  'calendar.cancel': 'Zrušit',
  'calendar.edit': 'Upravit',
  'calendar.delete': 'Smazat',
  'calendar.more': 'více',
  'calendar.mon': 'Po',
  'calendar.tue': 'Út',
  'calendar.wed': 'St',
  'calendar.thu': 'Čt',
  'calendar.fri': 'Pá',
  'calendar.sat': 'So',
  'calendar.sun': 'Ne',
  'calendar.january': 'Leden',
  'calendar.february': 'Únor',
  'calendar.march': 'Březen',
  'calendar.april': 'Duben',
  'calendar.may': 'Květen',
  'calendar.june': 'Červen',
  'calendar.july': 'Červenec',
  'calendar.august': 'Srpen',
  'calendar.september': 'Září',
  'calendar.october': 'Říjen',
  'calendar.november': 'Listopad',
  'calendar.december': 'Prosinec',
  'calendar.year': 'Rok',
  'calendar.deleteConfirm': 'Opravdu chcete smazat tuto událost?',
  'calendar.trash': 'Nedávno smazáno',
  'calendar.trashDesc': 'Události jsou trvale smazány po 30 dnech',
  'calendar.trashEmpty': 'Žádné smazáné události',
  'calendar.trashEmptyDesc': 'Smazáné události se zobrazí zde',
  'calendar.restore': 'Obnovit',
  'calendar.deleteForever': 'Trvale smazat',
  'calendar.loading': 'Načítání...',
  'calendar.searchEvents': 'Hledat události...',
  'calendar.noEvents': 'Žádné události nenalezeny',
  'calendar.upcoming': 'Nadcházející',
  'calendar.myCalendars': 'Moje kalendáře',
  'calendar.addCategory': 'Přidat kategorii',
  'calendar.categoryName': 'Název kategorie...',
  'calendar.category': 'Kategorie',
  'calendar.sunday': 'Neděle',
  'calendar.monday': 'Pondělí',
  'calendar.tuesday': 'Úterý',
  'calendar.wednesday': 'Středa',
  'calendar.thursday': 'Čtvrtek',
  'calendar.friday': 'Pátek',
  'calendar.saturday': 'Sobota',
  'calendar.cat.meetings': 'Schůzky',
  'calendar.cat.deadlines': 'Termíny',
  'calendar.cat.reminders': 'Připomínky',
  'calendar.cat.reviews': 'Recenze',
  'calendar.cat.presentations': 'Prezentace',
  'calendar.cat.training': 'Školení',
  'calendar.cat.travel': 'Cestování',
  'calendar.cat.holidays': 'Svátky',

  // ── User Management (CS) ──────────────────────────────────────
  'userMgmt.empty.noWorkspaceTitle': 'Žádný pracovní prostor nevybrán',
  'userMgmt.empty.noWorkspaceDesc': 'Vyberte nebo vytvořte pracovní prostor pro správu členů',
  'userMgmt.empty.goToWorkspaces': 'Přejít na pracovní prostory',
  'userMgmt.header.badge': 'Lidé a týmy',
  'userMgmt.header.description': 'Správa členů, rolí, týmů a přístupových oprávnění',
  'userMgmt.header.invitePeople': 'Pozvat lidi',
  'userMgmt.header.convertToOrg': 'Převést na organizaci',
  'userMgmt.header.transferOwnership': 'Převést vlastnictví',
  'userMgmt.stats.members': 'Členové',
  'userMgmt.stats.pending': 'Čekající',
  'userMgmt.tabs.members': 'Členové',
  'userMgmt.tabs.invitations': 'Pozvánky',
  'userMgmt.tabs.teams': 'Týmy',
  'userMgmt.tabs.roleGuide': 'Průvodce rolemi',
  'userMgmt.tabs.suggestions': 'Návrhy',
  'userMgmt.members.searchPlaceholder': 'Hledat podle jména, e-mailu nebo role...',
  'userMgmt.members.sortByRole': 'Podle role',
  'userMgmt.members.sortByName': 'Podle jména',
  'userMgmt.members.sortByJoined': 'Nedávno připojení',
  'userMgmt.members.loading': 'Načítání členů...',
  'userMgmt.members.notFoundTitle': 'Žádní členové nenalezeni',
  'userMgmt.members.noResults': 'Žádné výsledky pro „{query}"',
  'userMgmt.members.noMembers': 'Žádní členové v tomto pracovním prostoru',
  'userMgmt.members.unknownName': 'Neznámý',
  'userMgmt.members.changeRoleTooltip': 'Změnit roli',
  'userMgmt.members.removeMemberTooltip': 'Odebrat člena',
  'userMgmt.invitations.title': 'Čekající pozvánky',
  'userMgmt.invitations.sendInvite': 'Poslat pozvánku',
  'userMgmt.invitations.loading': 'Načítání pozvánek...',
  'userMgmt.invitations.emptyTitle': 'Žádné čekající pozvánky',
  'userMgmt.invitations.emptyDesc': 'Pozvěte lidi ke spolupráci v pracovním prostoru',
  'userMgmt.invitations.inviteSomeone': 'Pozvat někoho',
  'userMgmt.invitations.expires': 'Vyprší',
  'userMgmt.invitations.revokeTooltip': 'Zrušit',
  'userMgmt.teams.title': 'Týmy',
  'userMgmt.teams.newTeam': 'Nový tým',
  'userMgmt.teams.loading': 'Načítání týmů...',
  'userMgmt.teams.emptyTitle': 'Zatím žádné týmy',
  'userMgmt.teams.emptyDesc': 'Týmy pomáhají organizovat členy a spravovat přístup k položkám',
  'userMgmt.teams.createFirst': 'Vytvořit první tým',
  'userMgmt.teams.noDescription': 'Bez popisu',
  'userMgmt.roles.title': 'Hierarchie rolí a oprávnění',
  'userMgmt.roles.description': 'Pochopte, co každá role může dělat ve vašem pracovním prostoru',
  'userMgmt.roles.memberCount': 'člen',
  'userMgmt.roles.membersCount': 'členů',
  'userMgmt.roles.permissionsHeading': 'Oprávnění',
  'userMgmt.perm.fullWorkspaceControl': 'Plná kontrola pracovního prostoru',
  'userMgmt.perm.transferOwnership': 'Převést vlastnictví',
  'userMgmt.perm.deleteWorkspace': 'Smazat pracovní prostor',
  'userMgmt.perm.manageBilling': 'Spravovat fakturaci',
  'userMgmt.perm.allAdminPermissions': 'Všechna oprávnění správce',
  'userMgmt.perm.manageSettings': 'Spravovat nastavení pracovního prostoru',
  'userMgmt.perm.inviteRemoveMembers': 'Pozvat/odebrat členy',
  'userMgmt.perm.assignRoles': 'Přiřadit role (až po Manažera)',
  'userMgmt.perm.viewAllItems': 'Zobrazit všechny položky a analýzy',
  'userMgmt.perm.configureAI': 'Konfigurovat AI funkce',
  'userMgmt.perm.createEditItems': 'Vytvářet a upravovat položky',
  'userMgmt.perm.approveSuggestions': 'Schvalovat návrhy',
  'userMgmt.perm.moderateComments': 'Moderovat komentáře',
  'userMgmt.perm.useAI': 'Používat AI funkce',
  'userMgmt.perm.createShareLinks': 'Vytvářet odkazy ke sdílení',
  'userMgmt.perm.cannotManageMembers': 'Nemůže spravovat členy',
  'userMgmt.perm.cannotChangeSettings': 'Nemůže měnit nastavení',
  'userMgmt.perm.viewWorkspaceItems': 'Zobrazit položky pracovního prostoru',
  'userMgmt.perm.suggestEdits': 'Navrhovat úpravy',
  'userMgmt.perm.postComments': 'Psát komentáře',
  'userMgmt.perm.cannotEditItems': 'Nemůže přímo upravovat položky',
  'userMgmt.perm.cannotCreateItems': 'Nemůže vytvářet položky',
  'userMgmt.perm.viewItems': 'Zobrazit položky',
  'userMgmt.perm.downloadFiles': 'Stahovat soubory',
  'userMgmt.perm.viewComments': 'Zobrazit komentáře',
  'userMgmt.perm.cannotEditOrComment': 'Nemůže upravovat ani komentovat',
  'userMgmt.perm.readOnlyAccess': 'Přístup pouze pro čtení',
  'userMgmt.perm.cannotManageAnything': 'Nemůže nic spravovat',
  'userMgmt.perm.limitedViewAccess': 'Omezený přístup pro čtení',
  'userMgmt.perm.accessSharedItems': 'Přístup ke konkrétním sdíleným položkám',
  'userMgmt.perm.cannotSeeFullWorkspace': 'Nevidí celý pracovní prostor',
  'userMgmt.perm.mostRestrictiveRole': 'Nejvíce omezující role',
  'userMgmt.perm.noManagementRights': 'Žádná práva ke správě',
  'userMgmt.suggestions.loading': 'Načítání návrhů...',
  'userMgmt.suggestions.emptyTitle': 'Žádné čekající návrhy',
  'userMgmt.suggestions.emptyDesc': 'Návrhy úprav od recenzentů se zde zobrazí ke schválení',
  'userMgmt.suggestions.currentLabel': 'Aktuální',
  'userMgmt.suggestions.empty': '(prázdné)',
  'userMgmt.suggestions.suggestedLabel': 'Navrženo',
  'userMgmt.suggestions.approve': 'Schválit',
  'userMgmt.suggestions.reject': 'Zamítnout',
  'userMgmt.inviteModal.title': 'Pozvat lidi',
  'userMgmt.inviteModal.emailLabel': 'E-mailová adresa',
  'userMgmt.inviteModal.emailPlaceholder': 'kolega@firma.cz',
  'userMgmt.inviteModal.roleLabel': 'Role',
  'userMgmt.inviteModal.sendInvitation': 'Odeslat pozvánku',
  'userMgmt.roleModal.title': 'Změnit roli',
  'userMgmt.roleModal.changingRoleFor': 'Změna role pro',
  'userMgmt.roleModal.currentTag': 'Aktuální',
  'userMgmt.transferModal.title': 'Převést vlastnictví',
  'userMgmt.transferModal.warningTitle': 'Tuto akci nelze vrátit zpět',
  'userMgmt.transferModal.warningDesc': 'Po převodu budete degradováni na Org Admina.',
  'userMgmt.transferModal.newOwnerLabel': 'Nový vlastník',
  'userMgmt.transferModal.selectMemberPlaceholder': 'Vyberte člena...',
  'userMgmt.transferModal.transferBtn': 'Převést vlastnictví',
  'userMgmt.teamModal.title': 'Vytvořit tým',
  'userMgmt.teamModal.nameLabel': 'Název týmu',
  'userMgmt.teamModal.namePlaceholder': 'např. Marketing, Vývoj',
  'userMgmt.teamModal.descLabel': 'Popis (nepovinné)',
  'userMgmt.teamModal.descPlaceholder': 'Na čem tento tým pracuje?',
  'userMgmt.teamModal.createBtn': 'Vytvořit tým',
  'userMgmt.convertModal.title': 'Převést na organizaci',
  'userMgmt.convertModal.heroTitle': 'Upgradovat na organizaci',
  'userMgmt.convertModal.heroDesc': 'Převeďte svůj osobní pracovní prostor na organizaci a odemkněte týmovou spolupráci.',
  'userMgmt.convertModal.featureInviteTitle': 'Pozvat členy týmu',
  'userMgmt.convertModal.featureInviteDesc': 'Přidat kolegy a přiřadit role',
  'userMgmt.convertModal.featureRbacTitle': 'Rolový přístup',
  'userMgmt.convertModal.featureRbacDesc': 'Řiďte, kdo může zobrazovat, upravovat a spravovat',
  'userMgmt.convertModal.featureTeamsTitle': 'Týmy a skupiny',
  'userMgmt.convertModal.featureTeamsDesc': 'Organizujte členy do týmů',
  'userMgmt.convertModal.note': 'Všechna vaše stávající data budou zachována. Stanete se vlastníkem organizace.',
  'userMgmt.convertModal.converting': 'Převádí se...',
  'userMgmt.convertModal.convertNow': 'Převést nyní',
  'userMgmt.common.cancel': 'Zrušit',
  'userMgmt.error.personalNoInvitations': 'Osobní pracovní prostory nepodporují pozvánky. Nejprve převeďte na organizaci.',
  'userMgmt.success.invitationSent': 'Pozvánka odeslána na {email}',
  'userMgmt.error.invitationFailed': 'Pozvánku se nepodařilo odeslat',
  'userMgmt.success.convertedToOrg': 'Pracovní prostor převeden na organizaci! Nyní můžete zvát členy.',
  'userMgmt.error.convertFailed': 'Převod se nezdařil',
  'userMgmt.success.roleUpdated': 'Role úspěšně aktualizována',
  'userMgmt.error.roleChangeFailed': 'Změna role se nezdařila',
  'userMgmt.success.memberRemoved': '{name} odebrán z pracovního prostoru',
  'userMgmt.error.removeMemberFailed': 'Člena se nepodařilo odebrat',
  'userMgmt.success.invitationRevoked': 'Pozvánka pro {email} zrušena',
  'userMgmt.error.revokeInvitationFailed': 'Pozvánku se nepodařilo zrušit',
  'userMgmt.success.ownershipTransferred': 'Vlastnictví úspěšně převedeno',
  'userMgmt.error.transferOwnershipFailed': 'Převod vlastnictví se nezdařil',
  'userMgmt.success.teamCreated': 'Tým „{name}" vytvořen',
  'userMgmt.error.createTeamFailed': 'Tým se nepodařilo vytvořit',
  'userMgmt.success.suggestionApproved': 'Návrh schválen',
  'userMgmt.success.suggestionRejected': 'Návrh zamítnut',
  'userMgmt.error.reviewSuggestionFailed': 'Návrh se nepodařilo posoudit',
  'userMgmt.confirm.removeMember': 'Odebrat {name} z tohoto pracovního prostoru?',
  'userMgmt.confirm.revokeInvitation': 'Zrušit pozvánku pro {email}?',
  'userMgmt.confirm.transferOwnership': 'Převést vlastnictví? Stanete se Org Adminem. Toto nelze vrátit zpět.',
  'userMgmt.time.today': 'Dnes',
  'userMgmt.time.yesterday': 'Včera',
  'userMgmt.time.daysAgo': 'před {count} d.',
  'userMgmt.time.monthsAgo': 'před {count} měs.',
  'userMgmt.time.yearsAgo': 'před {count} r.',
  'audit.statsLogins': 'přihlášení a odhlášení',
  'audit.statsCrud': 'vytvoření, aktualizace, smazání',
  'audit.statsAlerts': 'vyžaduje pozornost',
  'audit.statsReverts': 'Vrácení',
  'audit.statsRollbacks': 'provedené rollbacky',
  'audit.allStatuses': 'Všechny stavy',
  'audit.statusSuccess': 'Úspěšné',
  'audit.statusFailed': 'Neúspěšné',
  'audit.allTime': 'Celé období',
  'audit.pastHour': 'Poslední hodina',
  'audit.past24Hours': 'Posledních 24 hodin',
  'audit.past7Days': 'Posledních 7 dní',
  'audit.past30Days': 'Posledních 30 dní',
  'audit.reset': 'Resetovat',
  'audit.live': 'Živě',
  'audit.liveMonitoring': 'Živé sledování',
  'audit.event': 'Událost',
  'audit.description': 'Popis',
  'audit.status': 'Stav',
  'audit.view': 'Zobrazit',
  'audit.twoFaVerified': '2FA ověřeno',

  // ═══ Homepage (SecureFlow) ═══
  'hp.legalBar.compliance': 'GDPR Compliant • ISO 27001 Certified • SOC 2 Type II',
  'hp.legalBar.contact': 'Kontaktujte nás',

  'hp.nav.menu': 'Menu',
  'hp.nav.home': 'Domů',
  'hp.nav.about': 'O nás',
  'hp.nav.products': 'Produkty',
  'hp.nav.features': 'Funkce',
  'hp.nav.security': 'Bezpečnost',
  'hp.nav.compliance': 'Soulad',
  'hp.nav.pricing': 'Ceník',
  'hp.nav.resources': 'Zdroje',
  'hp.nav.contact': 'Kontakt',
  'hp.nav.tryFree': 'Vyzkoušet zdarma',
  'hp.nav.login': 'Přihlásit se',
  'hp.nav.language': 'Jazyk',

  'hp.hero.badge': 'Soulad s předpisy',
  'hp.hero.title1': 'VAULT & TIMECALC',
  'hp.hero.title2': 'PRO PODNIKY',
  'hp.hero.subtitle': 'Komplexní řešení pro správu aktiv a sledování času s pokročilou bezpečností a souladem s předpisy.',
  'hp.hero.cta': 'Začít zdarma',
  'hp.hero.ctaSecondary': 'Kontaktovat obchod',
  'hp.hero.dashboardAlt': 'ELVOAQ Dashboard Screenshot',

  'hp.complianceBadges.title': 'CERTIFIKACE A SOULAD',
  'hp.complianceBadges.gdpr': 'GDPR Compliant',
  'hp.complianceBadges.ccpa': 'CCPA Compliant',
  'hp.complianceBadges.hipaa': 'HIPAA Compliant',
  'hp.complianceBadges.iso': 'ISO 27001',
  'hp.complianceBadges.soc2': 'SOC 2 Type II',
  'hp.complianceBadges.gdprDesc': 'Úplný soulad s nařízením GDPR pro ochranu osobních údajů',
  'hp.complianceBadges.ccpaDesc': 'Soulad s kalifornským zákonem o ochraně spotřebitele',
  'hp.complianceBadges.hipaaDesc': 'Soulad s HIPAA pro zdravotnická data',
  'hp.complianceBadges.isoDesc': 'Mezinárodní standard pro informační bezpečnost',
  'hp.complianceBadges.soc2Desc': 'Certifikace pro kontrolu bezpečnosti a dostupnosti',

  'hp.features.title': 'POKROČILÉ FUNKCE',
  'hp.features.subtitle': 'Vše co potřebujete pro profesionální správu aktiv a sledování času',
  'hp.features.ai.title': 'AI Kategorizace',
  'hp.features.ai.desc': 'Automatické rozpoznávání a kategorizace položek pomocí umělé inteligence',
  'hp.features.duplicate.title': 'Detekce Duplikátů',
  'hp.features.duplicate.desc': 'Inteligentní algoritmy pro identifikaci duplicitních položek ve vaší sbírce',
  'hp.features.valuation.title': 'Odhad Hodnoty',
  'hp.features.valuation.desc': 'Real-time odhady tržní hodnoty vašich aktiv',
  'hp.features.gallery.title': 'Galerie Obrázků',
  'hp.features.gallery.desc': 'Vysoce kvalitní galerie s až 30 fotografiemi na položku',
  'hp.features.search.title': 'Pokročilé Vyhledávání',
  'hp.features.search.desc': 'Rychlé vyhledávání ve všech vašich datech pomocí fulltext vyhledávání',
  'hp.features.collaboration.title': 'Týmová Spolupráce',
  'hp.features.collaboration.desc': 'Sdílené kolekce a přístup založený na rolích pro týmy',

  'hp.security.title': 'ENTERPRISE BEZPEČNOST',
  'hp.security.subtitle': 'Vaše data jsou chráněna nejvyššími standardy bezpečnosti',
  'hp.security.encryption.title': 'End-to-End Šifrování',
  'hp.security.encryption.desc': 'Všechna data jsou šifrována během přenosu i uložení',
  'hp.security.european.title': 'Evropská Datová Centra',
  'hp.security.european.desc': 'Vaše data zůstávají v Evropě pod přísnými regulatorními standardy',
  'hp.security.zeroKnowledge.title': 'Zero-Knowledge Architektura',
  'hp.security.zeroKnowledge.desc': 'Vaše data jsou přístupná pouze vám - ani my je nevidíme',
  'hp.security.audit.title': 'Kompletní Auditování',
  'hp.security.audit.desc': 'Úplná historie všech změn s možnostmi exportu',

  'hp.compliance.title': 'SOULAD S PŘEDPISY',
  'hp.compliance.subtitle': 'Navrženo pro nejnáročnější regulatorní požadavky',
  'hp.compliance.gdpr.title': 'GDPR Soulad',
  'hp.compliance.gdpr.desc': 'Úplný soulad s evropským nařízením o ochraně osobních údajů',
  'hp.compliance.ccpa.title': 'CCPA Soulad',
  'hp.compliance.ccpa.desc': 'Soulad s kalifornským zákonem o ochraně soukromí spotřebitelů',
  'hp.compliance.hipaa.title': 'HIPAA Soulad',
  'hp.compliance.hipaa.desc': 'Certifikace pro zdravotnické organizace a zpracování citlivých dat',

  'hp.pricing.title': 'FLEXIBILNÍ CENOVÉ PLÁNY',
  'hp.pricing.subtitle': 'Vyberte si plán, který vyhovuje vašim potřebám',
  'hp.pricing.starter.title': 'Starter',
  'hp.pricing.starter.price': '€9',
  'hp.pricing.starter.period': '/měsíc',
  'hp.pricing.starter.desc': 'Ideální pro jednotlivce a malé týmy',
  'hp.pricing.starter.feature1': 'Až 1,000 položek',
  'hp.pricing.starter.feature2': 'Základní AI funkce',
  'hp.pricing.starter.feature3': 'Mobilní aplikace',
  'hp.pricing.starter.feature4': 'E-mail podpora',
  'hp.pricing.starter.cta': 'Začít se Starter',
  'hp.pricing.professional.title': 'Profesionál',
  'hp.pricing.professional.price': '€29',
  'hp.pricing.professional.period': '/měsíc',
  'hp.pricing.professional.desc': 'Pro rostoucí podniky a týmy',
  'hp.pricing.professional.badge': 'Nejoblíbenější',
  'hp.pricing.professional.feature1': 'Neomezené položky',
  'hp.pricing.professional.feature2': 'Pokročilé AI funkce',
  'hp.pricing.professional.feature3': 'Týmová spolupráce',
  'hp.pricing.professional.feature4': 'Prioritní podpora',
  'hp.pricing.professional.feature5': 'API přístup',
  'hp.pricing.professional.cta': 'Začít s Profesionál',
  'hp.pricing.enterprise.title': 'Enterprise',
  'hp.pricing.enterprise.price': 'Kontaktujte nás',
  'hp.pricing.enterprise.period': '',
  'hp.pricing.enterprise.desc': 'Pro velké organizace se specifickými potřebami',
  'hp.pricing.enterprise.feature1': 'Všechny Profesionál funkce',
  'hp.pricing.enterprise.feature2': 'Vlastní integrace',
  'hp.pricing.enterprise.feature3': 'Dedikovaný manažer',
  'hp.pricing.enterprise.feature4': 'SLA záruky',
  'hp.pricing.enterprise.feature5': 'On-premise nasazení',
  'hp.pricing.enterprise.cta': 'Kontaktovat obchod',

  'hp.legal.title': 'PRÁVNÍ DOKUMENTY',
  'hp.legal.subtitle': 'Přečtěte si naše právní dokumenty a zásady',
  'hp.legal.dpa.title': 'Dohoda o zpracování dat',
  'hp.legal.dpa.desc': 'Podrobnosti o tom, jak zpracováváme vaše data v souladu s GDPR',
  'hp.legal.terms.title': 'Obchodní podmínky',
  'hp.legal.terms.desc': 'Naše všeobecné obchodní podmínky a pravidla používání',
  'hp.legal.privacy.title': 'Zásady soukromí',
  'hp.legal.privacy.desc': 'Jak chráníme a používáme vaše osobní údaje',
  'hp.legal.cookies.title': 'Zásady cookies',
  'hp.legal.cookies.desc': 'Informace o používání cookies na našich webových stránkách',

  'hp.trust.title': 'DŮVĚRA A ZÁRUKY',
  'hp.trust.subtitle': 'Proč si nás vybírají tisíce společností',
  'hp.trust.security.title': 'Bankovní úroveň bezpečnosti',
  'hp.trust.security.desc': 'Vaše data jsou chráněna stejnými standardy jako banky',
  'hp.trust.compliance.title': 'Regulatorní soulad',
  'hp.trust.compliance.desc': 'Splňujeme všechny hlavní mezinárodní standardy souladu',
  'hp.trust.support.title': '24/7 Podpora',
  'hp.trust.support.desc': 'Náš tým je k dispozici vždy, když potřebujete pomoc',
  'hp.trust.uptime.title': '99.9% dostupnost',
  'hp.trust.uptime.desc': 'Zaručujeme vysokou dostupnost našich služeb',

  'hp.contact.title': 'KONTAKTUJTE NÁS',
  'hp.contact.subtitle': 'Máte otázky? Rádi vám pomůžeme',
  'hp.contact.form.title': 'Napište nám',
  'hp.contact.form.name': 'Jméno *',
  'hp.contact.form.namePlaceholder': 'Vaše jméno',
  'hp.contact.form.email': 'E-mail *',
  'hp.contact.form.emailPlaceholder': 'vas@email.cz',
  'hp.contact.form.company': 'Společnost',
  'hp.contact.form.companyPlaceholder': 'Název vaší společnosti',
  'hp.contact.form.message': 'Zpráva *',
  'hp.contact.form.messagePlaceholder': 'Vaše zpráva...',
  'hp.contact.form.submit': 'Odeslat zprávu',
  'hp.contact.form.sending': 'Odesílání...',
  'hp.contact.info.title': 'Kontaktní informace',
  'hp.contact.info.email': 'E-mail',
  'hp.contact.info.emailValue': 'hello@elvoaq.com',
  'hp.contact.info.phone': 'Telefon',
  'hp.contact.info.phoneValue': '+41 44 123 45 67',
  'hp.contact.info.address': 'Adresa',
  'hp.contact.info.addressValue': 'Bahnhofstrasse 12, 8001 Curych, Švýcarsko',

  'hp.footer.company': 'Společnost',
  'hp.footer.about': 'O nás',
  'hp.footer.careers': 'Kariéra',
  'hp.footer.press': 'Tisk',
  'hp.footer.blog': 'Blog',
  'hp.footer.products': 'Produkty',
  'hp.footer.vault': 'ELVOAQ Vault',
  'hp.footer.timecalc': 'ELVOAQ TimeCalc',
  'hp.footer.features': 'Funkce',
  'hp.footer.security': 'Bezpečnost',
  'hp.footer.compliance': 'Soulad',
  'hp.footer.pricing': 'Ceník',
  'hp.footer.resources': 'Zdroje',
  'hp.footer.documentation': 'Dokumentace',
  'hp.footer.help': 'Centrum nápovědy',
  'hp.footer.api': 'API',
  'hp.footer.community': 'Komunita',
  'hp.footer.legal': 'Právní',
  'hp.footer.terms': 'Obchodní podmínky',
  'hp.footer.privacy': 'Zásady soukromí',
  'hp.footer.cookies': 'Cookies',
  'hp.footer.dpa': 'DPA',
  'hp.footer.contact': 'Kontakt',
  'hp.footer.support': 'Podpora',
  'hp.footer.sales': 'Obchod',
  'hp.footer.status': 'Stav systému',
  'hp.footer.social': 'Sociální sítě',
  'hp.footer.linkedin': 'LinkedIn',
  'hp.footer.twitter': 'Twitter',
  'hp.footer.copyright': '© 2024 ELVOAQ AG. Všechna práva vyhrazena.',
  'hp.footer.location': 'Curych, Švýcarsko',

  'hp.cookie.title': 'Používáme cookies',
  'hp.cookie.desc': 'Naše webové stránky používají cookies pro lepší zážitek. Pokračováním souhlasíte s našimi zásadami cookies.',
  'hp.cookie.accept': 'Přijmout vše',
  'hp.cookie.reject': 'Odmítnout vše',
  'hp.cookie.settings': 'Nastavení',
};

/* ═══════════════════════════════════════════════════════════════
   TRANSLATION KEYS — Slovak (Slovenčina)
   ═══════════════════════════════════════════════════════════════ */

const sk: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Prehľad',
  'nav.back': 'Späť',
  'nav.library': 'Knižnica',
  'nav.favorites': 'Obľúbené',
  'nav.categories': 'Kategórie',
  'nav.workspaces': 'Pracovné priestory',
  'nav.collections': 'Kolekcie',
  'nav.notifications': 'Oznámenia',
  'nav.settings': 'Nastavenia',
  'nav.history': 'História',
  'nav.trash': 'Kôš',
  'nav.users': 'Používatelia',
  'nav.usersRoles': 'Používatelia a role',
  'nav.teams': 'Tímy',
  'nav.feedback': 'Spätná väzba',
  'nav.subscription': 'Predplatné',
  'nav.pricing': 'Cenník',
  'nav.logout': 'Odhlásiť sa',
  'nav.auditTrail': 'Protokol aktivít',
  'nav.accounting': 'Účtovníctvo',
  'nav.smartHub': 'Smart Hub',
  'nav.aiAssistant': 'AI Asistent',
  'nav.duplicateFinder': 'Hľadanie duplikátov',
  'nav.hiddenFolders': 'Skryté priečinky',
  'nav.importExport': 'Import / Export',
  'nav.sectionMenu': 'Menu',
  'nav.sectionOther': 'Ostatné',
  'nav.sectionBilling': 'Fakturácia',
  'nav.searchPlaceholder': 'Hľadať sekcie…',
  'nav.searchNoResults': 'Nič sa nenašlo',

  // Common Actions
  'action.save': 'Uložiť',
  'action.saveChanges': 'Uložiť zmeny',
  'action.cancel': 'Zrušiť',
  'action.delete': 'Vymazať',
  'action.edit': 'Upraviť',
  'action.add': 'Pridať',
  'action.create': 'Vytvoriť',
  'action.search': 'Hľadať',
  'action.filter': 'Filtrovať',
  'action.filters': 'Filtre',
  'action.export': 'Exportovať',
  'action.import': 'Importovať',
  'action.refresh': 'Obnoviť',
  'action.close': 'Zavrieť',
  'action.confirm': 'Potvrdiť',
  'action.back': 'Späť',
  'action.next': 'Ďalej',
  'action.loading': 'Načítavanie...',
  'action.saving': 'Ukladanie...',
  'action.selectAll': 'Vybrať všetko',
  'action.deselectAll': 'Zrušiť výber',
  'action.enableAll': 'Povoliť všetko',
  'action.disableAll': 'Zakázať všetko',
  'action.view': 'Zobraziť',
  'action.move': 'Presunúť',
  'action.restore': 'Obnoviť',
  'action.remove': 'Odstrániť',
  'action.clear': 'Vymazať',
  'action.clearAll': 'Vymazať všetko',
  'action.done': 'Hotovo',
  'action.apply': 'Použiť',
  'action.reset': 'Resetovať',
  'action.resetFilters': 'Resetovať filtre',
  'action.upgrade': 'Upgradovať',
  'action.download': 'Stiahnuť',
  'action.upload': 'Nahrať',
  'action.submit': 'Odoslať',
  'action.continue': 'Pokračovať',
  'action.getStarted': 'Začať',
  'action.learnMore': 'Zistiť viac',
  'action.viewAll': 'Zobraziť všetko',
  'action.showMore': 'Zobraziť viac',
  'action.showLess': 'Zobraziť menej',
  'action.hideStats': 'Skryť štatistiky',
  'action.showStats': 'Zobraziť štatistiky',
  'action.reply': 'Odpovedať',
  'action.post': 'Odoslať',
  'action.posting': 'Odosielanie...',

  // Intervals
  'interval.day': 'Deň',
  'interval.week': 'Týždeň',
  'interval.month': 'Mesiac',
  'interval.quarter': 'Štvrťrok',
  'interval.year': 'Rok',

  // Sort Options
  'sort.newestFirst': 'Najnovšie prvé',
  'sort.oldestFirst': 'Najstaršie prvé',
  'sort.nameAZ': 'Názov (A-Z)',
  'sort.nameZA': 'Názov (Z-A)',
  'sort.highestValue': 'Najvyššia hodnota',
  'sort.lowestValue': 'Najnižšia hodnota',
  'sort.recentlyUpdated': 'Nedávno aktualizované',
  'sort.mostItems': 'Najviac položiek',

  // Item Fields
  'item.name': 'Názov',
  'item.namePlaceholder': 'Zadajte názov',
  'item.description': 'Popis',
  'item.descriptionPlaceholder': 'Zadajte popis',
  'item.category': 'Kategória',
  'item.selectCategory': 'Vyberte kategóriu',
  'item.condition': 'Stav',
  'item.location': 'Umiestnenie',
  'item.locationPlaceholder': 'Kde je táto položka uložená?',
  'item.purchasePrice': 'Kúpna cena',
  'item.purchaseDate': 'Dátum nákupu',
  'item.estimatedValue': 'Odhadovaná hodnota',
  'item.aiValue': 'AI hodnota',
  'item.tags': 'Štítky',
  'item.tagsPlaceholder': 'Pridajte štítok a stlačte Enter',
  'item.images': 'Obrázky',
  'item.chooseImages': 'Vyberte obrázky (max. 50)',
  'item.addMore': 'Pridať ďalšie',
  'item.createdAt': 'Vytvorené',
  'item.updatedAt': 'Aktualizované',

  // Item Modal
  'itemModal.editItem': 'Upraviť položku',
  'itemModal.addNewItem': 'Pridať novú položku',
  'itemModal.updateItem': 'Aktualizovať položku',
  'itemModal.createItem': 'Vytvoriť položku',
  'itemModal.saving': 'Ukladanie...',
  'itemModal.aiAnalyzing': 'AI analyzuje...',
  'itemModal.aiSuggestions': 'Návrhy AI',
  'itemModal.purchasePlace': 'Miesto nákupu',
  'itemModal.purchasePlacePlaceholder': 'Obchod, web alebo predajca',
  'itemModal.currency': 'Mena',
  'itemModal.purchaseDateTime': 'Dátum a čas nákupu',
  'itemModal.selectDate': 'Vyberte dátum',
  'itemModal.timeOptional': 'Čas (voliteľný)',
  'itemModal.noTime': 'Bez času',
  'itemModal.timezone': 'Časové pásmo',
  'itemModal.autoDetected': 'Automaticky rozpoznané',
  'itemModal.highMatch': 'Vysoká zhoda',
  'itemModal.mediumMatch': 'Stredná zhoda',
  'itemModal.lowMatch': 'Nízka zhoda',
  'itemModal.add': 'Pridať',

  // Conditions
  'condition.new': 'Nový',
  'condition.likeNew': 'Ako nový',
  'condition.excellent': 'Výborný',
  'condition.good': 'Dobrý',
  'condition.fair': 'Uspokojivý',
  'condition.poor': 'Zlý',
  'condition.all': 'Všetky stavy',

  // Advanced Filters
  'filter.advancedFilters': 'Rozšírené filtre',
  'filter.condition': 'Stav',
  'filter.conditionAll': 'Všetko',
  'filter.valueRange': 'Rozsah hodnôt',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.purchaseDate': 'Dátum nákupu',
  'filter.from': 'Od',
  'filter.to': 'Do',
  'filter.aiValueTrend': 'AI trend hodnoty',
  'filter.trendAll': 'Všetko',
  'filter.appreciating': 'Rastúci',
  'filter.stable': 'Stabilný',
  'filter.depreciating': 'Klesajúci',
  'filter.locations': 'Umiestnenia',
  'filter.tags': 'Štítky',
  'filter.additionalOptions': 'Ďalšie možnosti',
  'filter.hasAIValue': 'Má AI predikciu hodnoty',
  'filter.favoritesOnly': 'Iba obľúbené',
  'filter.sortBy': 'Zoradiť podľa',
  'filter.newestFirst': 'Najnovšie',
  'filter.oldestFirst': 'Najstaršie',
  'filter.nameAZ': 'Názov (A-Z)',
  'filter.nameZA': 'Názov (Z-A)',
  'filter.highestValue': 'Najvyššia hodnota',
  'filter.lowestValue': 'Najnižšia hodnota',
  'filter.bestCondition': 'Najlepší stav',
  'filter.worstCondition': 'Najhorší stav',
  'filter.resetAll': 'Obnoviť všetko',
  'filter.applyFilters': 'Použiť filtre',

  // Collections — delete confirm
  'collections.deleteConfirmDesc': 'Položky v tejto kolekcii nebudú vymazané.',

  // Collections — library search
  'collections.addFromLibrary': 'Pridať z knižnice',
  'collections.searchLibraryPlaceholder': 'Hľadať položky...',
  'collections.noLibraryResults': 'Žiadne položky nenájdené',
  'collections.orAddManually': 'alebo pridať ručne',
  'collections.searchTab': 'Hľadať',
  'collections.browseAllTab': 'Prechádzať všetko',
  'collections.filterItems': 'Filtrovať položky...',
  'collections.allCategories': 'Všetko',
  'collections.allConditions': 'Všetky stavy',
  'collections.searchHint': 'Zadajte aspoň 2 znaky pre vyhľadávanie',
  'collections.itemsTab': 'Položky',
  'collections.addItemsTab': 'Pridať položky',
  'collections.searchItems': 'Hľadať položky...',

  // Categories
  'categories.title': 'Kategórie',
  'categories.addCategory': 'Pridať kategóriu',
  'categories.editCategory': 'Upraviť kategóriu',
  'categories.deleteCategory': 'Vymazať kategóriu',
  'categories.noCategories': 'Zatiaľ žiadne kategórie',
  'categories.noCategoriesFound': 'Žiadne kategórie nenájdené',
  'categories.totalItems': 'Celkom položiek',
  'categories.totalValue': 'Celková hodnota',
  'categories.avgValue': 'Priem. hodnota',
  'categories.sortBy': 'Zoradiť podľa:',
  'categories.name': 'Názov',
  'categories.items': 'Položky',
  'categories.color': 'Farba',
  'categories.icon': 'Ikona',
  'categories.subtitle': 'Organizujte svoje položky do kolekcií',
  'categories.showStats': 'Zobraziť štatistiky',
  'categories.hideStats': 'Skryť štatistiky',
  'categories.newCategory': 'Nová kategória',
  'categories.categories': 'Kategórie',
  'categories.avgValuePerItem': 'Priem. hodnota/položku',
  'categories.mostValuable': 'Najcennejší',
  'categories.conditionDistribution': 'Rozloženie stavu',
  'categories.searchPlaceholder': 'Hľadať kategórie...',
  'categories.itemCount': 'Počet položiek',
  'categories.dateCreated': 'Dátum vytvorenia',
  'categories.customizeDefault': 'Upraviť predvolenú kategóriu',
  'categories.createNew': 'Vytvoriť novú kategóriu',
  'categories.infoBanner': 'Predvolené kategórie môžete prispôsobiť svojim potrebám. Zmeny budú uložené.',
  'categories.categoryName': 'Názov kategórie *',
  'categories.enterName': 'Zadajte názov kategórie',
  'categories.description': 'Popis',
  'categories.describeCategory': 'Popíšte túto kategóriu',
  'categories.searchIcons': 'Hľadať ikony...',
  'categories.updateCategory': 'Aktualizovať kategóriu',
  'categories.createCategory': 'Vytvoriť kategóriu',
  'categories.cancel': 'Zrušiť',
  'categories.default': 'Predvolená',
  'categories.noDescription': 'Bez popisu',
  'categories.avg': 'Priem.:',
  'categories.tryDifferent': 'Skúste iný hľadaný výraz',
  'categories.createToOrganize': 'Vytvorte kategórie na organizáciu svojich položiek',
  'categories.recentItems': 'Posledné položky',
  'categories.viewAllInLibrary': 'Zobraziť všetko v knižnici',
  'categories.loadingItems': 'Načítanie položiek...',
  'categories.noItemsInCategory': 'V tejto kategórii nie sú žiadne položky.',
  'categories.cannotDeleteWithItems': 'Nemožno vymazať kategóriu s {count} položkou/položkami. Najprv ich presuňte alebo vymažte.',
  'categories.confirmDelete': 'Naozaj chcete vymazať túto kategóriu?',
  'categories.confirmDeleteDefault': 'Toto je predvolená kategória. Naozaj ju chcete vymazať? Môžete ju kedykoľvek znovu vytvoriť.',
  'categories.duplicateTitle': 'Duplikovať kategóriu',
  'categories.loading': 'Načítanie kategórií...',

  // Item Detail
  'itemDetail.photos': 'fotky',
  'itemDetail.noImage': 'Bez obrázka',
  'itemDetail.removeFromFavorites': 'Odobrať z obľúbených',
  'itemDetail.addToFavorites': 'Pridať do obľúbených',
  'itemDetail.added': 'Pridané',
  'itemDetail.uncategorized': 'Bez kategórie',
  'itemDetail.info': 'Info',
  'itemDetail.files': 'Súbory',
  'itemDetail.comments': 'Komentáre',
  'itemDetail.log': 'Protokol',
  'itemDetail.description': 'Popis',
  'itemDetail.noDescription': 'Popis nebol zadaný.',
  'itemDetail.location': 'Miesto',
  'itemDetail.notSet': 'Nenastavené',
  'itemDetail.purchasePlace': 'Miesto nákupu',
  'itemDetail.purchased': 'Zakúpené',
  'itemDetail.purchasePrice': 'Nákupná cena',
  'itemDetail.condition': 'Stav',
  'itemDetail.tags': 'Štítky',
  'itemDetail.aiPrediction': 'AI odhad hodnoty',
  'itemDetail.highConfidence': 'Vysoká spoľahlivosť',
  'itemDetail.mediumConfidence': 'Stredná spoľahlivosť',
  'itemDetail.lowConfidence': 'Nízka spoľahlivosť',
  'itemDetail.basisPurchase': 'Na základe nákupnej ceny a stavu',
  'itemDetail.basisCategory': 'Na základe priemeru kategórie',
  'itemDetail.basisAI': 'AI odhad',
  'itemDetail.previousPrediction': 'Predchádzajúci odhad',
  'itemDetail.recalculate': 'Prepočítať',
  'itemDetail.analyzing': 'Analyzujem dáta položky...',
  'itemDetail.tryAgain': 'Skúsiť znova',
  'itemDetail.aiEstimateDesc': 'Získajte AI odhad hodnoty na základe dát položky, kategórie a stavu.',
  'itemDetail.predictValue': 'Odhadnúť hodnotu',
  'itemDetail.itemInfo': 'Informácie o položke',
  'itemDetail.created': 'Vytvorené',
  'itemDetail.lastUpdated': 'Naposledy aktualizované',
  'itemDetail.itemId': 'ID položky',
  'itemDetail.uploadFile': 'Nahrať súbor',
  'itemDetail.uploading': 'Nahrávanie...',
  'itemDetail.deselectAll': 'Zrušiť výber',
  'itemDetail.selectAll': 'Vybrať všetko',
  'itemDetail.downloadSelected': 'Stiahnuť {count} vybraných',
  'itemDetail.noFiles': 'Zatiaľ žiadne priložené súbory',
  'itemDetail.addComment': 'Pridať komentár...',
  'itemDetail.posting': 'Odosielanie...',
  'itemDetail.post': 'Odoslať',
  'itemDetail.loadingComments': 'Načítanie komentárov...',
  'itemDetail.loadingFiles': 'Načítanie súborov...',
  'itemDetail.loadingHistory': 'Načítanie histórie...',
  'itemDetail.edited': '(upravené)',
  'itemDetail.editHistory': 'História úprav',
  'itemDetail.save': 'Uložiť',
  'itemDetail.cancel': 'Zrušiť',
  'itemDetail.noComments': 'Zatiaľ žiadne komentáre. Buďte prvý!',
  'itemDetail.noHistory': 'Pre túto položku zatiaľ nie je k dispozícii žiadna história.',
  'itemDetail.changesTracked': 'Zmeny budú automaticky sledované.',
  'itemDetail.itemCreated': 'Položka vytvorená',
  'itemDetail.updatedField': '{field} aktualizované',
  'itemDetail.itemDeleted': 'Položka vymazaná',
  'itemDetail.locked': 'Zamknuté',
  'itemDetail.changePin': 'Zmeniť PIN',
  'itemDetail.removeLock': 'Odstrániť zámok',
  'itemDetail.lockItem': 'Zamknúť položku',
  'itemDetail.edit': 'Upraviť',
  'itemDetail.delete': 'Vymazať',
  'itemDetail.conditionNew': 'Nový',
  'itemDetail.conditionLikeNew': 'Ako nový',
  'itemDetail.conditionExcellent': 'Výborný',
  'itemDetail.conditionGood': 'Dobrý',
  'itemDetail.conditionFair': 'Uspokojivý',
  'itemDetail.conditionPoor': 'Zlý',
  'itemDetail.conditionNA': 'Neuvedený',
  'itemDetail.viewFullSize': 'Zobraziť v plnej veľkosti',

  // Lock Modal
  'lockModal.lockItem': 'Zamknúť položku',
  'lockModal.unlockItem': 'Odomknúť položku',
  'lockModal.verifyPassword': 'Overiť heslo',
  'lockModal.changeLockPassword': 'Zmeniť heslo zámku',
  'lockModal.lockDesc': 'Nastavte heslo na ochranu tejto položky. Bude skrytá a bude vyžadovať heslo na zobrazenie.',
  'lockModal.unlockDesc': 'Zadajte heslo na trvalé odomknutie tejto položky.',
  'lockModal.verifyDesc': 'Zadajte heslo na zobrazenie tejto zamknutej položky.',
  'lockModal.changeDesc': 'Zmeňte heslo zámku pre túto položku.',
  'lockModal.removeLock': 'Odstrániť zámok',
  'lockModal.removeLockDesc': 'Zámok bude odstránený bez nutnosti zadania hesla.',
  'lockModal.currentPassword': 'Aktuálne heslo',
  'lockModal.password': 'Heslo',
  'lockModal.newPassword': 'Nové heslo',
  'lockModal.confirmPassword': 'Potvrdiť heslo',
  'lockModal.confirmNewPassword': 'Potvrdiť nové heslo',
  'lockModal.choosePlaceholder': 'Zvoľte heslo (min. 4 znaky)',
  'lockModal.enterPlaceholder': 'Zadajte heslo',
  'lockModal.newPlaceholder': 'Nové heslo (min. 4 znaky)',
  'lockModal.reenterPlaceholder': 'Zadajte heslo znovu',
  'lockModal.processing': 'Spracovanie...',
  'lockModal.verify': 'Overiť',
  'lockModal.changePassword': 'Zmeniť heslo',
  'lockModal.minCharsError': 'Heslo musí mať aspoň 4 znaky',
  'lockModal.mismatchError': 'Heslá sa nezhodujú',
  'lockModal.newMinCharsError': 'Nové heslo musí mať aspoň 4 znaky',
  'lockModal.newMismatchError': 'Nové heslá sa nezhodujú',
  'lockModal.operationFailed': 'Operácia zlyhala. Skúste to prosím znova.',

  // Favorites
  'favorites.title': 'Obľúbené',
  'favorites.noFavorites': 'Zatiaľ žiadne obľúbené',
  'favorites.noFavoritesDesc': 'Kliknite na ikonu srdca pri položkách pre pridanie do obľúbených',
  'favorites.browseLibrary': 'Prechádzať knižnicu',
  'favorites.shown': 'zobrazených',
  'favorites.averageValue': 'Priemerná hodnota',
  'favorites.minPrice': 'Min. cena',
  'favorites.maxPrice': 'Max. cena',
  'favorites.location': 'Umiestnenie',
  'favorites.allLocations': 'Všetky umiestnenia',
  'favorites.tag': 'Štítok',
  'favorites.allTags': 'Všetky štítky',
  'favorites.loading': 'Načítavanie obľúbených...',
  'favorites.searchPlaceholder': 'Hľadať v obľúbených...',
  'favorites.filters': 'Filtre',
  'favorites.toggleView': 'Prepnúť zobrazenie',
  'favorites.export': 'Exportovať',
  'favorites.exportCSV': 'Exportovať CSV',
  'favorites.items': 'Položky',
  'favorites.totalValue': 'Celková hodnota',
  'favorites.average': 'Priemer',
  'favorites.category': 'Kategória',
  'favorites.condition': 'Stav',
  'favorites.allCategories': 'Všetky kategórie',
  'favorites.allConditions': 'Všetky stavy',
  'favorites.reset': 'Resetovať',
  'favorites.sortMostRecent': 'Najnovšie',
  'favorites.sortPrice': 'Cena',
  'favorites.sortName': 'Názov',
  'favorites.sortCondition': 'Stav',
  'favorites.sortLocation': 'Umiestnenie',
  'favorites.all': 'Všetko',
  'favorites.remove': 'Odstrániť',
  'favorites.removeFromFavorites': 'Odstrániť z obľúbených',
  'favorites.removeConfirm': 'Odstrániť {count} položiek z obľúbených?',
  'favorites.noMatch': 'Žiadne položky nezodpovedajú filtrom',
  'favorites.noMatchDesc': 'Skúste upraviť filtre alebo vyhľadávací dotaz',
  'favorites.addFromCollection': 'Označte položky ako obľúbené vo svojej zbierke, aby sa tu zobrazili',

  // Default Category Names
  'categoryName.Electronics': 'Elektronika',
  'categoryName.Furniture': 'Nábytok',
  'categoryName.Books': 'Knihy',
  'categoryName.Clothing': 'Oblečenie',
  'categoryName.Jewelry': 'Šperky',
  'categoryName.Sports': 'Šport',
  'categoryName.Art & Collectibles': 'Umenie & Zberateľstvo',
  'categoryName.Tools': 'Náradie',
  'categoryDesc.Electronics & gadgets': 'Elektronika & gadgety',
  'categoryDesc.Furniture & decor': 'Nábytok & dekorácie',
  'categoryDesc.Books & media': 'Knihy & médiá',
  'categoryDesc.Clothing & accessories': 'Oblečenie & doplnky',
  'categoryDesc.Jewelry & watches': 'Šperky & hodinky',
  'categoryDesc.Sports & outdoors': 'Šport & outdoor',
  'categoryDesc.Art & collectibles': 'Umenie & zberateľstvo',
  'categoryDesc.Tools & equipment': 'Náradie & vybavenie',

  // Unit of Measure
  'unit.title': 'Merná jednotka',
  'unit.type': 'Typ jednotky',
  'unit.subtype': 'Jednotka',
  'unit.quantity': 'Množstvo',
  'unit.selectType': 'Vyberte typ...',
  'unit.selectUnit': 'Vyberte jednotku...',
  'unit.enterQuantity': 'Zadajte množstvo...',
  'unit.customUnit': 'Vlastný názov jednotky',
  'unit.type.weight': 'Hmotnosť',
  'unit.type.length': 'Dĺžka',
  'unit.type.volume': 'Objem',
  'unit.type.area': 'Plocha',
  'unit.type.quantity': 'Množstvo',
  'unit.type.digital': 'Digitálne úložisko',
  'unit.type.time': 'Čas',
  'unit.type.energy': 'Energia',
  'unit.type.temperature': 'Teplota',
  'unit.type.speed': 'Rýchlosť',
  'unit.type.custom': 'Vlastné',
  'unit.sub.mg': 'Miligramy (mg)',
  'unit.sub.g': 'Gramy (g)',
  'unit.sub.kg': 'Kilogramy (kg)',
  'unit.sub.t': 'Tony (t)',
  'unit.sub.oz': 'Unce (oz)',
  'unit.sub.lb': 'Libry (lb)',
  'unit.sub.mm': 'Milimetre (mm)',
  'unit.sub.cm': 'Centimetre (cm)',
  'unit.sub.m': 'Metre (m)',
  'unit.sub.km': 'Kilometre (km)',
  'unit.sub.in': 'Palce (in)',
  'unit.sub.ft': 'Stopy (ft)',
  'unit.sub.yd': 'Yardy (yd)',
  'unit.sub.mi': 'Míle (mi)',
  'unit.sub.ml': 'Mililitre (ml)',
  'unit.sub.cl': 'Centilitre (cl)',
  'unit.sub.dl': 'Decilitre (dl)',
  'unit.sub.l': 'Litre (L)',
  'unit.sub.hl': 'Hektolitre (hL)',
  'unit.sub.floz': 'Tekuté unce (fl oz)',
  'unit.sub.cup': 'Hrnčeky',
  'unit.sub.pt': 'Pinty (pt)',
  'unit.sub.qt': 'Kvarty (qt)',
  'unit.sub.gal': 'Galóny (gal)',
  'unit.sub.sqcm': 'Štvorcové cm (cm²)',
  'unit.sub.sqm': 'Štvorcové metre (m²)',
  'unit.sub.ha': 'Hektáre (ha)',
  'unit.sub.sqkm': 'Štvorcové km (km²)',
  'unit.sub.sqft': 'Štvorcové stopy (ft²)',
  'unit.sub.sqyd': 'Štvorcové yardy (yd²)',
  'unit.sub.ac': 'Akre (ac)',
  'unit.sub.sqmi': 'Štvorcové míle (mi²)',
  'unit.sub.pcs': 'Kusy (ks)',
  'unit.sub.pairs': 'Páry',
  'unit.sub.sets': 'Sady',
  'unit.sub.packs': 'Balenia',
  'unit.sub.boxes': 'Škatule',
  'unit.sub.rolls': 'Kotúče',
  'unit.sub.sheets': 'Hárky',
  'unit.sub.bottles': 'Fľaše',
  'unit.sub.cans': 'Plechovky',
  'unit.sub.bags': 'Tašky',
  'unit.sub.dozens': 'Tucty (tuc)',
  'unit.sub.b': 'Bajty (B)',
  'unit.sub.kb': 'Kilobajty (KB)',
  'unit.sub.mb': 'Megabajty (MB)',
  'unit.sub.gb': 'Gigabajty (GB)',
  'unit.sub.tb': 'Terabajty (TB)',
  'unit.sub.sec': 'Sekundy (s)',
  'unit.sub.min': 'Minúty (min)',
  'unit.sub.hr': 'Hodiny (h)',
  'unit.sub.day': 'Dni (d)',
  'unit.sub.wk': 'Týždne (týž)',
  'unit.sub.mo': 'Mesiace (mes)',
  'unit.sub.yr': 'Roky (r)',
  'unit.sub.wh': 'Watthodiny (Wh)',
  'unit.sub.kwh': 'Kilowatthodiny (kWh)',
  'unit.sub.cal': 'Kalórie (cal)',
  'unit.sub.kcal': 'Kilokalórie (kcal)',
  'unit.sub.j': 'Jouly (J)',
  'unit.sub.kj': 'Kilojouly (kJ)',
  'unit.sub.c': 'Celsius (°C)',
  'unit.sub.f': 'Fahrenheit (°F)',
  'unit.sub.k': 'Kelvin (K)',
  'unit.sub.kmh': 'km/h',
  'unit.sub.mph': 'mph',
  'unit.sub.ms': 'm/s',
  'unit.sub.kn': 'Uzly (kn)',

  // Trash
  'trash.title': 'Kôš',
  'trash.noItems': 'Kôš je prázdny',
  'trash.noItemsDesc': 'Vymazané položky sa zobrazia tu',
  'trash.emptyTrash': 'Vyprázdniť kôš',
  'trash.restoreAll': 'Obnoviť všetko',
  'trash.deleted': 'Vymazané',
  'trash.expiresIn': 'Vyprší za',
  'trash.days': 'dní',
  'trash.permanentDelete': 'Naozaj? Túto akciu nie je možné vrátiť späť.',
  'trash.loading': 'Načítavanie koša...',
  'trash.items': '{count} položiek',
  'trash.warning': 'Položky v koši budú po 30 dňoch trvalo vymazané.',
  'trash.emptyTitle': 'Kôš je prázdny',
  'trash.emptyDesc': 'Vymazané položky sa zobrazia tu. Položky sú trvalo odstránené po 30 dňoch.',
  'trash.confirmDelete': 'Naozaj chcete trvalo vymazať túto položku? Toto nie je možné vrátiť späť.',
  'trash.confirmEmpty': 'Naozaj chcete trvalo vymazať VŠETKY položky v koši? Toto nie je možné vrátiť späť.',
  'trash.unknown': 'Neznáme',
  'trash.daysAgo': 'pred {count} dňami',
  'trash.hoursAgo': 'pred {count} hodinami',
  'trash.justNow': 'Práve teraz',
  'trash.restore': 'Obnoviť',
  'trash.delete': 'Vymazať',

  // Knižnica
  'library.title': 'Knižnica',
  'library.noItemsDesc': 'Začnite pridaním prvej položky do knižnice',
  'library.searchPlaceholder': 'Hľadať položky...',
  'library.addItem': 'Pridať položku',
  'library.allCategories': 'Všetky kategórie',
  'library.newestFirst': 'Najnovšie',
  'library.oldestFirst': 'Najstaršie',
  'library.nameAZ': 'Názov (A-Z)',
  'library.nameZA': 'Názov (Z-A)',
  'library.highestValue': 'Najvyššia hodnota',
  'library.lowestValue': 'Najnižšia hodnota',
  'library.advancedFilters': 'Pokročilé filtre',
  'library.gridView': 'Mriežka',
  'library.listView': 'Zoznam',
  'library.edit': 'Upraviť',
  'library.move': 'Presunúť',
  'library.delete': 'Zmazať',
  'library.value': 'Hodnota',
  'library.original': 'Originál',
  'library.aiValue': 'AI Hodnota',
  'library.noItems': 'Žiadne položky nenájdené',
  'library.noItemsSearch': 'Skúste upraviť vyhľadávanie alebo filtre',
  'library.noItemsStart': 'Začnite pridaním prvej položky',
  'library.addFirstItem': '+ Pridať prvú položku',
  'library.moveItem': 'Presunúť položku',
  'library.moving': 'Presúva sa:',
  'library.currentCategory': 'Aktuálna kategória:',
  'library.uncategorized': 'Nezaradené',
  'library.selectCategory': 'Vyberte novú kategóriu:',
  'library.loading': 'Načítava sa knižnica...',
  'library.confirmDelete': 'Naozaj chcete zmazať túto položku?',
  'library.moveFailed': 'Presun položky zlyhal',
  'library.lockedItem': 'Táto položka je zamknutá',

  // Dashboard
  'dashboard.analytics': 'Analytika',
  'dashboard.itemsTracked': 'sledovaných položiek',
  'dashboard.categoriesLabel': 'kategórií',
  'dashboard.customize': 'Prispôsobiť',
  'dashboard.widgets': 'Widgety',
  'dashboard.save': 'Uložiť',
  'dashboard.loading': 'Načítavanie analytiky…',
  'dashboard.addWidget': 'Pridať widget',
  'dashboard.totalItems': 'Celkom položiek',
  'dashboard.portfolioValue': 'Hodnota portfólia',
  'dashboard.favorites': 'Obľúbené',
  'dashboard.avgItemValue': 'Priem. hodnota položky',
  'dashboard.newThisPeriod': 'nových v tomto období',
  'dashboard.vsLastPeriod': 'oproti minulému obdobiu',
  'dashboard.marked': 'označené',
  'dashboard.perItem': 'na položku',
  'dashboard.day': 'Deň',
  'dashboard.week': 'Týždeň',
  'dashboard.month': 'Mesiac',
  'dashboard.quarter': 'Štvrťrok',
  'dashboard.year': 'Rok',
  'dashboard.valueTrend': 'Vývoj hodnoty',
  'dashboard.itemsAdded': 'Pridané položky',
  'dashboard.conditionBreakdown': 'Rozdelenie podľa stavu',
  'dashboard.categoryDistribution': 'Rozdelenie podľa kategórií',
  'dashboard.priceDistribution': 'Rozdelenie podľa cien',
  'dashboard.mostValuable': 'Najhodnotnejšie položky',
  'dashboard.byLocation': 'Položky podľa umiestnenia',
  'dashboard.cumulativeValue': 'Kumulatívna hodnota',
  'dashboard.favoritesOverTime': 'Obľúbené v čase',
  'dashboard.topTags': 'Top štítky',
  'dashboard.purchaseSources': 'Zdroje nákupov',
  'dashboard.avgByCategory': 'Priem. hodnota podľa kategórií',
  'dashboard.valueTrendDesc': 'Celková pridaná hodnota za obdobie',
  'dashboard.itemsAddedDesc': 'Počet nových položiek za obdobie',
  'dashboard.conditionDesc': 'Rozdelenie podľa stavu položiek',
  'dashboard.categoryDesc': 'Položky zoskupené podľa kategórií',
  'dashboard.priceDistDesc': 'Ako sú hodnoty položiek rozložené cez cenové rozsahy',
  'dashboard.mostValuableDesc': 'Vaše najhodnotnejšie položky',
  'dashboard.byLocationDesc': 'Položky zoskupené podľa umiestnenia',
  'dashboard.cumulativeDesc': 'Priebežný súčet hodnoty portfólia v čase',
  'dashboard.favTrendDesc': 'Obľúbené položky pridané za obdobie',
  'dashboard.topTagsDesc': 'Najčastejšie používané štítky',
  'dashboard.purchaseDesc': 'Kde boli položky zakúpené',
  'dashboard.avgCatDesc': 'Priemerná hodnota položky podľa kategórií',
  'dashboard.chooseChartType': 'Zvoľte typ grafu',
  'dashboard.configureData': 'Konfigurovať dáta',
  'dashboard.previewCreate': 'Náhľad a vytvorenie',
  'dashboard.stepType': '1. Typ',
  'dashboard.stepData': '2. Dáta',
  'dashboard.stepPreview': '3. Náhľad',
  'dashboard.barChart': 'Stĺpcový graf',
  'dashboard.pieChart': 'Koláčový graf',
  'dashboard.lineChart': 'Čiarový graf',
  'dashboard.metricCard': 'Metrika',
  'dashboard.barChartDesc': 'Porovnanie hodnôt naprieč kategóriami',
  'dashboard.pieChartDesc': 'Zobrazenie pomerov celku',
  'dashboard.lineChartDesc': 'Sledovanie trendov v čase',
  'dashboard.metricCardDesc': 'Zobrazenie jednej kľúčovej metriky',
  'dashboard.chartTitle': 'Názov grafu *',
  'dashboard.description': 'Popis',
  'dashboard.value': 'Hodnota',
  'dashboard.dataPoints': 'Dátové body',
  'dashboard.addDataPoint': 'Pridať dátový bod',
  'dashboard.back': 'Späť',
  'dashboard.nextConfigure': 'Ďalej: Konfigurovať dáta',
  'dashboard.nextPreview': 'Ďalej: Náhľad',
  'dashboard.createChart': 'Vytvoriť graf',
  'dashboard.dashboardWidgets': 'Widgety dashboardu',
  'dashboard.widgetHint': 'Prepnúť viditeľnosť · potiahnite pre zmenu poradia',

  // Predplatné navyše
  'subscription.noWorkspaceError': 'Nie je vybraný žiadny pracovný priestor. Najprv vyberte pracovný priestor.',
  'subscription.checkoutError': 'Nepodarilo sa vytvoriť platobnú reláciu. Skúste to prosím znova.',
  'subscription.redirecting': 'Presmerovanie na Stripe...',

  // Nastavenia oznámení
  'notifSettings.loading': 'Načítavanie nastavení oznámení...',
  'notifSettings.title': 'Nastavenia oznámení',
  'notifSettings.subtitle': 'Nastavte kedy a ako dostávate oznámenia',
  'notifSettings.disableAll': 'Zakázať všetko',
  'notifSettings.enableAll': 'Povoliť všetko',
  'notifSettings.saving': 'Ukladanie...',
  'notifSettings.saveChanges': 'Uložiť zmeny',
  'notifSettings.saved': 'Nastavenia oznámení uložené!',
  'notifSettings.saveFailed': 'Uloženie nastavení zlyhalo',
  'notifSettings.typesTitle': 'Typy oznámení',
  'notifSettings.typesDesc': 'Vyberte, ktoré oznámenia chcete dostávať.',
  'notifSettings.emailToggleTitle': 'Odoslať e-mail pre toto oznámenie',
  'notifSettings.itemUpdates': 'Aktualizácie položiek',
  'notifSettings.itemUpdatesDesc': 'Oznámenie pri aktualizácii sledovaných položiek',
  'notifSettings.comments': 'Komentáre',
  'notifSettings.commentsDesc': 'Oznámenie keď niekto komentuje vaše položky',
  'notifSettings.mentions': 'Zmienky',
  'notifSettings.mentionsDesc': 'Oznámenie keď vás niekto zmieni v komentári',
  'notifSettings.duplicateDetection': 'Detekcia duplikátov',
  'notifSettings.duplicateDetectionDesc': 'Oznámenie keď AI deteguje možné duplikáty',
  'notifSettings.valuePredictions': 'Predikcie hodnoty',
  'notifSettings.valuePredictionsDesc': 'Oznámenie keď AI predpovedá zmeny hodnoty',
  'notifSettings.securityAlerts': 'Bezpečnostné upozornenia',
  'notifSettings.securityAlertsDesc': 'Dôležité bezpečnostné oznámenia (pokusy o prihlásenie, zmeny hesla)',
  'notifSettings.emailPrefs': 'E-mailové predvoľby',
  'notifSettings.emailPrefsDesc': 'Spravovať e-mailové oznámenia',
  'notifSettings.weeklyDigest': 'Týždenné zhrnutie',
  'notifSettings.weeklyDigestDesc': 'Týždenné zhrnutie aktivity vašich pracovných priestorov',
  'notifSettings.marketingEmails': 'Marketingové e-maily',
  'notifSettings.marketingEmailsDesc': 'Aktualizácie produktov, tipy a propagačný obsah',
  'notifSettings.channels': 'Doručovacie kanály',
  'notifSettings.channelsDesc': 'Zvoľte ako chcete dostávať oznámenia',
  'notifSettings.email': 'E-mail',
  'notifSettings.emailDesc': 'Dostávať oznámenia e-mailom',
  'notifSettings.push': 'Push oznámenia',
  'notifSettings.pushDesc': 'Dostávať push oznámenia na zariadeniach',
  'notifSettings.inApp': 'Oznámenia v aplikácii',
  'notifSettings.inAppDesc': 'Zobraziť oznámenia v aplikácii',
  'notifSettings.quietHours': 'Kľudové hodiny',
  'notifSettings.quietHoursDesc': 'Pozastaviť oznámenia v určitých hodinách',
  'notifSettings.enableQuietHours': 'Povoliť kľudové hodiny',
  'notifSettings.quietHoursSilenced': 'Oznámenia budú počas určenej doby stlmené',
  'notifSettings.startTime': 'Čas začiatku',
  'notifSettings.endTime': 'Čas konca',
  'notifSettings.timeTo': 'do',
  'notifSettings.unsavedChanges': 'Máte neuložené zmeny',

  // Time
  'time.today': 'Dnes',
  'time.yesterday': 'Včera',
  'time.daysAgo': 'dní späť',
  'time.justNow': 'práve teraz',
  'time.minutesAgo': 'minút späť',
  'time.hoursAgo': 'hodín späť',

  // Settings
  'settings.title': 'Nastavenia',
  'settings.subtitle': 'Spravujte nastavenia účtu a zabezpečenie',
  'settings.general': 'Všeobecné',
  'settings.appearance': 'Vzhľad',
  'settings.security': 'Zabezpečenie',
  'settings.accessibility': 'Prístupnosť',
  'settings.privacy': 'Súkromie',
  'settings.theme': 'Motív',
  'settings.themeDesc': 'Prepnúť medzi svetlým a tmavým režimom',
  'settings.language': 'Jazyk',
  'settings.languageDesc': 'Vyberte si preferovaný jazyk',
  'settings.darkMode': 'Tmavý režim',
  'settings.lightMode': 'Svetlý režim',
  'settings.darkTheme': 'Tmavý',
  'settings.lightTheme': 'Svetlý',
  'settings.profile': 'Informácie o profile',
  'settings.profileDesc': 'Aktualizujte svoje osobné údaje',
  'settings.saved': 'Nastavenia uložené!',
  'settings.saveFailed': 'Uloženie zlyhalo',

  // Accounting & Assets
  'accounting.title': 'Účtovníctvo majetku',
  'accounting.subtitle': 'Správa dlhodobého majetku, odpisov a alokácií',
  'accounting.assetRegister': 'Register majetku',
  'accounting.allItems': 'Všetky položky',
  'accounting.transactions': 'Transakcie',
  'accounting.createAsset': 'Vytvoriť majetek',
  'accounting.assetDetails': 'Detail majetku',
  'accounting.trackedAssets': 'Sledovaný majetek',
  'accounting.totalBookValue': 'Celková účtovná hodnota',
  'accounting.accumulatedDepreciation': 'Kumulované odpisy',
  'accounting.acquisitionCost': 'Obstarávacia cena',
  'accounting.assetNumber': 'Číslo majetku',
  'accounting.assetClass': 'Trieda majetku',
  'accounting.subClass': 'Podtrieda',
  'accounting.depreciationMethod': 'Metóda odpisovania',
  'accounting.usefulLifeMonths': 'Doba použiteľnosti (mesiace)',
  'accounting.salvageValue': 'Zostatková hodnota',
  'accounting.acquisitionDate': 'Dátum obstarania',
  'accounting.bookValue': 'Účtovná hodnota',
  'accounting.fairMarketValue': 'Trhová hodnota',
  'accounting.status': 'Stav',
  'accounting.active': 'Aktívny',
  'accounting.disposed': 'Vyradený',
  'accounting.writtenOff': 'Odpísaný',
  'accounting.heldForSale': 'Na predaj',
  'accounting.impaired': 'Znehodnotený',
  'accounting.transferred': 'Prevedený',
  'accounting.straightLine': 'Rovnomerné',
  'accounting.decliningBalance': 'Zrýchlené',
  'accounting.doubleDeclining': 'Dvojito zrýchlené',
  'accounting.sumOfYears': 'Súčet čísiel',
  'accounting.unitsOfProduction': 'Výkonové',
  'accounting.noDepreciation': 'Bez odpisov',
  'accounting.classBuildings': 'Budovy',
  'accounting.classMachinery': 'Stroje',
  'accounting.classEquipment': 'Vybavenie',
  'accounting.classVehicles': 'Vozidlá',
  'accounting.classFurniture': 'Nábytok',
  'accounting.classComputers': 'Počítače',
  'accounting.classSoftware': 'Softvér',
  'accounting.classCollectibles': 'Zberateľstvo',
  'accounting.classArt': 'Umenie',
  'accounting.classJewelry': 'Šperky',
  'accounting.classRealEstate': 'Nehnuteľnosti',
  'accounting.classInvestments': 'Investície',
  'accounting.classOther': 'Ostatné',
  'accounting.monthlyDepreciation': 'Mesačný odpis',
  'accounting.yearlyDepreciation': 'Ročný odpis',
  'accounting.remainingLife': 'Zostávajúca doba',
  'accounting.percentDepreciated': 'Percento odpísané',
  'accounting.selectItem': 'Vyberte položku',
  'accounting.selectMethod': 'Vyberte metódu odpisovania',
  'accounting.selectClass': 'Vyberte triedu majetku',
  'accounting.selectStatus': 'Vyberte stav',
  'accounting.recordImpairment': 'Zaznamenať znehodnotenie',
  'accounting.recordRevaluation': 'Zaznamenať precenenie',
  'accounting.disposeAsset': 'Vyradiť majetek',
  'accounting.impairmentAmount': 'Suma znehodnotenia',
  'accounting.revaluedAmount': 'Precenená suma',
  'accounting.disposalDate': 'Dátum vyradenia',
  'accounting.disposalProceeds': 'Výnos z predaja',
  'accounting.notes': 'Poznámky',
  'accounting.depreciationSchedule': 'Odpisový plán',
  'accounting.transactionHistory': 'História transakcií',
  'accounting.noAssets': 'Žiadny majetek nenájdený',
  'accounting.noItems': 'Žiadne položky nenájdené',
  'accounting.noTransactions': 'Žiadne transakcie nenájdené',
  'accounting.searchAssets': 'Hľadať majetek...',
  'accounting.searchItems': 'Hľadať položky...',
  'accounting.filterByStatus': 'Filtrovať podľa stavu',
  'accounting.filterByMethod': 'Filtrovať podľa metódy',
  'accounting.filterByClass': 'Filtrovať podľa triedy',
  'accounting.filterByCategory': 'Filtrovať podľa kategórie',
  'accounting.showTracked': 'Iba sledované',
  'accounting.showUntracked': 'Iba nesledované',
  'accounting.sortBy': 'Radiť podľa',
  'accounting.sortByName': 'Názov',
  'accounting.sortByCost': 'Cena',
  'accounting.sortByBook': 'Účtovná hodnota',
  'accounting.sortByDepreciation': 'Odpisy',
  'accounting.sortByDate': 'Dátum',
  'accounting.page': 'Strana',
  'accounting.of': 'z',
  'accounting.showing': 'Zobrazené',
  'accounting.assets': 'majetek',
  'accounting.items': 'položky',
  'accounting.months': 'mesiace',
  'accounting.years': 'roky',
  'accounting.assetCreated': 'Majetek úspešne vytvorený',
  'accounting.assetUpdated': 'Majetek úspešne aktualizovaný',
  'accounting.assetDisposed': 'Majetek úspešne vyradený',
  'accounting.impairmentRecorded': 'Znehodnotenie úspešne zaznamenané',
  'accounting.revaluationRecorded': 'Precenenie úspešne zaznamenané',
  'accounting.costAllocation': 'Priradenie nákladov',
  'accounting.allocations': 'Priradenia',
  'accounting.department': 'Oddelenie',
  'accounting.project': 'Projekt',
  'accounting.costCenter': 'Nákladové stredisko',
  'accounting.allocatedTo': 'Priradené k',
  'accounting.allocationPercent': 'Priradenie %',
  'accounting.addAllocation': 'Pridať priradenie',
  'accounting.removeAllocation': 'Odstrániť priradenie',
  'accounting.totalAllocation': 'Celkové priradenie',
  'accounting.mustBe100': 'Celkové priradenie musí byť 100%',
  'accounting.selectDepartment': 'Vyberte oddelenie',
  'accounting.selectProject': 'Vyberte projekt',
  'accounting.selectCostCenter': 'Vyberte nákladové stredisko',
  'accounting.noDepartments': 'Žiadne oddelenia nenájdené',
  'accounting.noProjects': 'Žiadne projekty nenájdené',
  'accounting.noCostCenters': 'Žiadne nákladové strediská nenájdené',
  'accounting.createDepartment': 'Vytvoriť oddelenie',
  'accounting.createProject': 'Vytvoriť projekt',
  'accounting.createCostCenter': 'Vytvoriť nákladové stredisko',
  'accounting.departmentName': 'Názov oddelenia',
  'accounting.projectName': 'Názov projektu',
  'accounting.costCenterName': 'Názov strediska',
  'accounting.departmentCode': 'Kód oddelenia',
  'accounting.projectCode': 'Kód projektu',
  'accounting.costCenterCode': 'Kód strediska',
  'accounting.manager': 'Manažér',
  'accounting.budget': 'Rozpočet',
  'accounting.startDate': 'Dátum začatia',
  'accounting.endDate': 'Dátum ukončenia',
  'accounting.allocationReport': 'Report priradenia',
  'accounting.byDepartment': 'Podľa oddelenia',
  'accounting.byProject': 'Podľa projektu',
  'accounting.byCostCenter': 'Podľa strediska',
  'accounting.exportReport': 'Exportovať report',
  'accounting.depreciationReport': 'Report odpisov',
  'accounting.assetValuation': 'Oceňovanie majetku',
  'accounting.gainLoss': 'Zisk/Strata',

  // Accounting KPIs
  'accounting.kpi.trackedAssets': 'Sledované aktíva',
  'accounting.kpi.itemsWithAccounting': 'Položky s účtovnictvom',
  'accounting.kpi.totalBookValue': 'Celková účtovná hodnota',
  'accounting.kpi.currentNetValue': 'Aktuálna čistá hodnota',
  'accounting.kpi.accDepreciation': 'Kumulované odpisy',
  'accounting.kpi.ofCost': 'z nákladov',
  'accounting.kpi.acquisitionCost': 'Náklady na nadobudnutie',
  'accounting.kpi.originalInvestment': 'Pôvodná investícia',

  // Accounting Tabs
  'accounting.tabs.register': 'Register majetku',
  'accounting.tabs.items': 'Všetky položky',
  'accounting.tabs.transactions': 'Transakcie',
  'accounting.allStatus': 'Všetky stavy',
  'accounting.allMethods': 'Všetky metódy',
  'accounting.allClasses': 'Všetky triedy',
  'accounting.allCategories': 'Všetky kategórie',
  'accounting.allConditions': 'Všetky stavy',
  'accounting.allItemsFilter': 'Všetky položky',
  'accounting.trackedOnly': 'Len sledované',
  'accounting.untrackedOnly': 'Len nesledované',
  'accounting.allTypes': 'Všetky typy',
  'accounting.itemCol': 'Položka',
  'accounting.acquisitionCol': 'Obstaranie',
  'accounting.bookValueCol': 'Účtovná hodnota',
  'accounting.depreciationCol': 'Odpisy',
  'accounting.methodCol': 'Metóda',
  'accounting.statusCol': 'Stav',
  'accounting.actionsCol': 'Akcie',
  'accounting.categoryCol': 'Kategória',
  'accounting.purchasePriceCol': 'Nákupná cena',
  'accounting.dateCol': 'Dátum',
  'accounting.trackingCol': 'Sledovanie',
  'accounting.actionCol': 'Akcia',
  'accounting.noTrackedYet': 'Zatiaľ žiadne sledované aktíva',
  'accounting.noAssetsMatch': 'Žiadne aktíva nezodpovedajú filtrom',
  'accounting.browseItems': 'Prehľadávať položky',
  'accounting.clearFilters': 'Vymazať filtre',
  'accounting.tracked': 'Sledované',
  'accounting.notTracked': 'Nesledované',
  'accounting.viewDetail': 'Zobraziť',
  'accounting.trackItem': 'Sledovať',
  'accounting.valueInfo': 'Informácie o hodnote',
  'accounting.depInfo': 'Odpisy',
  'accounting.accDep': 'Kum. odpisy',
  'accounting.monthly': 'Mesačne',
  'accounting.remainingMonths': 'Zostávajúce',
  'accounting.depPercent': 'Odpísané',
  'accounting.recentTx': 'Nedávne transakcie',
  'accounting.depreciate': 'Odpísať',
  'accounting.impairmentAction': 'Znehodnotenie',
  'accounting.revalueAction': 'Precenenie',
  'accounting.disposeAction': 'Vyradiť',
  'accounting.trackAsset': 'Sledovať aktívum',
  'accounting.cancel': 'Zrušiť',
  'accounting.creating': 'Vytvára sa…',
  'accounting.create': 'Vytvoriť aktívum',
  'accounting.confirmDisposal': 'Potvrdiť vyradenie',
  'accounting.searchAssetsPlaceholder': 'Hľadať aktíva…',
  'accounting.searchItemsPlaceholder': 'Hľadať položky…',
  'accounting.searchTxPlaceholder': 'Hľadať transakcie…',
  'accounting.noItemsInLib': 'Žiadne položky nenájdené',
  'accounting.noItemsMatch': 'Žiadne položky nezodpovedajú filtrom',
  'accounting.addItemsFirst': 'Najprv pridajte položky do knižnice.',
  'accounting.noTxYet': 'Zatiaľ žiadne transakcie',
  'accounting.noTxMatch': 'Žiadne transakcie nezodpovedajú filtrom',
  'accounting.startDepreciation': 'Začnite sledovať aktíva a spracovávať odpisy.',
  'accounting.adjustTxFilter': 'Skúste upraviť vyhľadávanie alebo filter.',
  'accounting.adjustFilters': 'Skúste upraviť vyhľadávanie alebo filtre.',
  'accounting.trackedAssetCount': 'Sledované aktíva ({count})',
  'accounting.filteredAssets': '{filtered} z {total} aktív',
  'accounting.yourItemsCount': 'Vaše položky ({count})',
  'accounting.filteredItems': '{filtered} z {total} položiek',
  'accounting.txCount': 'Transakcie ({count})',
  'accounting.filteredTx': '{filtered} z {total} transakcií',
  'accounting.unnamed': 'Nepomenované',
  'accounting.unclassified': 'Neklasifikované',
  'accounting.unknown': 'Neznáme',
  'accounting.txDateCol': 'Dátum',
  'accounting.txAssetCol': 'Aktívum',
  'accounting.txTypeCol': 'Typ',
  'accounting.txDescCol': 'Popis',
  'accounting.txAmountCol': 'Suma',
  'accounting.bookValueAfterCol': 'Účtovná hodnota po',
  'accounting.startTracking': 'Začnite pridávaním položiek do registra aktív.',
  'accounting.tipAutoFill': 'Tip: Použite záložku „Všetky položky" pre jednoduchšie sledovanie.',
  'accounting.itemIdLabel': 'ID položky *',
  'accounting.pasteItemId': 'Vložte ID položky',
  'accounting.selectClassOption': 'Vyberte triedu…',
  'accounting.impairmentDesc': 'Znehodnotenie znižuje účtovnú hodnotu, keď spätne získateľná suma klesne pod účtovnú hodnotu.',
  'accounting.impairmentAmountLabel': 'Suma znehodnotenia *',
  'accounting.reasonLabel': 'Dôvod',
  'accounting.reasonPlaceholder': 'Dôvod znehodnotenia',
  'accounting.effectiveDateLabel': 'Dátum účinnosti',
  'accounting.revalDesc': 'Upraví hodnotu aktíva na základe profesionálneho ocenenia alebo trhového posúdenia.',
  'accounting.newAppraisedValue': 'Nová odhadná hodnota *',
  'accounting.appraiserName': 'Meno odhadcu',
  'accounting.valuationMethodLabel': 'Metóda ocenenia',
  'accounting.valuationMethodPlaceholder': 'napr. Trhové porovnanie',
  'accounting.notesLabel': 'Poznámky',
  'accounting.optionalNotes': 'Voliteľné poznámky',
  'accounting.disposeDesc': 'Vyradenie odstráni aktívum z aktívneho sledovania.',
  'accounting.disposalAmountLabel': 'Suma predaja / vyradenia',
  'accounting.reasonSelect': 'Dôvod',
  'accounting.sold': 'Predané',
  'accounting.donated': 'Darované',
  'accounting.scrapped': 'Zošrotované',
  'accounting.stolenLost': 'Ukradnuté / Stratené',
  'accounting.tradedIn': 'Vymenené',
  'accounting.otherReason': 'Iné',
  'accounting.optional': 'Voliteľné',
  'accounting.expectedValue': 'Očakávaná hodnota na konci doby použiteľnosti',

  // Workspaces
  'workspaces.title': 'Pracovné priestory',
  'workspaces.myWorkspaces': 'Moje pracovné priestory',
  'workspaces.create': 'Vytvoriť pracovný priestor',
  'workspaces.switchTo': 'Prepnúť na',
  'workspaces.settings': 'Nastavenia pracovného priestoru',
  'workspaces.members': 'Členovia',
  'workspaces.personal': 'Osobný',
  'workspaces.organization': 'Organizácia',
  'workspaces.name': 'Názov pracovného priestoru',
  'workspaces.description': 'Popis',
  'workspaces.type': 'Typ',
  'workspaces.items': 'položky',
  'workspaces.totalItems': 'Celkový počet položiek',
  'workspaces.active': 'Aktívny',
  'workspaces.viewAll': 'Zobraziť všetky pracovné priestory',
  'workspaces.noWorkspaces': 'Zatiaľ žiadne pracovné priestory',
  'workspaces.createFirst': 'Vytvorte svoj prvý pracovný priestor',
  'workspaces.searchPlaceholder': 'Hľadať pracovné priestory...',
  'workspaces.sortByRecent': 'Nedávne',
  'workspaces.sortByName': 'Názov',
  'workspaces.sortByItems': 'Položky',
  'workspaces.sortByMembers': 'Členovia',
  'workspaces.hub': 'Centrum pracovných priestorov',
  'workspaces.manageSubtitle': 'Spravujte, organizujte a spolupracujte naprieč všetkými pracovnými priestormi',
  'workspaces.new': 'Nový pracovný priestor',
  'workspaces.organizations': 'Organizácie',
  'workspaces.loading': 'Načítavanie pracovných priestorov...',
  'workspaces.welcome': 'Vitajte v centre pracovných priestorov',
  'workspaces.welcomeSubtitle': 'Vytvorte si svoj prvý pracovný priestor.',
  'workspaces.personalDesc': 'Súkromný pracovný priestor len pre vás',
  'workspaces.orgDesc': 'Spolupracujte so svojím tímom',
  'workspaces.enterName': 'Zadajte názov pracovného priestoru...',
  'workspaces.creating': 'Vytvára sa...',
  'workspaces.getStarted': 'Začať',
  'workspaces.activeWorkspace': 'Aktívny pracovný priestor',
  'workspaces.switchToWorkspace': 'Prepnúť na pracovný priestor',
  'workspaces.openDashboard': 'Otvoriť dashboard',
  'workspaces.createNew': 'Vytvoriť nový pracovný priestor',
  'workspaces.personalOrOrg': 'Osobný alebo organizácia',
  'workspaces.noFound': 'Žiadne pracovné priestory nenájdené',
  'workspaces.clearSearch': 'Vymazať hľadanie',
  'workspaces.edit': 'Upraviť pracovný priestor',
  'workspaces.delete': 'Zmazať pracovný priestor',
  'workspaces.cannotBeUndone': 'Túto akciu nie je možné vrátiť',
  'workspaces.deletePermanently': 'Zmazať natrvalo',
  'workspaces.cancel': 'Zrušiť',
  'workspaces.save': 'Uložiť zmeny',
  'workspaces.continue': 'Pokračovať',
  'workspaces.back': 'Späť',
  'workspaces.typeStep': 'Typ',
  'workspaces.detailsStep': 'Podrobnosti',
  'workspaces.reviewStep': 'Kontrola',
  'workspaces.chooseType': 'Vyberte typ pracovného priestoru',
  'workspaces.personalWorkspace': 'Osobný pracovný priestor',
  'workspaces.configurePersonal': 'Nastavte svoj osobný pracovný priestor',
  'workspaces.configureOrg': 'Nastavte svoju organizáciu',
  'workspaces.reviewAndCreate': 'Skontrolujte a vytvorte',
  'workspaces.whatYouGet': 'Čo získate:',
  'workspaces.nameRequired': 'Názov pracovného priestoru *',
  'workspaces.descLabel': 'Popis',
  'workspaces.whatFor': 'Na čo je tento pracovný priestor?',
  'workspaces.privateSecure': 'Súkromné a bezpečné',
  'workspaces.fullItemMgmt': 'Kompletná správa položiek',
  'workspaces.personalAnalytics': 'Osobná analytika',
  'workspaces.teamCollaboration': 'Tímová spolupráca',
  'workspaces.roleBasedAccess': 'Riadenie prístupu podľa rolí',
  'workspaces.orgDashboard': 'Dashboard organizácie',
  'workspaces.inviteMembers': 'Pozvať členov',
  'workspaces.unlimitedItems': 'Neobmedzené položky',
  'workspaces.private': 'Súkromné',
  'workspaces.itemMgmtCategories': 'Správa položiek s kategóriami',
  'workspaces.aiCategorization': 'AI kategorizácia',
  'workspaces.analyticsDashboard': 'Analytický dashboard',
  'workspaces.teamInvitations': 'Pozvánky členov tímu',
  'workspaces.rbac': 'Riadenie prístupu podľa rolí',
  'workspaces.auditTrail': 'Auditná stopa organizácie',
  'workspaces.untitled': 'Bez názvu',
  'workspaces.createWorkspace': 'Vytvoriť pracovný priestor',
  'workspaces.switchThis': 'Prepnúť sem',
  'workspaces.viewOrg': 'Zobraziť org.',
  'workspaces.convertToOrg': 'Previesť na org.',
  'workspaces.star': 'Obľúbené',
  'workspaces.unstar': 'Odobrať z obľúbených',
  'workspaces.grid': 'Mriežka',
  'workspaces.list': 'Zoznam',

  // Workspace Settings
  'wsSettings.loading': 'Načítanie pracovného priestoru...',
  'wsSettings.notFound': 'Pracovný priestor nenájdený',
  'wsSettings.backToWorkspaces': 'Späť na pracovné priestory',
  'wsSettings.savedSuccess': 'Nastavenia úspešne uložené',
  'wsSettings.inviteSent': 'Pozvánka úspešne odoslaná',
  'wsSettings.roleUpdated': 'Rola úspešne aktualizovaná',
  'wsSettings.confirmRemoveMember': 'Naozaj chcete odstrániť tohto člena?',
  'wsSettings.memberRemoved': 'Člen úspešne odstránený',
  'wsSettings.inviteRevoked': 'Pozvánka zrušená',
  'wsSettings.ownershipTransferred': 'Vlastníctvo úspešne prevedené',
  'wsSettings.typeNameToConfirm': 'Zadajte názov pracovného priestoru na potvrdenie',
  'wsSettings.convertedSuccess': 'Pracovný priestor prevedený na organizáciu!',
  'wsSettings.organization': 'Organizácia',
  'wsSettings.personalWorkspace': 'Osobný pracovný priestor',
  'wsSettings.owner': 'Vlastník',
  'wsSettings.general': 'Všeobecné',
  'wsSettings.members': 'Členovia',
  'wsSettings.convertTab': 'Konverzia',
  'wsSettings.dangerZone': 'Nebezpečná zóna',
  'wsSettings.generalSettings': 'Všeobecné nastavenia',
  'wsSettings.workspaceName': 'Názov pracovného priestoru',
  'wsSettings.enterName': 'Zadajte názov',
  'wsSettings.description': 'Popis',
  'wsSettings.descriptionPlaceholder': 'Popíšte tento pracovný priestor...',
  'wsSettings.workspaceId': 'ID pracovného priestoru',
  'wsSettings.created': 'Vytvorené',
  'wsSettings.items': 'Položky',
  'wsSettings.categories': 'Kategórie',
  'wsSettings.saving': 'Ukladanie...',
  'wsSettings.saveChanges': 'Uložiť zmeny',
  'wsSettings.inviteMember': 'Pozvať člena',
  'wsSettings.emailPlaceholder': 'E-mailová adresa',
  'wsSettings.sendInvite': 'Odoslať pozvánku',
  'wsSettings.pendingInvitations': 'Čakajúce pozvánky',
  'wsSettings.revoke': 'Zrušiť',
  'wsSettings.currentMembers': 'Aktuálni členovia',
  'wsSettings.loadingMembers': 'Načítanie členov...',
  'wsSettings.remove': 'Odstrániť',
  'wsSettings.convertToOrg': 'Previesť na organizáciu',
  'wsSettings.upgradeTitle': 'Povýšiť na organizáciu',
  'wsSettings.upgradeDesc': 'Odomknite funkcie spolupráce prevodom osobného priestoru na organizáciu.',
  'wsSettings.featInvite': 'Pozvať členov',
  'wsSettings.featInviteDesc': 'Pridajte členov tímu a spolupracujte',
  'wsSettings.featRBAC': 'Prístup podľa rolí',
  'wsSettings.featRBACDesc': 'Priraďte role a spravujte oprávnenia',
  'wsSettings.featTeams': 'Tímy a skupiny',
  'wsSettings.featTeamsDesc': 'Organizujte členov do tímov',
  'wsSettings.convertNote': 'Táto akcia je nevratná. Vaše dáta a nastavenia budú zachované.',
  'wsSettings.convertNow': 'Previesť teraz',
  'wsSettings.transferOwnership': 'Previesť vlastníctvo',
  'wsSettings.transferDesc': 'Preveďte tento priestor na iného člena.',
  'wsSettings.transfer': 'Previesť',
  'wsSettings.deleteWorkspace': 'Vymazať pracovný priestor',
  'wsSettings.deleteDesc': 'Trvalo vymazať tento priestor a všetky dáta.',
  'wsSettings.delete': 'Vymazať',
  'wsSettings.cannotBeUndone': 'Túto akciu nie je možné vrátiť späť!',
  'wsSettings.transferWarn': 'Stratíte prístup vlastníka a stanete sa bežným členom.',
  'wsSettings.newOwner': 'Nový vlastník',
  'wsSettings.selectMember': 'Vyberte člena',
  'wsSettings.cancel': 'Zrušiť',
  'wsSettings.transferring': 'Prevádzanie...',
  'wsSettings.deleteWarning': 'Toto trvalo vymaže pracovný priestor',
  'wsSettings.typeToConfirm': 'Pre potvrdenie zadajte názov:',
  'wsSettings.deleting': 'Mazanie...',
  'wsSettings.deletePermanently': 'Vymazať trvalo',
  'wsSettings.converting': 'Prevádzanie...',

  // Notifications
  'notifications.title': 'Oznámenia',
  'notifications.markAsRead': 'Označiť ako prečítané',
  'notifications.markAllAsRead': 'Označiť všetko ako prečítané',
  'notifications.delete': 'Vymazať',
  'notifications.deleteAll': 'Vymazať všetko',
  'notifications.clearAll': 'Vymazať všetko',
  'notifications.unread': 'Neprečítané',
  'notifications.all': 'Všetko',
  'notifications.noNotifications': 'Žiadne oznámenia',
  'notifications.noUnread': 'Žiadne neprečítané oznámenia',
  'notifications.today': 'Dnes',
  'notifications.yesterday': 'Včera',
  'notifications.older': 'Staršie',
  'notifications.justNow': 'Práve teraz',
  'notifications.minutesAgo': 'pred {count}m',
  'notifications.hoursAgo': 'pred {count}h',
  'notifications.daysAgo': 'pred {count}d',
  'notifications.search': 'Hľadať oznámenia...',
  'notifications.settings': 'Nastavenia oznámení',
  'notifications.types.itemUpdate': 'Aktualizácie položiek',
  'notifications.types.comment': 'Komentáre',
  'notifications.types.aiSuggestion': 'Návrhy AI',
  'notifications.types.duplicate': 'Duplikáty',
  'notifications.types.valueChanged': 'Zmeny hodnôt',
  'notifications.types.security': 'Zabezpečenie',
  'notifications.types.storage': 'Úložisko',
  'notifications.types.mention': 'Zmienky',
  'notifications.types.share': 'Zdieľané',
  'notifications.types.system': 'Systém',
  'notifications.loading': 'Načítavanie oznámení...',
  'notifications.noMatching': 'Žiadne zodpovedajúce oznámenia',
  'notifications.tryAdjusting': 'Skúste upraviť filtre',
  'notifications.deleteConfirm': 'Zmazať všetky oznámenia? Túto akciu nie je možné vrátiť.',
  'notifications.markAllRead': 'Označiť všetko ako prečítané',
  'notifications.markRead': 'Označiť ako prečítané',
  'notifications.markUnread': 'Označiť ako neprečítané',
  'notifications.allCaughtUp': 'Všetko je prečítané!',
  'notifications.showAll': 'Zobraziť všetko',
  'notifications.empty': 'Žiadne oznámenia',
  'notifications.viewAll': 'Zobraziť všetky oznámenia',
  'notifications.clickToView': 'Kliknite pre zobrazenie',
  'notifications.moreNotifications': '+{count} ďalších oznámení',

  // Two-Factor Authentication
  'twofa.title': 'Dvojfaktorové overenie',
  'twofa.subtitle': 'Pridajte svojmu účtu ďalšiu vrstvu zabezpečenia',
  'twofa.enabled': '2FA povolené',
  'twofa.disabled': '2FA zakázané',
  'twofa.enable': 'Povoliť 2FA',
  'twofa.disable': 'Zakázať 2FA',
  'twofa.status': 'Stav',
  'twofa.setup': 'Nastavenie',
  'twofa.setupInstructions': 'Naskenujte QR kód pomocou aplikácie autentikátora',
  'twofa.manualEntry': 'Alebo zadajte tento kód ručne',
  'twofa.verificationCode': 'Overovací kód',
  'twofa.enterCode': 'Zadajte 6miestny kód',
  'twofa.verify': 'Overiť',
  'twofa.backupCodes': 'Záložné kódy',
  'twofa.backupCodesInfo': 'Uložte tieto kódy na bezpečné miesto',
  'twofa.backupCodesRemaining': 'Zostáva {count} záložných kódov',
  'twofa.regenerateBackupCodes': 'Regenerovať záložné kódy',
  'twofa.downloadBackupCodes': 'Stiahnuť kódy',
  'twofa.copyCode': 'Kopírovať kód',
  'twofa.copied': 'Skopírované!',
  'twofa.setupSuccess': 'Dvojfaktorové overenie bolo úspešne povolené',
  'twofa.disableSuccess': 'Dvojfaktorové overenie zakázané',
  'twofa.invalidCode': 'Neplatný overovací kód',
  'twofa.required': 'Vyžadovaný 2FA kód',
  'twofa.step1': 'Krok 1: Nainštalujte aplikáciu autentikátora',
  'twofa.step2': 'Krok 2: Naskenujte QR kód',
  'twofa.step3': 'Krok 3: Overte',
  'twofa.apps': 'Odporúčané aplikácie: Google Authenticator, Authy, Microsoft Authenticator',
  'twofa.loading': 'Načítavanie nastavení 2FA...',
  'twofa.whyEnable': 'Prečo aktivovať 2FA?',
  'twofa.protectsAccount': 'Chráni váš účet aj pri prezradení hesla',
  'twofa.timeBasedCodes': 'Používa časové kódy z autentifikačných aplikácií',
  'twofa.backupAccess': 'Záložné kódy pre núdzový prístup',
  'twofa.enableTwoFactor': 'Aktivovať dvojfaktorovú autentifikáciu',
  'twofa.accountProtected': 'Váš účet je chránený 2FA',
  'twofa.scanQrCode': 'Krok 1: Naskenujte QR kód',
  'twofa.scanQrDesc': 'Naskenujte QR kód pomocou autentifikačnej aplikácie',
  'twofa.saveBackupCodes': 'Krok 2: Uložte záložné kódy',
  'twofa.saveBackupDesc': 'Uložte tieto kódy na bezpečné miesto.',
  'twofa.verifyStep': 'Krok 3: Overte',
  'twofa.verifyDesc': 'Zadajte 6-miestny kód z autentifikačnej aplikácie',
  'twofa.cantScan': 'Nemôžete skenovať? Zadajte kód ručne:',
  'twofa.verifyAndEnable': 'Overiť a aktivovať',
  'twofa.cancelSetup': 'Zrušiť nastavenie',
  'twofa.disableConfirm': 'Deaktivovať dvojfaktorovú autentifikáciu?',
  'twofa.lessSecure': 'Váš účet bude bez 2FA menej bezpečný.',
  'twofa.enterCodeConfirm': 'Pre potvrdenie zadajte overovací kód:',
  'twofa.codeOrBackup': 'Kód alebo záložný kód',
  'twofa.regenerateTitle': 'Pregenerovať záložné kódy',
  'twofa.regenerateWarning': 'Tým sa zneplatnia všetky existujúce záložné kódy.',
  'twofa.generateNew': 'Vygenerovať nové kódy',
  'twofa.newCodes': 'Nové záložné kódy',
  'twofa.saveNewCodes': 'Uložte tieto kódy. Staré kódy sú neplatné.',
  'twofa.download': 'Stiahnuť',
  'twofa.done': 'Hotovo',
  'twofa.twofaEnabled': '2FA Aktivované!',
  'twofa.saveBeforeClose': 'Pred zatvorením sa uistite, že máte uložené záložné kódy.',
  'twofa.savedMyCodes': 'Uložil som si kódy',

  // Misc
  'misc.noData': 'Žiadne dáta',
  'misc.error': 'Vyskytla sa chyba',
  'misc.success': 'Úspech',
  'misc.to': 'do',
  'misc.yes': 'Áno',
  'misc.no': 'Nie',

  // Audit Trail
  'audit.title': 'Protokol aktivít',
  'audit.subtitle': 'Sledujte všetky zmeny a aktivity vo vašom účte',
  'audit.tableView': 'Tabuľka',
  'audit.timelineView': 'Časová os',
  'audit.dashboardView': 'Dashboard organizácie',
  'audit.filters': 'Filtre',
  'audit.exportCsv': 'Exportovať CSV',
  'audit.search': 'Hľadať podľa používateľa, akcie alebo zdroja...',
  'audit.totalEvents': 'Celkom udalostí',
  'audit.eventsToday': 'Udalosti dnes',
  'audit.activeUsers': 'Aktívni používatelia',
  'audit.topActions': 'Najčastejšie akcie',
  'audit.actionType': 'Typ akcie',
  'audit.allActions': 'Všetky akcie',
  'audit.resourceType': 'Typ zdroja',
  'audit.allResources': 'Všetky zdroje',
  'audit.startDate': 'Dátum od',
  'audit.endDate': 'Dátum do',
  'audit.clear': 'Vymazať',
  'audit.time': 'Čas',
  'audit.user': 'Používateľ',
  'audit.action': 'Akcia',
  'audit.resource': 'Zdroj',
  'audit.details': 'Podrobnosti',
  'audit.source': 'Zdroj',
  'audit.noLogs': 'Žiadne záznamy nenájdené',
  'audit.emptyState': 'Aktivity sa tu zobrazia, keď budú vykonané akcie',
  'audit.loading': 'Načítavanie...',
  'audit.loadingTimeline': 'Načítavanie časovej osi...',
  'audit.loadingDashboard': 'Načítavanie dashboardu organizácie...',
  'audit.page': 'Stránka {current} z {total}',
  'audit.events': 'udalostí',
  'audit.actions': 'akcií',
  'audit.system': 'Systém',
  'audit.unknown': 'Neznámy',
  'audit.empty': '(prázdne)',
  'audit.revert': 'Vrátiť túto zmenu',
  'audit.reverting': 'Vrátenie...',
  'audit.revertToPrevious': 'Vrátiť na predchádzajúcu hodnotu',
  'audit.logDetails': 'Podrobnosti záznamu',
  'audit.eventInfo': 'Informácie o udalosti',
  'audit.timestamp': 'Časová pečiatka',
  'audit.resourceId': 'ID zdroja',
  'audit.resourceName': 'Názov zdroja',
  'audit.userInfo': 'Informácie o používateľovi',
  'audit.userId': 'ID používateľa',
  'audit.role': 'Rola',
  'audit.fieldChange': 'Zmena poľa',
  'audit.oldValue': 'Stará hodnota',
  'audit.newValue': 'Nová hodnota',
  'audit.techDetails': 'Technické podrobnosti',
  'audit.ipAddress': 'IP adresa',
  'audit.device': 'Zariadenie',
  'audit.userAgent': 'User Agent',
  'audit.metadata': 'Ďalšie metadáta',
  'audit.revertChange': 'Vrátiť zmenu',
  'audit.dashboardTitle': 'Dashboard auditu organizácie',
  'audit.lastDays': 'Posledných {days} dní',
  'audit.today': 'Dnes',
  'audit.mostActiveUsers': 'Najaktívnejší používatelia',
  'audit.recentSecurity': 'Nedávne bezpečnostné udalosti',
  'audit.actionDistribution': 'Rozloženie akcií',
  'audit.noData': 'Dáta dashboardu organizácie nie sú k dispozícii',
  'audit.minutesAgo': 'pred {count}m',
  'audit.hoursAgo': 'pred {count}h',
  'audit.daysAgo': 'pred {count}d',
  'audit.justNow': 'práve teraz',
  'audit.group.items': 'Položky',
  'audit.group.imagesFiles': 'Obrázky a súbory',
  'audit.group.comments': 'Komentáre',
  'audit.group.categories': 'Kategórie',
  'audit.group.auth': 'Autentifikácia',
  'audit.group.security': 'Bezpečnosť',
  'audit.group.members': 'Členovia a pozvánky',
  'audit.group.ai': 'AI a automatizácia',
  'audit.group.notifications': 'Oznámenia a systém',
  'audit.group.bulk': 'Hromadné akcie a export',
  'audit.action.itemCreated': 'Položka vytvorená',
  'audit.action.itemUpdated': 'Položka aktualizovaná',
  'audit.action.itemDeleted': 'Položka zmazaná',
  'audit.action.itemRestored': 'Položka obnovená',
  'audit.action.favoriteAdded': 'Pridané do obľúbených',
  'audit.action.favoriteRemoved': 'Odobraté z obľúbených',
  'audit.action.imageUploaded': 'Obrázok nahraný',
  'audit.action.imageDeleted': 'Obrázok zmazaný',
  'audit.action.fileUploaded': 'Súbor nahraný',
  'audit.action.fileDeleted': 'Súbor zmazaný',
  'audit.action.commentAdded': 'Komentár pridaný',
  'audit.action.commentEdited': 'Komentár upravený',
  'audit.action.commentDeleted': 'Komentár zmazaný',
  'audit.action.categoryCreated': 'Kategória vytvorená',
  'audit.action.categoryUpdated': 'Kategória aktualizovaná',
  'audit.action.categoryDeleted': 'Kategória zmazaná',
  'audit.action.login': 'Prihlásenie',
  'audit.action.logout': 'Odhlásenie',
  'audit.action.registration': 'Registrácia',
  'audit.action.loginFailed': 'Prihlásenie zlyhalo',
  'audit.action.passwordChanged': 'Heslo zmenené',
  'audit.action.passwordReset': 'Požiadavka na reset hesla',
  'audit.action.profileUpdated': 'Profil aktualizovaný',
  'audit.action.twoFaEnabled': '2FA povolené',
  'audit.action.twoFaDisabled': '2FA zakázané',
  'audit.action.accountDeleted': 'Účet zmazaný',
  'audit.action.unauthorizedAccess': 'Neautorizovaný prístup',
  'audit.action.roleChanged': 'Rola zmenená',
  'audit.action.permissionChanged': 'Oprávnenie zmenené',
  'audit.action.invitationSent': 'Pozvánka odoslaná',
  'audit.action.memberRemoved': 'Člen odobratý',
  'audit.action.aiCategorization': 'AI kategorizácia',
  'audit.action.aiDuplicate': 'AI detekcia duplikátov',
  'audit.action.aiValue': 'AI predikcia hodnoty',
  'audit.action.aiAccepted': 'AI návrh prijatý',
  'audit.action.aiRejected': 'AI návrh odmietnutý',
  'audit.action.notificationSent': 'Oznámenie odoslané',
  'audit.action.systemConfig': 'Konfigurácia systému zmenená',
  'audit.action.bulkExport': 'Hromadný export',
  'audit.action.bulkImport': 'Hromadný import',
  'audit.action.bulkDelete': 'Hromadné zmazanie',
  'audit.resource.items': 'Položky',
  'audit.resource.categories': 'Kategórie',
  'audit.resource.users': 'Používatelia',
  'audit.resource.organization': 'Organizácia',
  'audit.resource.comments': 'Komentáre',
  'audit.resource.images': 'Obrázky',
  'audit.resource.files': 'Súbory',
  'audit.resource.notifications': 'Oznámenia',
  'audit.resource.system': 'Systém',

  // Auth Pages
  'auth.welcomeBack': 'Vitajte späť',
  'auth.signInSubtitle': 'Prihláste sa a pokračujte do svojho pracovného priestoru',
  'auth.email': 'E-mailová adresa',
  'auth.emailPlaceholder': 'vas@email.com',
  'auth.password': 'Heslo',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signIn': 'Prihlásiť sa',
  'auth.signingIn': 'Prihlasovanie...',
  'auth.forgotPassword': 'Zabudli ste heslo?',
  'auth.noAccount': 'Nemáte účet?',
  'auth.createOne': 'Vytvoriť účet',
  'auth.orContinueWith': 'alebo',
  'auth.rememberMe': 'Zapamätať si ma',
  'auth.secureLogin': 'Bezpečné a šifrované prihlásenie',
  'auth.benefit1': 'Inteligentná správa položiek',
  'auth.benefit2': 'Spolupráca v reálnom čase',
  'auth.benefit3': 'Pokročilé analýzy',
  'auth.emailRequired': 'E-mail a heslo sú povinné',
  'auth.loginFailed': 'Prihlásenie zlyhalo. Skúste to prosím znova.',

  'auth.createAccount': 'Vytvoriť účet',
  'auth.signUpSubtitle': 'Začnite svoju cestu s nami ešte dnes',
  'auth.firstName': 'Meno',
  'auth.firstNamePlaceholder': 'Ján',
  'auth.lastName': 'Priezvisko',
  'auth.lastNamePlaceholder': 'Novák',
  'auth.confirmPassword': 'Potvrdiť heslo',
  'auth.signUp': 'Vytvoriť účet',
  'auth.signingUp': 'Vytváranie účtu...',
  'auth.hasAccount': 'Už máte účet?',
  'auth.signInLink': 'Prihlásiť sa',
  'auth.passwordMismatch': 'Heslá sa nezhodujú',
  'auth.passwordTooShort': 'Heslo musí mať aspoň 8 znakov',
  'auth.fillAllFields': 'Vyplňte prosím všetky povinné polia',
  'auth.signupFailed': 'Registrácia zlyhala. Skúste to prosím znova.',
  'auth.emailAlreadyExists': 'Účet s touto e-mailovou adresou už existuje. Prihláste sa alebo použite inú e-mailovú adresu.',
  'auth.networkError': 'Nie je možné pripojiť sa k serveru. Skontrolujte prosím pripojenie na internet a skúste to znova.',
  'auth.signupSuccess': 'Účet vytvorený! Skontrolujte e-mail na overenie.',
  'auth.joinCommunity': 'Pripojte sa k našej komunite',
  'auth.benefitSecure': 'Bezpečné a šifrované',
  'auth.benefitVerified': 'E-mailom overené účty',
  'auth.benefitSmart': 'Inteligentná kategorizácia',
  'auth.agreeTerms': 'Registráciou súhlasíte s našimi',
  'auth.termsOfService': 'Podmienky služby',
  'auth.and': 'a',
  'auth.privacyPolicy': 'Zásady ochrany osobných údajov',
  'auth.passwordStrength': 'Sila hesla',
  'auth.weak': 'Slabé',
  'auth.fair': 'Priemerné',
  'auth.good': 'Dobré',
  'auth.strong': 'Silné',

  'auth.forgotPasswordTitle': 'Zabudli ste heslo?',
  'auth.forgotPasswordSubtitle': 'Zadajte svoj e-mail a my vám pošleme odkaz na obnovenie',
  'auth.sendResetLink': 'Odoslať odkaz',
  'auth.sending': 'Odosielanie...',
  'auth.resetLinkSent': 'Odkaz na obnovenie hesla bol odoslaný na váš e-mail',
  'auth.backToLogin': 'Späť na prihlásenie',
  'auth.resetYourPassword': 'Obnovte svoje heslo',
  'auth.resetDescription': 'Zadajte svoju e-mailovú adresu a my vám pošleme odkaz na obnovenie hesla',
  'auth.emailRequired2': 'E-mail je povinný',
  'auth.sendFailed': 'Nepodarilo sa odoslať e-mail na obnovenie',
  'auth.checkInbox': 'Skontrolujte svoju schránku',
  'auth.resetEmailSentDesc': 'Odoslali sme odkaz na obnovenie hesla na váš e-mail. Skontrolujte svoju schránku a priečinok so spamom.',

  'auth.resetPasswordTitle': 'Obnoviť heslo',
  'auth.resetPasswordSubtitle': 'Zadajte svoje nové heslo',
  'auth.newPassword': 'Nové heslo',
  'auth.confirmNewPassword': 'Potvrdiť nové heslo',
  'auth.resetPassword': 'Obnoviť heslo',
  'auth.resetting': 'Obnovuje sa...',
  'auth.resetSuccess': 'Heslo úspešne obnovené! Presmerovanie...',
  'auth.resetFailed': 'Obnovenie hesla zlyhalo',
  'auth.invalidResetLink': 'Neplatný odkaz',
  'auth.invalidResetDesc': 'Tento odkaz na obnovenie hesla je neplatný alebo vypršal',
  'auth.requestNewLink': 'Vyžiadať nový odkaz',
  'auth.noTokenProvided': 'Nebol poskytnutý žiadny token',
  'auth.createNewPassword': 'Vytvorte nové heslo',
  'auth.createNewPasswordDesc': 'Zvoľte silné heslo pre svoj účet',
  'auth.tryAgain': 'Skúsiť znova',

  'auth.verifyEmail': 'Overte svoj e-mail',
  'auth.verifyEmailDesc': 'Odoslali sme overovací odkaz na váš e-mail',
  'auth.checkYourInbox': 'Skontrolujte svoju schránku',
  'auth.weSentVerification': 'Odoslali sme overovací odkaz na',
  'auth.checkSpam': 'Kliknite na odkaz v e-maili na overenie účtu. Ak ho nevidíte, skontrolujte priečinok so spamom.',
  'auth.resendVerification': 'Znovu odoslať overovací e-mail',
  'auth.resendSuccess': 'Overovací e-mail znovu odoslaný! Skontrolujte svoju schránku.',
  'auth.alreadyVerified': 'Už overené? Prihlásiť sa',
  'auth.verifying': 'Overovanie e-mailu',
  'auth.verifyingDesc': 'Počkajte prosím, kým potvrdíme vašu e-mailovú adresu...',
  'auth.emailVerified': 'E-mail overený!',
  'auth.verifiedSuccess': 'E-mail úspešne overený! Presmerovanie na dashboard...',
  'auth.verificationFailed': 'Overenie zlyhalo',
  'auth.resendFailed': 'Nepodarilo sa znovu odoslať overovací e-mail',
  'auth.goToLogin': 'Prejsť na prihlásenie',
  'auth.goToDashboard': 'Prejsť na dashboard',
  'auth.enterEmailResend': 'Zadajte e-mail na opätovné odoslanie',
  'auth.verificationSent': 'Overovací e-mail odoslaný! Skontrolujte svoju schránku.',

  // Support
  'auth.needHelp': 'Potrebujete pomoc?',
  'auth.contactSupport': 'Kontaktujte podporu',
  'auth.havingProblems': 'Máte problémy?',
  'auth.reachOutToUs': 'Kontaktujte nás',
  'auth.contactUsForHelp': 'Kontaktujte nás pre pomoc s vaším účtom',
  'auth.supportEmail': 'Pošlite nám e-mail',
  'auth.supportSubject': 'ELVOAQ žiadosť o podporu',
  'auth.supportBody': 'Ahoj,\n\nPotrebujem pomoc so svojím účtom.\n\nProsím popíšte svoj problém:',

  // Sidebar
  'sidebar.collapse': 'Zbaliť',
  'sidebar.collapseSidebar': 'Zbaliť bočný panel',
  'sidebar.expandSidebar': 'Rozbaliť bočný panel',

  // User Dropdown
  'dropdown.settings': 'Nastavenia',
  'dropdown.subscription': 'Predplatné',
  'dropdown.logout': 'Odhlásiť sa',
  'dropdown.openProfile': 'Otvoriť profil',

  // Settings — Profile
  'settings.profileTitle': 'Informácie o profile',
  'role.OWNER': 'Vlastník',
  'role.ADMIN': 'Super admin',
  'role.MEMBER': 'Člen',
  'role.LEAD': 'Lead',
  'role.VIEWER': 'Priazerátel',
  'settings.firstName': 'Meno',
  'settings.lastName': 'Priezvisko',
  'settings.firstNamePlaceholder': 'Zadajte svoje meno',
  'settings.lastNamePlaceholder': 'Zadajte svoje priezvisko',
  'settings.emailAddress': 'E-mailová adresa',
  'settings.changeEmail': 'Zmeniť',
  'settings.newEmailAddress': 'Nová e-mailová adresa',
  'settings.enterNewEmail': 'Zadajte novú e-mailovú adresu',
  'settings.currentPassword': 'Aktuálne heslo',
  'settings.currentPasswordConfirm': 'Zadajte aktuálne heslo na potvrdenie',
  'settings.updatingEmail': 'Aktualizácia...',
  'settings.updateEmail': 'Aktualizovať e-mail',
  'settings.bio': 'O mne',
  'settings.bioPlaceholder': 'Povedzte nám niečo o sebe...',
  'settings.defaultCurrency': 'Predvolená mena',
  'settings.currencyHint': 'Táto mena sa použije na zobrazenie prepočítaných hodnôt v celej aplikácii.',
  'settings.savingChanges': 'Ukladanie...',

  // Settings — Security
  'settings.securityTitle': 'Nastavenia zabezpečenia',
  'settings.changePassword': 'Zmeniť heslo',
  'settings.enterCurrentPassword': 'Zadajte aktuálne heslo',
  'settings.newPassword': 'Nové heslo',
  'settings.enterNewPassword': 'Zadajte nové heslo',
  'settings.confirmPassword': 'Potvrdiť heslo',
  'settings.confirmNewPassword': 'Potvrďte nové heslo',
  'settings.updatingPassword': 'Aktualizácia...',

  // Settings — Notifications
  'settings.notifPreferences': 'Nastavenia oznámení',
  'settings.notifTypes': 'Typy oznámení',
  'settings.notifItemUpdates': 'Aktualizácie položiek',
  'settings.notifItemUpdatesDesc': 'Upozornenie pri vytvorení, aktualizácii alebo zmazaní položiek',
  'settings.notifComments': 'Komentáre',
  'settings.notifCommentsDesc': 'Oznámenia o nových komentároch k vašim položkám',
  'settings.notifMentions': 'Zmienky',
  'settings.notifMentionsDesc': 'Upozornenie keď vás niekto zmieni',
  'settings.notifDuplicates': 'Detekcia duplikátov',
  'settings.notifDuplicatesDesc': 'AI upozornenia pri nájdení potenciálnych duplikátov',
  'settings.notifValuePredictions': 'Predikcie hodnôt',
  'settings.notifValuePredictionsDesc': 'Oznámenia o zmenách hodnôt a predikciách',
  'settings.notifSecurityAlerts': 'Bezpečnostné upozornenia',
  'settings.notifSecurityAlertsDesc': 'Dôležité bezpečnostné oznámenia',
  'settings.notifEmailPrefs': 'E-mailové preferencie',
  'settings.notifWeeklyDigest': 'Týždenný prehľad',
  'settings.notifWeeklyDigestDesc': 'Týždenný súhrn aktivít',
  'settings.notifMarketing': 'Marketingové e-maily',
  'settings.notifMarketingDesc': 'Novinky o funkciách a akciách',
  'settings.notifChannels': 'Kanály doručenia',
  'settings.notifEmail': 'E-mailové oznámenia',
  'settings.notifEmailDesc': 'Dostávať oznámenia e-mailom',
  'settings.notifPush': 'Push oznámenia',
  'settings.notifPushDesc': 'Prehliadačové a mobilné push oznámenia',
  'settings.notifInApp': 'Oznámenia v aplikácii',
  'settings.notifInAppDesc': 'Zobrazovať oznámenia vnútri aplikácie',
  'settings.notifQuietHours': 'Hodiny kľudu',
  'settings.notifEnableQuietHours': 'Povoliť hodiny kľudu',
  'settings.notifQuietHoursDesc': 'Pozastaviť nekritické oznámenia počas určitých hodín',
  'settings.notifStartTime': 'Čas začiatku',
  'settings.notifEndTime': 'Čas konca',
  'settings.notifSaving': 'Ukladanie...',
  'settings.notifSaveBtn': 'Uložiť nastavenia oznámení',
  'settings.loadingNotifications': 'Načítavanie nastavení oznámení...',

  // Settings — Accessibility
  'settings.accessibilityTitle': 'Prístupnosť & Zobrazenie',
  'settings.accessibilityDesc': 'Prispôsobte animácie a interakcie pre váš komfort a zariadenie.',
  'settings.motionAnimations': 'Pohyb & Animácie',
  'settings.reduceMotion': 'Obmedziť pohyb',
  'settings.reduceMotionDesc': 'Vypnúť prechody, hover animácie a dekoratívne pohybové efekty v celej aplikácii',
  'settings.reduceMotionHint': 'Automaticky rešpektuje aj nastavenie "Obmedziť pohyb" vášho operačného systému.',
  'settings.touchMobile': 'Dotyk & Mobil',
  'settings.touchFriendly': 'Režim pre dotykové ovládanie',
  'settings.touchFriendlyDesc': 'Nahradiť hover interakcie alternatívami na kliknutie. Zväčší tlačidlá a prepínače pre jednoduchšie dotykové ovládanie.',
  'settings.touchFriendlyHint': 'Odporúčané pri použití tabletu alebo dotykového zariadenia.',
  'settings.mobileWarningTitle': 'Upozornenie k mobilnému použitiu',
  'settings.mobileWarningText': 'ELVOAQ Vault je optimalizovaný pre desktopové a tabletové obrazovky. Na menších mobilných zariadeniach môžu byť niektoré funkcie obmedzené alebo vyžadovať posúvanie. Pre najlepší zážitok odporúčame použitie desktopového prehliadača alebo tabletu.',
  'settings.mobileWarningDarkHint': 'Tip: Tmavý režim znižuje namáhanie očí na mobilných obrazovkách.',
  'settings.mobileWarningLightHint': 'Tip: Svetlý režim môže zlepšiť čitateľnosť v jasnom prostredí.',

  // Settings — Privacy
  'settings.privacyTitle': 'Ochrana súkromia & Dáta',
  'settings.privacyTab': 'Súkromie',
  'settings.yourData': 'Vaše dáta',
  'settings.yourDataDesc': 'Vaše osobné údaje sú chránené a bezpečne uložené. Vaše dáta nikdy nezdieľame s tretími stranami.',
  'settings.dangerZone': '⚠️ Nebezpečná zóna',
  'settings.dangerZoneDesc': 'Zmazanie účtu je trvalé a nedá sa vrátiť späť. Všetky vaše položky, dáta a nastavenia budú trvalo zmazané.',
  'settings.deleteAccount': 'Zmazať môj účet',
  'settings.deleteConfirmTitle': '⚠️ Ste si naprosto istí?',
  'settings.deleteConfirmDesc': 'Túto akciu nemožno vrátiť späť. Zadajte prosím heslo na potvrdenie zmazania účtu.',
  'settings.enterPassword': 'Zadajte svoje heslo',
  'settings.deleting': 'Mazanie...',
  'settings.confirmDeleteBtn': 'Áno, zmazať môj účet',
  'settings.privacyPolicy': 'Zásady ochrany osobných údajov',
  'settings.privacyPolicyDesc': 'Prečítajte si naše zásady ochrany osobných údajov a podmienky služby.',
  'settings.viewPrivacyPolicy': 'Zobraziť zásady ochrany osobných údajov',

  // Settings — AI
  'settings.aiTitle': 'Nastavenia AI',
  'settings.aiTab': 'AI Poskytovateľ',
  'settings.aboutTab': 'O aplikácii',
  // Tutorial
  'tutorial.tourBadge': 'Prehliadka platformy',
  'tutorial.back': 'Späť',
  'tutorial.next': 'Ďalej',
  'tutorial.skip': 'Preskočiť prehliadku',
  'tutorial.getStarted': 'Začať',
  'tutorial.welcome.title': 'Vitajte v ELVOAQ',
  'tutorial.welcome.desc': 'ELVOAQ je vaša platforma všetko v jednom pre digitálny inventár. Spravujte, sledujte a zabezpečte všetky svoje cenné predmety na jednom mieste.',
  'tutorial.library.title': 'Vaša knižnica položiek',
  'tutorial.library.desc': 'Knižnica je srdcom ELVOAQ. Pridávajte položky s fotografiami, popismi, cenami a stavmi. Výkonné vyhľadávanie a filtre vám pomôžu nájsť čokoľvek okamžite.',
  'tutorial.categories.title': 'Kategórie',
  'tutorial.categories.desc': 'Organizujte položky do vlastných kategórií s farbami a ikonami. Celková hodnota a počet položiek na kategóriu na jeden pohľad.',
  'tutorial.favorites.title': 'Obľúbené',
  'tutorial.favorites.desc': 'Označte najdôležitejšie položky ako obľúbené pre rýchly prístup. Kedykoľvek exportujte zoznam obľúbených ako CSV.',
  'tutorial.collections.title': 'Kolekcie',
  'tutorial.collections.desc': 'Zoskupte súvisiace položky do pomenovaných kolekcií — cestovná sada, domáca kancelária alebo poistný balík. Pridávajte položky z knižnice jedným kliknutím.',
  'tutorial.accounting.title': 'Účtovníctvo a aktíva',
  'tutorial.accounting.desc': 'Sledujte odpisy, spravujte záznamy aktív a monitorujte finančnú hodnotu svojho inventára v priebehu času.',
  'tutorial.ai.title': 'Funkcie s podporou AI',
  'tutorial.ai.desc': 'Nechajte AI pracovať pre vás. Získajte predpovede hodnôt, automaticky odhaľujte duplikáty, chatujte s AI asistentom a nastavte inteligentné pripomienky.',
  'tutorial.security.title': 'Zabezpečenie a súkromie',
  'tutorial.security.desc': 'Zamknite jednotlivé položky heslom, vytvorte skryté súkromné priečinky, povolte dvojfaktorové overenie a spravujte prístupové roly.',
  'tutorial.calendar.title': 'Kalendár a pripomienky',
  'tutorial.calendar.desc': 'Plánujte pripomienky na údržbu, upozornenia na uplynutie záruky, obnovy poistenia a vlastné udalosti — všetko prepojené s vašimi položkami.',
  'tutorial.settings.title': 'Prispôsobte všetko',
  'tutorial.settings.desc': 'Prepínajte medzi tmavým a svetlým režimom, vyberte jazyk, nastavte výchozú menu a spravujte všetky svoje predvoľby.',
  'tutorial.audit.title': 'Audit log',
  'tutorial.audit.desc': 'Sledujte každú akciu na platforme v reálnom čase. Zistite, kto čo zmenil, kedy a odkiaľ \u2014 s úplnými detailmi, sledovaním IP adries a možnosťou vrátiť zmeny.',
  'tutorial.clickHint': 'Kliknite sem pre prechod do tejto sekcie',
  'tutorial.done.title': 'Všetko je pripravené!',
  'tutorial.done.desc': 'Teraz poznáte všetky základy ELVOAQ. Začnite pridaním svojej prvej položky do knižnice. Túto prehliadku môžete kedykoľvek zopakovať v Nastaveniach → O aplikácii.',
  'tutorial.startTour': 'Spustiť prehliadku',
  'tutorial.exploreOwn': 'Prezriem si sám',
  'tutorial.replayTour': 'Zopakovať prehliadku',
  // About / Version
  'about.version': 'Verzia aplikácie',
  'about.releaseDate': 'Dátum vydania',
  'about.changelog': 'História zmien',
  'about.newFeatures': 'Nové funkcie',
  'about.replayTutorial': 'Zopakovať tutoriál',
  'about.replayTutorialDesc': 'Znova zobraziť interaktívnu prehliadku platformy',
  'about.autoLogout': 'Automatické odhlásenie',
  'about.autoLogoutDesc': 'Automaticky sa odhlásiť po období nečinnosti',
  'about.timeout15': '15 minút',
  'about.timeout30': '30 minút',
  'about.timeout60': '1 hodina',
  'about.timeout120': '2 hodiny',
  'about.timeoutNever': 'Nikdy (neodporúčané)',
  'about.privacyPolicy': 'Zásady ochrany osobných údajov',
  'about.termsOfService': 'Podmienky použitia',
  'about.viewPrivacy': 'Zobraziť zásady ochrany osobných údajov',
  'about.viewTerms': 'Zobraziť podmienky použitia',
  'about.legalDocuments': 'Právne dokumenty',
  // CAPTCHA
  'auth.captchaQuestion': 'Bezpečnostná kontrola: Koľko je {a} + {b}?',
  'auth.captchaPlaceholder': 'Zadajte odpoveď',
  'auth.captchaError': 'Nesprávna odpoveď. Skúste to prosím znova.',
  'settings.aiDescription': 'ELVOAQ AI je nakonfigurovaná a aktívna. Môžete využívať všetky AI funkcie vrátane asistenta, detekcie duplikátov, predikcie hodnôt a ďalších.',
  'settings.aiActive': 'ELVOAQ AI Aktívna',
  'settings.aiActiveDesc': 'Poháňaná našou pokročilou AI. Všetky funkcie sú pripravené na použitie na celej platforme.',
  'settings.aiFeatures': 'Dostupné AI funkcie:',
  'settings.aiAssistant': 'AI Asistent',
  'settings.aiAssistantDesc': 'Pomoc so správou vášho inventára',
  'settings.aiDuplicateDetection': 'Detekcia duplikátov',
  'settings.aiDuplicateDetectionDesc': 'Automaticky nájsť podobné položky',
  'settings.aiValuePrediction': 'Predikcia hodnôt',
  'settings.aiValuePredictionDesc': 'Odhad hodnoty položiek v čase',
  'settings.aiSmartCategorization': 'Inteligentná kategorizácia',
  'settings.aiSmartCategorizationDesc': 'Automaticky navrhovať kategórie',

  // Feedback Modal
  'feedback.title': 'Odoslať spätnú väzbu',
  'feedback.successTitle': 'Spätná väzba odoslaná!',
  'feedback.successMessage': 'Ďakujeme! Vaša spätná väzba bola úspešne doručená nášmu tímu.',
  'feedback.typeLabel': 'Typ',
  'feedback.titleLabel': 'Názov *',
  'feedback.titlePlaceholder': 'Stručný súhrn vašej spätnej väzby',
  'feedback.descriptionLabel': 'Popis *',
  'feedback.descriptionPlaceholder': 'Uveďte prosím podrobné informácie...',
  'feedback.categoryLabel': 'Kategória *',
  'feedback.categoryPlaceholder': 'Vyberte kategóriu...',
  'feedback.attachmentLabel': 'Príloha (Voliteľné)',
  'feedback.uploadBtn': 'Nahrať snímku obrazovky alebo video',
  'feedback.maxFileSize': 'Max 5MB. Podporované: JPG, PNG, GIF, WebP, MP4, WebM',
  'feedback.systemInfo': '📊 Informácie o systéme (automaticky zachytené)',
  'feedback.submitBtn': 'Odoslať spätnú väzbu',
  'feedback.submitting': 'Odosielanie...',
  'feedback.bugReport': 'Hlásenie chyby',
  'feedback.bugReportDesc': 'Nahlásiť chybu',
  'feedback.featureRequest': 'Žiadosť o funkciu',
  'feedback.featureRequestDesc': 'Navrhnúť novú funkciu',
  'feedback.improvement': 'Vylepšenie',
  'feedback.improvementDesc': 'Navrhnúť vylepšenie',
  'feedback.question': 'Otázka',
  'feedback.questionDesc': 'Položiť otázku',
  'feedback.complaint': 'Sťažnosť',
  'feedback.complaintDesc': 'Nahlásiť problém alebo obavu',
  'feedback.other': 'Ostatné',
  'feedback.otherDesc': 'Všeobecná spätná väzba',
  'feedback.fileTooLarge': 'Veľkosť súboru musí byť menšia ako 5MB',
  'feedback.invalidFileType': 'Povolené sú len obrázky (JPG, PNG, GIF, WebP) a videá (MP4, WebM)',
  'feedback.titleDescRequired': 'Názov a popis sú povinné',
  'feedback.categoryRequired': 'Vyberte prosím kategóriu',
  'feedback.remove': 'Odstrániť',
  'feedback.cancel': 'Zrušiť',
  'feedback.display': 'Zobrazenie',
  'feedback.browser': 'Prehliadač',
  'feedback.locationLang': 'Poloha & Jazyk',
  'feedback.pageContext': 'Kontext stránky',
  'feedback.screenResolution': 'Rozlíšenie obrazovky',
  'feedback.viewportSize': 'Veľkosť okna',
  'feedback.colorDepth': 'Farebná hĺbka',
  'feedback.userAgent': 'User Agent',
  'feedback.device': 'Zariadenie',
  'feedback.os': 'Operačný systém',
  'feedback.cookiesEnabled': 'Cookies povolené',
  'feedback.language': 'Jazyk',
  'feedback.timezone': 'Časové pásmo',
  'feedback.currentUrl': 'Aktuálna URL',
  'feedback.referrer': 'Referrer',
  'feedback.onlineStatus': 'Stav pripojenia',
  'feedback.errorSubmit': 'Nepodarilo sa odoslať spätnú väzbu',

  // Feedback Categories
  'feedback.category.Dashboard': 'Dashboard',
  'feedback.category.Items & Library': 'Položky & Knižnica',
  'feedback.category.Categories': 'Kategórie',
  'feedback.category.Notifications': 'Oznámenia',
  'feedback.category.Settings': 'Nastavenia',
  'feedback.category.Workspaces & Teams': 'Pracovné priestory & Tímy',
  'feedback.category.Authentication': 'Autentifikácia',
  'feedback.category.Performance': 'Výkon',
  'feedback.category.User Interface': 'Užívateľské rozhranie',
  'feedback.category.Mobile Experience': 'Mobilná skúsenosť',
  'feedback.category.Data & Analytics': 'Dáta & Analytika',
  'feedback.category.Other': 'Ostatné',

  // Subscription / Plans
  'subscription.title': 'Predplatné',
  'subscription.overview': 'Prehľad',
  'subscription.plans': 'Plány',
  'subscription.storage': 'Úložisko',
  'subscription.billing': 'Fakturácia',
  'subscription.header': 'Predplatné & Plány',
  'subscription.headerDesc': 'Spravujte svoj plán, úložisko a fakturáciu na jednom mieste',
  'subscription.loading': 'Načítanie predplatného...',
  'subscription.currentPlan': 'Aktuálny plán',
  'subscription.upgrade': 'Upgrade',
  'subscription.usageOverview': 'Prehľad využitia',
  'subscription.items': 'Položky',
  'subscription.categories': 'Kategórie',
  'subscription.workspaces': 'Pracovné priestory',
  'subscription.aiMessages': 'AI správy',
  'subscription.imagesPerItem': 'Obrázky / Položka',
  'subscription.max': 'max',
  'subscription.yourPlanFeatures': 'Funkcie vášho plánu',
  'subscription.unlimited': 'Neobmedzene',
  'subscription.itemsLabel': 'položiek',
  'subscription.customCategories': 'vlastných kategórií',
  'subscription.storageLabel': 'úložisko',
  'subscription.imagesPerItemLabel': 'obrázkov na položku',
  'subscription.workspacesLabel': 'pracovný(ch) priestor(ov)',
  'subscription.aiMessagesMonth': 'AI správ/mesiac',
  'subscription.aiCategorization': 'AI kategorizácia',
  'subscription.valuePrediction': 'Predikcia hodnoty',
  'subscription.advancedAnalytics': 'Pokročilé analýzy',
  'subscription.prioritySupport': 'Prioritná podpora',
  'subscription.compareAllPlans': 'Porovnať všetky plány',
  'subscription.storageWarningPrefix': 'Využívate',
  'subscription.storageWarningSuffix': 'vášho úložiska. Zvážte upgrade alebo pridanie úložiska.',
  'subscription.manageStorage': 'Spravovať úložisko',
  'subscription.monthly': 'Mesačne',
  'subscription.yearly': 'Ročne',
  'subscription.save20': 'Ušetrite 20%',
  'subscription.mostPopular': 'Najpopulárnejší',
  'subscription.currentPlanLabel': 'Aktuálny plán',
  'subscription.free': 'Zadarmo',
  'subscription.custom': 'Vlastný',
  'subscription.contactSales': 'Kontaktovať obchod',
  'subscription.switch': 'Prepnúť',
  'subscription.viewFullPricing': 'Zobraziť kompletný cenník',
  'subscription.storageBreakdown': 'Rozloženie úložiska',
  'subscription.images': 'Obrázky',
  'subscription.documents': 'Dokumenty',
  'subscription.metadata': 'Metadáta',
  'subscription.perMonth': '/mes',
  'subscription.perYear': '/rok',
  'subscription.savePerYear': 'Ušetrite €{amount}/rok',
  'subscription.used': 'Použité',
  'subscription.freeSpace': 'Voľné',
  'subscription.total': 'Celkom',
  'subscription.needMoreSpace': 'Potrebujete viac miesta?',
  'subscription.addExtraStorage': 'Pridajte k plánu ďalšie úložisko',
  'subscription.addStorage': 'Pridať úložisko',
  'subscription.saveTilde20': 'Ušetrite ~20%',
  'subscription.save20Approx': 'Ušetrite ~20%',
  'subscription.billingSummary': 'Prehľad fakturácie',
  'subscription.billingCycle': 'Fakturačný cyklus',
  'subscription.yearlyCost': 'Ročná cena',
  'subscription.monthlyCost': 'Mesačná cena',
  'subscription.nextBillingDate': 'Ďalší dátum fakturácie',
  'subscription.paymentMethod': 'Platobná metóda',
  'subscription.naFree': 'N/A — Bezplatný plán',
  'subscription.manageKofi': 'Spravovať na Ko-fi',
  'subscription.manageStripe': 'Spravovať predplatné',
  'subscription.downloadInvoice': 'Stiahnuť faktúru',
  'subscription.cancelSubscription': 'Zrušiť predplatné',
  'subscription.cancelDesc': 'Pre zrušenie predplatného ho spravujte cez Stripe. Váš prístup bude pokračovať do konca aktuálneho fakturačného obdobia.',
  'subscription.cancelOnKofi': 'Zrušiť na Ko-fi',
  'subscription.cancelStripe': 'Zrušiť predplatné',
  'subscription.paymentHistory': 'História platieb',
  'subscription.noPayments': 'Zatiaľ žiadna história platieb',
  'subscription.noPaymentsDesc': 'Vaša história platieb sa tu zobrazí po prvej platbe',
  'subscription.needHelpBilling': 'Potrebujete pomoc s fakturáciou?',
  'subscription.contactBilling': 'Kontaktujte nás na',
  'subscription.billingQuestions': 'pre akékoľvek otázky k fakturácii.',
  'subscription.securePayments': 'Bezpečné platby',
  'subscription.securePaymentsDesc': 'Všetky platby sú bezpečne spracované cez Stripe. Nikdy neukladáme vaše platobné údaje.',
  'subscription.upgradeTo': 'Upgrade na',
  'subscription.customPricing': 'Individuálne ceny',
  'subscription.whatsIncluded': 'Čo je zahrnuté:',
  'subscription.save20Yearly': 'Ušetrite 20% s ročnou fakturáciou',
  'subscription.subscribeKofi': 'Predplatiť na Ko-fi',
  'subscription.subscribeStripe': 'Predplatiť cez Stripe',
  'subscription.afterPayment': 'Po platbe bude váš plán aktualizovaný do 24 hodín.',
  'subscription.afterPaymentStripe': 'Budete presmerovaní na bezpečnú platbu Stripe. Váš plán bude aktivovaný automaticky.',
  'subscription.secureVia': 'Bezpečná platba cez Ko-fi. Kedykoľvek zrušiteľné.',
  'subscription.secureViaStripe': 'Bezpečné platby spracované cez Stripe. Kedykoľvek zrušiteľné.',
  'subscription.contactForAddon': 'Kontaktujte nás',
  'subscription.contactSalesDesc': 'Ozveme sa vám do 24 hodín s individuálnou ponukou.',
  'subscription.compareFeatures': 'Porovnať všetky funkcie',
  'subscription.feature': 'Funkcia',
  'subscription.coreLimits': 'Základné limity',
  'subscription.teamMembers': 'Členovia tímu',
  'subscription.aiFeatures': 'AI funkcie',
  'subscription.duplicateDetect': 'Detekcia duplikátov',
  'subscription.aiReportsMonth': 'AI reporty/mesiac',
  'subscription.security': 'Zabezpečenie',
  'subscription.twoFactorAuth': 'Dvojfaktorové overenie',
  'subscription.auditLogs': 'Audit logy',
  'subscription.sso': 'SSO',
  'subscription.support': 'Podpora',
  'subscription.emailSupportLabel': 'E-mailová podpora',
  'subscription.dedicatedManager': 'Dedikovaný manažér',
  'subscription.faqTitle': 'Často kladené otázky',
  'subscription.faqSwitchQ': 'Môžem kedykoľvek zmeniť plán?',
  'subscription.faqSwitchA': 'Áno! Môžete kedykoľvek upgradovať alebo downgradovať. Pri upgrade bude zvyšná čiastka pomerne prepočítaná. Pri downgrade sa nová sadzba uplatní v ďalšom fakturačnom cykle.',
  'subscription.faqDowngradeQ': 'Čo sa stane s mojimi dátami pri downgrade?',
  'subscription.faqDowngradeA': 'Vaše dáta sú v bezpečí. Ak prekročíte limity nového plánu, budete mať 30 dní na zníženie využitia alebo opätovný upgrade.',
  'subscription.faqRefundQ': 'Ponúkate vrátenie peňazí?',
  'subscription.faqRefundA': 'Áno, ponúkame 30-dňovú záruku vrátenia peňazí pre všetky platené plány. Kontaktujte nás pre plné vrátenie.',
  'subscription.faqPaymentQ': 'Aké platobné metódy prijímate?',
  'subscription.faqPaymentA': 'Prijímame platby cez Stripe (kreditné/debetné karty). Enterprise plány je možné fakturovať aj faktúrou.',
  'subscription.faqTrialQ': 'Je k dispozícii bezplatná skúšobná verzia?',
  'subscription.faqTrialA': 'Áno! Všetky platené plány zahŕňajú 14-dňovú bezplatnú skúšobnú verziu. Kreditná karta nie je vyžadovaná.',
  'subscription.faqStorageQ': 'Môžem pridať úložisko neskôr?',
  'subscription.faqStorageA': 'Samozrejme! Doplnky úložiska si môžete kedykoľvek vyžiadať. Kontaktujte nás pre podrobnosti.',
  'subscription.trialActive': 'Skúšobná verzia aktívna',
  'subscription.trialDaysLeft': 'dní zostáva',
  'subscription.upgradeNow': 'Upgradovať teraz',
  'subscription.trialAvailable': 'Vyskúšajte akýkoľvek plán zadarmo na 14 dní!',
  'subscription.trialAvailableDesc': 'Kreditná karta nie je potrebná. Vyskúšajte všetky prémiové funkcie bez rizika.',
  'subscription.startTrial': 'Spustiť skúšobnú verziu',
  'subscription.startFreeTrial': 'Spustiť 14-dňovú skúšobnú verziu',
  'subscription.trialActiveLabel': 'Skúšobná verzia',
  'subscription.trialUsed': 'Skúšobná verzia už využitá',
  'subscription.trialExpired': 'Vaša skúšobná verzia skončila. Upgradujte pre zachovanie prémiových funkcií.',
  'subscription.cancelTrial': 'Zrušiť skúšobnú verziu',
  'subscription.cancelTrialConfirm': 'Naozaj chcete zrušiť svoju bezplatnú skúšobnú verziu? Budete okamžite prepnutí na bezplatný plán.',
  'subscription.ctaTitle': 'Pripravení začať?',
  'subscription.ctaDesc': 'Pripojte sa k tisícom používateľov spravujúcich svoje predmety našou platformou.',
  'subscription.startFree': 'Začať zadarmo',
  'subscription.contactUs': 'Kontaktovať nás',
  'subscription.planTagline.FREE': 'Zadarmo navždy',
  'subscription.planTagline.STARTER': 'Začnite zbierať inteligentnejšie',
  'subscription.planTagline.COLLECTOR': 'Najpopulárnejšie pre zberateľov',
  'subscription.planTagline.PRO': 'Plne vybavené pre profesionálov',
  'subscription.planTagline.BUSINESS': 'Vytvorené pre tímy',
  'subscription.planTagline.ULTIMATE': 'Bez obmedzení, bez kompromisov',
  'subscription.planTagline.ENTERPRISE': 'Porozprávajme sa',
  'subscription.planTagline.CUSTOM': 'Prispôsobené vašim potrebám',
  'subscription.planDesc.FREE': 'Začnite — katalogizujte až 30 predmetov so základnými funkciami.',
  'subscription.planDesc.STARTER': 'Pre nadšencov a malých zberateľov, ktorí to myslia vážne.',
  'subscription.planDesc.COLLECTOR': 'Pre oddaných zberateľov, ktorí potrebujú viac priestoru a chytrejšie nástroje.',
  'subscription.planDesc.PRO': 'Pre pokročilých používateľov a profesionálov spravujúcich veľké inventáre.',
  'subscription.planDesc.BUSINESS': 'Pre tímy a organizácie, ktoré potrebujú škálovanie, bezpečnosť a kontrolu.',
  'subscription.planDesc.ULTIMATE': 'Vrchol — neobmedzené všetko s prémiovou podporou a prispôsobením.',
  'subscription.planDesc.ENTERPRISE': 'Plán na mieru pre veľké organizácie so špecifickými potrebami.',

  // Prednosti plánu
  'subscription.planHighlight.FREE.0': 'Až 30 predmetov',
  'subscription.planHighlight.FREE.1': '300 MB úložisko',
  'subscription.planHighlight.FREE.2': '5 fotiek na predmet',
  'subscription.planHighlight.FREE.3': 'AI kategorizácia',
  'subscription.planHighlight.FREE.4': 'Základná analytika',
  'subscription.planHighlight.FREE.5': 'Dvojfaktorové overenie',
  'subscription.planHighlight.FREE.6': 'Základná analytika',
  'subscription.planHighlight.FREE.7': '10 AI správ/mesiac',
  'subscription.planHighlight.STARTER.0': 'Až 250 predmetov',
  'subscription.planHighlight.STARTER.1': '2 GB úložisko',
  'subscription.planHighlight.STARTER.2': '10 fotiek na predmet',
  'subscription.planHighlight.STARTER.3': 'AI kategorizácia',
  'subscription.planHighlight.STARTER.4': 'Export dát',
  'subscription.planHighlight.STARTER.5': 'Komentáre',
  'subscription.planHighlight.COLLECTOR.0': 'Až 1 000 predmetov',
  'subscription.planHighlight.COLLECTOR.1': '10 GB úložisko',
  'subscription.planHighlight.COLLECTOR.2': '20 fotiek na predmet',
  'subscription.planHighlight.COLLECTOR.3': 'AI predikcia hodnoty',
  'subscription.planHighlight.COLLECTOR.4': 'Detekcia duplikátov',
  'subscription.planHighlight.COLLECTOR.5': 'Synchronizácia v reálnom čase',
  'subscription.planHighlight.COLLECTOR.6': '30-dňové audit logy',
  'subscription.planHighlight.PRO.0': 'Až 10 000 predmetov',
  'subscription.planHighlight.PRO.1': '50 GB úložisko',
  'subscription.planHighlight.PRO.2': '40 fotiek na predmet',
  'subscription.planHighlight.PRO.3': 'Neobmedzené AI reporty',
  'subscription.planHighlight.PRO.4': 'Pokročilá analytika a reporty',
  'subscription.planHighlight.PRO.5': 'API prístup (120 pož./min)',
  'subscription.planHighlight.PRO.6': 'Prioritná podpora',
  'subscription.planHighlight.PRO.7': 'Tímová spolupráca (10)',
  'subscription.planHighlight.BUSINESS.0': 'Neobmedzené predmety',
  'subscription.planHighlight.BUSINESS.1': '200 GB úložisko',
  'subscription.planHighlight.BUSINESS.2': '60 fotiek na predmet',
  'subscription.planHighlight.BUSINESS.3': 'Až 25 členov tímu',
  'subscription.planHighlight.BUSINESS.4': 'SSO / Single Sign-On',
  'subscription.planHighlight.BUSINESS.5': 'Vlastný branding',
  'subscription.planHighlight.BUSINESS.6': '365-dňové audit logy',
  'subscription.planHighlight.BUSINESS.7': 'Webhooky & API (300 pož./min)',
  'subscription.planHighlight.BUSINESS.8': 'Prioritná podpora',
  'subscription.planHighlight.ULTIMATE.0': 'Všetko neobmedzene',
  'subscription.planHighlight.ULTIMATE.1': '1 TB úložisko',
  'subscription.planHighlight.ULTIMATE.2': 'Neobmedzené fotky na predmet',
  'subscription.planHighlight.ULTIMATE.3': 'Neobmedzení členovia tímu',
  'subscription.planHighlight.ULTIMATE.4': 'Osobný account manažér',
  'subscription.planHighlight.ULTIMATE.5': 'SLA garancia',
  'subscription.planHighlight.ULTIMATE.6': 'White-label možnosť',
  'subscription.planHighlight.ULTIMATE.7': 'Vlastná doména',
  'subscription.planHighlight.ULTIMATE.8': 'Neobmedzený API prístup',
  'subscription.planHighlight.ENTERPRISE.0': 'Všetko neobmedzene',
  'subscription.planHighlight.ENTERPRISE.1': 'Vlastné úložisko',
  'subscription.planHighlight.ENTERPRISE.2': 'Osobný account manažér',
  'subscription.planHighlight.ENTERPRISE.3': 'SLA garancia',
  'subscription.planHighlight.ENTERPRISE.4': 'White-label možnosť',
  'subscription.planHighlight.ENTERPRISE.5': 'Vlastná doména',
  'subscription.planHighlight.ENTERPRISE.6': 'On-premise nasadenie',
  'subscription.planHighlight.ENTERPRISE.7': 'Vlastné integrácie',
  'subscription.planHighlight.ENTERPRISE.8': 'Onboarding na mieru',
  'subscription.planHighlight.CUSTOM.0': 'Všetko neobmedzene',
  'subscription.planHighlight.CUSTOM.1': 'Vlastné úložisko',
  'subscription.planHighlight.CUSTOM.2': 'Osobný account manažér',
  'subscription.planHighlight.CUSTOM.3': 'SLA garancia',
  'subscription.planHighlight.CUSTOM.4': 'White-label možnosť',
  'subscription.planHighlight.CUSTOM.5': 'Vlastná doména',
  'subscription.planHighlight.CUSTOM.6': 'On-premise nasadenie',
  'subscription.planHighlight.CUSTOM.7': 'Vlastné integrácie',
  'subscription.planHighlight.CUSTOM.8': 'Onboarding na mieru',

  // ── Collections ──
  'collections.title': 'Kolekcie',
  'collections.subtitle': 'Vytvorte vlastné skupiny a organizujte položky podľa seba',
  'collections.newCollection': 'Nová kolekcia',
  'collections.searchPlaceholder': 'Hľadať kolekcie...',
  'collections.customOrder': 'Vlastné poradie',
  'collections.name': 'Názov',
  'collections.recentlyUpdated': 'Naposledy aktualizované',
  'collections.mostItems': 'Najviac položiek',
  'collections.collections': 'Kolekcie',
  'collections.totalItems': 'Celkom položiek',
  'collections.active': 'Aktívne',
  'collections.items': 'položka',
  'collections.itemsPlural': 'položiek',
  'collections.updated': 'Aktualizované',
  'collections.noMatchingCollections': 'Žiadne zodpovedajúce kolekcie',
  'collections.tryDifferentSearch': 'Skúste iný hľadaný výraz',
  'collections.noCollectionsYet': 'Zatiaľ žiadne kolekcie',
  'collections.noCollectionsDesc': 'Vytvorte svoju prvú kolekciu a začnite organizovať položky po svojom',
  'collections.createCollection': 'Vytvoriť kolekciu',
  'collections.editCollection': 'Upraviť kolekciu',
  'collections.createNewCollection': 'Vytvoriť novú kolekciu',
  'collections.collectionName': 'Názov kolekcie *',
  'collections.collectionNamePlaceholder': 'napr. Vintage hodinky, Vzácne knihy...',
  'collections.description': 'Popis',
  'collections.descriptionPlaceholder': 'O čom je táto kolekcia?',
  'collections.color': 'Farba',
  'collections.saveChanges': 'Uložiť zmeny',
  'collections.cancel': 'Zrušiť',
  'collections.addItems': 'Pridať položky',
  'collections.addRemoveItems': 'Pridať / odobrať položky',
  'collections.done': 'Hotovo',
  'collections.itemsInCollection': '{count} položiek v tejto kolekcii',
  'collections.loadingItems': 'Načítavanie položiek...',
  'collections.noItemsInCollection': 'V tejto kolekcii nie sú žiadne položky',
  'collections.noItemsDesc': 'Kliknite na "Pridať položky" pre pridanie položiek z vašej knižnice',
  'collections.noItemsFound': 'Žiadne položky nenájdené',
  'collections.deleteConfirm': 'Zmazať túto kolekciu? Položky nebudú zmazané.',
  'collections.loading': 'Načítavanie kolekcií...',
  'collections.icon': 'Ikona',
  'collections.item': 'položka',
  'collections.addItem': 'Pridať položku',
  'collections.itemName': 'Názov položky',
  'collections.itemNamePlaceholder': 'Názov položky...',
  'collections.itemType': 'Typ',
  'collections.noItems': 'Zatiaľ žiadne položky. Pridajte prvú položku vyššie.',
  'collections.type.document': 'Dokument',
  'collections.type.image': 'Obrázok',
  'collections.type.link': 'Odkaz',
  'collections.type.note': 'Poznámka',
  'collections.type.video': 'Video',
  'collections.type.audio': 'Audio',
  'collections.type.spreadsheet': 'Tabuľka',
  'collections.type.presentation': 'Prezentácia',
  'collections.type.report': 'Správa',
  'collections.type.task': 'Úloha',
  'collections.type.invoice': 'Faktúra',
  'collections.type.contract': 'Zmluva',
  'collections.type.email': 'E-mail',
  'collections.type.contact': 'Kontakt',
  'collections.type.blueprint': 'Plán',
  'collections.type.code': 'Kód',

  // ── Duplicate Finder ──
  'duplicates.title': 'Hľadač duplikátov',
  'duplicates.subtitle': 'Nahrajte fotografiu pre nájdenie zodpovedajúcich položiek vo vašej zbierke a odhad hodnoty',
  'duplicates.dropHere': 'Pretiahnite obrázok sem alebo kliknite pre nahranie',
  'duplicates.supportedFormats': 'Podporuje JPG, PNG, GIF, WebP — do 20MB',
  'duplicates.scanning': 'Prehľadávanie zbierky...',
  'duplicates.scanButton': 'Hľadať duplikáty',
  'duplicates.aiAnalysis': 'AI analýza',
  'duplicates.description': 'Popis',
  'duplicates.category': 'Kategória',
  'duplicates.brand': 'Značka',
  'duplicates.model': 'Model',
  'duplicates.condition': 'Stav',
  'duplicates.estimatedValue': 'Odhadovaná trhová hodnota',
  'duplicates.low': 'Nízka',
  'duplicates.mid': 'Stredná',
  'duplicates.high': 'Vysoká',
  'duplicates.matchesFound': '{count} zhoda/zhôd nájdená/nájdených',
  'duplicates.noDuplicates': 'Žiadne duplikáty nenájdené',
  'duplicates.noDuplicatesDesc': 'Táto položka sa zdá, že nezodpovedá ničomu vo vašej zbierke. Môže to byť nová položka!',
  'duplicates.highMatch': 'Vysoká zhoda',
  'duplicates.possibleMatch': 'Možná zhoda',
  'duplicates.lowMatch': 'Nízka zhoda',
  'duplicates.estValue': 'Odhadovaná hodnota:',
  'duplicates.selectImage': 'Prosím vyberte súbor obrázku',

  // ── AI Assistant ──
  'ai.newChat': 'Nový chat',
  'ai.loading': 'Načítavanie...',
  'ai.noChats': 'Zatiaľ žiadne konverzácie',
  'ai.startChat': 'Začnite nový chat',
  'ai.typeMessage': 'Napíšte správu...',
  'ai.maxImages': 'Maximálne 5 obrázkov na správu',
  'ai.welcomeTitle': 'ELVOAQ AI Asistent',
  'ai.welcomeDesc': 'Opýtajte sa ma na čokoľvek o vašej zbierke, hodnotách položiek alebo tipoch na organizáciu.',
  'ai.deleteConfirm': 'Zmazať túto konverzáciu?',

  // ── Hidden Folders ──
  'hiddenFolders.title': 'Skryté priečinky',
  'hiddenFolders.subtitle': 'Priečinky chránené heslom pre zabezpečenie vašich súkromných položiek',
  'hiddenFolders.newFolder': 'Nový priečinok',
  'hiddenFolders.loading': 'Načítavanie priečinkov...',
  'hiddenFolders.noFolders': 'Zatiaľ žiadne skryté priečinky',
  'hiddenFolders.noFoldersDesc': 'Vytvorte priečinok chránený heslom pre zabezpečenie vašich najsúkromnejších položiek.',
  'hiddenFolders.createFirst': 'Vytvoriť prvý priečinok',
  'hiddenFolders.open': 'Otvoriť',
  'hiddenFolders.items': 'položiek',
  'hiddenFolders.back': 'Späť',
  'hiddenFolders.addItem': 'Pridať položku',
  'hiddenFolders.noItems': 'V tomto priečinku zatiaľ nie sú žiadne položky',
  'hiddenFolders.addFirstItem': 'Pridať prvú položku',
  'hiddenFolders.viewItem': 'Zobraziť položku',
  'hiddenFolders.removeFromFolder': 'Odstrániť z priečinka',
  'hiddenFolders.enterPassword': 'Zadajte heslo',
  'hiddenFolders.passwordProtected': 'Tento priečinok je chránený heslom. Zadajte heslo pre zobrazenie obsahu.',
  'hiddenFolders.folderPassword': 'Heslo priečinka',
  'hiddenFolders.verifying': 'Overovanie...',
  'hiddenFolders.viewFolder': 'Zobraziť priečinok',
  'hiddenFolders.createFolder': 'Vytvoriť skrytý priečinok',
  'hiddenFolders.folderName': 'Názov priečinka *',
  'hiddenFolders.folderNamePlaceholder': 'napr. Cennosti, Súkromná zbierka',
  'hiddenFolders.descriptionLabel': 'Popis',
  'hiddenFolders.descriptionPlaceholder': 'Voliteľný popis...',
  'hiddenFolders.iconLabel': 'Ikona',
  'hiddenFolders.colorLabel': 'Farba',
  'hiddenFolders.password': 'Heslo *',
  'hiddenFolders.confirmPassword': 'Potvrdiť heslo *',
  'hiddenFolders.creating': 'Vytváranie...',
  'hiddenFolders.editFolder': 'Upraviť priečinok',
  'hiddenFolders.changeFolderPassword': 'Zmeniť heslo priečinka',
  'hiddenFolders.leaveEmptyKeep': 'Ponechajte prázdne pre zachovanie aktuálneho hesla',
  'hiddenFolders.currentPassword': 'Aktuálne heslo',
  'hiddenFolders.newPassword': 'Nové heslo',
  'hiddenFolders.confirmNewPassword': 'Potvrdiť nové heslo',
  'hiddenFolders.saveChanges': 'Uložiť zmeny',
  'hiddenFolders.deleteFolder': 'Zmazať priečinok',
  'hiddenFolders.deleteConfirm': 'Naozaj chcete zmazať',
  'hiddenFolders.deleteDesc': '? Priečinok bude odstránený, ale položky zostanú zachované.',
  'hiddenFolders.deletePasswordPrompt': 'Zadajte heslo priečinka pre potvrdenie',
  'hiddenFolders.cancel': 'Zrušiť',
  'hiddenFolders.addItemTo': 'Pridať položku do',
  'hiddenFolders.searchItems': 'Hľadať položky...',
  'hiddenFolders.enterPasswordToAdd': 'Zadajte heslo priečinka pre pridanie tejto položky:',
  'hiddenFolders.confirm': 'Potvrdiť',
  'hiddenFolders.alreadyAdded': 'Už pridané',
  'hiddenFolders.noResults': 'Žiadne položky nenájdené',
  'hiddenFolders.allCategories': 'Všetky kategórie',
  'hiddenFolders.favoritesOnly': 'Len obľúbené',
  'hiddenFolders.favorites': 'Obľúbené',
  'hiddenFolders.passwordsNoMatch': 'Heslá sa nezhodujú',
  'hiddenFolders.passwordMinLength': 'Heslo musí mať aspoň 4 znaky',
  'hiddenFolders.folderCreated': 'Skrytý priečinok vytvorený!',
  'hiddenFolders.itemAdded': 'Položka pridaná do skrytého priečinka',
  'hiddenFolders.itemRemoved': 'Položka odstránená zo skrytého priečinka',
  'hiddenFolders.folderUpdated': 'Priečinok úspešne aktualizovaný',
  'hiddenFolders.folderPasswordChanged': 'Heslo priečinka zmenené',
  'hiddenFolders.folderDeleted': 'Priečinok úspešne vymazaný',
  'hiddenFolders.nameLabel': 'Názov',
  'hiddenFolders.currentPasswordPlaceholder': 'Zadajte aktuálne heslo',
  'hiddenFolders.newPasswordPlaceholder': 'Nové heslo (min 4 znaky)',
  'hiddenFolders.confirmPasswordPlaceholder': 'Zadajte nové heslo znova',
  'hiddenFolders.folderPasswordPlaceholder': 'Heslo priečinka',
  'hiddenFolders.confirmFolderPasswordPlaceholder': 'Potvrďte heslo',
  'hiddenFolders.currentPasswordRequired': 'Aktuálne heslo je povinné',
  'hiddenFolders.newPasswordMinLength': 'Nové heslo musí mať aspoň 4 znaky',
  'hiddenFolders.newPasswordsNoMatch': 'Nové heslá sa nezhodujú',
  'hiddenFolders.deleteButton': 'Vymazať priečinok',
  'hiddenFolders.addItemButton': 'Pridať položku',
  'hiddenFolders.nameAndPasswordRequired': 'Názov a heslo sú povinné',
  'hiddenFolders.enterFolderPassword': 'Zadajte heslo priečinka',
  'hiddenFolders.wrongPassword': 'Nesprávne heslo',
  'hiddenFolders.failedToCreateFolder': 'Nepodarilo sa vytvoriť priečinok',
  'hiddenFolders.failedToAddItem': 'Nepodarilo sa pridať položku',
  'hiddenFolders.failedToRemoveItem': 'Nepodarilo sa odstrániť položku',
  'hiddenFolders.failedToUpdateFolder': 'Nepodarilo sa aktualizovať priečinok',
  'hiddenFolders.failedToDeleteFolder': 'Nepodarilo sa vymazať priečinok',
  'hiddenFolders.folderUpdatedAndPasswordChanged': 'Priečinok aktualizovaný & heslo zmenené',

  // ── Data Transfer ──
  'dataTransfer.title': 'Import & Export',
  'dataTransfer.subtitle': 'Preneste svoje dáta vo formáte CSV alebo JSON',
  'dataTransfer.export': 'Export',
  'dataTransfer.import': 'Import',
  'dataTransfer.exportTitle': 'Exportovať vaše položky',
  'dataTransfer.exportDesc': 'Stiahnite si dáta položiek vo vami preferovanom formáte.',
  'dataTransfer.format': 'Formát:',
  'dataTransfer.fieldsToInclude': 'Polia na zahrnutie:',
  'dataTransfer.selectAll': 'Vybrať všetko',
  'dataTransfer.deselectAll': 'Zrušiť výber',
  'dataTransfer.exporting': 'Exportovanie...',
  'dataTransfer.download': 'Stiahnuť',
  'dataTransfer.importTitle': 'Importovať položky',
  'dataTransfer.importDesc': 'Nahrajte súbor CSV alebo JSON pre import položiek do vašej zbierky.',
  'dataTransfer.importMode': 'Režim importu:',
  'dataTransfer.addToExisting': 'Pridať k existujúcim',
  'dataTransfer.addToExistingDesc': 'Nové položky budú pridané vedľa vašich aktuálnych položiek',
  'dataTransfer.overwriteAll': 'Prepísať všetko',
  'dataTransfer.overwriteAllDesc': 'Aktuálne položky budú archivované a nahradené importovanými dátami',
  'dataTransfer.overwriteWarning': 'Upozornenie: Toto archivuje všetky vaše aktuálne položky. Môžu byť obnovené z koša.',
  'dataTransfer.clickToSelect': 'Kliknite pre výber súboru CSV alebo JSON',
  'dataTransfer.supported': 'Podporované: .csv, .json',
  'dataTransfer.preview': 'Náhľad',
  'dataTransfer.itemsFound': 'položiek nájdených',
  'dataTransfer.showingOf': 'Zobrazených 5 z {count} položiek',
  'dataTransfer.importing': 'Importovanie...',
  'dataTransfer.importItems': 'Importovať {count} položiek',
  'dataTransfer.confirmOverwrite': 'Potvrdiť prepísanie',
  'dataTransfer.overwriteBody1': 'Chystáte sa prepísať všetky existujúce položky {count} importovanými položkami.',
  'dataTransfer.overwriteBody2': 'Vaše aktuálne položky budú presunuté do koša a môžu byť neskôr obnovené.',
  'dataTransfer.yesOverwrite': 'Áno, prepísať',
  'dataTransfer.selectOneField': 'Vyberte aspoň jedno pole pre export',
  'dataTransfer.noDataToImport': 'Žiadne dáta na import',
  'dataTransfer.exportSuccess': 'Export stiahnutý ako {format} súbor!',
  'dataTransfer.importSuccess': 'Úspešne importovaných {count} položiek!',
  'dataTransfer.overwriteNote': '(Predchádzajúce položky boli archivované)',

  // ── Smart Hub ──
  'smartHub.loading': 'Načítavanie Smart Hub...',
  'smartHub.title': 'Smart Hub',
  'smartHub.subtitle': 'Prehľady a správa s umelou inteligenciou',
  'smartHub.analyzing': 'Analyzujem...',
  'smartHub.aiAnalyze': 'AI Analýza',
  'smartHub.overview': 'Prehľad',
  'smartHub.reminders': 'Pripomienky',
  'smartHub.notes': 'Poznámky',
  'smartHub.insights': 'Postrehy',
  'smartHub.aiReport': 'AI Report',
  'smartHub.totalItems': 'Celkom položiek',
  'smartHub.collectionValue': 'Hodnota zbierky',
  'smartHub.categories': 'Kategórie',
  'smartHub.activeReminders': 'Aktívne pripomienky',
  'smartHub.aiSummary': 'AI Zhrnutie',
  'smartHub.topCategories': 'Top kategórie:',
  'smartHub.mostValuable': 'Najcennejšie:',
  'smartHub.summaryContains': 'Vaša zbierka obsahuje {count} položiek.',
  'smartHub.summaryTotalValue': 'Celková hodnota: {total} (priemer: {avg}).',
  'smartHub.summaryTopCategory': 'Top kategória: {name} ({count} položiek).',
  'smartHub.summaryRecentItems': '{count} položiek pridaných tento týždeň.',
  'smartHub.summaryMostValuable': 'Najcennejšie: "{name}" ({value}).',
  'smartHub.summaryEmpty': 'Vaša zbierka je prázdna. Začnite pridaním prvej položky!',
  'smartHub.overdueReminders': 'oneskorených pripomienok',
  'smartHub.insightsAvailable': 'dostupných postrehov',
  'smartHub.pinnedNotes': 'pripnutých poznámok',
  'smartHub.all': 'Všetko',
  'smartHub.active': 'Aktívne',
  'smartHub.completed': 'Dokončené',
  'smartHub.addReminder': 'Pridať pripomienku',
  'smartHub.editReminder': 'Upraviť pripomienku',
  'smartHub.newReminder': 'Nová pripomienka',
  'smartHub.reminderTitle': 'Názov pripomienky',
  'smartHub.descriptionOptional': 'Popis (voliteľný)',
  'smartHub.custom': 'Vlastné',
  'smartHub.maintenance': 'Údržba',
  'smartHub.review': 'Kontrola',
  'smartHub.insurance': 'Poistenie',
  'smartHub.warranty': 'Záruka',
  'smartHub.low': 'Nízka',
  'smartHub.medium': 'Stredná',
  'smartHub.high': 'Vysoká',
  'smartHub.urgent': 'Urgentná',
  'smartHub.unassigned': 'Nepriradené',
  'smartHub.me': 'Ja',
  'smartHub.assignHint': 'Priradiť: "Ja" znamená, že sa zobrazí na vašom zozname. Kolegovia sú členovia vášho workspace.',
  'smartHub.assignedTo': 'Priradené',
  'smartHub.cancel': 'Zrušiť',
  'smartHub.saveChanges': 'Uložiť zmeny',
  'smartHub.createReminder': 'Vytvoriť pripomienku',
  'smartHub.noReminders': 'Žiadne pripomienky',
  'smartHub.noRemindersDesc': 'Vytvorte jednu alebo kliknite na "AI Analýza" pre vygenerovanie návrhov',
  'smartHub.allNotes': 'Všetky poznámky',
  'smartHub.pinned': 'Pripnuté',
  'smartHub.aiGenerated': 'Generované AI',
  'smartHub.addNote': 'Pridať poznámku',
  'smartHub.editNote': 'Upraviť poznámku',
  'smartHub.newNote': 'Nová poznámka',
  'smartHub.writeNote': 'Napíšte svoju poznámku...',
  'smartHub.addTag': 'Pridať štítok...',
  'smartHub.add': 'Pridať',
  'smartHub.saveNote': 'Uložiť poznámku',
  'smartHub.noNotes': 'Zatiaľ žiadne poznámky',
  'smartHub.noNotesDesc': 'Vytvorte poznámku pre sledovanie dôležitých informácií',
  'smartHub.noInsights': 'Žiadne aktívne postrehy',
  'smartHub.noInsightsDesc': 'Kliknite na "AI Analýza" pre prehľadanie vašej zbierky a odporúčania',
  'smartHub.relatedItems': 'Súvisiace položky:',
  'smartHub.confidence': 'Spoľahlivosť:',
  'smartHub.generatingReport': 'Generovanie AI reportu...',
  'smartHub.reportTitle': 'Analytický report zbierky',
  'smartHub.generated': 'Vygenerované:',
  'smartHub.regenerate': 'Pregenerovať',
  'smartHub.items': 'Položky',
  'smartHub.value': 'Hodnota',
  'smartHub.avgValue': 'Priem. hodnota',
  'smartHub.noReport': 'Zatiaľ nebol vygenerovaný žiadny report',
  'smartHub.generateReport': 'Vygenerovať AI report',
  'smartHub.daysOverdue': '{count} dní po termíne',
  'smartHub.today': 'Dnes',
  'smartHub.tomorrow': 'Zajtra',
  'smartHub.inDays': 'Za {count} dní',
  'smartHub.inWeeks': 'Za {count} týždňov',
  'smartHub.inMonths': 'Za {count} mesiacov',
  'smartHub.lockedItem': 'Zamknutá položka',
  'smartHub.markIncomplete': 'Označiť ako nedokončené',
  'smartHub.markComplete': 'Označiť ako dokončené',
  'smartHub.editTitle': 'Upraviť',
  'smartHub.deleteTitle': 'Vymazať',
  'smartHub.unpin': 'Odopnúť',
  'smartHub.pinInsight': 'Pripnúť',
  'smartHub.dismiss': 'Zatvoriť',
  'smartHub.missing': 'Chýba:',
  'smartHub.member': 'Člen',

  // Calendar
  'nav.calendar': 'Kalendár',
  'calendar.title': 'Kalendár',
  'calendar.today': 'Dnes',
  'calendar.day': 'Deň',
  'calendar.week': 'Týždeň',
  'calendar.month': 'Mesiac',
  'calendar.newEvent': 'Nová udalosť',
  'calendar.editEvent': 'Upraviť udalosť',
  'calendar.eventTitle': 'Názov',
  'calendar.titlePlaceholder': 'Pridať názov...',
  'calendar.date': 'Dátum',
  'calendar.startTime': 'Začiatok',
  'calendar.endTime': 'Koniec',
  'calendar.allDay': 'Celý deň',
  'calendar.location': 'Miesto',
  'calendar.locationPlaceholder': 'Pridať miesto...',
  'calendar.description': 'Popis',
  'calendar.descriptionPlaceholder': 'Pridať popis...',
  'calendar.color': 'Farba',
  'calendar.save': 'Uložiť',
  'calendar.create': 'Vytvoriť',
  'calendar.cancel': 'Zrušiť',
  'calendar.edit': 'Upraviť',
  'calendar.delete': 'Zmazať',
  'calendar.more': 'viac',
  'calendar.mon': 'Po',
  'calendar.tue': 'Ut',
  'calendar.wed': 'St',
  'calendar.thu': 'Št',
  'calendar.fri': 'Pi',
  'calendar.sat': 'So',
  'calendar.sun': 'Ne',
  'calendar.january': 'Január',
  'calendar.february': 'Február',
  'calendar.march': 'Marec',
  'calendar.april': 'Apríl',
  'calendar.may': 'Máj',
  'calendar.june': 'Jún',
  'calendar.july': 'Júl',
  'calendar.august': 'August',
  'calendar.september': 'September',
  'calendar.october': 'Október',
  'calendar.november': 'November',
  'calendar.december': 'December',
  'calendar.year': 'Rok',
  'calendar.deleteConfirm': 'Naozaj chcete zmazať túto udalosť?',
  'calendar.trash': 'Nedávno vymazané',
  'calendar.trashDesc': 'Udalosti sú trvalo vymazané po 30 dňoch',
  'calendar.trashEmpty': 'Žiadne vymazané udalosti',
  'calendar.trashEmptyDesc': 'Vymazané udalosti sa zobrazia tu',
  'calendar.restore': 'Obnoviť',
  'calendar.deleteForever': 'Natrvalo vymazať',
  'calendar.loading': 'Načítavanie...',
  'calendar.searchEvents': 'Hľadať udalosti...',
  'calendar.noEvents': 'Žiadne udalosti nenájdené',
  'calendar.upcoming': 'Nadchádzajúce',
  'calendar.myCalendars': 'Moje kalendáre',
  'calendar.addCategory': 'Pridať kategóriu',
  'calendar.categoryName': 'Názov kategórie...',
  'calendar.category': 'Kategória',
  'calendar.sunday': 'Nedeľa',
  'calendar.monday': 'Pondelok',
  'calendar.tuesday': 'Utorok',
  'calendar.wednesday': 'Streda',
  'calendar.thursday': 'Štvrtok',
  'calendar.friday': 'Piatok',
  'calendar.saturday': 'Sobota',
  'calendar.cat.meetings': 'Stretnutia',
  'calendar.cat.deadlines': 'Termíny',
  'calendar.cat.reminders': 'Pripomienky',
  'calendar.cat.reviews': 'Hodnotenia',
  'calendar.cat.presentations': 'Prezentácie',
  'calendar.cat.training': 'Školenia',
  'calendar.cat.travel': 'Cestovanie',
  'calendar.cat.holidays': 'Sviatky',

  // ── User Management (SK) ──────────────────────────────────────
  'userMgmt.empty.noWorkspaceTitle': 'Žiadny pracovný priestor nevybraný',
  'userMgmt.empty.noWorkspaceDesc': 'Vyberte alebo vytvorte pracovný priestor na správu členov',
  'userMgmt.empty.goToWorkspaces': 'Prejsť na pracovné priestory',
  'userMgmt.header.badge': 'Ľudia a tímy',
  'userMgmt.header.description': 'Správa členov, rolí, tímov a prístupových oprávnení',
  'userMgmt.header.invitePeople': 'Pozvať ľudí',
  'userMgmt.header.convertToOrg': 'Previesť na organizáciu',
  'userMgmt.header.transferOwnership': 'Previesť vlastníctvo',
  'userMgmt.stats.members': 'Členovia',
  'userMgmt.stats.pending': 'Čakajúce',
  'userMgmt.tabs.members': 'Členovia',
  'userMgmt.tabs.invitations': 'Pozvánky',
  'userMgmt.tabs.teams': 'Tímy',
  'userMgmt.tabs.roleGuide': 'Sprievodca rolami',
  'userMgmt.tabs.suggestions': 'Návrhy',
  'userMgmt.members.searchPlaceholder': 'Hľadať podľa mena, e-mailu alebo role...',
  'userMgmt.members.sortByRole': 'Podľa role',
  'userMgmt.members.sortByName': 'Podľa mena',
  'userMgmt.members.sortByJoined': 'Nedávno pripojení',
  'userMgmt.members.loading': 'Načítavanie členov...',
  'userMgmt.members.notFoundTitle': 'Žiadni členovia nenájdení',
  'userMgmt.members.noResults': 'Žiadne výsledky pre „{query}"',
  'userMgmt.members.noMembers': 'Žiadni členovia v tomto pracovnom priestore',
  'userMgmt.members.unknownName': 'Neznámy',
  'userMgmt.members.changeRoleTooltip': 'Zmeniť rolu',
  'userMgmt.members.removeMemberTooltip': 'Odobrať člena',
  'userMgmt.invitations.title': 'Čakajúce pozvánky',
  'userMgmt.invitations.sendInvite': 'Poslať pozvánku',
  'userMgmt.invitations.loading': 'Načítavanie pozvánok...',
  'userMgmt.invitations.emptyTitle': 'Žiadne čakajúce pozvánky',
  'userMgmt.invitations.emptyDesc': 'Pozvite ľudí na spoluprácu v pracovnom priestore',
  'userMgmt.invitations.inviteSomeone': 'Pozvať niekoho',
  'userMgmt.invitations.expires': 'Vyprší',
  'userMgmt.invitations.revokeTooltip': 'Zrušiť',
  'userMgmt.teams.title': 'Tímy',
  'userMgmt.teams.newTeam': 'Nový tím',
  'userMgmt.teams.loading': 'Načítavanie tímov...',
  'userMgmt.teams.emptyTitle': 'Zatiaľ žiadne tímy',
  'userMgmt.teams.emptyDesc': 'Tímy pomáhajú organizovať členov a spravovať prístup k položkám',
  'userMgmt.teams.createFirst': 'Vytvoriť prvý tím',
  'userMgmt.teams.noDescription': 'Bez popisu',
  'userMgmt.roles.title': 'Hierarchia rolí a oprávnení',
  'userMgmt.roles.description': 'Pochopte, čo každá rola môže robiť vo vašom pracovnom priestore',
  'userMgmt.roles.memberCount': 'člen',
  'userMgmt.roles.membersCount': 'členov',
  'userMgmt.roles.permissionsHeading': 'Oprávnenia',
  'userMgmt.perm.fullWorkspaceControl': 'Plná kontrola pracovného priestoru',
  'userMgmt.perm.transferOwnership': 'Previesť vlastníctvo',
  'userMgmt.perm.deleteWorkspace': 'Zmazať pracovný priestor',
  'userMgmt.perm.manageBilling': 'Spravovať fakturáciu',
  'userMgmt.perm.allAdminPermissions': 'Všetky oprávnenia správcu',
  'userMgmt.perm.manageSettings': 'Spravovať nastavenia pracovného priestoru',
  'userMgmt.perm.inviteRemoveMembers': 'Pozvať/odobrať členov',
  'userMgmt.perm.assignRoles': 'Prideliť role (až po Manažéra)',
  'userMgmt.perm.viewAllItems': 'Zobraziť všetky položky a analýzy',
  'userMgmt.perm.configureAI': 'Konfigurovať AI funkcie',
  'userMgmt.perm.createEditItems': 'Vytvárať a upravovať položky',
  'userMgmt.perm.approveSuggestions': 'Schvaľovať návrhy',
  'userMgmt.perm.moderateComments': 'Moderovať komentáre',
  'userMgmt.perm.useAI': 'Používať AI funkcie',
  'userMgmt.perm.createShareLinks': 'Vytvárať odkazy na zdieľanie',
  'userMgmt.perm.cannotManageMembers': 'Nemôže spravovať členov',
  'userMgmt.perm.cannotChangeSettings': 'Nemôže meniť nastavenia',
  'userMgmt.perm.viewWorkspaceItems': 'Zobraziť položky pracovného priestoru',
  'userMgmt.perm.suggestEdits': 'Navrhovať úpravy',
  'userMgmt.perm.postComments': 'Písať komentáre',
  'userMgmt.perm.cannotEditItems': 'Nemôže priamo upravovať položky',
  'userMgmt.perm.cannotCreateItems': 'Nemôže vytvárať položky',
  'userMgmt.perm.viewItems': 'Zobraziť položky',
  'userMgmt.perm.downloadFiles': 'Sťahovať súbory',
  'userMgmt.perm.viewComments': 'Zobraziť komentáre',
  'userMgmt.perm.cannotEditOrComment': 'Nemôže upravovať ani komentovať',
  'userMgmt.perm.readOnlyAccess': 'Prístup len na čítanie',
  'userMgmt.perm.cannotManageAnything': 'Nemôže nič spravovať',
  'userMgmt.perm.limitedViewAccess': 'Obmedzený prístup na čítanie',
  'userMgmt.perm.accessSharedItems': 'Prístup ku konkrétnym zdieľaným položkám',
  'userMgmt.perm.cannotSeeFullWorkspace': 'Nevidí celý pracovný priestor',
  'userMgmt.perm.mostRestrictiveRole': 'Najviac obmedzujúca rola',
  'userMgmt.perm.noManagementRights': 'Žiadne práva na správu',
  'userMgmt.suggestions.loading': 'Načítavanie návrhov...',
  'userMgmt.suggestions.emptyTitle': 'Žiadne čakajúce návrhy',
  'userMgmt.suggestions.emptyDesc': 'Návrhy úprav od recenzentov sa tu zobrazia na schválenie',
  'userMgmt.suggestions.currentLabel': 'Aktuálne',
  'userMgmt.suggestions.empty': '(prázdne)',
  'userMgmt.suggestions.suggestedLabel': 'Navrhované',
  'userMgmt.suggestions.approve': 'Schváliť',
  'userMgmt.suggestions.reject': 'Zamietnuť',
  'userMgmt.inviteModal.title': 'Pozvať ľudí',
  'userMgmt.inviteModal.emailLabel': 'E-mailová adresa',
  'userMgmt.inviteModal.emailPlaceholder': 'kolega@firma.sk',
  'userMgmt.inviteModal.roleLabel': 'Rola',
  'userMgmt.inviteModal.sendInvitation': 'Odoslať pozvánku',
  'userMgmt.roleModal.title': 'Zmeniť rolu',
  'userMgmt.roleModal.changingRoleFor': 'Zmena role pre',
  'userMgmt.roleModal.currentTag': 'Aktuálne',
  'userMgmt.transferModal.title': 'Previesť vlastníctvo',
  'userMgmt.transferModal.warningTitle': 'Túto akciu nie je možné vrátiť',
  'userMgmt.transferModal.warningDesc': 'Po prevode budete degradovaný na Org Admina.',
  'userMgmt.transferModal.newOwnerLabel': 'Nový vlastník',
  'userMgmt.transferModal.selectMemberPlaceholder': 'Vyberte člena...',
  'userMgmt.transferModal.transferBtn': 'Previesť vlastníctvo',
  'userMgmt.teamModal.title': 'Vytvoriť tím',
  'userMgmt.teamModal.nameLabel': 'Názov tímu',
  'userMgmt.teamModal.namePlaceholder': 'napr. Marketing, Vývoj',
  'userMgmt.teamModal.descLabel': 'Popis (voliteľné)',
  'userMgmt.teamModal.descPlaceholder': 'Na čom tento tím pracuje?',
  'userMgmt.teamModal.createBtn': 'Vytvoriť tím',
  'userMgmt.convertModal.title': 'Previesť na organizáciu',
  'userMgmt.convertModal.heroTitle': 'Upgradovať na organizáciu',
  'userMgmt.convertModal.heroDesc': 'Preveďte svoj osobný pracovný priestor na organizáciu a odomknite tímovú spoluprácu.',
  'userMgmt.convertModal.featureInviteTitle': 'Pozvať členov tímu',
  'userMgmt.convertModal.featureInviteDesc': 'Pridať kolegov a prideliť role',
  'userMgmt.convertModal.featureRbacTitle': 'Rolový prístup',
  'userMgmt.convertModal.featureRbacDesc': 'Riaďte, kto môže zobrazovať, upravovať a spravovať',
  'userMgmt.convertModal.featureTeamsTitle': 'Tímy a skupiny',
  'userMgmt.convertModal.featureTeamsDesc': 'Organizujte členov do tímov',
  'userMgmt.convertModal.note': 'Všetky vaše existujúce dáta budú zachované. Stanete sa vlastníkom organizácie.',
  'userMgmt.convertModal.converting': 'Prevádza sa...',
  'userMgmt.convertModal.convertNow': 'Previesť teraz',
  'userMgmt.common.cancel': 'Zrušiť',
  'userMgmt.error.personalNoInvitations': 'Osobné pracovné priestory nepodporujú pozvánky. Najprv preveďte na organizáciu.',
  'userMgmt.success.invitationSent': 'Pozvánka odoslaná na {email}',
  'userMgmt.error.invitationFailed': 'Pozvánku sa nepodarilo odoslať',
  'userMgmt.success.convertedToOrg': 'Pracovný priestor prevedený na organizáciu! Teraz môžete pozývať členov.',
  'userMgmt.error.convertFailed': 'Prevod sa nepodaril',
  'userMgmt.success.roleUpdated': 'Rola úspešne aktualizovaná',
  'userMgmt.error.roleChangeFailed': 'Zmena role sa nepodarila',
  'userMgmt.success.memberRemoved': '{name} odobratý z pracovného priestoru',
  'userMgmt.error.removeMemberFailed': 'Člena sa nepodarilo odobrať',
  'userMgmt.success.invitationRevoked': 'Pozvánka pre {email} zrušená',
  'userMgmt.error.revokeInvitationFailed': 'Pozvánku sa nepodarilo zrušiť',
  'userMgmt.success.ownershipTransferred': 'Vlastníctvo úspešne prevedené',
  'userMgmt.error.transferOwnershipFailed': 'Prevod vlastníctva sa nepodaril',
  'userMgmt.success.teamCreated': 'Tím „{name}" vytvorený',
  'userMgmt.error.createTeamFailed': 'Tím sa nepodarilo vytvoriť',
  'userMgmt.success.suggestionApproved': 'Návrh schválený',
  'userMgmt.success.suggestionRejected': 'Návrh zamietnutý',
  'userMgmt.error.reviewSuggestionFailed': 'Návrh sa nepodarilo posúdiť',
  'userMgmt.confirm.removeMember': 'Odobrať {name} z tohto pracovného priestoru?',
  'userMgmt.confirm.revokeInvitation': 'Zrušiť pozvánku pre {email}?',
  'userMgmt.confirm.transferOwnership': 'Previesť vlastníctvo? Stanete sa Org Adminom. Toto nie je možné vrátiť.',
  'userMgmt.time.today': 'Dnes',
  'userMgmt.time.yesterday': 'Včera',
  'userMgmt.time.daysAgo': 'pred {count} d.',
  'userMgmt.time.monthsAgo': 'pred {count} mes.',
  'userMgmt.time.yearsAgo': 'pred {count} r.',
  'audit.statsLogins': 'prihlásenia a odhlásenia',
  'audit.statsCrud': 'vytvorenie, aktualizácia, zmazanie',
  'audit.statsAlerts': 'vyžaduje pozornosť',
  'audit.statsReverts': 'Vrátenie',
  'audit.statsRollbacks': 'vykonané rollbacky',
  'audit.allStatuses': 'Všetky stavy',
  'audit.statusSuccess': 'Úspešné',
  'audit.statusFailed': 'Neúspešné',
  'audit.allTime': 'Celé obdobie',
  'audit.pastHour': 'Posledná hodina',
  'audit.past24Hours': 'Posledných 24 hodín',
  'audit.past7Days': 'Posledných 7 dní',
  'audit.past30Days': 'Posledných 30 dní',
  'audit.reset': 'Resetovať',
  'audit.live': 'Naživo',
  'audit.liveMonitoring': 'Živé sledovanie',
  'audit.event': 'Udalosť',
  'audit.description': 'Popis',
  'audit.status': 'Stav',
  'audit.view': 'Zobraziť',
  'audit.twoFaVerified': '2FA overené',

  // ═══ Homepage (SecureFlow) ═══
  'hp.legalBar.compliance': 'GDPR Compliant • ISO 27001 Certified • SOC 2 Type II',
  'hp.legalBar.contact': 'Kontaktujte nás',

  'hp.nav.menu': 'Menu',
  'hp.nav.home': 'Domov',
  'hp.nav.about': 'O nás',
  'hp.nav.products': 'Produkty',
  'hp.nav.features': 'Funkcie',
  'hp.nav.security': 'Bezpečnosť',
  'hp.nav.compliance': 'Súlad',
  'hp.nav.pricing': 'Cenník',
  'hp.nav.resources': 'Zdroje',
  'hp.nav.contact': 'Kontakt',
  'hp.nav.tryFree': 'Vyskúšať zadarmo',
  'hp.nav.login': 'Prihlásiť sa',
  'hp.nav.language': 'Jazyk',

  'hp.hero.badge': 'Súlad s predpismi',
  'hp.hero.title1': 'VAULT & TIMECALC',
  'hp.hero.title2': 'PRE PODNIKY',
  'hp.hero.subtitle': 'Komplexné riešenie pre správu aktív a sledovanie času s pokročilou bezpečnosťou a súladom s predpismi.',
  'hp.hero.cta': 'Začať zadarmo',
  'hp.hero.ctaSecondary': 'Kontaktovať obchod',
  'hp.hero.dashboardAlt': 'ELVOAQ Dashboard Screenshot',

  'hp.complianceBadges.title': 'CERTIFIKÁCIE A SÚLAD',
  'hp.complianceBadges.gdpr': 'GDPR Compliant',
  'hp.complianceBadges.ccpa': 'CCPA Compliant',
  'hp.complianceBadges.hipaa': 'HIPAA Compliant',
  'hp.complianceBadges.iso': 'ISO 27001',
  'hp.complianceBadges.soc2': 'SOC 2 Type II',
  'hp.complianceBadges.gdprDesc': 'Úplný súlad s nariadením GDPR pre ochranu osobných údajov',
  'hp.complianceBadges.ccpaDesc': 'Súlad s kalifornským zákonom o ochrane spotrebiteľa',
  'hp.complianceBadges.hipaaDesc': 'Súlad s HIPAA pre zdravotnícke dáta',
  'hp.complianceBadges.isoDesc': 'Medzinárodný štandard pre informačnú bezpečnosť',
  'hp.complianceBadges.soc2Desc': 'Certifikácia pre kontrolu bezpečnosti a dostupnosti',

  'hp.features.title': 'POKROČILÉ FUNKCIE',
  'hp.features.subtitle': 'Všetko čo potrebujete pre profesionálnu správu aktív a sledovanie času',
  'hp.features.ai.title': 'AI Kategorizácia',
  'hp.features.ai.desc': 'Automatické rozpoznávanie a kategorizácia položiek pomocou umelej inteligencie',
  'hp.features.duplicate.title': 'Detekcia Duplikátov',
  'hp.features.duplicate.desc': 'Inteligentné algoritmy na identifikáciu duplicitných položiek vo vašej zbierke',
  'hp.features.valuation.title': 'Odhad Hodnoty',
  'hp.features.valuation.desc': 'Real-time odhady trhovej hodnoty vašich aktív',
  'hp.features.gallery.title': 'Galéria Obrázkov',
  'hp.features.gallery.desc': 'Vysokokvalitné galérie s až 30 fotografiami na položku',
  'hp.features.search.title': 'Pokročilé Vyhľadávanie',
  'hp.features.search.desc': 'Rýchle vyhľadávanie vo všetkých vašich dátach pomocou fulltext vyhľadávania',
  'hp.features.collaboration.title': 'Tímová Spolupráca',
  'hp.features.collaboration.desc': 'Zdieľané kolekcie a prístup založený na roliach pre tímy',

  'hp.security.title': 'ENTERPRISE BEZPEČNOSŤ',
  'hp.security.subtitle': 'Vaše dáta sú chránené najvyššími štandardmi bezpečnosti',
  'hp.security.encryption.title': 'End-to-End Šifrovanie',
  'hp.security.encryption.desc': 'Všetky dáta sú šifrované počas prenosu aj uloženia',
  'hp.security.european.title': 'Európske Dátové Centrá',
  'hp.security.european.desc': 'Vaše dáta zostávajú v Európe pod prísnymi regulačnými štandardami',
  'hp.security.zeroKnowledge.title': 'Zero-Knowledge Architektúra',
  'hp.security.zeroKnowledge.desc': 'Vaše dáta sú prístupné iba vám - ani my ich nevidíme',
  'hp.security.audit.title': 'Kompletné Auditovanie',
  'hp.security.audit.desc': 'Úplná história všetkých zmien s exportnými možnosťami',

  'hp.compliance.title': 'SÚLAD S PREDPISMI',
  'hp.compliance.subtitle': 'Navrhnuté pre najprísnejšie regulačné požiadavky',
  'hp.compliance.gdpr.title': 'GDPR Súlad',
  'hp.compliance.gdpr.desc': 'Úplný súlad s európskym nariadením o ochrane osobných údajov',
  'hp.compliance.ccpa.title': 'CCPA Súlad',
  'hp.compliance.ccpa.desc': 'Súlad s kalifornským zákonom o ochrane súkromia spotrebiteľov',
  'hp.compliance.hipaa.title': 'HIPAA Súlad',
  'hp.compliance.hipaa.desc': 'Certifikácia pre zdravotnícke organizácie a spracovanie citlivých dát',

  'hp.pricing.title': 'FLEXIBILNÉ CENOVÉ PLÁNY',
  'hp.pricing.subtitle': 'Vyberte si plán, ktorý vyhovuje vašim potrebám',
  'hp.pricing.starter.title': 'Starter',
  'hp.pricing.starter.price': '€9',
  'hp.pricing.starter.period': '/mesiac',
  'hp.pricing.starter.desc': 'Ideálne pre jednotlivcov a malé tímy',
  'hp.pricing.starter.feature1': 'Až 1,000 položiek',
  'hp.pricing.starter.feature2': 'Základné AI funkcie',
  'hp.pricing.starter.feature3': 'Mobilná aplikácia',
  'hp.pricing.starter.feature4': 'E-mail podpora',
  'hp.pricing.starter.cta': 'Začať s Starter',
  'hp.pricing.professional.title': 'Profesionál',
  'hp.pricing.professional.price': '€29',
  'hp.pricing.professional.period': '/mesiac',
  'hp.pricing.professional.desc': 'Pre rastúce podniky a tímy',
  'hp.pricing.professional.badge': 'Najobľúbenejšie',
  'hp.pricing.professional.feature1': 'Neobmedzené položky',
  'hp.pricing.professional.feature2': 'Pokročilé AI funkcie',
  'hp.pricing.professional.feature3': 'Tímová spolupráca',
  'hp.pricing.professional.feature4': 'Prioritná podpora',
  'hp.pricing.professional.feature5': 'API prístup',
  'hp.pricing.professional.cta': 'Začať s Profesionál',
  'hp.pricing.enterprise.title': 'Enterprise',
  'hp.pricing.enterprise.price': 'Kontaktujte nás',
  'hp.pricing.enterprise.period': '',
  'hp.pricing.enterprise.desc': 'Pre veľké organizácie s špecifickými potrebami',
  'hp.pricing.enterprise.feature1': 'Všetky Profesionál funkcie',
  'hp.pricing.enterprise.feature2': 'Vlastné integrácie',
  'hp.pricing.enterprise.feature3': 'Dedikovaný manažér',
  'hp.pricing.enterprise.feature4': 'SLA záruky',
  'hp.pricing.enterprise.feature5': 'On-premise nasadenie',
  'hp.pricing.enterprise.cta': 'Kontaktovať obchod',

  'hp.legal.title': 'PRÁVNE DOKUMENTY',
  'hp.legal.subtitle': 'Prečítajte si naše právne dokumenty a zásady',
  'hp.legal.dpa.title': 'Dohoda o spracovaní dát',
  'hp.legal.dpa.desc': 'Podrobnosti o tom, ako spracovávame vaše dáta v súlade s GDPR',
  'hp.legal.terms.title': 'Obchodné podmienky',
  'hp.legal.terms.desc': 'Naše všeobecné obchodné podmienky a pravidlá používania',
  'hp.legal.privacy.title': 'Zásady súkromia',
  'hp.legal.privacy.desc': 'Ako chránime a používame vaše osobné údaje',
  'hp.legal.cookies.title': 'Zásady cookies',
  'hp.legal.cookies.desc': 'Informácie o používaní cookies na našich webových stránkach',

  'hp.trust.title': 'DÔVERA A ZÁRUKY',
  'hp.trust.subtitle': 'Prečo si nás vyberajú tisíce spoločností',
  'hp.trust.security.title': 'Banková úroveň bezpečnosti',
  'hp.trust.security.desc': 'Vaše dáta sú chránené rovnakými štandardami ako banky',
  'hp.trust.compliance.title': 'Regulačný súlad',
  'hp.trust.compliance.desc': 'Splňujeme všetky hlavné medzinárodné štandardy súladu',
  'hp.trust.support.title': '24/7 Podpora',
  'hp.trust.support.desc': 'Naša tím je k dispozícii vždy, keď potrebujete pomoc',
  'hp.trust.uptime.title': '99.9% dostupnosť',
  'hp.trust.uptime.desc': 'Zaručujeme vysokú dostupnosť našich služieb',

  'hp.contact.title': 'KONTAKTUJTE NÁS',
  'hp.contact.subtitle': 'Máte otázky? Radi vám pomôžeme',
  'hp.contact.form.title': 'Napíšte nám',
  'hp.contact.form.name': 'Meno *',
  'hp.contact.form.namePlaceholder': 'Vaše meno',
  'hp.contact.form.email': 'E-mail *',
  'hp.contact.form.emailPlaceholder': 'vas@email.sk',
  'hp.contact.form.company': 'Spoločnosť',
  'hp.contact.form.companyPlaceholder': 'Názov vašej spoločnosti',
  'hp.contact.form.message': 'Správa *',
  'hp.contact.form.messagePlaceholder': 'Vaša správa...',
  'hp.contact.form.submit': 'Odoslať správu',
  'hp.contact.form.sending': 'Odosielanie...',
  'hp.contact.info.title': 'Kontaktné informácie',
  'hp.contact.info.email': 'E-mail',
  'hp.contact.info.emailValue': 'hello@elvoaq.com',
  'hp.contact.info.phone': 'Telefón',
  'hp.contact.info.phoneValue': '+41 44 123 45 67',
  'hp.contact.info.address': 'Adresa',
  'hp.contact.info.addressValue': 'Bahnhofstrasse 12, 8001 Zürich, Švajčiarsko',

  'hp.footer.company': 'Spoločnosť',
  'hp.footer.about': 'O nás',
  'hp.footer.careers': 'Kariéra',
  'hp.footer.press': 'Tlač',
  'hp.footer.blog': 'Blog',
  'hp.footer.products': 'Produkty',
  'hp.footer.vault': 'ELVOAQ Vault',
  'hp.footer.timecalc': 'ELVOAQ TimeCalc',
  'hp.footer.features': 'Funkcie',
  'hp.footer.security': 'Bezpečnosť',
  'hp.footer.compliance': 'Súlad',
  'hp.footer.pricing': 'Cenník',
  'hp.footer.resources': 'Zdroje',
  'hp.footer.documentation': 'Dokumentácia',
  'hp.footer.help': 'Centrum pomoci',
  'hp.footer.api': 'API',
  'hp.footer.community': 'Komunita',
  'hp.footer.legal': 'Právne',
  'hp.footer.terms': 'Obchodné podmienky',
  'hp.footer.privacy': 'Zásady súkromia',
  'hp.footer.cookies': 'Cookies',
  'hp.footer.dpa': 'DPA',
  'hp.footer.contact': 'Kontakt',
  'hp.footer.support': 'Podpora',
  'hp.footer.sales': 'Obchod',
  'hp.footer.status': 'Stav systému',
  'hp.footer.social': 'Sociálne siete',
  'hp.footer.linkedin': 'LinkedIn',
  'hp.footer.twitter': 'Twitter',
  'hp.footer.copyright': '© 2024 ELVOAQ AG. Všetky práva vyhradené.',
  'hp.footer.location': 'Zürich, Švajčiarsko',

  'hp.cookie.title': 'Používame cookies',
  'hp.cookie.desc': 'Naše webové stránky používajú cookies pre lepší zážitok. Pokračovaním súhlasíte s našimi zásadami cookies.',
  'hp.cookie.accept': 'Prijať všetky',
  'hp.cookie.reject': 'Odmietnuť všetky',
  'hp.cookie.settings': 'Nastavenia',
};

/* ═══════════════════════════════════════════════════════════════
   Combined Translations
   ═══════════════════════════════════════════════════════════════ */

const translations = { en, de, cs, sk } as const;

/* ═══════════════════════════════════════════════════════════════
   Context & Provider
   ═══════════════════════════════════════════════════════════════ */

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

const STORAGE_KEY = 'app_language';

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored && ['en', 'de', 'cs', 'sk'].includes(stored)) return stored;
    // Default to English — users choose language explicitly in Settings
    return 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute('lang', lang);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const dict = translations[language] as Record<string, string>;
    let value = dict[key] || en[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return value;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
export default useI18n;
